import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid
} from 'recharts';
import BacktestButton, { checkBotBacktestable, UnsupportedAlert } from '../../components/backtestButton';
// Backtesting page design system — see backtestUI.js. Tokens and the
// meaning-bearing components (honesty card, verdict pills, consequential load)
// live there so the page's judgement has one implementation, not fifty inline
// copies that drift apart.
import {
  BT, Micro, Num, Panel as BtPanel, GhostBtn as BtGhostBtn,
  PrimaryBtn as BtPrimaryBtn, Chip as BtChip, TextInput as BtTextInput,
  VerdictPill, surfaceTone, rowVerdict, ProgressStepper, RISK_PHASES, HonestyCard,
  TrustBanner, SplitBar, ConfirmBar, SuccessNote, Notice, SurfaceCells,
  LB_COLUMNS, LbHideSmall,
} from './backtestUI';

import botApiV2 from '../../services/botapi';
import AIStrategyChat from '../../components/AIStrategyChat';
import AIStrategyService from '../../services/aiStrategyService';
import StrategyTemplates from '../../components/strategyTemplates';
import StrategyBuilder, {
  SPOT_TEMPLATES,
  templateToConditions,
  ConditionsPanel,
  CANDLE_INDICATORS,
  TICK_INDICATORS,
  FUTURES_INDICATORS,
} from '../../components/conditionBuilder';
import PositionSizeSection from '../../components/PositionSizeSection';
import AITradingAdvisor from '../../components/aiTradingAdvisor';


import {
  StockStatusBadgeRow,
  BuyingPowerChip,
} from '../../components/StockStatusBadges';
import { HedgePositionPanel } from '../../components/HedgePositionPanel';
import { PendingOrderProgress } from '../../components/PendingOrderProgress';
import { useAlpacaAccountStatus, getAccountStatusForBot } from '../../hooks/useAlpacaAccountStatus';

// Phase B
import { AssetClassChip, useAssetClass, getAssetClassFromExchange } from '../../components/AssetClassChip';
import { ShortabilityBadge } from '../../components/ShortabilityBadge';
import { ExtendedHoursToggle } from '../../components/ExtendedHoursToggle';
import OptionsInstrumentSection from '../../components/OptionsInstrumentSection';
import { formatOccForDisplay } from '../../utils/occSymbol';
import { ShortEligibilityWarning } from '../../components/ShortEligibilityWarning';

import { useShortability } from '../../hooks/useShortability';

import {
  buildLogicFromConditions,
  canRepresentInBuilder,
  extractIndicatorsFromConditions,
  extractIndicatorsFromLogic,
  parseLogicToConditions,
  validateLogicExpression
} from '../../components/strategyUtils';

import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  Typography,
  Box,
  Stack,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  InputAdornment,
  Snackbar, Alert

} from '@mui/material';
import {
  AddCircleOutline,
  Bolt,
  RemoveCircleOutline,
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Settings,
  Delete,
  Edit,
  PlayArrow,
  Pause,
  Visibility,
  VisibilityOff,
  Functions,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Schedule,
  AttachMoney,
  ShowChart,
  History,
  BarChart,
  Timeline,
  DateRange,
  Cancel,
  CheckCircle,
  RadioButtonUnchecked,
  WarningAmber,
  Search,
  ContentCopy,
  Email
} from '@mui/icons-material';
import exchangeApiService from '../../services/exchangeApiService';
import tradingBotService from '../../services/tradingBotService';
import backtestService from '../../services/backtestService'; // Add this at top
import { defaultBacktestCosts, backtestCostNote, isBacktestableBot, getBacktestAssetClass, BT_OPTION_COSTS } from '../../utils/backtestCosts';
import { useOnboarding } from '../../components/Onboarding/OnboardingProvider';


import Toolbar from '../../components/Toolbar/Toolbar';
import Logout from '../../containers/Login/Logout';

import { format } from 'date-fns';
import { io } from "socket.io-client";
import Autocomplete from '@mui/material/Autocomplete';
import symbolService from '../../services/symbolService';
// The scanner's universe resolver — Redis-cached symbol metadata including 24h
// volume, populated by the metadata cron. Reused rather than duplicated.
import scannerApi from '../../services/scannerApi';
// Same evaluator the backend uses, for client-side syntax checking of raw
// expressions. Only `parse` is needed - nothing is evaluated in the browser.
import { baseExchangeLabel, exchangeDisplayName, getAccountMeta, alpacaModeSuffix } from '../../config/exchangeConfig';
import ExchangeOptionRow from '../../components/ExchangeOptionRow';




const socket = io(process.env.REACT_APP_SOCKET_URL || "http://localhost:3005", {
  transports: ["websocket"],     // 👈 match the server
});

// ── AutoTrader matte/steel-blue theme (shared design system w/ Portfolio) ──
const AT_SANS = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const AT_MONO = '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace';
const AT = {
  bg: '#0a0c11', card: '#0f1218', inner: '#0b0e15', tile: '#070a0f',
  bright: '#f2f6ff', text: '#e8edf7', body: '#9aa6bd', muted: '#6f7c96', faint: '#5e6a82',
  accent: '#4d8fc7', accent2: '#3f6fb0', positive: '#46b88a', negative: '#d97b72', amber: '#d9a441',
  hairline: 'rgba(255,255,255,.06)',
};
const AT_THEME = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: AT.accent },
    success: { main: AT.positive },
    error: { main: AT.negative },
    warning: { main: AT.amber },
    info: { main: AT.accent },
    background: { default: AT.bg, paper: AT.card },
    divider: AT.hairline,
  },
  shape: { borderRadius: 12 },
  typography: { fontFamily: AT_SANS },
  components: {
    // Make every MUI field/select in the wizard match the handoff's matte inputs.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#0b0e15',
          borderRadius: 9,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4d8fc7', borderWidth: 1 },
        },
        input: { color: '#f2f6ff' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#7b88a2', '&.Mui-focused': { color: '#4d8fc7' } } } },
    MuiPaper: { styleOverrides: { outlined: { borderColor: 'rgba(255,255,255,.06)' } } },
    MuiSlider: { styleOverrides: { root: { color: '#4d8fc7' } } },
    MuiStepConnector: { styleOverrides: { line: { borderColor: 'rgba(255,255,255,.12)' } } },
  },
});

// Mono numeric/label span used across the restyled AutoTrader surfaces.
const Mono = (props) => <Box component="span" {...props} sx={{ fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', ...(props.sx || {}) }} />;

// Create-bot glass-cockpit right rail (handoff §8a). The single source of truth
// for the wizard — identity, illustrative estimate, readiness gates (clickable),
// logic-at-a-glance and plain-language risk — all derived from wizard state.
// Is this bot a US-equity bot? Checks trade_info.asset_class first, then the
// asset_class COLUMN — bots created before trade_info started mirroring the
// column carry it only in the second place.
function isStockBot(bot) {
  if (!bot) return false;
  let ti = {};
  try { ti = JSON.parse(bot.trade_info || '{}'); } catch { /* column fallback */ }
  return (ti.asset_class || bot.asset_class) === 'stock_equity';
}

function RailRow({ k, v, c }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
      <Typography sx={{ fontSize: '12px', color: AT.muted }}>{k}</Typography>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: c || AT.text, fontFamily: AT_MONO }}>{v}</Typography>
    </Box>
  );
}

function StrategyRail({ readiness, newBot, buyConditions, sellConditions, profitStop, lossStop, exchangeLabel, onJump, expressionMode = false }) {
  const amount = Number(newBot.positionValue || newBot.tradeAmount) || 0;
  const tp = Number(profitStop) || 0, sl = Number(lossStop) || 0;
  const riskAmt = amount * sl / 100, rewardAmt = amount * tp / 100;
  const rr = sl > 0 ? tp / sl : 0;
  const band = rr >= 2 ? { label: 'Favorable', c: AT.positive } : rr >= 1 ? { label: 'Balanced', c: AT.accent } : { label: 'Aggressive', c: AT.negative };
  const orderLabel = { market: 'Market', limit: 'Limit', aggressive_limit: 'Smart Limit' }[newBot.orderExecution] || 'Market';
  const signalLabel = newBot.signalSource === 'tradingview' ? 'Webhook' : 'Internal';
  const sizeUnit = newBot.sizeMode === 'base' ? (newBot.symbol || '').split('/')[0] : (newBot.funding_asset || 'USDT');
  const sizeLabel = amount > 0 ? `${amount.toLocaleString()} ${sizeUnit}` : '—';

  const Section = ({ label, children, mb = 2.25 }) => (
    <Box sx={{ mb }}>
      <Mono sx={{ fontSize: '9.5px', letterSpacing: '.12em', color: AT.muted2, display: 'block', mb: '8px' }}>{label}</Mono>
      {children}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: AT.card, border: `1px solid ${AT.hairline}`, borderRadius: '14px', p: '18px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '4px' }}>
        <Mono sx={{ fontSize: '9.5px', letterSpacing: '.14em', color: AT.accentText }}>STRATEGY</Mono>
        <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '9px', fontWeight: 700, letterSpacing: '.08em', color: AT.amber, background: 'rgba(217,164,65,.12)', border: '1px solid rgba(217,164,65,.34)', px: '7px', py: '2px', borderRadius: '5px' }}>PAPER</Box>
      </Box>
      <Typography sx={{ fontSize: '17px', fontWeight: 700, color: AT.bright, mb: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{newBot.name?.trim() || 'Untitled bot'}</Typography>
      <Typography sx={{ fontSize: '12px', color: AT.body, mb: '16px' }}>{newBot.symbol || '—'} · {signalLabel}</Typography>

      <Section label={`READINESS · ${readiness.doneCount}/${readiness.total}`}>
        <Box sx={{ height: 6, borderRadius: '4px', background: 'rgba(255,255,255,.06)', overflow: 'hidden', mb: '10px' }}>
          <Box sx={{ width: `${(readiness.doneCount / readiness.total) * 100}%`, height: '100%', background: `linear-gradient(90deg,${AT.accent2},${AT.accent})`, transition: 'width .3s' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {readiness.gates.map((g) => (
            <Box key={g.key} onClick={() => onJump && onJump(g.step)} sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', borderRadius: '6px', px: '4px', py: '3px', '&:hover': { background: 'rgba(255,255,255,.03)' } }}>
              {g.done ? <CheckCircle sx={{ fontSize: 15, color: AT.positive }} /> : <RadioButtonUnchecked sx={{ fontSize: 15, color: AT.muted }} />}
              <Typography sx={{ fontSize: '12px', color: g.done ? AT.muted : AT.text, textDecoration: g.done ? 'line-through' : 'none' }}>{g.label}</Typography>
            </Box>
          ))}
        </Box>
      </Section>

      <Section label="LOGIC AT A GLANCE">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <RailRow k="Exchange" v={exchangeLabel || '—'} />
          <RailRow k="Order type" v={orderLabel} />
          <RailRow k="Size" v={sizeLabel} />
          {/* Expression mode has no rows to count. Reporting 0 would read as
              "no logic" for a bot that has plenty. */}
          <RailRow k="Entry rules" v={expressionMode ? 'expression' : String(buyConditions.length)} c={AT.positive} />
          <RailRow k="Exit rules" v={expressionMode ? 'expression' : String(sellConditions.length)} c={AT.negative} />
        </Box>
      </Section>

      <Section label="RISK PROFILE" mb={0}>
        <Typography sx={{ fontSize: '12.5px', color: AT.text, mb: '10px' }}>
          {amount > 0
            ? <>Risk <Box component="b" sx={{ color: AT.negative }}>{atShortUsd(riskAmt)}</Box> to make <Box component="b" sx={{ color: AT.positive }}>{atShortUsd(rewardAmt)}</Box> per trade</>
            : 'Set a position size to see per-trade risk'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.positive, background: 'rgba(70,184,138,.12)', border: '1px solid rgba(70,184,138,.32)', px: '8px', py: '3px', borderRadius: '6px' }}>TP {tp}%</Box>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.negative, background: 'rgba(217,123,114,.12)', border: '1px solid rgba(217,123,114,.32)', px: '8px', py: '3px', borderRadius: '6px' }}>SL {sl}%</Box>
          <Box component="span" sx={{ ml: 'auto', fontFamily: AT_MONO, fontSize: '11px', fontWeight: 700, color: band.c }}>{rr ? rr.toFixed(2) : '—'} R:R · {band.label}</Box>
        </Box>
      </Section>
    </Box>
  );
}

// ── Per-bot "Total Value" equity chart (handoff §6b) ──
const atShortUsd = (n) => {
  const a = Math.abs(Number(n) || 0), sgn = n < 0 ? '-' : '';
  return a >= 1000 ? `${sgn}$${(a / 1000).toFixed(a >= 100000 ? 0 : 1)}K` : `${sgn}$${Math.round(a)}`;
};
function atBuildPath(values) {
  const w = 600, h = 150, pad = 10;
  const vals = values.length >= 2 ? values : [values[0] ?? 0, values[0] ?? 0];
  const min = Math.min(...vals), max = Math.max(...vals), rng = (max - min) || 1, n = vals.length;
  const flat = max === min;
  const xs = (i) => (i / (n - 1)) * w;
  const ys = (v) => (flat ? h / 2 : h - ((v - min) / rng) * (h - pad * 2) - pad);
  let d = `M${xs(0).toFixed(1)} ${ys(vals[0]).toFixed(1)}`;
  for (let i = 1; i < n; i++) {
    const x0 = xs(i - 1), y0 = ys(vals[i - 1]), x1 = xs(i), y1 = ys(vals[i]), xc = (x0 + x1) / 2;
    d += ` C${xc.toFixed(1)} ${y0.toFixed(1)} ${xc.toFixed(1)} ${y1.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }
  return { line: d, area: `${d} L${w} ${h} L0 ${h} Z`, lastX: xs(n - 1).toFixed(1), lastY: ys(vals[n - 1]).toFixed(1), firstY: ys(vals[0]).toFixed(1), min, max };
}
// Path on a SHARED y-scale (so strategy + benchmark lines align). Backtest §7d.
function atPathScaled(values, min, max, w = 600, h = 150, pad = 8) {
  const vals = values.length >= 2 ? values : [values[0] ?? 0, values[0] ?? 0];
  const rng = (max - min) || 1, n = vals.length;
  const xs = (i) => (i / (n - 1)) * w;
  const ys = (v) => (max === min ? h / 2 : h - ((v - min) / rng) * (h - pad * 2) - pad);
  let d = `M${xs(0).toFixed(1)} ${ys(vals[0]).toFixed(1)}`;
  for (let i = 1; i < n; i++) {
    const x0 = xs(i - 1), y0 = ys(vals[i - 1]), x1 = xs(i), y1 = ys(vals[i]), xc = (x0 + x1) / 2;
    d += ` C${xc.toFixed(1)} ${y0.toFixed(1)} ${xc.toFixed(1)} ${y1.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }
  return { line: d, area: `${d} L${w} ${h} L0 ${h} Z`, lastX: xs(n - 1).toFixed(1), lastY: ys(vals[n - 1]).toFixed(1) };
}

function BotEquityChart({ initial, equity }) {
  const uid = React.useId();
  const end = Number(equity);
  if (!Number.isFinite(end)) return null;
  const startRaw = Number(initial);
  const haveStart = Number.isFinite(startRaw) && startRaw > 0;
  const start = haveStart ? startRaw : end;
  const pct = haveStart && start !== 0 ? ((end - start) / start) * 100 : 0;
  const up = pct >= 0;
  // Smooth start→current curve (real per-bot time series not yet stored).
  const n = 24, values = [];
  for (let i = 0; i < n; i++) { const t = i / (n - 1); values.push(start + (end - start) * t); }
  const p = atBuildPath(values);
  const color = up ? AT.accent : AT.negative;
  return (
    <Box sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: AT.hairline, borderRadius: 2, bgcolor: AT.inner }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: AT.tile, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShowChart sx={{ color: AT.accent, fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: AT.bright }}>Total Value</Typography>
            <Typography sx={{ fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: 18, fontWeight: 600, color: AT.bright }}>{atShortUsd(end)}</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 600, color: up ? AT.positive : AT.negative }}>
          {(pct >= 0 ? '+' : '') + pct.toFixed(2)}% since start
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 150, py: '4px', minWidth: 42, textAlign: 'right', fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: '10px', color: AT.faint }}>
          <span>{atShortUsd(p.max)}</span><span>{atShortUsd((p.max + p.min) / 2)}</span><span>{atShortUsd(p.min)}</span>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, color }}>
          <svg viewBox="0 0 600 150" preserveAspectRatio="none" style={{ width: '100%', height: 150, display: 'block' }}>
            <defs><linearGradient id={`bot-area-${uid}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="currentColor" stopOpacity="0.22" /><stop offset="1" stopColor="currentColor" stopOpacity="0" /></linearGradient></defs>
            <g stroke="rgba(255,255,255,.05)" strokeWidth="1"><line x1="0" y1="10" x2="600" y2="10" /><line x1="0" y1="75" x2="600" y2="75" /><line x1="0" y1="140" x2="600" y2="140" /></g>
            <line x1="0" y1={p.firstY} x2="600" y2={p.firstY} stroke="rgba(255,255,255,.18)" strokeWidth="1" strokeDasharray="4 5" />
            <path d={p.area} fill={`url(#bot-area-${uid})`} />
            <path d={p.line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={p.lastX} cy={p.lastY} r="3.5" fill="currentColor" />
          </svg>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '8px', fontFamily: AT_MONO, fontSize: '10px', color: AT.faint }}>
            <span>24h ago</span><span>18h</span><span>12h</span><span>6h</span><span>now</span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---- trade normalization helpers (entry/exit rendering) ----
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const toMs = (t) => {
  if (t == null) return undefined;
  const n = Number(t);
  if (Number.isFinite(n)) return n < 1e12 ? n * 1000 : n;
  const d = Date.parse(t);
  return Number.isFinite(d) ? d : undefined;
};

const isNum = (v) => Number.isFinite(Number(v));

const fmtPct = (v, d = 2) =>
  isNum(v) ? `${Number(v).toFixed(d)}%` : '—';

// Leading + so a positive figure is unmistakable beside a negative one in the
// same row. Returns an em dash for anything non-numeric rather than throwing:
// several server summaries legitimately omit these fields (a batch where every
// symbol failed carries no median at all), and a raw .toFixed() on that took
// down the whole screen.
const fmtPctSigned = (v, d = 2) =>
  isNum(v) ? `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(d)}%` : '—';

const fmtMoney = (v, d = 2) => {
  if (!isNum(v)) return '—';
  const n = Number(v);
  const s = n < 0 ? '-' : '';
  return `${s}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};

const fmtRatio = (v, d = 2) => {
  if (v === Infinity) return '∞';
  return isNum(v) ? Number(v).toFixed(d) : '—';
};

// Convert raw user_history rows ({ date, buy_sell_history }) into the
// { _ts, timestamp, text } entries the bot history table consumes. `_ts` is a
// sortable epoch so older entries paged in later merge into the right order.
const HISTORY_PAGE_SIZE = 50;

function mapHistoryRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((entry) => {
    let text = entry.buy_sell_history;
    try { text = JSON.parse(text); } catch (_) {}
    const ms = new Date(entry.date).getTime();
    return {
      _ts: Number.isFinite(ms) ? ms : 0,
      timestamp: new Date(entry.date).toLocaleString(),
      text,
    };
  });
}

// Merge new history entries into an existing per-bot array: dedup by the
// composite key and keep oldest-first order (the table reverses for display).
function historyKeyOf(e) {
  const t = (e && e.text) || {};
  return `${e.timestamp}|${t.side}|${t.price}|${t.quantity}|${t.symbol}`;
}
function mergeHistoryEntries(existing = [], incoming = []) {
  const byKey = new Map();
  for (const e of existing) byKey.set(historyKeyOf(e), e);
  for (const e of incoming) { const k = historyKeyOf(e); if (!byKey.has(k)) byKey.set(k, e); }
  return Array.from(byKey.values()).sort((a, b) => (a._ts || 0) - (b._ts || 0));
}

function computeStatsFromHistory(historyEntries = []) {
  if (!Array.isArray(historyEntries) || historyEntries.length === 0) return null;

  const sorted = historyEntries
    .map(e => ({ ts: new Date(e.timestamp).getTime(), t: e.text || {} }))
    .filter(e =>
      Number.isFinite(e.ts) &&
      e.t &&
      e.t.side &&
      e.t.type !== 'order_pending' &&
      e.t.type !== 'order_cancelled' &&
      e.t.type !== 'order_expired' &&
      e.t.type !== 'reconciliation'
    )
    .sort((a, b) => a.ts - b.ts);

  const buyQueue = []; // FIFO: { qty, costPerUnit, feePerUnit }
  let trades = 0, wins = 0, losses = 0;
  let grossProfit = 0, grossLoss = 0;
  let openQty = 0, openCost = 0;

  for (const { t } of sorted) {
    const side = String(t.side).toUpperCase();
    const qty = Number(t.quantity) || 0;
    const cost = Number(t.cost) || 0;
    const proceeds = Number(t.proceeds) || 0;
    const fee = Number(t.fee) || 0;
    if (qty <= 0) continue;

    if (side === 'BUY') {
      buyQueue.push({
        qty,
        costPerUnit: cost / qty,
        feePerUnit: fee / qty,
      });
      openQty += qty;
      openCost += cost;
    } else if (side === 'SELL') {
      let remaining = qty;
      // `proceeds` in buy_sell_history is proceedsAfterFees — ALREADY net of the
      // sell-side fee (see tradeExecutors.computeProceeds / historyPayload). So we
      // must NOT subtract the sell `fee` again here; the original `pnl -= fee`
      // double-counted it. The buy-side `cost`/costPerUnit is gross notional, so
      // the buy-side feePerUnit IS still subtracted to get a true net P&L.
      const proceedsPerUnit = proceeds / qty;
      let pnl = 0;
      let matchedQty = 0;

      while (remaining > 1e-12 && buyQueue.length > 0) {
        const head = buyQueue[0];
        const m = Math.min(remaining, head.qty);
        // Net proceeds per unit minus gross cost minus the prorated buy-side fee.
        pnl += m * (proceedsPerUnit - head.costPerUnit - head.feePerUnit);
        head.qty -= m;
        remaining -= m;
        openQty -= m;
        openCost -= m * head.costPerUnit;
        matchedQty += m;
        if (head.qty <= 1e-12) buyQueue.shift();
      }

      // Only count the trade for the portion that actually closed a position;
      // an unmatched sell (no open long) isn't charged a fee or counted.
      if (matchedQty > 1e-12) {
        trades += 1;
        if (pnl >= 0) { wins += 1; grossProfit += pnl; }
        else { losses += 1; grossLoss += Math.abs(pnl); }
      }
    }
  }

  return {
    Trades: trades,
    Wins: wins,
    Losses: losses,
    WinRatePct: trades > 0 ? (wins / trades) * 100 : 0,
    GrossProfit: grossProfit,
    GrossLoss: grossLoss,
    ProfitFactor: grossLoss > 0
      ? grossProfit / grossLoss
      : (grossProfit > 0 ? Infinity : 0),
    RealizedPnL: grossProfit - grossLoss,
    OpenPositionQty: openQty,
    OpenPositionCost: openCost,
  };
}

const getEmail = () => (localStorage.getItem('email') || '').trim() || null;


const API_BASE_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:3005"; // matches your socket base
const WEBHOOK_BASE_URL = "https://trade.cryptologics-ai.com"; // ✅ Force production webhook domain

const TV_WEBHOOK_PATH = "/api/webhooks/tradingview";

// botId can be "BOT_ID" for template usage in UI
const buildTradingViewWebhookUrl = (botId, secret) => {
  const s = (secret || "").trim();
  const qs = new URLSearchParams();
  qs.set("bot_id", String(botId));
  if (s) qs.set("secret", s);
  return `${WEBHOOK_BASE_URL}${TV_WEBHOOK_PATH}?${qs.toString()}`;
};




function FuturesBadge({ bot }) {
  // Parse trade_info to get futures config
  const ti = React.useMemo(() => {
    try { return JSON.parse(bot.trade_info || '{}'); }
    catch { return {}; }
  }, [bot.trade_info]);

  if (ti.marketType !== 'futures') return null;

  const f = ti.futures || {};
  return (
    <>
      <Chip
        size="small"
        label={`Futures ${f.leverage || 1}x`}
        color="warning"
        variant="outlined"
        sx={{ height: 22, fontWeight: 600 }}
      />
      <Chip
        size="small"
        label={f.marginMode === 'isolated' ? 'Isolated' : 'Cross'}
        variant="outlined"
        sx={{ height: 22 }}
      />
      {f.positionSide === 'hedge' && (
        <Chip
          size="small"
          label="Hedge"
          variant="outlined"
          color="info"
          sx={{ height: 22 }}
        />
      )}
    </>
  );
}

function OrderTypeBadge({ bot }) {
  const ti = React.useMemo(() => {
    try { return JSON.parse(bot.trade_info || '{}'); }
    catch { return {}; }
  }, [bot.trade_info]);

  const oe = ti.orderExecution || {};
  const mode = oe.mode || 'market';

  if (mode === 'market') return null; // Don't show badge for default

  const config = ORDER_EXECUTION_MODES[mode] || {};
  return (
    <Tooltip title={config.description || ''}>
      <Chip
        size="small"
        label={config.label || mode}
        variant="outlined"
        sx={{ height: 22 }}
      />
    </Tooltip>
  );
}


function validateFuturesConfig(bot, userExchanges) {
  const errors = [];
  if (bot.marketType !== 'futures') return errors;

  const exchange = userExchanges.find(ex => ex.id === bot.exchangeId);
  if (!exchangeSupportsFutures(exchange)) {
    errors.push(`${exchange?.exchange || 'This exchange'} does not support futures trading.`);
  }

  const lev = Number(bot.leverage);
  if (!Number.isFinite(lev) || lev < 1 || lev > 125) {
    errors.push('Leverage must be between 1x and 125x.');
  }

  if (!MARGIN_MODES.includes(bot.marginMode)) {
    errors.push('Invalid margin mode. Choose "cross" or "isolated".');
  }

  return errors;
}


function FuturesInfoPanel({ bot, futuresData }) {
  const ti = React.useMemo(() => {
    try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; }
  }, [bot.trade_info]);

  if (ti.marketType !== 'futures') return null;

  const d = futuresData || {};
  const f = ti.futures || {};

  const markIndexSpread = (d.markPrice && d.indexPrice && d.indexPrice !== 0)
    ? ((d.markPrice - d.indexPrice) / d.indexPrice * 100)
    : null;

  const fundingAnnualized = d.lastFundingRate != null
    ? (d.lastFundingRate * 3 * 365 * 100) // 3 funding periods/day × 365
    : null;

  // Estimated liq distance (simplified)
  const leverage = Number(f.leverage) || 1;
  const liqDistancePct = leverage > 0 ? (100 / leverage) : null;

  const fmtRate = (v, digits = 4) => {
    if (v == null || !Number.isFinite(v)) return '—';
    return `${(v * 100).toFixed(digits)}%`;
  };

  const fmtPrice = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtOI = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v.toLocaleString();
  };

  const fundingColor = d.lastFundingRate == null
    ? '#9aa6bd'
    : d.lastFundingRate > 0 ? '#46b88a' : d.lastFundingRate < 0 ? '#d97b72' : '#9aa6bd';

  const spreadColor = markIndexSpread == null
    ? '#9aa6bd'
    : Math.abs(markIndexSpread) > 0.05 ? '#d9a441' : '#46b88a';

  const items = [
    {
      label: 'Funding Rate',
      value: fmtRate(d.lastFundingRate),
      sub: fundingAnnualized != null ? `${fundingAnnualized.toFixed(1)}% ann.` : null,
      color: fundingColor,
      tooltip: d.lastFundingRate > 0
        ? 'Longs pay shorts'
        : d.lastFundingRate < 0
          ? 'Shorts pay longs'
          : null,
    },
    {
      label: 'Next Funding',
      value: d.nextFundingTime
        ? new Date(d.nextFundingTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '—',
      sub: d.nextFundingTime
        ? `in ${Math.max(0, Math.round((d.nextFundingTime - Date.now()) / 60_000))} min`
        : null,
    },
    {
      label: 'Mark Price',
      value: fmtPrice(d.markPrice),
    },
    {
      label: 'Index Price',
      value: fmtPrice(d.indexPrice),
    },
    {
      label: 'Mark/Index Spread',
      value: markIndexSpread != null ? `${markIndexSpread.toFixed(4)}%` : '—',
      color: spreadColor,
      tooltip: markIndexSpread != null
        ? (markIndexSpread > 0 ? 'Futures premium (contango)' : 'Futures discount (backwardation)')
        : null,
    },
    {
      label: 'Open Interest',
      value: fmtOI(d.openInterest),
    },
    {
      label: 'Leverage',
      value: `${leverage}x`,
      color: leverage > 20 ? '#d97b72' : leverage > 5 ? '#d9a441' : '#46b88a',
    },
    {
      label: 'Est. Liq Distance',
      value: liqDistancePct != null ? `~${liqDistancePct.toFixed(1)}%` : '—',
      color: liqDistancePct != null && liqDistancePct < 5 ? '#d97b72' : '#d9a441',
      tooltip: 'Approximate % move to liquidation (isolated, no extra margin)',
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        p: 2.5,
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.06)',
        borderRadius: 2,
        bgcolor: '#0f1218',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1.5,
              bgcolor: '#070a0f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TrendingUp sx={{ color: '#d9a441', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>
            Futures Market Data
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {d.lastFundingRate != null && (
            <Chip
              size="small"
              label={d.lastFundingRate > 0 ? 'Longs Pay' : d.lastFundingRate < 0 ? 'Shorts Pay' : 'Neutral'}
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: d.lastFundingRate > 0 ? '#052e16' : d.lastFundingRate < 0 ? '#2a0a0a' : '#0f1218',
                color: fundingColor,
                border: '1px solid',
                borderColor: fundingColor,
              }}
            />
          )}
          {d.fetchedAt && (
            <Chip
              size="small"
              variant="outlined"
              label={`Updated ${new Date(d.fetchedAt).toLocaleTimeString()}`}
              sx={{ height: 22, fontSize: '0.65rem', color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)' }}
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        {items.map((item, idx) => (
          <Grid item xs={6} sm={3} key={idx}>
            <Tooltip title={item.tooltip || ''} arrow disableHoverListener={!item.tooltip}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#070a0f',
                  border: '1px solid',
                  borderColor: 'rgba(255,255,255,0.06)',
                  minHeight: 72,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  cursor: item.tooltip ? 'help' : 'default',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9aa6bd',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.6rem',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: item.color || '#f2f6ff',
                    fontFamily: '"SF Mono", Monaco, Consolas, monospace',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {item.value}
                </Typography>
                {item.sub && (
                  <Typography variant="caption" sx={{ color: '#6f7c96', fontSize: '0.6rem' }}>
                    {item.sub}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}


function SymbolFeeDisplay({ symbol, exchangeId, userExchanges, symbolsByExchange, orderExecutionMode }) {
  // Resolve symbol metadata from the cache
  const meta = useMemo(() => {
    if (!symbol || !exchangeId) return null;
    const ex = userExchanges.find(e => e.id === exchangeId);
    const cacheKey = ex?.exchange || ex?.name || exchangeId;
    const list = symbolsByExchange?.[cacheKey] || [];
    return list.find(s => String(s.symbol).toUpperCase() === String(symbol).toUpperCase()) || null;
  }, [symbol, exchangeId, userExchanges, symbolsByExchange]);

  // Derive exchange identity for accurate copy + attribution.
  // Bug fix: the panel previously hardcoded "Binance" everywhere, so Alpaca
  // stocks (which legitimately set is_zero_fee=true for commission-free trading)
  // rendered "Binance has waived fees" — wrong brand, confusing UX.
  const exchangeMeta = useMemo(() => {
    const ex = userExchanges.find(e => e.id === exchangeId);
    const raw = String(ex?.exchange || '').toLowerCase();
    const isAlpaca = raw.startsWith('alpaca');
    const isBinance = raw.startsWith('binance');
    const displayName = ex?.name || ex?.exchange || 'this exchange';
    return {
      isAlpaca,
      isBinance,
      brand: isAlpaca ? 'Alpaca' : (isBinance ? 'Binance' : displayName),
      feeWord: isAlpaca ? 'commissions' : 'fees',
    };
  }, [exchangeId, userExchanges]);

  if (!meta) return null;

  const makerFee = meta.default_maker_fee;
  const takerFee = meta.default_taker_fee;
  const isZero = meta.is_zero_fee;
  const hasFees = makerFee != null && takerFee != null;

  if (!hasFees && !isZero) {
    // No fee data — don't show anything (avoid misleading empty state)
    return null;
  }

  // Predict effective fee based on order type
  // - market / aggressive_limit (fallback) → taker fee
  // - limit (post_only) → maker fee
  // - aggressive_limit → uncertain (likely taker)
  const effectiveFee = (() => {
    if (isZero) return 0;
    if (orderExecutionMode === 'limit') return makerFee;
    return takerFee; // market and aggressive_limit lean taker
  })();

  const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(3)}%`);

  if (isZero) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mt: 1.5,
          bgcolor: '#052e16',
          borderColor: '#46b88a',
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CheckCircle sx={{ color: '#46b88a', fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
<Typography variant="body2" fontWeight={600} sx={{ color: '#46b88a' }}>
            {exchangeMeta.isAlpaca ? 'Commission-Free Trading' : 'Zero Fee Trading'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#a7f3d0' }}>
            {exchangeMeta.isAlpaca
              ? `${exchangeMeta.brand} offers commission-free trading on ${symbol}. Your bot pays $0 in commissions per trade. (Regulatory and SEC fees may still apply on sells.)`
              : `${exchangeMeta.brand} has waived maker and taker ${exchangeMeta.feeWord} on ${symbol}. Your bot pays $0 in ${exchangeMeta.feeWord} per trade.`
            }
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mt: 1.5,
        borderRadius: 1.5,
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            Trading Fees
          </Typography>
        </Box>
<Tooltip title={exchangeMeta.isAlpaca
          ? 'Source: Alpaca trading account. Stocks are commission-free; regulatory fees may apply.'
          : `Source: ${exchangeMeta.brand} VIP-tier service account. Updated every 5 minutes.`
        }>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            via {exchangeMeta.brand}
          </Typography>
        </Tooltip>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={4}>
          <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Maker
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
              {pct(makerFee)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              Limit orders
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Taker
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
              {pct(takerFee)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              Market orders
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: '#0f1218',
              border: '1px solid',
              borderColor: '#3f6fb0',
            }}
          >
            <Typography variant="caption" sx={{ color: '#4d8fc7', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Your Fee
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', color: '#4d8fc7' }}>
              {pct(effectiveFee)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9aa6bd', fontSize: '0.6rem' }}>
              {orderExecutionMode === 'limit' ? 'Maker rate' : 'Taker rate'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}

// ============================================================================
// 4. UI COMPONENT — FuturesConfig (add inside Step 1, after Signal Source)
// ============================================================================

// 🔧 CHANGE: Add this as a new <Grid item xs={12}> block in your Step 1 (activeStep === 0),
// AFTER the Signal Source section and BEFORE the "Order Size In" select.

function FuturesConfigSection({ newBot, setNewBot, userExchanges }) {
  const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
  const futuresAvailable = exchangeSupportsFutures(selectedExchange);
  const isFutures = newBot.marketType === 'futures';

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 2,
          borderColor: isFutures ? 'warning.main' : 'divider',
          transition: 'border-color 0.2s',
        }}
      >
        {/* Market Type Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountBalance color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Market Type
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: isFutures ? 3 : 0 }}>
          {/* Spot card */}
          <Paper
            variant="outlined"
            onClick={() => setNewBot(prev => ({ ...prev, marketType: 'spot', leverage: 1 }))}
            sx={{
              flex: 1, p: 2, cursor: 'pointer',
              borderColor: !isFutures ? 'primary.main' : 'divider',
              borderWidth: !isFutures ? 2 : 1,
              bgcolor: !isFutures ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.light' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccountBalance color={!isFutures ? 'primary' : 'action'} fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>Spot</Typography>
              {!isFutures && <Chip size="small" label="Selected" color="primary" sx={{ ml: 'auto' }} />}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Buy and sell assets directly. No leverage, no liquidation risk.
            </Typography>
          </Paper>

          {/* Futures card */}
          <Paper
            variant="outlined"
            onClick={() => {
              if (!futuresAvailable && newBot.exchangeId) return; // disabled
              setNewBot(prev => ({ ...prev, marketType: 'futures' }));
            }}
            sx={{
              flex: 1, p: 2,
              cursor: futuresAvailable || !newBot.exchangeId ? 'pointer' : 'not-allowed',
              opacity: !futuresAvailable && newBot.exchangeId ? 0.5 : 1,
              borderColor: isFutures ? 'warning.main' : 'divider',
              borderWidth: isFutures ? 2 : 1,
              bgcolor: isFutures ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': futuresAvailable ? { borderColor: 'warning.light' } : {},
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TrendingUp color={isFutures ? 'warning' : 'action'} fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>Futures</Typography>
              {isFutures && <Chip size="small" label="Selected" color="warning" sx={{ ml: 'auto' }} />}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Trade with leverage. Supports long & short positions.
            </Typography>
            {!futuresAvailable && newBot.exchangeId && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                Not available for this exchange
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Futures Settings (conditional) */}
        {isFutures && (
          <>
            {/* Warning banner */}
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              <Typography variant="body2">
                <strong>Futures trading carries significant risk.</strong> You can lose more
                than your initial margin. Make sure you understand leverage and liquidation
                before proceeding.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              {/* Futures Type */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Contract Type</InputLabel>
                  <Select
                    value={newBot.futuresType}
                    onChange={e => setNewBot(prev => ({ ...prev, futuresType: e.target.value }))}
                    label="Contract Type"
                  >
                    <MenuItem value="usdt_m">USDT-M (Linear)</MenuItem>
                    <MenuItem value="coin_m">COIN-M (Inverse)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Margin Mode */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Margin Mode</InputLabel>
                  <Select
                    value={newBot.marginMode}
                    onChange={e => setNewBot(prev => ({ ...prev, marginMode: e.target.value }))}
                    label="Margin Mode"
                  >
                    <MenuItem value="cross">
                      <Box>
                        <Typography variant="body2">Cross</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Shares margin across positions
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="isolated">
                      <Box>
                        <Typography variant="body2">Isolated</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Margin locked per position
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Position Mode */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Position Mode</InputLabel>
                  <Select
                    value={newBot.positionSide}
                    onChange={e => setNewBot(prev => ({ ...prev, positionSide: e.target.value }))}
                    label="Position Mode"
                  >
                    <MenuItem value="one_way">One-Way (Net)</MenuItem>
                    <MenuItem value="hedge">Hedge (Long + Short)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Leverage */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Leverage: <strong>{newBot.leverage}x</strong>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Slider
                    value={newBot.leverage}
                    onChange={(_, v) => setNewBot(prev => ({ ...prev, leverage: v }))}
                    min={1}
                    max={125}
                    step={1}
                    marks={LEVERAGE_PRESETS.map(v => ({ value: v, label: `${v}x` }))}
                    valueLabelDisplay="auto"
                    valueLabelFormat={v => `${v}x`}
                    sx={{
                      flex: 1,
                      '& .MuiSlider-markLabel': { fontSize: '0.7rem' },
                      color: newBot.leverage > 20 ? 'error.main'
                        : newBot.leverage > 5 ? 'warning.main'
                          : 'primary.main',
                    }}
                  />
                  <TextField
                    value={newBot.leverage}
                    onChange={e => {
                      const v = Math.max(1, Math.min(125, parseInt(e.target.value) || 1));
                      setNewBot(prev => ({ ...prev, leverage: v }));
                    }}
                    type="number"
                    inputProps={{ min: 1, max: 125, step: 1 }}
                    sx={{ width: 90 }}
                    size="small"
                  />
                </Box>

                {/* Leverage risk indicator */}
                {newBot.leverage > 10 && (
                  <Alert
                    severity={newBot.leverage > 50 ? 'error' : 'warning'}
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="caption">
                      {newBot.leverage > 50
                        ? `${newBot.leverage}x leverage is extremely risky. A ${(100 / newBot.leverage).toFixed(1)}% move against you = liquidation.`
                        : `${newBot.leverage}x leverage. A ${(100 / newBot.leverage).toFixed(1)}% adverse move = liquidation.`
                      }
                    </Typography>
                  </Alert>
                )}
              </Grid>

              {/* Reduce Only toggle (futures-specific) */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
                    border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => setNewBot(prev => ({ ...prev, reduceOnly: !prev.reduceOnly }))}
                >
                  <Chip
                    label={newBot.reduceOnly ? 'ON' : 'OFF'}
                    size="small"
                    color={newBot.reduceOnly ? 'info' : 'default'}
                    variant={newBot.reduceOnly ? 'filled' : 'outlined'}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Reduce Only</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Orders can only reduce existing position, never increase
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </Grid>
  );
}


// ============================================================================
// 5. UI COMPONENT — OrderExecutionSection (Option B)
//    Add inside Step 1, AFTER the FuturesConfig and BEFORE "Order Size In"
// ============================================================================

function OrderExecutionSection({ newBot, setNewBot }) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const mode = newBot.orderExecution || 'market';

  return (
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Order Execution
            </Typography>
          </Box>
          <Chip
            label={ORDER_EXECUTION_MODES[mode]?.label || 'Market'}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* Default: Market order, shown as the recommended choice */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: showAdvanced ? 2 : 0 }}>
          {Object.entries(ORDER_EXECUTION_MODES).map(([key, config]) => (
            <Paper
              key={key}
              variant="outlined"
              onClick={() => setNewBot(prev => ({ ...prev, orderExecution: key }))}
              sx={{
                flex: 1, p: 1.5, cursor: 'pointer',
                borderColor: mode === key ? 'primary.main' : 'divider',
                borderWidth: mode === key ? 2 : 1,
                bgcolor: mode === key ? 'action.selected' : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'primary.light' },
                position: 'relative',
              }}
            >
              {key === 'market' && (
                <Chip
                  label="Recommended"
                  size="small"
                  color="success"
                  sx={{
                    position: 'absolute', top: -10, right: 8,
                    fontSize: '0.6rem', height: 20,
                  }}
                />
              )}
              <Typography variant="body1" sx={{ mb: 0.5 }}>{config.icon}</Typography>
              <Typography variant="subtitle2" fontWeight={600}>{config.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {config.description}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Advanced toggle — Option B pattern */}
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            startIcon={<Settings fontSize="small" />}
            onClick={() => setShowAdvanced(!showAdvanced)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Order Settings
          </Button>

          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Grid container spacing={2}>
                {/* Limit offset — only relevant for limit/aggressive_limit */}
                {(mode === 'limit' || mode === 'aggressive_limit') && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Limit Offset (%)"
                      type="number"
                      value={newBot.limitOffsetPct}
                      onChange={e => setNewBot(prev => ({
                        ...prev,
                        limitOffsetPct: Math.max(0, Number(e.target.value) || 0)
                      }))}
                      inputProps={{ step: 0.01, min: 0, max: 5 }}
                      fullWidth
                      size="small"
                      helperText="How far above/below market price to place limit. 0 = exact market price."
                    />
                  </Grid>
                )}

                {/* Timeout — only for aggressive_limit */}
                {mode === 'aggressive_limit' && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Fallback Timeout (sec)"
                      type="number"
                      value={newBot.limitTimeoutSec}
                      onChange={e => setNewBot(prev => ({
                        ...prev,
                        limitTimeoutSec: Math.max(1, parseInt(e.target.value) || 10)
                      }))}
                      inputProps={{ step: 1, min: 1, max: 120 }}
                      fullWidth
                      size="small"
                      helperText="Seconds to wait before converting unfilled limit to market order."
                    />
                  </Grid>
                )}

                {/* Post-only — for limit modes */}
                {(mode === 'limit') && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
                        border: '1px solid', borderColor: 'divider', borderRadius: 1,
                        cursor: 'pointer', height: '100%',
                      }}
                      onClick={() => setNewBot(prev => ({ ...prev, postOnly: !prev.postOnly }))}
                    >
                      <Chip
                        label={newBot.postOnly ? 'ON' : 'OFF'}
                        size="small"
                        color={newBot.postOnly ? 'info' : 'default'}
                        variant={newBot.postOnly ? 'filled' : 'outlined'}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Post Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Maker fees only. Rejects if would fill immediately.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Explanation of what each mode does on the backend */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  {mode === 'market' && 'Market orders execute instantly at the best available price. Fees are typically 0.1% (taker). Best for volatile conditions where fill speed matters most.'}
                  {mode === 'limit' && 'Limit orders are placed at current price ± offset. You pay lower maker fees (0.02-0.04%), but the order may not fill if the price moves away.'}
                  {mode === 'aggressive_limit' && `A limit order is placed at market price ± ${newBot.limitOffsetPct}%. If not filled within ${newBot.limitTimeoutSec}s, it auto-converts to a market order. Best balance of fee savings and fill guarantee.`}
                </Typography>
              </Alert>
            </Box>
          </Collapse>
        </Box>
      </Paper>
    </Grid>
  );
}

// ✅ Module-level — defined OUTSIDE TradingBotDashboard
function ExchangeBadge({ type, label }) {
  const text = String(label || type || '—');
  return (
    <Chip
      size="small"
      variant="outlined"
      color="default"
      label={text}
      sx={{ height: 22 }}
    />
  );
}


/**
 * Accepts:
 *  A) closed-trade rows: { entryPrice, exitPrice, entryDate/Time, exitDate/Time, amount, pnl/pnlPct, ... }
 *  B) leg rows:          { side:'buy'|'sell', time, price, qty, pnl? }
 * Returns an array of display objects with entry+exit when available.
 */
// eslint-disable-next-line no-unused-vars -- orphaned after the backtest report was restyled to native dark trade rows; kept for a possible paired-trade view
function normalizeAndPairTrades(raw = []) {
  if (!Array.isArray(raw)) return [];

  // If any row already looks like a closed trade, just normalize and return.
  const hasClosed = raw.some(t =>
    t.entryPrice != null || t.exitPrice != null || t.entryDate || t.exitDate || t.exitTime || t.closeTime
  );
  if (hasClosed) {
    return raw.map(t => {
      const entryPrice = toNum(t.entryPrice ?? t.price);
      const exitPrice = toNum(t.exitPrice ?? t.closePrice);
      const pnlPct = toNum(String(t.pnlPct ?? t.profit ?? '').toString().replace('%', ''));
      const pnl = toNum(t.pnl ?? t.profitValue);
      return {
        entryTime: toMs(t.entryDate || t.entryTime || t.time),
        exitTime: toMs(t.exitDate || t.exitTime || t.closeTime),
        entryPrice, exitPrice,
        amount: toNum(t.amount ?? t.qty),
        pnl: pnl ?? (Number.isFinite(entryPrice) && Number.isFinite(exitPrice) && t.amount != null
          ? (exitPrice - entryPrice) * Number(t.amount) : undefined),
        pnlPct: pnlPct ?? (Number.isFinite(entryPrice) && Number.isFinite(exitPrice)
          ? ((exitPrice - entryPrice) / entryPrice) * 100 : undefined),
        side: (t.type || t.side || 'BUY').toUpperCase(),
        raw: t,
      };
    });
  }

  // Otherwise we have BUY/SELL legs. Pair FIFO with partial-lot handling so a
  // sell that closes only part of a lot — or spans several lots — is measured
  // on the quantity actually sold, not on the whole buy lot.
  const buys = []; // FIFO lots: { time, price, qty (remaining), feePerUnit, leg }
  const trips = [];
  for (const leg of raw) {
    const side = String(leg.side || leg.type || '').toUpperCase();
    const time = toMs(leg.time);
    const price = toNum(leg.price);
    const qty = toNum(leg.qty ?? leg.amount);

    if (side === 'BUY') {
      const q = Number.isFinite(qty) ? qty : 0;
      const fee = toNum(leg.fee) || 0;
      buys.push({ time, price, qty: q, feePerUnit: q > 0 ? fee / q : 0, leg });
    } else if (side === 'SELL') {
      let remaining = Number.isFinite(qty) ? qty : 0;
      const sellFeePerUnit = remaining > 0 ? (toNum(leg.fee) || 0) / remaining : 0;

      // No open long to close → surface the sell instead of silently dropping it.
      if (remaining <= 1e-12 || !buys.length) {
        trips.push({
          exitTime: time, exitPrice: price, amount: qty,
          side: 'SELL (unmatched)', raw: { sell: leg },
        });
        continue;
      }

      let matchedQty = 0, costBasis = 0, buyFee = 0, entryTime = null;
      const matchedLots = [];
      while (remaining > 1e-12 && buys.length) {
        const head = buys[0];
        const m = Math.min(remaining, head.qty);
        if (entryTime == null) entryTime = head.time;
        matchedQty += m;
        costBasis += m * head.price;
        buyFee += m * head.feePerUnit;
        matchedLots.push(head.leg);
        head.qty -= m;
        remaining -= m;
        if (head.qty <= 1e-12) buys.shift();
      }

      const avgEntry = matchedQty > 0 ? costBasis / matchedQty : undefined;
      // Prefer an engine-supplied pnl; otherwise compute on the matched qty,
      // net of the prorated buy- and sell-side fees (mirrors the live calc).
      const computedPnl = Number.isFinite(avgEntry)
        ? (price - avgEntry) * matchedQty - buyFee - sellFeePerUnit * matchedQty
        : undefined;
      const pnlPct = Number.isFinite(avgEntry) && avgEntry !== 0
        ? ((price - avgEntry) / avgEntry) * 100
        : undefined;

      trips.push({
        entryTime, exitTime: time,
        entryPrice: avgEntry, exitPrice: price,
        amount: matchedQty,
        pnl: toNum(leg.pnl) ?? computedPnl,
        pnlPct,
        side: 'BUY→SELL',
        raw: { buys: matchedLots, sell: leg },
      });

      // Sell qty beyond the available buys = unmatched remainder.
      if (remaining > 1e-12) {
        trips.push({
          exitTime: time, exitPrice: price, amount: remaining,
          side: 'SELL (unmatched)', raw: { sell: leg },
        });
      }
    }
  }
  // Unpaired buys (still open)
  for (const o of buys) {
    if (o.qty > 1e-12) {
      trips.push({
        entryTime: o.time, entryPrice: o.price, amount: o.qty,
        side: 'BUY (open)', raw: { buy: o.leg },
      });
    }
  }
  return trips;
}


const MS_DAY = 24 * 60 * 60 * 1000;

const endOfTodayMs = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};
const monthsAgoMs = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const yearsAgoMs = (n) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const isTickBasedBot = (bot) => {

  console.log('bot_check', bot)
  if (!bot) return false;
  if ((bot.interval || '').toLowerCase() === 'tick') return true;
  const s = `${bot.logic?.buy || ''}${bot.logic?.sell || ''}`.toLowerCase();
  return s.includes('tick');
};
const computeBacktestBounds = (bot) => {
  const max = endOfTodayMs();                          // always up to today
  // 5 years for candle bots. The BAR budget is the real governor, not the
  // calendar: the server clamps any run to BACKTEST_MAX_BARS (50k), which
  // covers the full 5 years at 1h and above (5y of 1h ≈ 43.8k bars) and
  // trims minute timeframes to whatever 50k bars reaches — a 1m request
  // measures its most recent ~34 days and says so, rather than the picker
  // pretending short timeframes can span half a decade.
  const min = isTickBasedBot(bot) ? monthsAgoMs(2)     // tick: last 2 months
    : yearsAgoMs(5);
  return { min, max };
};
const defaultBacktestRange = () => [monthsAgoMs(1), endOfTodayMs()]; // last 1 month




// Mock data and utilities
const initialBots = [];

// "BTC/USDT" or "BTCUSDT" → ["BTC","USDT"]
const splitPair = (sym) => {
  const S = String(sym || '').toUpperCase();
  if (!S) return ['—', '—'];
  if (S.includes('/')) return S.split('/');
  // Try common crypto quote suffixes first (longest first)
  const quotes = ['USDT', 'USDC', 'BUSD', 'TUSD', 'USD', 'DAI', 'BTC', 'ETH'];
  quotes.sort((a, b) => b.length - a.length);
  for (const q of quotes) {
    if (S.endsWith(q) && S.length > q.length) {
      return [S.slice(0, -q.length), q];
    }
  }
  // Bare ticker (stocks): default quote to USD
  return [S, 'USD'];
};

// Prefer symbol metadata (base_asset/quote_asset) from symbolsByExchange cache.
// Fallback to old heuristic splitPair if not found.
function getPairMeta({ symbol, exchangeId, userExchanges, symbolsByExchange }) {
  const sym = String(symbol || '').toUpperCase();
  if (!sym) return { base: '—', quote: '—', raw: null };

  // Find cacheKey the same way you do elsewhere
  const selectedExchange = (userExchanges || []).find(ex => ex.id === exchangeId);
  const cacheKey = selectedExchange?.exchange || selectedExchange?.name || exchangeId;

  const list = symbolsByExchange?.[cacheKey] || [];
  const found = list.find(s => String(s.symbol || '').toUpperCase() === sym);

  if (found?.base_asset && found?.quote_asset) {
    return {
      base: String(found.base_asset).toUpperCase(),
      quote: String(found.quote_asset).toUpperCase(),
      raw: found,
    };
  }

  // fallback (your old behavior)
  const [BASE, QUOTE] = splitPair(sym);
  return { base: BASE, quote: QUOTE, raw: null };
}

function buildOrderSizing(symbol, sizeMode, positionValue, baseAsset, quoteAsset) {
  const BASE = String(baseAsset || '').toUpperCase();
  const QUOTE = String(quoteAsset || '').toUpperCase();

  // final fallback if caller didn’t pass assets
  const [fbBase, fbQuote] = splitPair(symbol);
  const base = BASE || fbBase;
  const quote = QUOTE || fbQuote;

  const assetSymbol = sizeMode === 'base' ? base : quote;

  return {
    mode: sizeMode,                       // 'quote' | 'base'
    value: Number(positionValue) || 0,
    assetType: sizeMode,
    assetSymbol,
  };
}


// Exchange display helpers (logo, venue name, account chip) all come from
// config/exchangeConfig.js. The local EX_LOGOS map used to be `{ binance }` —
// key 'binance' — which never matched the stored values ('binance.com',
// 'binance.us', 'alpaca'), so no exchange rendered a logo. The local
// three-state getAccountMeta was promoted into that config so the bot, scanner
// and backtest pickers all label accounts identically.

// --- ExchangeSelect (drop-in) ---
function ExchangeSelect({ exchanges, value, onChange, label = 'Exchange', disabled }) {
  return (
    <Autocomplete
      options={exchanges}
      value={value || null}                  // <= object or null
      onChange={(_, v) => onChange(v)}       // <= pass object (or null)
      getOptionLabel={(o) => (o ? exchangeDisplayName(o) : '')}
      isOptionEqualToValue={(o, v) => o.id === v?.id}
      disableClearable={false}
      disabled={disabled}
      blurOnSelect
      openOnFocus={false}
      disablePortal
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password',    // <= tame browser autofill
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', px: 1.25, py: 1 }}>
          <ExchangeOptionRow exchange={option} />
        </Box>
      )}
    />
  );
}



const mockTransactions = [
  { id: 1, botId: 1, type: 'BUY', pair: 'BTC/USDT', amount: 0.05, price: 42000, value: 2100, fee: 2.10, date: '2023-05-15 10:30:22' },
  { id: 2, botId: 1, type: 'SELL', pair: 'BTC/USDT', amount: 0.05, price: 43500, value: 2175, fee: 2.18, date: '2023-05-16 15:45:10' },
  { id: 3, botId: 2, type: 'BUY', pair: 'ETH/USDT', amount: 2.5, price: 2800, value: 7000, fee: 7.00, date: '2023-05-17 09:15:33' },
];

const indicators = [
  { name: 'RSI', params: ['period', 'source'], info: 'RSI(period, source) — Relative Strength Index' },
  { name: 'MACD', params: [], info: 'MACD() — Moving Average Convergence Divergence' },
  { name: 'Volume', params: [], info: 'Volume — Candle volume' },
  { name: 'SMA', params: ['period', 'source'], info: 'SMA(period, source) — Simple Moving Average' },
  { name: 'EMA', params: ['period', 'source'], info: 'EMA(period, source) — Exponential Moving Average' },

  // ✅ Tick-based indicators
  { name: 'tickPrice', params: [], info: 'tickPrice — Latest tick price' },
  { name: 'tickDelta', params: [], info: 'tickDelta — Change from last tick' },
  { name: 'tickVolume', params: [], info: 'tickVolume — Tick count in interval' },
  { name: 'tickTradeVolume', params: [], info: 'tickTradeVolume — Total quantity from recent ticks' },
  { name: 'tickRSI', params: ['period'], info: 'tickRSI(period) — RSI on tick prices' },
  { name: 'tickVWAP', params: [], info: 'tickVWAP — VWAP from tick data' }
];
const operators = ['<', '<=', '==', '>=', '>', '+', '-', '*', '/', '(', ')'];
const combinators = ['AND', 'OR'];

const TICK_INDICATOR_NAMES = new Set([
  'tickPrice', 'tickDelta', 'tickVolume', 'tickTradeVolume', 'tickRSI', 'tickVWAP'
]);


// Mock backtest data generator
const generateBacktestResults = (bot) => {
  const startDate = new Date('2023-01-01');
  const endDate = new Date('2023-06-01');
  const days = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));

  const trades = Math.floor(Math.random() * 50) + 10;
  const profit = (Math.random() * 30 - 5).toFixed(2);
  const winRate = (Math.random() * 40 + 50).toFixed(1);

  const equityCurve = Array.from({ length: days }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      date,
      value: 10000 + (i * (profit * 100 / days)) + (Math.random() * 200 - 100)
    };
  });

  const metrics = {
    totalReturn: `${profit}%`,
    winRate: `${winRate}%`,
    sharpeRatio: (Math.random() * 2 + 0.5).toFixed(2),
    maxDrawdown: `-${(Math.random() * 15 + 5).toFixed(1)}%`,
    trades,
    profitFactor: (Math.random() * 1.5 + 0.5).toFixed(2)
  };

  return { equityCurve, metrics, trades: generateMockTrades(bot, trades) };
};

// eslint-disable-next-line no-unused-vars -- orphaned after the backtest report restyle; kept as a formatting helper
const pctStr = (v, digits = 2) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${Number(v).toFixed(digits)}%`;
};
// eslint-disable-next-line no-unused-vars -- orphaned after the backtest report restyle; kept as a formatting helper
const moneyStr = (v, digits = 2) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const sign = Number(v) >= 0 ? '' : '-';
  return `${sign}$${Math.abs(Number(v)).toFixed(digits)}`;
};
const pfStr = (v) => {
  if (v === Infinity) return '∞';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
};


const generateMockTrades = (bot, count) => {
  return Array.from({ length: count }, (_, i) => {
    const isBuy = Math.random() > 0.5;
    const entryPrice = isBuy ? 40000 + Math.random() * 5000 : 45000 - Math.random() * 5000;
    const exitPrice = isBuy ? entryPrice * (1 + Math.random() * 0.1) : entryPrice * (1 - Math.random() * 0.05);
    const profit = isBuy ? (exitPrice - entryPrice) / entryPrice * 100 : (entryPrice - exitPrice) / entryPrice * 100;

    return {
      id: i + 1,
      type: isBuy ? 'BUY' : 'SELL',
      entryPrice: entryPrice.toFixed(2),
      exitPrice: exitPrice.toFixed(2),
      entryDate: format(new Date(2023, 0, Math.floor(Math.random() * 150) + 1), 'yyyy-MM-dd HH:mm'),
      exitDate: format(new Date(2023, 0, Math.floor(Math.random() * 150) + 2), 'yyyy-MM-dd HH:mm'),
      profit: profit.toFixed(2) + '%',
      amount: (Math.random() * 0.1 + 0.01).toFixed(4),
      profitValue: (Math.random() * 200 - 50).toFixed(2)
    };
  });
};

const downsample = (arr, max = 2000) => {
  if (!Array.isArray(arr) || arr.length <= max) return arr || [];
  const step = Math.ceil(arr.length / max);
  const out = [];
  for (let i = 0; i < arr.length; i += step) out.push(arr[i]);
  if (out[out.length - 1] !== arr[arr.length - 1]) out.push(arr[arr.length - 1]);
  return out;
};

const toChartData = (equityCurve = []) =>
  downsample(equityCurve).map(([t, e]) => ({ time: toMs(t), equity: Number(e) }));

function evaluateLogic(ruleString) {
  const mockValues = {
    RSI: 55,
    SMA: 1800,
    EMA: 1825,
    MACD: 5,
    Volume: 100000,

    // ✅ Tick-based mock values
    tickPrice: 42780,
    tickDelta: -1.25,
    tickVolume: 63,
    tickTradeVolume: 18.9,
    tickRSI: 60,
    tickVWAP: 42750.3
  };

  try {
    const expr = ruleString
      .replace(/\b(tickRSI|tickVWAP|tickPrice|tickDelta|tickVolume|tickTradeVolume|RSI|SMA|EMA|MACD|Volume)\s*\(\s*[^)]*\)/g, (_, name) => {
        return mockValues[name] !== undefined ? mockValues[name] : '0';
      })
      .replace(/\b(tickRSI|tickVWAP|tickPrice|tickDelta|tickVolume|tickTradeVolume|RSI|SMA|EMA|MACD|Volume)\b/g, match => {
        return mockValues[match] !== undefined ? mockValues[match] : '0';
      })
      .replace(/\bAND\b/gi, '&&')
      .replace(/\bOR\b/gi, '||');

    const result = eval(expr);
    return {
      valid: typeof result === 'boolean',
      result: typeof result === 'boolean' ? result : null
    };
  } catch (e) {
    return { valid: false, result: null };
  }
}




const FUTURES_SUPPORTED_EXCHANGES = new Set([
  'binance.com',   // ✅ has USDT-M and COIN-M futures
  'binance',       // fallback if your DB stores it without TLD
  // 'binance.us' intentionally excluded — no futures
]);
// Futures position actions (replaces simple buy/sell for futures)
const FUTURES_ACTIONS = {
  OPEN_LONG: 'OPEN_LONG',
  CLOSE_LONG: 'CLOSE_LONG',
  OPEN_SHORT: 'OPEN_SHORT',
  CLOSE_SHORT: 'CLOSE_SHORT',
};

// Order execution presets for Option B
const ORDER_EXECUTION_MODES = {
  market: {
    label: 'Market Order',
    description: 'Immediate fill at current price. Best for fast-moving strategies.',
    icon: '⚡',
  },
  limit: {
    label: 'Limit Order',
    description: 'Place at specific price. Lower fees, risk of no fill.',
    icon: '🎯',
  },
  aggressive_limit: {
    label: 'Smart Limit',
    description: 'Limit order with small buffer + auto-fallback to market after timeout.',
    icon: '🧠',
  },
};

// Which margin modes Binance supports
const MARGIN_MODES = ['cross', 'isolated'];

// Leverage presets for quick selection
const LEVERAGE_PRESETS = [1, 2, 3, 5, 10, 20, 50];

// ─────────────────────────────────────────────────────────────
// Funding asset — balance source for bot capital, P&L, equity.
// Distinct from the pair's quote asset (the settlement currency).
// When they differ, the bot converts at startup and back at exit.
// ─────────────────────────────────────────────────────────────
const FUNDING_ASSETS_CRYPTO = ['USDT', 'USDC', 'FDUSD', 'BUSD', 'BTC', 'ETH'];
const FUNDING_ASSETS_STOCK = ['USD'];

function getFundingAssetsForClass(assetClass) {
  return assetClass === 'stock_equity' ? FUNDING_ASSETS_STOCK : FUNDING_ASSETS_CRYPTO;
}

// Fee filter for the symbol picker — mirrors the scanner's UniverseSelector
// (same values, labels and descriptions) so both pickers mean the same thing.
// Categories present in the live symbol catalog, most-populated first. Values
// must match Symbols.categories exactly — the scanner's UniverseSelector uses
// the same vocabulary.
const SYMBOL_CATEGORIES = [
  { value: '',               label: 'Any category' },
  { value: 'smart-contract', label: 'Smart contract' },
  { value: 'defi',           label: 'DeFi' },
  { value: 'layer1',         label: 'Layer 1' },
  { value: 'layer2',         label: 'Layer 2' },
  { value: 'ai',             label: 'AI' },
  { value: 'gaming',         label: 'Gaming' },
  { value: 'meme',           label: 'Memes' },
  { value: 'dex',            label: 'DEX' },
  { value: 'rwa',            label: 'RWA' },
  { value: 'governance',     label: 'Governance' },
  { value: 'exchange',       label: 'Exchange' },
];

// Stock facets for the equity universe picker. Values are what the server
// matches: 'etf' -> is_etf, otherwise exchange_listed/sector (case-insensitive).
// Sectors join this list automatically server-side once the enrichment job
// populates Symbols.sector; until then listing venue is the real, always-set
// axis (ARCA/BATS listings are overwhelmingly ETFs).
const STOCK_UNIVERSE_CATEGORIES = [
  { value: '',       label: 'Any category' },
  { value: 'etf',    label: 'ETFs' },
  { value: 'nyse',   label: 'NYSE' },
  { value: 'nasdaq', label: 'NASDAQ' },
  { value: 'arca',   label: 'NYSE Arca' },
  { value: 'bats',   label: 'BATS' },
  { value: 'amex',   label: 'AMEX' },
  { value: 'otc',    label: 'OTC' },
];

const SYMBOL_FEE_FILTERS = [
  { value: 'any',         label: 'Any',         desc: 'No fee filter' },
  { value: 'zero_maker',  label: 'Zero Maker',  desc: 'Maker fee = 0 (limit orders free)' },
  { value: 'zero_taker',  label: 'Zero Taker',  desc: 'Taker fee = 0 (market orders free)' },
  { value: 'zero_either', label: 'Zero Either', desc: 'Either maker or taker = 0' },
  { value: 'zero_both',   label: 'Zero Both',   desc: 'Both maker AND taker = 0' },
];

/**
 * Does a symbol satisfy the chosen fee filter?
 *
 * A missing fee is treated as UNKNOWN, not free. The scanner's server-side
 * filter does `Number(s.default_maker_fee) === 0`, and Number(null) is 0 — so
 * it currently reports symbols with no fee data as zero-fee. We don't copy that
 * here: claiming a pair is free when we simply have no data would be worse than
 * omitting it. `is_zero_fee` stays a positive signal, since that's the explicit
 * "fees waived" flag the fee panel already trusts (e.g. commission-free stocks).
 */
function symbolMatchesFeeFilter(s, mode) {
  if (!mode || mode === 'any') return true;
  const zeroFlag = !!s?.is_zero_fee;
  const m = s?.default_maker_fee;
  const t = s?.default_taker_fee;
  const makerFree = zeroFlag || (m != null && Number(m) === 0);
  const takerFree = zeroFlag || (t != null && Number(t) === 0);
  switch (mode) {
    case 'zero_maker':  return makerFree;
    case 'zero_taker':  return takerFree;
    case 'zero_either': return makerFree || takerFree;
    case 'zero_both':   return makerFree && takerFree;
    default:            return true;
  }
}

function defaultFundingAssetForClass(assetClass) {
  return assetClass === 'stock_equity' ? 'USD' : 'USDT';
}

const FUNDING_STABLES = new Set(['USDT', 'USDC', 'FDUSD', 'BUSD', 'TUSD', 'DAI', 'USD']);

// For a single-pair manual bot, describe whether funding asset matches
// the pair's quote. Returns null when matched (no warning).
function describeFundingMismatchForPair(fundingAsset, quoteAsset) {
  if (!fundingAsset || !quoteAsset) return null;
  const f = String(fundingAsset).toUpperCase();
  const q = String(quoteAsset).toUpperCase();
  if (f === q) return null;

  const bothStable = FUNDING_STABLES.has(f) && FUNDING_STABLES.has(q);
  return {
    severity: bothStable ? 'info' : 'warning',
    title: bothStable
      ? `Stablecoin conversion: ${f} → ${q} at entry`
      : `Cross-asset conversion: ${f} → ${q} at entry`,
    body: bothStable
      ? `This pair quotes in ${q} but your bot is funded from ${f}. The bot will swap ${f} → ${q} at startup (and back at exit). Stablecoin swaps cost less than 0.01%, so impact is minimal — but you'll see two extra trades per bot lifecycle.`
      : `This pair quotes in ${q} but your bot is funded from ${f}. The bot will swap ${f} → ${q} at startup and back on exit, exposing you to spread (~0.05–0.2%) and ${f} price movement during the position. Funding directly in ${q} would be cheaper.`,
  };
}





// Helper: does the selected exchange support futures?
function exchangeSupportsFutures(exchangeObj) {
  if (!exchangeObj) return false;
  const name = (exchangeObj.exchange || exchangeObj.name || '').toLowerCase().trim();
  return FUTURES_SUPPORTED_EXCHANGES.has(name);
}

// Add near the top of your fi

const steps = ['Basic Configuration', 'Trading Logic', 'Risk Management'];

export default function TradingBotDashboard() {
  const [activeStep, setActiveStep] = useState(0);
  const routerHistory = useHistory();
  const rfLocation = useLocation();
  const quickBotRanRef = useRef(false);
  const [exchangesLoaded, setExchangesLoaded] = useState(false);
  const [bots, setBots] = useState(initialBots);
  // Lets bot creation tick the "Create your first bot" onboarding step.
  const { refreshStatus } = useOnboarding();

  // Stable list of bot ids (used for websocket room joins/listeners)
  const botIds = useMemo(() => {
    const ids = (bots || []).map(b => Number(b.id)).filter(n => Number.isFinite(n));
    ids.sort((a, b) => a - b);
    return ids;
  }, [bots]);

  const botIdsKey = useMemo(() => botIds.join(','), [botIds]);

  const [newBot, setNewBot] = useState({
    name: '',
    interval: '1H',
    symbol: 'BTC/USDT',
    sizeMode: 'quote',
    positionValue: '',
    totalAmount: '',
    maxLoss: '5',
    maxTrades: '10',
    trailingStop: '2',
    // Per-position exits (trade_info.risk). Blank = disabled, matching the
    // backtest's semantics for the same fields.
    riskStopLossPct: '',
    riskTakeProfitPct: '',
    riskMaxBarsInTrade: '',
    riskFlattenBeforeCloseMin: '',
    exchangeId: null,

    // Signal source (existing)
    signalSource: 'logic',
    webhookSecret: '',
    webhookActionField: 'action',
    webhookBuyValue: 'BUY',
    webhookSellValue: 'SELL',

    // ✅ NEW: Futures configuration
    marketType: 'spot',            // 'spot' | 'futures'
    futuresType: 'usdt_m',        // 'usdt_m' | 'coin_m' (Binance-specific)
    leverage: 1,                   // 1-125
    marginMode: 'cross',          // 'cross' | 'isolated'
    positionSide: 'one_way',      // 'one_way' | 'hedge'
    // Short logic lives in shortOpenConditions/shortCloseConditions (builder)
    // and wizRawLogic.shortOpen/shortClose (expression) — not here.

    // ✅ NEW: Order execution (Option B)
    orderExecution: 'market',      // 'market' | 'limit' | 'aggressive_limit'
    // Advanced order settings (revealed on toggle):
    limitOffsetPct: 0.05,          // % above/below current price for limit orders
    limitTimeoutSec: 10,           // seconds before fallback to market
    postOnly: false,               // maker-only flag
    reduceOnly: false,             // futures: only reduce position
    futureSizeMode: 'margin_pct',
    marginPct: '',
    fixedNotional: '',
    maxConcurrentTrades: 1,
    marginAsset: undefined,
    baseAsset: undefined,
    quoteAsset: undefined,
    basePrecision: null,
    quotePrecision: null,
    contractType: null,

    // Phase B — stock-specific
// Phase B — stock-specific
    extendedHours: false,     // allow PRE/POST market on stock_equity
    allowShorts: false,       // enable short_open_logic / short_close_logic on stock_equity
    // Instrument switch (stock_equity only): 'shares' | 'options'. Options
    // rules live in newBot.options and travel as trade_info.options.
    instrument: 'shares',
    options: null,

    // Funding asset — balance source for bot capital & P&L
    funding_asset: 'USDT',
  });



  // top of your component
// top of your component
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });
  const closeToast = () => setToast(t => ({ ...t, open: false }));

  // Soft-warning state for the duplicate-bot 409 path.
  // shape: { conflict: { bot_id, bot_name, symbol, exchange_id, created_at }, pendingPayload }
  const [duplicateBotConflict, setDuplicateBotConflict] = useState(null);


  const [buyConditions, setBuyConditions] = useState([]);
  const [sellConditions, setSellConditions] = useState([]);

  // Raw-expression escape hatch for the wizard, mirroring btDraft's on the
  // backtest tab. In 'expression' mode these strings ARE the strategy and the
  // condition arrays above are ignored — see handleSave, which also has to
  // derive trade_info.indicators from the strings rather than the rows.
  const [wizLogicMode, setWizLogicMode] = useState('builder');
  const [wizRawLogic, setWizRawLogic] = useState({ buy: '', sell: '', shortOpen: '', shortClose: '' });
  const [wizForcedExpression, setWizForcedExpression] = useState(false);
  const isWizExpression = wizLogicMode === 'expression';

  const [userExchanges, setUserExchanges] = useState([]);
  const [tempToken, setTempToken] = useState({ side: 'buy', value: '', combinator: 'AND' });
  const [profitStop, setProfitStop] = useState('20');
  const [lossStop, setLossStop] = useState('10');
  const [expandedBot, setExpandedBot] = useState(null);
  const [activeTab, setActiveTab] = useState('bots');
  const [isCreating, setIsCreating] = useState(false);
  // Quick Bot exchange prompt — only shown when the user has more than one.
  const [quickBotPickerOpen, setQuickBotPickerOpen] = useState(false);
  const [quickBotExchange, setQuickBotExchange] = useState(null);
  const [backtestProgress, setBacktestProgress] = useState(0);
  const [isBacktesting, setIsBacktesting] = useState(false);
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestDialogOpen, setBacktestDialogOpen] = useState(false);
  const [backtestDateRange, setBacktestDateRange] = useState(defaultBacktestRange());
  const [btRange, setBtRange] = useState('1M'); // active date-range preset chip (§7a)
  const [btAdvOpen, setBtAdvOpen] = useState(false); // collapsible Advanced params in the backtest config bar (§7a)
  const [rangeBounds, setRangeBounds] = useState(() => computeBacktestBounds(null));

  const [activeBacktestTab, setActiveBacktestTab] = useState('summary');
  // The Backtest page now renders the FULL trade list (paginated) inline instead
  // of a 5-row preview, and no longer auto-opens the results popup on completion.
  // Reset to the first page whenever a new run finishes or a saved result loads.
  const [btTradesPage, setBtTradesPage] = useState(0);
  useEffect(() => { setBtTradesPage(0); }, [backtestResults]);
  // Scroll target: opening a saved backtest loads it into the inline panel (no
  // popup), so we bring that panel into view.
  const backtestPanelRef = useRef(null);
  const [selectedBotForBacktest, setSelectedBotForBacktest] = useState(null);
  const [liveBotHistories, setLiveBotHistories] = useState({});
  // Per-bot history pagination state: { [botId]: { offset, total, loading, done } }
  const [historyPaging, setHistoryPaging] = useState({});
  const listenersRef = useRef(new Map());  // ✅ Top-level hook call
  const [liveBacktest, setLiveBacktest] = useState(null);
  const liveBacktestRef = useRef(null); // ← ADD THIS

  const [recentBacktests, setRecentBacktests] = useState([]);
  const [btHistoryPage, setBtHistoryPage] = useState(0);
  // Ten rows read as "you only have ten backtests", because the count beside it
  // is easy to miss. The retained set is 60, so 50 shows essentially all of it
  // on one page while keeping the pager for the overflow.
  const BT_HISTORY_PAGE_SIZE = 50;
  // 'refine' edits the strategy on screen; 'explore' asks for different ideas.
  const [btSearchMode, setBtSearchMode] = useState('refine');
  const [loadingSavedBacktest, setLoadingSavedBacktest] = useState(false);
  const [viewingSavedBacktestId, setViewingSavedBacktestId] = useState(null);
  // Row pending deletion from the history list (independent of the report view).
  const [confirmDeleteBacktest, setConfirmDeleteBacktest] = useState(null);
  const isSavedView = Boolean(viewingSavedBacktestId);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const backtestListenersRef = useRef(new Map()); // jobId -> handler
  const backtestPollRef = useRef(null); // ← ADD THIS
  const [futuresInfo, setFuturesInfo] = useState({}); // { [botId]: { fundingRate, nextFundingTime, markPrice, indexPrice, openInterest, ... } }

  const [isCanceling, setIsCanceling] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [backtestParams, setBacktestParams] = useState({
    initialCapital: 10000,
    // Overwritten from the active venue (see the btCostsTouchedFor effect
    // below) until the user edits either field by hand.
    ...defaultBacktestCosts(null),
    // Risk exits. Blank/0 = disabled, which is the old behaviour (the sell
    // expression is the only way out). These are percentages: stop/target are
    // measured from the entry price, the trailing stop from the best price
    // reached since entry.
    stopLossPct: '',
    takeProfitPct: '',
    trailingStopPct: '',
    maxBarsInTrade: '',
    // Minutes before the session close at which any open position is flattened.
    // Stocks only — crypto has no close. Blank = hold through, the old behaviour.
    flattenBeforeCloseMin: '',
  });
  // WHICH context the user's hand-typed commission/slippage belong to —
  // `bot:<id>` or `explore:<exchangeId>` — not a global boolean. The boolean
  // version latched forever: edit fees once and every bot, symbol and session
  // afterwards ran on those numbers instead of its venue's defaults. A hand
  // edit now applies to the context it was made in; selecting a different bot
  // or explore venue snaps back to that venue's defaults.
  const [btCostsTouchedFor, setBtCostsTouchedFor] = useState(null);

  // Collect the risk-exit fields into the shape the engine reads
  // (bot.config.risk). Returns undefined when nothing is set so the payload
  // stays byte-identical to before for anyone not using stops — that keeps the
  // "did the config change?" dirty checks honest.
  const buildRiskConfig = useCallback(() => {
    const num = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const risk = {
      stopLossPct: num(backtestParams.stopLossPct),
      takeProfitPct: num(backtestParams.takeProfitPct),
      trailingStopPct: num(backtestParams.trailingStopPct),
      maxBarsInTrade: Math.floor(num(backtestParams.maxBarsInTrade)),
      // Stocks only. The input is hidden for crypto but its value survives
      // switching bots, so without this a number typed against a stock bot
      // would follow you to a crypto one and come back as "ignored, not a
      // stock bot" — a warning about something you cannot even see.
      //
      // Derived here rather than reusing btDraftIsStock: that is declared far
      // below this hook, so naming it in the dependency array would read it in
      // its temporal dead zone and throw on first render.
      flattenBeforeCloseMin: isStockBot(selectedBotForBacktest)
        ? Math.floor(num(backtestParams.flattenBeforeCloseMin))
        : 0,
    };
    return Object.values(risk).some((v) => v > 0) ? risk : undefined;
  }, [backtestParams, selectedBotForBacktest]);

  // ── Backtest-page bot editing ────────────────────────────────────────────
  // A DRAFT of the selected bot's timeframe + entry/exit logic. Edits stay
  // local so you can iterate (tweak → backtest → tweak) without mutating a bot
  // that may be live; nothing reaches the server until "Deploy" is pressed.
  const [btDraft, setBtDraft] = useState(null);   // { botId, timeframe, buyConditions, sellConditions }
  // Open by default: the reconfigure section hosts the AI advisor, and keeping
  // it collapsed meant nothing AI-related was visible on the Backtest page
  // until the user found the toggle.
  const [btEditOpen, setBtEditOpen] = useState(true);
  const [btDeploying, setBtDeploying] = useState(false);
  const [btDeployConfirm, setBtDeployConfirm] = useState(null); // bot pending live-update confirmation
  // What the last backtest actually RAN with. buildSavePayload used the bot's
  // stored logic, so a run of reconfigured (draft) logic was saved describing
  // the deployed strategy instead — the history then showed the old logic.
  // Captured at submit time, not read from the draft at save time, so later
  // edits to the panel can't retroactively mislabel a completed run.
  const btLastRunRef = useRef(null); // { botId, buy, sell, timeframe, symbol, startMs, endMs }
  // Strategy to seed the reconfigure panel with when a saved backtest is opened.
  // Without it the panel seeds from the BOT's current logic, so opening a
  // historical result and hitting "Reconfigure" showed the deployed strategy
  // rather than the one you just loaded — you couldn't iterate on it.
  const btSeedFromRef = useRef(null); // { botId, buy, sell, timeframe }

  // ── Reconfigure-panel AI advisor ─────────────────────────────────────────
  // Suggests refinements to the draft using this bot's backtest results. The
  // suggestion is shown for review rather than written straight into the draft:
  // the draft is what "Run backtest" tests, so a silent overwrite would change
  // the experiment without the user agreeing to it.
  const [btAiPrompt, setBtAiPrompt] = useState('');
  const [btAiLoading, setBtAiLoading] = useState(false);
  const [btAiResult, setBtAiResult] = useState(null);
  // Conversation so far, so a follow-up ("now make the exit tighter") builds on
  // the previous answer instead of starting cold. Sent to the backend, which
  // appends the current prompt itself.
  const [btAiHistory, setBtAiHistory] = useState([]);
  const btAiService = useRef(new AIStrategyService());

  // ── Closed-loop search ──
  // The advisor above proposes; this measures. It asks for several candidates,
  // backtests every one of them, and ranks on data they were not tuned against.
  // Polled rather than awaited: several model calls plus several backtests is
  // minutes of work, well past any request timeout.
  const [btSearch, setBtSearch] = useState(null);     // { id, status, phase, results, error }
  const [btSearchBusy, setBtSearchBusy] = useState(false);
  const btSearchPollRef = useRef(null);

  // ── Parameter sweep ──
  // The numbers in a strategy were chosen once and never tested. This varies
  // them and reports the SHAPE of the response — a broad plateau is plausibly
  // real, a lone spike is noise, a flat line means stop tuning that parameter.
  const [btSweep, setBtSweep] = useState(null);       // the polled status record
  const [btSweepBusy, setBtSweepBusy] = useState(false);
  const [btSweepKind, setBtSweepKind] = useState('risk');   // 'risk' | 'indicator' | 'discover'
  // Discovery has no axes to pick — the server generates candidates — so the
  // only dial is how many to screen. More candidates is not more power: the FDR
  // correction is across everything looked at, so a bigger sweep makes each
  // individual finding harder to establish, not easier.
  const [btSweepMaxCandidates, setBtSweepMaxCandidates] = useState(300);
  // Consequential load: a row the honesty check flagged as noise does not go
  // straight into a bot draft. It asks first. The friction is the feature — this
  // click is one step from real money.
  const [btPendingLoad, setBtPendingLoad] = useState(null);   // the row awaiting confirmation
  const [btLoadedMsg, setBtLoadedMsg] = useState('');         // "✓ loaded…" confirmation
  // Sweep timing, for the progress stepper's ETA. Bare phase strings read as
  // "hung" to users, so the panel always shows movement and a remaining estimate.
  const [btSweepStartedAt, setBtSweepStartedAt] = useState(null);
  // When the record last MOVED (phase or completed-count changed), not when it
  // was last polled — a dead worker's record polls fine forever, unchanged.
  // This is what tells "slow" apart from "dead".
  const btSweepProgressRef = useRef({ mark: '', at: 0 });
  // Explore's tuning step. Breadth answers "which symbol", tuning answers "what
  // numbers on it" — and it runs inline so the user never leaves Explore and
  // loses the strategy they just found. Defaults to the breadth winner.
  const [btTuneSymbol, setBtTuneSymbol] = useState('');
  // The tuning card, so a discovery row can hand its symbol forward and take the
  // user there. Discovery used to end at "Start a bot here": the one action it
  // offered committed to the symbol, and the cheaper next steps — tune it, or
  // explore around it — had no entry point from the results at all.
  const btTunePanelRef = useRef(null);
  // Explore's own lookback (handoff explore §2, 7–365d). Breadth and the signal
  // check use THIS window in explore, not the optimize backtest range — sharing
  // the range meant changing one experiment silently changed the other.
  const [btExLookbackDays, setBtExLookbackDays] = useState(90);
  // AI ASSIST (Explore Step 1): natural language → conditions. Output lands in
  // the SAME editor state the hand-written path uses — the assistant fills the
  // editor, it does not own a parallel strategy. Named btNl* because btAi* is
  // taken by the Optimize advisor above, which is a different tool: that one
  // suggests refinements for review, this one writes the editor directly.
  const [btNlPrompt, setBtNlPrompt] = useState('');
  const [btNlBusy, setBtNlBusy] = useState(false);
  const [btNlOpen, setBtNlOpen] = useState(true);
  const [btNlNote, setBtNlNote] = useState(null);   // { tone: 'ok'|'info'|'error', text, warnings? }
  const [btSweepPlan, setBtSweepPlan] = useState(null);     // axes the server says are tunable
  const [btSweepSel, setBtSweepSel] = useState({});         // axisKey -> boolean
  const [btSweepVals, setBtSweepVals] = useState({});       // axisKey -> "1, 2, 3"
  const btSweepPollRef = useRef(null);
  // Finished (and in-flight) sweeps by RESEARCH TAB and kind, so the tuner
  // behaves as independent result sets rather than one slot that each switch
  // wipes. The tab is part of the key because Optimize and Explore tune
  // DIFFERENT SYMBOLS — Optimize the selected bot's, Explore the tuning pick —
  // and a single per-kind slot showed one symbol's results under the other
  // symbol's heading the moment the user changed tabs. A ref, not state:
  // nothing renders from the stash — the visible entry is always mirrored into
  // the four states above.
  const btSweepStashRef = useRef({});
  const sweepStashKey = (tab, kind) => `${tab}:${kind}`;
  // What the user is LOOKING at, readable from inside the poll closure. The
  // poll captures the key it was started under; comparing the two is how a
  // sweep that finishes off-screen lands in the stash instead of stomping
  // whatever tab+kind is on screen.
  const btSweepCtxRef = useRef('optimize:risk');

  // ── Feature scan ──
  // Cheaper and earlier than a backtest: does this condition predict anything
  // at all, across many symbols, before any fills or exits are simulated.
  const [btScanBusy, setBtScanBusy] = useState(false);

  // ── Symbol universe picker ──
  // Shared by the scan and breadth panels. Typing twenty tickers by hand is the
  // friction that stops these tools being used, and a hand-picked list is the
  // worst kind of sample — it is chosen by what the user already believes.
  const [btUniQuote, setBtUniQuote] = useState('USDT');
  const [btUniFee, setBtUniFee] = useState('any');
  const [btUniCategory, setBtUniCategory] = useState('');
  const [btUniShortable, setBtUniShortable] = useState(false);
  // ── Carry research ──
  // Set when Resume is refused because the logic has never been backtested.
  const [btDeployBlocked, setBtDeployBlocked] = useState(null);
  // Two readings of the same batch. BREADTH asks "does this strategy generalise"
  // and is answered by the share of symbols that worked. DISCOVERY asks "which
  // symbol should I trade this on" and is answered by ranking them. Same engine,
  // opposite sort, and a different question — worth making explicit rather than
  // leaving users to reinterpret a breadth verdict as a shopping list.

  // Two jobs that were sharing one screen. OPTIMISE improves the strategy on
  // the selected bot — reconfigure, signal check, breadth, search and rank.
  // EXPLORE looks outward for a symbol worth trading and turns a winner into a
  // new bot. They use much of the same machinery but answer different
  // questions, and mixing them made the page read as one long undifferentiated
  // list of tools.
  const [btResearchTab, setBtResearchTab] = useState('optimize');

  // ── Standalone exploration ──
  //
  // Explore answers "is there a symbol worth trading", which by definition
  // precedes having a bot. So it carries its own venue, timeframe, strategy and
  // risk rather than inheriting them from a selection that may not exist.
  const [btExploreExchangeId, setBtExploreExchangeId] = useState('');
  const [btExploreTimeframe, setBtExploreTimeframe] = useState('1h');
  const [btExploreBuy, setBtExploreBuy] = useState('');
  const [btExploreSell, setBtExploreSell] = useState('');
  const [btExploreMode, setBtExploreMode] = useState('builder');
  const [btExploreBuyConds, setBtExploreBuyConds] = useState([]);
  const [btExploreSellConds, setBtExploreSellConds] = useState([]);
  // Short legs (stock venues only — Alpaca supports shorting; crypto spot
  // cannot). Expression strings; empty = long-only exploration.
  const [btExploreShortOpen, setBtExploreShortOpen] = useState('');
  const [btExploreShortClose, setBtExploreShortClose] = useState('');

  // True only when exploring with nothing selected. With a bot selected, Explore
  // uses it — so "copy from bot" and "explore from this bot" are the same act.
  // Explore ALWAYS runs on its own context (setup exchange/timeframe, explore
  // editor). A selected bot is a strategy seed via the STARTING STRATEGY
  // dropdown, never a run target — so this is no longer conditioned on having
  // no bot. The name keeps its history; the meaning is now "explore mode".
  const btExploreStandalone = btResearchTab === 'explore';

  // A bot-shaped object for the standalone case. Every downstream handler already
  // reads symbol/interval/exchange/trade_info off a bot, so giving them one that
  // happens to have no id is far less invasive than teaching each of them about
  // a second shape. The null id is what the backend now accepts as an unanchored
  // batch.
  const btExploreContext = useMemo(() => {
    const ex = userExchanges.find(x => String(x.id) === String(btExploreExchangeId));
    const exName = String(ex?.exchange || '').toLowerCase();
    // startsWith, not equality — the same rule getBacktestAssetClass and the
    // hasAlpaca check use. An exact match would read an 'alpaca.paper' style
    // connection as crypto and route the whole exploration to Binance.
    const assetClass = exName.startsWith('alpaca') ? 'stock_equity' : 'crypto_spot';
    return {
      id: null,
      name: 'Exploration',
      symbol: '',
      interval: btExploreTimeframe,
      exchangeId: btExploreExchangeId || null,
      exchange: ex?.exchange || '',
      exchangeName: ex?.name || ex?.exchange || '',
      asset_class: assetClass,
      status: 'paused',
      logic: { buy: btExploreBuy, sell: btExploreSell },
      // exchange travels in trade_info so the batch can resolve per-symbol
      // catalog fees for this venue — a bot's trade_info carries its venue the
      // same way.
      trade_info: JSON.stringify({ timeframe: btExploreTimeframe, marketType: 'spot', asset_class: assetClass, exchange: exName || undefined }),
    };
  }, [userExchanges, btExploreExchangeId, btExploreTimeframe, btExploreBuy, btExploreSell]);

  // What the research handlers should act on. Resolving it in one place keeps
  // the two modes from drifting apart across five call sites.
  const btActiveBot = btExploreStandalone ? btExploreContext : selectedBotForBacktest;

  // The identity a cost edit is scoped to. Changes whenever the venue context
  // does — different bot, different explore exchange, or a tab switch between
  // the two — which is exactly when hand-typed fees stop describing what runs.
  const btCostsContextKey = btExploreStandalone
    ? `explore:${btExploreExchangeId || 'none'}`
    : `bot:${selectedBotForBacktest?.id ?? 'none'}`;
  const btCostsAreCustom = btCostsTouchedFor !== null && btCostsTouchedFor === btCostsContextKey;
  const [btUniCount, setBtUniCount] = useState(20);
  const [btUniBusy, setBtUniBusy] = useState(false);
  const [btUniNote, setBtUniNote] = useState('');

  // ── Deployment gate ──
  // The only place the research output becomes binding. Holds the blocked
  // result plus the override reason, so a deliberate bypass is recorded rather
  // than silently allowed.
  const [btGate, setBtGate] = useState(null);          // { checks, passed, ... }
  const [btGateChecking, setBtGateChecking] = useState(false);
  const [btGateReason, setBtGateReason] = useState('');

  // ── Validation funnel ──
  // The friendly front door onto every check, in cost order, stopping at the
  // first failure so a dead idea never spends twenty backtests proving it.
  const btValPollRef = useRef(null);

  // ── Multi-symbol batch ──
  // The same strategy across many symbols. One symbol over one window is a
  // single sample, and no statistics on top of a single sample make it evidence.
  const [btBatch, setBtBatch] = useState(null);
  // When THIS batch started, for the running readout. Client-side clock is
  // fine: it drives elapsed display and a stalled warning, not billing.
  const [btBatchStartedAt, setBtBatchStartedAt] = useState(null);
  const [btBatchBusy, setBtBatchBusy] = useState(false);
  const [btBatchSymbols, setBtBatchSymbols] = useState('');
  const btBatchPollRef = useRef(null);

  // ── Portfolio view ──
  // Correlation between saved runs. Weak uncorrelated edges combine; ten copies
  // of one idea do not.

  // AI-first bot creation: describe a strategy on the Bots tab, AI drafts the
  // logic, and the wizard opens with it already loaded. This is the intended
  // default path — the AI panel inside the wizard's Trading Logic step is step
  // 2 of 3, so on its own it never greets the user.
  const [aiKickoffPrompt, setAiKickoffPrompt] = useState('');
  const [aiKickoffLoading, setAiKickoffLoading] = useState(false);

  const [symbolsByExchange, setSymbolsByExchange] = useState({});   // { [exchangeId]: ["BTCUSDT", ...] }
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState(null);
  const [symbolSearch, setSymbolSearch] = useState('');
  // Fee filter for the symbol picker. Same five modes and values as the
  // scanner's UniverseSelector so the two pickers behave identically.
  const [symbolFeeFilter, setSymbolFeeFilter] = useState('any');
  // Stock category chip for the Alpaca picker — shown INSTEAD of the fee
  // filter there (Alpaca is commission-free, so fee modes are meaningless
  // for stocks and would silently empty the list).
  const [symbolStockCategory, setSymbolStockCategory] = useState('any');
  const [confirmDeleteBotId, setConfirmDeleteBotId] = useState(null);
  const [isDeletingBot, setIsDeletingBot] = useState(false);
  const [futuresSymbolsByExchange, setFuturesSymbolsByExchange] = useState({});
  const [shortOpenConditions, setShortOpenConditions] = useState([]);
  const [shortCloseConditions, setShortCloseConditions] = useState([]);
  const [botListTab, setBotListTab] = useState('manual');  // 'manual' | 'scanner'
  const [expandedScannerProfile, setExpandedScannerProfile] = useState(null);

// Phase A: poll Alpaca account status (PDT, buying power, market hours, halts)
  // every 30s. Returns global market state + per-exchange-id account state.
  // Hook self-disables if no Alpaca exchanges are configured.
   const hasAlpacaExchange = useMemo(
    () => userExchanges.some(ex => String(ex.exchange || '').toLowerCase().startsWith('alpaca')),
    [userExchanges]
  );
  const alpacaStatus = useAlpacaAccountStatus({ enabled: hasAlpacaExchange });

  // Phase B — asset class derived from selected exchange
  const selectedExchangeForAssetClass = useMemo(
    () => userExchanges.find(ex => ex.id === newBot.exchangeId) || null,
    [userExchanges, newBot.exchangeId]
  );
const assetClass = useAssetClass(selectedExchangeForAssetClass);
  const isStockEquity = assetClass === 'stock_equity';

  // When asset class flips between crypto and stock, snap funding_asset to a
  // valid default. Prevents saving an Alpaca bot with funding_asset='USDT'
  // or a Binance bot stuck on 'USD'.
  useEffect(() => {
    const allowed = getFundingAssetsForClass(assetClass);
    if (!allowed.includes(newBot.funding_asset)) {
      setNewBot(prev => ({
        ...prev,
        funding_asset: defaultFundingAssetForClass(assetClass),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetClass]);

  // Phase B — shortability cache (only relevant for stock_equity)
  const shortability = useShortability({ enabled: isStockEquity });

  // Phase B — account status for the currently-selected exchange (for PDT warning)
  const accountStatusForSelected = useMemo(() => {
    if (!isStockEquity || !newBot.exchangeId) return null;
    return alpacaStatus?.accounts?.[newBot.exchangeId] || null;
  }, [isStockEquity, newBot.exchangeId, alpacaStatus]);

  // Create-bot readiness — drives the rail checklist + the readiness-gated footer.
  const botReadiness = useMemo(() => {
    const sizeOk = newBot.marketType === 'futures'
      ? (newBot.futureSizeMode === 'fixed_notional' ? Number(newBot.fixedNotional) > 0 : Number(newBot.marginPct) > 0)
      // Must match handleSave's hasSize check exactly. It validates tradeAmount
      // alone, so accepting positionValue here let the rail report "size set"
      // for a form that save would then reject.
      : Number(newBot.tradeAmount) > 0;
    const logicOk = newBot.signalSource === 'tradingview'
      ? !!newBot.webhookSecret
      : isWizExpression
        // Expression mode has no rows to count, and both sides must parse —
        // otherwise the rail reports ready for a form save will reject.
        ? (!!(wizRawLogic.buy || '').trim() && !!(wizRawLogic.sell || '').trim()
           && !validateLogicExpression(wizRawLogic.buy) && !validateLogicExpression(wizRawLogic.sell))
        : (buyConditions.length > 0 && sellConditions.length > 0);
    const gates = [
      { key: 'name', label: 'Name your bot', done: !!newBot.name?.trim(), step: 0 },
      { key: 'exchange', label: 'Connect an exchange', done: !!newBot.exchangeId, step: 0 },
      { key: 'pair', label: 'Choose a trading pair', done: newBot.signalSource === 'tradingview' ? true : !!newBot.symbol, step: 0 },
      { key: 'logic', label: 'Define entry & exit logic', done: logicOk, step: 1 },
      { key: 'size', label: 'Set position size', done: sizeOk, step: 0 },
      { key: 'risk', label: 'Set risk limits', done: Number(profitStop) > 0 && Number(lossStop) > 0, step: 2 },
    ];
    const doneCount = gates.filter((g) => g.done).length;
    return { gates, doneCount, total: gates.length, ready: doneCount === gates.length, firstMissing: gates.find((g) => !g.done) };
  }, [newBot, buyConditions.length, sellConditions.length, profitStop, lossStop,
      isWizExpression, wizRawLogic]);

  useEffect(() => {
    liveBacktestRef.current = liveBacktest;
  }, [liveBacktest]);

  useEffect(() => {
    const fetchUserExchanges = async () => {
      try {
        const data = await exchangeApiService.list(); // assuming it returns exchanges owned by the user
        setUserExchanges(data);
      } catch (err) {
        console.error("Failed to fetch exchanges", err);
      } finally {
        setExchangesLoaded(true);
      }
    };

    fetchUserExchanges();
  }, []);


  useEffect(() => {
    if (!newBot.exchangeId) return;
    const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
    const exchangeName = selectedExchange?.exchange || selectedExchange?.name;
    if (!exchangeName) return;

    if (newBot.marketType === 'futures') {
      // Load the relevant futures market
      const marketType = newBot.futuresType === 'coin_m' ? 'coinm_futures' : 'usdm_futures';
      ensureFuturesSymbols(newBot.exchangeId, exchangeName, marketType);
    } else {
      ensureSymbols(newBot.exchangeId, exchangeName);
    }
  }, [newBot.exchangeId, newBot.marketType, newBot.futuresType, userExchanges]);


  const fmtMoneyAdaptive = (v) => {
    if (!isNum(v)) return '—';
    const n = Number(v);
    const digits = Math.abs(n) < 1 ? 4 : 2;
    return fmtMoney(n, digits);
  };



  // right inside your component
  const exchangeValue = React.useMemo(
    () => userExchanges.find(ex => ex.id === newBot.exchangeId) || null,
    [userExchanges, newBot.exchangeId]
  );
  // "BTCUSDT" <-> "BTC/USDT" helpers for display
  const compactToPretty = (s) => (typeof s === 'string' && s.length > 3
    ? s.replace(/(USDT|USDC|BTC|ETH|BUSD|USD|EUR)$/, '/$1')
    : s);

  const prettyToCompact = (s) => (typeof s === 'string' ? s.replace(/\//g, '') : s);

  // fetch & cache symbols for a given exchangeId (from Redis)
  async function ensureSymbols(exchangeId, exchangeName) {
    const cacheKey = exchangeName || exchangeId;
    if (!cacheKey || symbolsByExchange[cacheKey]) return;

    try {
      setSymbolsLoading(true);
      setSymbolsError(null);

      const list = await symbolService.get(exchangeName, 'objects');

      const normalized = Array.isArray(list)
        ? list.map(item => {
          if (typeof item === 'string') {
            const [BASE, QUOTE] = splitPair(item);
            return {
              symbol: item,
              base_asset: BASE,
              base_precision: null,
              quote_asset: QUOTE,
              quote_precision: null,
              categories: [],
              default_maker_fee: null,
              default_taker_fee: null,
              is_zero_fee: false,
            };
          }

          const sym = item.symbol;
          let base = item.base_asset;
          let quote = item.quote_asset;

          if (base && !quote) {
            quote = sym.toUpperCase().replace(new RegExp(`^${base}`, 'i'), '');
            if (!quote || quote === sym.toUpperCase()) {
              const [, fallbackQuote] = splitPair(sym);
              quote = fallbackQuote;
            }
          }
          if (!base || !quote) {
            const [fallbackBase, fallbackQuote] = splitPair(sym);
            base = base || fallbackBase;
            quote = quote || fallbackQuote;
          }

          return {
            symbol: sym,
            base_asset: base,
            base_precision: item.base_precision ?? item.precision ?? null,
            quote_asset: quote,
            quote_precision: item.quote_precision ?? 2,
            // ✅ Carry through metadata
            categories: Array.isArray(item.categories) ? item.categories : [],
            default_maker_fee: item.default_maker_fee != null ? Number(item.default_maker_fee) : null,
            default_taker_fee: item.default_taker_fee != null ? Number(item.default_taker_fee) : null,
            is_zero_fee: !!item.is_zero_fee,
            // Stock metadata (Alpaca catalogs) — powers the stock category
            // chips; absent on crypto rows.
            asset_class: item.asset_class ?? null,
            exchange_listed: item.exchange_listed ?? null,
            sector: item.sector ?? null,
            is_etf: !!item.is_etf,
          };
        })
        : [];

      setSymbolsByExchange(m => ({ ...m, [cacheKey]: normalized }));
    } catch (e) {
      console.error('symbols fetch failed', e);
      setSymbolsError('Failed to load symbols');
    } finally {
      setSymbolsLoading(false);
    }
  }

  useEffect(() => {
    const saved = localStorage.getItem('bt.params');
    if (saved) {
      try {
        // Costs are NEVER restored across sessions: commission/slippage come
        // from the selected venue's defaults unless edited for the current
        // context, and a stale 0.5% slippage restored from last week is
        // exactly the silent override that rule exists to prevent. Everything
        // else (capital, risk exits) is a user preference and comes back.
        const { costsTouched, commission, slippage, ...rest } = JSON.parse(saved);
        setBacktestParams(prev => ({ ...prev, ...rest }));
      } catch { }
    }
  }, []);


  // ── Fetch Binance futures info for all futures bots ──
  useEffect(() => {
    if (!bots.length) return;

    const futureBots = bots.filter(b => {
      try {
        const ti = JSON.parse(b.trade_info || '{}');
        return ti.marketType === 'futures';
      } catch { return false; }
    });

    if (!futureBots.length) return;

    const controller = new AbortController();

    const fetchFuturesData = async () => {
      const updates = {};

      for (const bot of futureBots) {
        let ti;
        try { ti = JSON.parse(bot.trade_info || '{}'); } catch { continue; }

        const symbol = (ti.symbol || bot.symbol || '').replace('/', '').toUpperCase();
        if (!symbol) continue;

        const isCoinM = ti.futures?.type === 'coin_m' || ti.futuresType === 'coin_m';
        const base = isCoinM
          ? 'https://dapi.binance.com/dapi/v1'
          : 'https://fapi.binance.com/fapi/v1';

        try {
          const [premiumRes, oiRes, fundingRes] = await Promise.all([
            fetch(`${base}/premiumIndex?symbol=${symbol}`, { signal: controller.signal }),
            fetch(`${base}/openInterest?symbol=${symbol}`, { signal: controller.signal }),
            fetch(`${base}/fundingRate?symbol=${symbol}&limit=1`, { signal: controller.signal }),
          ]);

          const premium = await premiumRes.json();
          const oi = await oiRes.json();
          const fundingArr = await fundingRes.json();

          // premiumIndex response shape (USDT-M):
          //   { symbol, markPrice, indexPrice, lastFundingRate, nextFundingTime, ... }
          const p = Array.isArray(premium) ? premium[0] : premium;
          const f = Array.isArray(fundingArr) ? fundingArr[0] : fundingArr;

          updates[bot.id] = {
            markPrice: Number(p?.markPrice) || null,
            indexPrice: Number(p?.indexPrice) || null,
            lastFundingRate: Number(p?.lastFundingRate ?? f?.fundingRate) || null,
            nextFundingTime: Number(p?.nextFundingTime) || null,
            interestRate: Number(p?.interestRate) || null,
            openInterest: Number(oi?.openInterest) || null,
            fetchedAt: Date.now(),
          };
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.warn(`Futures info fetch failed for ${symbol}:`, err);
          }
        }
      }

      setFuturesInfo(prev => ({ ...prev, ...updates }));
    };

    fetchFuturesData();
    const interval = setInterval(fetchFuturesData, 30_000); // refresh every 30s

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [botIdsKey]); // re-run when bot list changes


  useEffect(() => {
    // Costs excluded on the way OUT too — see the restore effect above.
    const { commission, slippage, ...rest } = backtestParams;
    localStorage.setItem('bt.params', JSON.stringify(rest));
  }, [backtestParams]);

  // Follow the ACTIVE bot's venue: Alpaca equities are commission-free, spot
  // and futures have different taker fees and book depth. Stops once the user
  // has entered their own numbers.
  //
  // btActiveBot, not the selection — in Explore the venue is the setup
  // exchange, and keying off the selection left an Alpaca exploration on the
  // crypto-spot preset: 0.10% commission charged against commission-free
  // equities, a ~3x overstated round trip that every breadth run, sweep and
  // discovery cost hurdle then used. Identity churn on the explore context is
  // harmless: the setter bails when the numbers are unchanged.
  useEffect(() => {
    if (!btActiveBot) return;
    // A touch made in THIS context holds; a touch made anywhere else is stale
    // the moment the context changes and is cleared, not honoured — otherwise
    // an optimize→explore→optimize round trip would leave explore's venue
    // numbers on screen under a flag still claiming "yours".
    if (btCostsTouchedFor === btCostsContextKey) return;
    if (btCostsTouchedFor !== null) setBtCostsTouchedFor(null);
    const { commission, slippage } = defaultBacktestCosts(btActiveBot);
    setBacktestParams(prev => (
      prev.commission === commission && prev.slippage === slippage
        ? prev
        : { ...prev, commission, slippage }
    ));
  }, [btActiveBot, btCostsTouchedFor, btCostsContextKey]);

  useEffect(() => {
    const bounds = computeBacktestBounds(selectedBotForBacktest);
    setRangeBounds(bounds);

    setBacktestDateRange(prev => {
      // if no prior range, fall back to your default 1-month window
      let [s, e] = Array.isArray(prev) ? prev : defaultBacktestRange();

      // clamp to new bounds
      s = Math.max(bounds.min, s);
      e = Math.min(bounds.max, e);

      // if inverted, show ~30 days ending at max
      if (s > e) {
        e = bounds.max;
        s = Math.max(bounds.min, e - 30 * MS_DAY);
      }
      return [s, e];
    });
  }, [selectedBotForBacktest]);


  useEffect(() => {
    return () => {
      // Clear poll interval on unmount
      if (backtestPollRef.current) clearInterval(backtestPollRef.current);
      // component unmount cleanup
      for (const { channel, handlers } of backtestListenersRef.current.values()) {
        for (const h of handlers || []) {
          socket.off(channel, h);
        }
      }
      backtestListenersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const fetchBots = async () => {
      try {
        const userId = parseInt(localStorage.getItem('userId'), 10);

        // Fetch regular bots (excludes scanner profiles by default)
        const regularBots = await tradingBotService.list(userId);

        // Fetch scanner profiles separately
        let scannerProfiles = [];
        try {
          scannerProfiles = await tradingBotService.list(userId, { status: 'scanner_profile' });
        } catch (err) {
          console.warn('Failed to fetch scanner profiles:', err);
        }

        // Merge: scanner profiles first, then regular bots
        // Merge with dedup — guards against any API call returning overlapping rows
        const seenIds = new Set();
        const botsFromServer = [];
        for (const bot of [...scannerProfiles, ...regularBots]) {
          if (seenIds.has(bot.id)) continue;
          seenIds.add(bot.id);
          botsFromServer.push(bot);
        }
        // Trade history is NOT bulk-loaded here anymore (that downloaded every
        // bot's entire history up front). The socket `join` seeds the most recent
        // N entries per bot, and expanding a bot lazy-loads older pages via
        // GET /api/userhistory/by_bot/:botId. Existing liveBotHistories is left
        // intact across refreshes.

        // 📦 Process bots
        const formattedBots = botsFromServer.map(bot => {
          let tradeInfo = {};
          try {
            tradeInfo = JSON.parse(bot.trade_info || '{}');
          } catch (_) { }

          const webhookSecret = (tradeInfo.webhook?.secret || '').trim();
          const signalSource = tradeInfo.signalSource || 'logic';

          return {
            id: bot.id,
            name: bot.name,
            symbol: tradeInfo.symbol,
            interval: tradeInfo.timeframe,
            exchangeId: bot.exchange_id ?? bot.exchangeId ?? null,
            exchange: bot.exchange?.exchange || '',
            trade_info: bot.trade_info,
            // The COLUMN, carried through alongside trade_info. Stock-only UI
            // reads `ti.asset_class || bot.asset_class`, and that second half
            // could never fire because this was dropped here — so a stock bot
            // whose asset class lives only in the column (anything created
            // before trade_info started mirroring it) read as crypto, and the
            // stock-only controls silently did not render.
            asset_class: bot.asset_class ?? null,
            exchangeName: bot.exchange?.name || bot.exchange?.exchange || "—",
            status: bot.stop_trade_ASSET ? 'paused' : 'active',
            stop_trade_ASSET: bot.stop_trade_ASSET,
            logic: {
              buy: bot.buy_logic,
              sell: bot.sell_logic,
              shortOpen: tradeInfo.futures?.shortOpenLogic || tradeInfo.short_open_logic || bot.short_open_logic || '',
              shortClose: tradeInfo.futures?.shortCloseLogic || tradeInfo.short_close_logic || bot.short_close_logic || '',
              profitStop: tradeInfo.profitStop,
              lossStop: tradeInfo.lossStop,
              maxLoss: tradeInfo.maxDailyLoss,
              maxTrades: tradeInfo.maxTradesPerDay,
              trailingStop: tradeInfo.trailingStop,
              investment: tradeInfo.totalInvestment,
            },
            // Normalize server stats at fetch time (the socket path already does)
            // so bot.stats is always the panel's PascalCase shape; otherwise the
            // first paint would read camelCase DB stats and show blanks.
            stats: bot.statistics ? normalizeBotStats(bot.statistics) : undefined,
            statusMessage: bot.status || '',
            errorMessage: bot.error_message || '',

            // ✅ TradingView webhook fields
            signalSource,
            webhookUrl: signalSource === 'tradingview'
              ? buildTradingViewWebhookUrl(bot.id, webhookSecret)
              : '',
            webhookConfig: tradeInfo.webhook || null,
            isScannerProfile: bot.status === 'scanner_profile',
            spawnedFromProfileId: bot.spawned_from_profile_id ?? null,
            scannerConfig: tradeInfo.scanner || null,
            spawnedFromMeta: tradeInfo._spawned_from || null,
          };
        });

        setBots(formattedBots);
      } catch (error) {
        console.error('Error fetching bots or histories:', error);
      }
    };

    fetchBots();
  }, []);


  // Preload symbol caches for every exchange the user's bots live on.
// Without this, spawned bots on a sim exchange (or any exchange the user
// hasn't browsed in the create-bot form yet) fall through to splitPair
// and mis-render fiat-quoted pairs (TRY, BRL, EUR, etc.) as USD.
useEffect(() => {
  if (!bots.length || !userExchanges.length) return;
  const seen = new Set();
  for (const bot of bots) {
    const ex = userExchanges.find(e => e.id === bot.exchangeId);
    const name = ex?.exchange || ex?.name;
    if (!name || seen.has(name)) continue;
    seen.add(name);
    ensureSymbols(bot.exchangeId, name);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [bots.length, userExchanges.length]);


  useEffect(() => {
    // Keep websocket room membership & per-bot listeners in sync with the current bot list.
    // This preserves existing behavior while preventing duplicate listeners on reconnects/rerenders.
    const userId = parseInt(localStorage.getItem("userId"), 10);

    const registerBotListener = (botId) => {
      const channel = `botUpdate:${botId}`;
      if (listenersRef.current.has(channel)) return;

      const listener = (data) => {
        console.log(`[${channel}] update received`, data);

        const actualBotId = parseInt(data.bot_id || data.id || botId, 10);
        if (!actualBotId) return;

        // ✅ Handle stats - could be object, string, or nested in botData
        let statsData = data.stats || data.botData?.stats;
        if (typeof statsData === 'string') {
          try { statsData = JSON.parse(statsData); } catch { statsData = null; }
        }
        const normalizedStats = statsData && Object.keys(statsData).length > 0
          ? normalizeBotStats(statsData)
          : null;

        // Parse history entries
        const historyData = Array.isArray(data.history) ? data.history : [];
        const parsedEntries = historyData.map(entry => {
          let parsed = entry;
          if (typeof entry === 'string') {
            try { parsed = JSON.parse(entry); } catch { return null; }
          }
          let cleanText = parsed.buy_sell_history;
          if (typeof cleanText === 'string') {
            try { cleanText = JSON.parse(cleanText); } catch { }
          }
          const ms = new Date(parsed.date).getTime();
          return { _ts: Number.isFinite(ms) ? ms : 0, timestamp: new Date(parsed.date).toLocaleString(), text: cleanText };
        }).filter(Boolean);

        if (parsedEntries.length > 0) {
          setLiveBotHistories(prev => {
            const updated = { ...prev };
            updated[actualBotId] = mergeHistoryEntries(updated[actualBotId] || [], parsedEntries);
            return updated;
          });
        }

        // Update bot state with ALL fields
        setBots(prev =>
          prev.map(bot => {
            if (bot.id !== actualBotId) return bot;

            const updatedBot = { ...bot };

            // ✅ Update stats if present
            if (normalizedStats) {
              updatedBot.stats = normalizedStats;
              console.log(`[${channel}] stats updated:`, normalizedStats);
            }

            // Status fields
            if (data.error_message !== undefined) updatedBot.errorMessage = data.error_message;
            if (data.error_side !== undefined) updatedBot.errorSide = data.error_side;
            if (data.error_code !== undefined) updatedBot.errorCode = data.error_code;
            if (data.error_at !== undefined) updatedBot.errorAt = data.error_at;
            if (data.status !== undefined) updatedBot.statusMessage = data.status;
            if (data.stop_trade_ASSET !== undefined) {
              updatedBot.stop_trade_ASSET = data.stop_trade_ASSET;
              updatedBot.status = data.stop_trade_ASSET ? 'paused' : 'active';
            }

            // Guard data from decision engine
            if (data.guard_tag !== undefined) updatedBot.guardTag = data.guard_tag || '';
            if (data.guard_reason !== undefined) updatedBot.guardReason = data.guard_reason || '';
            if (data.guard_at !== undefined) updatedBot.guardAt = data.guard_at || '';
            if (data.guard_side !== undefined) updatedBot.guardSide = data.guard_side || '';
            if (data.guard_price !== undefined) updatedBot.guardPrice = data.guard_price || '';

            // Current price
            if (data.ASSET_PRICE) updatedBot.currentPrice = data.ASSET_PRICE;

            // ✅ Last trade info
            if (data.last_trade_side) updatedBot.lastTradeSide = data.last_trade_side;
            if (data.last_trade_price) updatedBot.lastTradePrice = data.last_trade_price;
            if (data.last_trade_qty) updatedBot.lastTradeQty = data.last_trade_qty;
            if (data.last_trade_ts) updatedBot.lastTradeTs = data.last_trade_ts;

            // Pending limit order info
            // Pending limit order info
            if (data.pending_order_id !== undefined) updatedBot.pendingOrderId = data.pending_order_id || '';
            if (data.pending_order_side !== undefined) updatedBot.pendingOrderSide = data.pending_order_side || '';
            if (data.pending_order_price !== undefined) updatedBot.pendingOrderPrice = data.pending_order_price || '';
            // Phase A: partial fill progress
            if (data.pending_order_quantity !== undefined) updatedBot.pendingOrderQuantity = data.pending_order_quantity || '';
            if (data.pending_order_applied_qty !== undefined) updatedBot.pendingOrderAppliedQty = data.pending_order_applied_qty || '';
            // Legacy trading status fields
            if (data.trading_status !== undefined) updatedBot.tradingStatus = data.trading_status;
            if (data.trading_status_reason !== undefined) updatedBot.tradingStatusReason = data.trading_status_reason;

            // ✅ Clear guard state if status is active and no guard_tag
            if (data.status === 'active' && !data.guard_tag && !data.error_message) {
              updatedBot.guardTag = '';
              updatedBot.guardReason = '';
              updatedBot.guardAt = '';
              updatedBot.guardSide = '';
              updatedBot.guardPrice = '';
              updatedBot.errorMessage = '';
            }

            return updatedBot;
          })
        );
      };

      listenersRef.current.set(channel, listener);
      socket.on(channel, listener);
    };

    const unregisterBotListener = (botId) => {
      const channel = `botUpdate:${botId}`;
      const listener = listenersRef.current.get(channel);
      if (listener) {
        socket.off(channel, listener);
        listenersRef.current.delete(channel);
      } else {
        // keep parity with prior behavior (removes any listener if present)
        socket.off(channel);
      }
    };

    const joinAll = () => {
      console.log("✅ WebSocket connected");

      if (userId) {
        socket.emit('join', userId); // 🔁 Request full sync
      }

      // Join rooms + attach listeners (idempotent)
      botIds.forEach(botId => {
        socket.emit("joinBotRoom", botId);
        console.log(`🟢 Joined room: bot:${botId}`);
        registerBotListener(botId);
      });
    };

    const handleConnect = () => {
      joinAll();
    };

    socket.on("connect", handleConnect);

    // If already connected, trigger immediately
    if (socket.connected) {
      handleConnect();
    } else {
      // While disconnected, ensure no stale listeners stick around (matches previous cleanup behavior)
      botIds.forEach(unregisterBotListener);
    }

    return () => {
      // Leave rooms and remove only the listeners we registered.
      botIds.forEach(botId => {
        socket.emit("leaveBotRoom", botId);
        unregisterBotListener(botId);
      });
      socket.off("connect", handleConnect);
    };
  }, [botIdsKey]);


  function safeParseMaybe(objOrStr, fallback = {}) {
    if (objOrStr == null) return fallback;
    if (typeof objOrStr === 'string') {
      try { return JSON.parse(objOrStr); } catch { return fallback; }
    }
    return objOrStr;
  }

  // normalize detail → {metrics, trades, equityCurve}
  function normalizeBacktestDetail(detail = {}) {
    const metrics = safeParseMaybe(detail.metrics, {});
    const trades = Array.isArray(detail.trades)
      ? detail.trades
      : safeParseMaybe(detail.trades, []);
    const equityCurve =
      Array.isArray(detail.equity_curve) ? detail.equity_curve :
        Array.isArray(detail.equity) ? detail.equity :
          safeParseMaybe(detail.equity_curve || detail.equity, []);

    return { metrics, trades, equityCurve };
  }

  // Is the create form currently in tick mode?
  const isTickInterval = useMemo(
    () => String(newBot.interval || '').toLowerCase() === 'tick',
    [newBot.interval]
  );

  // Split indicators by family
  const availableIndicators = useMemo(() => {
    return indicators.filter(ind =>
      isTickInterval ? TICK_INDICATOR_NAMES.has(ind.name) : !TICK_INDICATOR_NAMES.has(ind.name)
    );
  }, [isTickInterval]);

  function normalizeBotStats(raw = {}) {
    if (!raw || typeof raw !== 'object') return {};

    const num = (v) => {
      if (v === null || v === undefined || v === '') return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    return {
      // Core capital
      InitialCapital: num(raw.initialCapital),
      CashQuote: num(raw.cashQuote),
      Equity: num(raw.equity),
      PeakEquity: num(raw.peakEquity),

      // Long position info
      PositionQty: num(raw.positionQty),
      PositionCost: num(raw.positionCost),
      EntryPrice: num(raw.entryPrice),
      MarkPrice: num(raw.markPrice),
      TrailPeakPrice: num(raw.trailPeakPrice),

      // ─── Short position info (Phase 7) ───
      ShortPositionQty: num(raw.shortPositionQty),
      ShortPositionCost: num(raw.shortPositionCost),
      ShortEntryPrice: num(raw.shortEntryPrice),
      ShortTrailLowPrice: num(raw.shortTrailLowPrice),

      // PnL
      RealizedPnL: num(raw.realizedPnL),
      RealizedPnLPct: num(raw.realizedPnLPct),
      GrossProfit: num(raw.grossProfit),
      GrossLoss: num(raw.grossLoss),

      // Drawdown
      MaxDrawdownPct: num(raw.maxDrawdownPct),

      // Trade stats
      Trades: num(raw.trades),
      Wins: num(raw.wins),
      Losses: num(raw.losses),
      WinRatePct: num(raw.winRatePct),
      // The engine emits 9999 as a "wins, no losses" sentinel; show it as ∞
      // (the panel's fmtRatio renders Infinity as '∞').
      ProfitFactor: (() => { const pf = num(raw.profitFactor); return pf === 9999 ? Infinity : pf; })(),

      // Volume & Fees
      VolumeBase: num(raw.volumeBase),
      VolumeQuote: num(raw.volumeQuote),
      FeesPaidQuote: num(raw.feesPaidQuote),

      // Meta
      Initialized: num(raw.initialized),
      UpdatedAt: raw.updatedAt,

      __raw: raw,
    };
  }


  // ✅ Trading status display helper
  // ✅ Trading status display helper
  // ✅ Enhanced Trading status display helper
  // ✅ Enhanced Trading status display helper - BLOCK states are normal flow

  // Augments checkBotBacktestable with a fallback extraction from buy/sell logic
  // strings, in case trade_info.indicators wasn't populated at save time.
  const gateBacktest = (bot, opts = { hasFuturesData: false }) => {
    if (!bot) return { supported: false, message: 'No bot selected', unsupported: [] };

    let ti = {};
    try {
      ti = typeof bot.trade_info === 'string'
        ? JSON.parse(bot.trade_info || '{}')
        : (bot.trade_info || {});
    } catch {
      return { supported: false, message: 'Bot configuration is malformed', unsupported: [] };
    }

    const hasIndicators = Array.isArray(ti.indicators) && ti.indicators.length > 0;

    if (!hasIndicators) {
      const buy = bot.logic?.buy || bot.buy_logic || '';
      const sell = bot.logic?.sell || bot.sell_logic || '';
      const shortOpen = bot.logic?.shortOpen || ti.futures?.shortOpenLogic || ti.short_open_logic || '';
      const shortClose = bot.logic?.shortClose || ti.futures?.shortCloseLogic || ti.short_close_logic || '';
      const extracted = extractIndicatorsFromLogic(`${buy} ${sell} ${shortOpen} ${shortClose}`);
      ti = { ...ti, indicators: extracted };
    }

    // Pass a patched bot so checkBotBacktestable sees the indicators
    const patched = { ...bot, trade_info: JSON.stringify(ti) };
    return checkBotBacktestable(patched, opts);
  };

  function getTradingStatusDisplay(bot) {
    let status = bot.guardTag || bot.tradingStatus;
    let reason = bot.guardReason || bot.tradingStatusReason;

    // ✅ BLOCK states are normal trading flow - don't show alerts
    if (status === 'BLOCK') {
      return null;
    }

    // Fallback: derive from errorMessage if no explicit status
    if (!status && bot.errorMessage) {
      const errLower = bot.errorMessage.toLowerCase();

      // Skip normal block reasons
      if (errLower === 'noposition' || errLower === 'alreadyinposition') {
        return null;
      }

      if (errLower.includes('maxtrades') || errLower.includes('maxtradesperday')) {
        status = 'PAUSE';
        reason = 'maxTradesPerDay';
      } else if (errLower.includes('profit') && !errLower.includes('factor')) {
        status = 'PAUSE';
        reason = 'profitStop';
      } else if (errLower.includes('dailyloss') || errLower.includes('daily loss')) {
        status = 'PAUSE';
        reason = 'maxDailyLoss';
      } else if (errLower.includes('loss') && !errLower.includes('daily')) {
        status = 'PAUSE';
        reason = 'lossStop';
      } else if (errLower.includes('trailing')) {
        status = 'FORCE_SELL';
        reason = 'trailingStop';
      } else if (errLower.includes('manual')) {
        status = 'PAUSE';
        reason = 'manualStop';
      }


      if (!status) {
        return {
          status: 'error',
          reason: 'tradeRejected',
          color: 'error',
          icon: <WarningAmber fontSize="small" />,
          label: 'Trade Rejected',
          description: bot.errorMessage,    // shows the full reason
          guardAt: bot.errorAt,
          guardSide: bot.errorSide,
        };
      }


      // If none match, don't show alert
      if (!status) return null;
    }

    // No alert needed for normal states
    if (!status || status === 'IDLE' || status === 'ALLOW') {
      return null;
    }

    const statusConfig = {
      error: {
        color: 'error',
        icon: <WarningAmber fontSize="small" />,
        reasons: {},  // dynamic — uses errorMessage directly
      },
      PAUSE: {
        color: 'warning',
        icon: <Schedule fontSize="small" />,
        reasons: {
          profitStop: { label: 'Profit Target Reached', description: 'Take profit threshold hit. Trading paused.' },
          lossStop: { label: 'Stop Loss Triggered', description: 'Maximum loss threshold reached.' },
          maxDailyLoss: { label: 'Daily Loss Limit', description: 'Maximum daily loss reached. Resumes tomorrow.' },
          maxTradesPerDay: { label: 'Daily Trade Limit Reached', description: 'Maximum trades per day reached. Resumes at midnight UTC.' },
          manualStop: { label: 'Manually Paused', description: 'Bot was manually paused by user.' },
        }
      },
      FORCE_SELL: {
        color: 'error',
        icon: <TrendingDown fontSize="small" />,
        reasons: {
          trailingStop: { label: 'Trailing Stop Hit', description: 'Price dropped below trailing stop. Position closed.' },
          lossStop: { label: 'Stop Loss Executed', description: 'Stop loss triggered. Position closed.' },
        }
      },
    };

    const config = statusConfig[status];
    if (!config) return null; // Unknown status - don't show

    const reasonInfo = config.reasons[reason] || {
      label: status === 'error' ? 'Trade Error' : formatReasonLabel(reason || status),
      description: status === 'error'
        ? (bot.errorMessage || `Trading error: ${formatReasonLabel(reason)}`)
        : `Trading ${status.toLowerCase()}: ${formatReasonLabel(reason)}`
    };

    return {
      status,
      reason,
      color: config.color,
      icon: config.icon,
      label: reasonInfo.label,
      description: reasonInfo.description,
      guardAt: bot.guardAt,
      guardPrice: bot.guardPrice,
      guardSide: bot.guardSide,
    };
  }

  // Helper to format camelCase/snake_case to readable text
  function formatReasonLabel(str) {
    if (!str) return '';
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\s+/, '')
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }




  const filteredSymbolOptions = useMemo(() => {
    const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
    const exchangeName = selectedExchange?.exchange || selectedExchange?.name || newBot.exchangeId;

    let all = [];
    if (newBot.marketType === 'futures') {
      const marketType = newBot.futuresType === 'coin_m' ? 'coinm_futures' : 'usdm_futures';
      const cacheKey = `${exchangeName}:${marketType}`;
      all = futuresSymbolsByExchange[cacheKey] || [];
      // Only show TRADING symbols for futures
      all = all.filter(s => !s.status || s.status === 'TRADING');
    } else {
      const cacheKey = exchangeName;
      all = symbolsByExchange[cacheKey] || [];
    }

    if (isStockEquity) {
      // Fee modes never apply to stocks — their fees are null, so any
      // zero-fee mode would exclude every row. The category chip does.
      if (symbolStockCategory && symbolStockCategory !== 'any') {
        all = all.filter(s => (symbolStockCategory === 'etf'
          ? s.is_etf
          : (s.sector === symbolStockCategory || s.exchange_listed === symbolStockCategory)));
      }
    } else if (symbolFeeFilter && symbolFeeFilter !== 'any') {
      all = all.filter(s => symbolMatchesFeeFilter(s, symbolFeeFilter));
    }

    if (!symbolSearch) return all;
    const q = symbolSearch.trim().toUpperCase().replace('/', '');
    return all.filter(s => {
      const sym = (s.symbol || '').toUpperCase();
      return sym.includes(q) || sym.replace('/', '').includes(q);
    });
  }, [symbolsByExchange, futuresSymbolsByExchange, newBot.exchangeId, newBot.marketType, newBot.futuresType, symbolSearch, symbolFeeFilter, symbolStockCategory, isStockEquity, userExchanges]);

  // How many symbols this exchange has fee data for at all — lets the picker
  // say "no fee data" instead of just showing an empty list, which would look
  // like a bug when the data simply hasn't been populated.
  const symbolsWithFeeData = useMemo(() => {
    const ex = userExchanges.find(e => e.id === newBot.exchangeId);
    const list = symbolsByExchange[ex?.exchange || ex?.name] || [];
    return list.filter(s => s.is_zero_fee || s.default_maker_fee != null || s.default_taker_fee != null).length;
  }, [symbolsByExchange, newBot.exchangeId, userExchanges]);

  // Stock category chips, data-driven from the loaded catalog: real sectors
  // once the enrichment job populates them, else listing venues (always set
  // by the Alpaca /v2/assets sync), plus ETF when that flag exists. The chip
  // value doubles as the match key in filteredSymbolOptions.
  const stockCategoryChips = useMemo(() => {
    if (!isStockEquity) return [];
    const ex = userExchanges.find(e => e.id === newBot.exchangeId);
    const list = symbolsByExchange[ex?.exchange || ex?.name] || [];
    const tally = (key) => {
      const m = new Map();
      for (const s of list) { const v = s[key]; if (v) m.set(v, (m.get(v) || 0) + 1); }
      return m;
    };
    const sectors = tally('sector');
    const source = sectors.size ? sectors : tally('exchange_listed');
    const chips = [...source.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10)
      .map(([v]) => ({ value: v, label: v }));
    if (list.some(s => s.is_etf)) chips.unshift({ value: 'etf', label: 'ETF' });
    return [{ value: 'any', label: 'Any' }, ...chips];
  }, [isStockEquity, symbolsByExchange, newBot.exchangeId, userExchanges]);

  // Changing exchange invalidates the chip vocabulary — snap back to Any.
  useEffect(() => { setSymbolStockCategory('any'); }, [newBot.exchangeId]);

  async function cancelBacktest() {
    const jobId = liveBacktest?.jobId;
    if (!jobId) return;
    try {
      setIsCanceling(true);
      setConfirmCancelOpen(false); // close the confirm dialog immediately

      await backtestService.cancel(jobId);

      // Immediately reflect in UI; the worker/status key will confirm shortly
      setLiveBacktest(prev => prev ? { ...prev, status: 'canceled' } : prev);
      setIsBacktesting(false);

      // Optional: stop listening right away
      socket.emit('leaveBacktest', jobId);
      detachBacktestListeners(jobId);
    } catch (err) {
      console.error('Cancel backtest failed:', err);
      alert('Failed to cancel. Check server logs.');
    } finally {
      setIsCanceling(false);
    }
  }

  // Delete by id. Previously this only ever read viewingSavedBacktestId, which
  // is set inside openSavedBacktest — so a row that FAILED to open could never
  // be deleted: the only delete button lived in the report dialog that never
  // appeared. Taking an explicit id lets the history list delete a row directly,
  // without depending on the detail fetch succeeding.
  async function handleDeleteSavedBacktest(idArg) {
    const id = idArg ?? viewingSavedBacktestId;
    if (!id) return;
    try {
      setDeleting(true);
      const userId = Number(localStorage.getItem('userId')); // optional ownership
      await backtestService.deleteResult(id, userId);
      setConfirmDeleteOpen(false);
      setConfirmDeleteBacktest(null);
      // Only tear down the report view if we deleted the row it was showing.
      if (id === viewingSavedBacktestId) closeBacktestDialog();
      await fetchRecentBacktests();
    } catch (err) {
      console.error('Delete backtest failed:', err);
      alert(err?.response?.data?.error || 'Failed to delete this backtest.');
    } finally {
      setDeleting(false);
    }
  }
  async function openSavedBacktest(btRow) {
    try {
      setViewingSavedBacktestId(btRow.id);
      setLoadingSavedBacktest(true);

      const detail = await backtestService.getDetail(btRow.id);
      const { metrics, trades, equityCurve } = normalizeBacktestDetail(detail || btRow);
      const parsedCfg =
        safeParseMaybe(detail?.backtest_config) ||
        safeParseMaybe(detail?.config) ||
        safeParseMaybe(btRow?.backtest_config) ||
        safeParseMaybe(btRow?.config) ||
        null;

      const botId = btRow.bot_id ?? detail.bot_id;
      // Compared as strings: trading_bots.id is INTEGER (arrives as a number)
      // but backtests.bot_id is BIGINT, which pg serialises as a STRING — so
      // `b.id === botId` was 347 === "347" and never matched. Every run opened
      // from history therefore fell through to the synthetic bot below, which
      // carries no asset class, and the stock-only controls (flatten before
      // close) silently disappeared even though the bot still exists.
      const found = bots.find(b => String(b.id) === String(botId));

      // pull strategy from the saved record
      const savedStrategy = (() => {
        const raw = detail?.strategy ?? btRow?.strategy;
        try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || {}); }
        catch { return {}; }
      })();

      // symbol / timeframe / strategy / range are TOP-LEVEL columns on the saved
      // row, but the report reads them out of backtest_config — so they always
      // came back undefined and fell through to the currently-selected bot (or
      // "—" once that bot was gone). Merge the record's own values in so a
      // historical report describes the run that actually happened.
      const savedCfg = {
        ...(parsedCfg || {}),
        symbol: parsedCfg?.symbol ?? btRow.symbol ?? detail?.symbol,
        timeframe: parsedCfg?.timeframe ?? btRow.timeframe ?? detail?.timeframe,
        strategy: parsedCfg?.strategy
          ?? ((savedStrategy.buy || savedStrategy.sell) ? savedStrategy : undefined),
        start_time_ms: parsedCfg?.start_time_ms ?? btRow.start_time_ms ?? detail?.start_time_ms,
        end_time_ms: parsedCfg?.end_time_ms ?? btRow.end_time_ms ?? detail?.end_time_ms,
      };

      const matchBot = found || {
        id: botId,
        name: btRow.bot_name || `Bot #${botId ?? '—'}`,
        symbol: btRow.symbol ?? detail.symbol,
        interval: (btRow.timeframe ?? detail.timeframe ?? '').toUpperCase(),
        // The run's own venue, so a bot that has since been deleted still
        // reconstructs as the asset class it actually ran as. backtests has
        // carried this column since the asset-class migration; without it
        // isStockBot() reads the placeholder as crypto and hides the
        // stock-only risk exits.
        asset_class: btRow.asset_class ?? detail?.asset_class ?? savedCfg.asset_class ?? null,
        // ✅ ensure logic exists so future runs don’t crash
        logic: {
          buy: savedStrategy.buy || '',
          sell: savedStrategy.sell || '',
        },
      };

      const bounds = computeBacktestBounds(matchBot); // ✅ define bounds before using it

      // Seed the reconfigure panel from THIS run's strategy (see btSeedFromRef).
      // Consumed by the draft effect that fires on the selection change below.
      btSeedFromRef.current = {
        botId: matchBot.id,
        buy: savedStrategy.buy || matchBot.logic?.buy || '',
        sell: savedStrategy.sell || matchBot.logic?.sell || '',
        timeframe: String(savedCfg.timeframe || matchBot.interval || '').toLowerCase(),
      };
      // A fresh run must not be described by the previous run's snapshot.
      btLastRunRef.current = null;

      setSelectedBotForBacktest(matchBot);
      setBacktestResults({ metrics, trades, equityCurve, backtest_config: savedCfg });

      // Put this run's risk exits back into the config form. buildSavePayload
      // has always written them into backtest_config.risk, but nothing ever
      // read them back — so opening a run from history showed whatever the form
      // happened to be holding, and the flatten-before-close it ran with simply
      // was not there. Same translation as the AI-apply path: a saved 0 means
      // "off", so blank the field rather than leave the previous value.
      //
      // Costs are deliberately NOT restored: setting them would mean latching
      // btCostsTouchedFor to stop the venue-defaults effect overwriting them, and
      // that latch outlives this screen. The run's own commission/slippage are
      // already shown in the report, which reads backtest_config directly.
      const savedRisk = savedCfg.risk || {};
      const fld = (v) => (Number(v) > 0 ? String(v) : '');
      setBacktestParams(p => ({
        ...p,
        stopLossPct: fld(savedRisk.stopLossPct),
        takeProfitPct: fld(savedRisk.takeProfitPct),
        trailingStopPct: fld(savedRisk.trailingStopPct),
        maxBarsInTrade: fld(savedRisk.maxBarsInTrade),
        flattenBeforeCloseMin: fld(savedRisk.flattenBeforeCloseMin),
      }));
      // The whole risk block lives behind the collapsed Advanced panel, so a
      // restored value would otherwise still be invisible.
      if (Object.values(savedRisk).some(v => Number(v) > 0)) setBtAdvOpen(true);
      // use saved range if present (works with both *_ms and ISO)
      const startMs =
        btRow.start_time_ms ?? detail.start_time_ms ??
        (btRow.start_time ? Date.parse(btRow.start_time) : undefined) ??
        (detail.start_time ? Date.parse(detail.start_time) : undefined) ??
        backtestDateRange[0];

      const endMs =
        btRow.end_time_ms ?? detail.end_time_ms ??
        (btRow.end_time ? Date.parse(btRow.end_time) : undefined) ??
        (detail.end_time ? Date.parse(detail.end_time) : undefined) ??
        backtestDateRange[1];

      let s = Number.isFinite(startMs) ? startMs : defaultBacktestRange()[0];
      let e = Number.isFinite(endMs) ? endMs : defaultBacktestRange()[1];

      s = Math.max(bounds.min, s);
      e = Math.min(bounds.max, e);
      if (s > e) { e = bounds.max; s = Math.max(bounds.min, e - 30 * MS_DAY); }

      setBacktestDateRange([s, e]);
      setActiveBacktestTab('summary');           // default tab for the (optional) report
      setIsBacktesting(false);                   // ensure progress UI is off
      setBacktestProgress(100);                  // show as complete
      // Load the saved backtest INTO the inline Backtest page (returns, plot,
      // config and the full paginated trade list) instead of a popup. Ensure the
      // Backtest tab is active and bring the results panel into view.
      setActiveTab('backtest');
      setTimeout(() => { try { backtestPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {} }, 60);
    } catch (err) {
      console.error('Failed to open saved backtest:', err);
      alert('Could not load this backtest. Please check server logs.');
    } finally {
      setLoadingSavedBacktest(false);
    }
  }



  // Handle backtest simulation
  const runBacktest = async (botArg) => {
    // choose explicit bot arg or fall back to the currently selected one
    const bot = botArg || selectedBotForBacktest;

    if (!bot) {
      alert('Please select a bot to backtest.');
      return;
    }

    setViewingSavedBacktestId(null);   // live run, not a saved record
    setIsBacktesting(true);
    setBacktestProgress(0);
    setSelectedBotForBacktest(bot);    // ✅ use the right variable
    setLiveBacktest(null);             // reset stream snapshot

    try {
      const userId = Number(localStorage.getItem("userId"));

      // Prefer canonical bot from state (has .logic), else use what we got
      const canonical = bot?.id ? (bots.find(b => b.id === bot.id) || bot) : bot;

      const getStr = (v) => (v == null ? '' : String(v));

      // Backtest what's on screen. When the reconfigure panel holds edits for
      // THIS bot, run the draft instead of the saved strategy — that's the
      // whole point of tweak → backtest → tweak. The backend takes `strategy`
      // and `timeframe` straight from the payload, so nothing needs saving
      // first. Falls back to the bot's stored values otherwise.
      // btDraftLogic, NOT buildLogicFromConditions. In expression mode the
      // condition arrays are empty by design and the strategy lives in the raw
      // strings, so building from the rows produced '' and this handler then
      // reported "no strategy logic to backtest" for a draft that plainly had
      // one on screen. btDraftLogic is the single place that resolves the mode;
      // every other consumer already went through it.
      const draft = (btDraft && btDraft.botId === canonical?.id) ? btDraft : null;
      const buyLogic = draft
        ? btDraftLogic.buy
        : getStr(canonical?.logic?.buy ?? canonical?.buy_logic ?? '');
      const sellLogic = draft
        ? btDraftLogic.sell
        : getStr(canonical?.logic?.sell ?? canonical?.sell_logic ?? '');
      // Short legs ride along so a four-sided bot backtests the strategy it
      // actually trades. Sourced from the bot (they are not draft-editable in
      // the Optimize drawer); empty strings mean long-only, as before.
      const shortOpenLogic = getStr(btDraftLogic.shortOpen ?? canonical?.logic?.shortOpen ?? '');
      const shortCloseLogic = getStr(btDraftLogic.shortClose ?? canonical?.logic?.shortClose ?? '');

      if (!buyLogic && !sellLogic) {
        setIsBacktesting(false);
        alert('This selection has no strategy logic to backtest.');
        return;
      }

      const symbol = canonical?.symbol ?? selectedBotForBacktest?.symbol ?? '';
      const timeframe = getStr(
        draft?.timeframe ?? canonical?.interval ?? canonical?.timeframe ?? selectedBotForBacktest?.interval
      ).toLowerCase();

      // 🔒 Clamp the requested range to the computed bounds for this bot
      const bounds = computeBacktestBounds(canonical);
      let s = Math.max(bounds.min, startOfDayMs(backtestDateRange[0]));
      let e = Math.min(bounds.max, endOfDayMs(backtestDateRange[1]));
      if (s > e) { e = bounds.max; s = Math.max(bounds.min, e - 30 * MS_DAY); }

      // Snapshot what this run is actually testing, so both the report and the
      // saved history record the strategy that ran rather than the bot's stored
      // one. The window is included because it's clamped above and the user can
      // move the date range afterwards.
      // MUST stay below the s/e declarations — reading them earlier is a
      // temporal-dead-zone ReferenceError, not undefined.
      btLastRunRef.current = {
        botId: canonical?.id, buy: buyLogic, sell: sellLogic,
        shortOpen: shortOpenLogic, shortClose: shortCloseLogic,
        timeframe, symbol,
        startMs: s, endMs: e,
      };


      const ti = (() => {
        try { return JSON.parse(canonical?.trade_info || '{}'); } catch { return {}; }
      })();

      const payload = {
        userId,
        botId: canonical?.id,
        strategy: {
          buy: buyLogic, sell: sellLogic,
          ...(shortOpenLogic || shortCloseLogic
            ? { short_open_logic: shortOpenLogic, short_close_logic: shortCloseLogic }
            : {}),
        },
        config: {
          initialCapital: Number(backtestParams.initialCapital) || 0,
          commission: Number(backtestParams.commission) || 0,
          slippage: Number(backtestParams.slippage) || 0,
          startTime: s,
          endTime: e,
          ...(buildRiskConfig() ? { risk: buildRiskConfig() } : {}),
          // Options cost knobs (per-contract commission + premium-spread
          // slippage) — read by the backtest's options context.
          ...(ti.instrument === 'options' ? { options: BT_OPTION_COSTS } : {}),
        },
        symbol,
        timeframe,
        isTick: timeframe === 'tick' || (buyLogic + sellLogic).toLowerCase().includes('tick'),

        // Venue/asset context — without this the backend can't tell a stock bot
        // from a crypto one and routes an Alpaca ticker to Binance.
        asset_class: canonical?.asset_class || ti.asset_class,
        trade_info: canonical?.trade_info,

        // ✅ ADD THESE — futures context passed to the worker
        marketType: ti.marketType || 'spot',
        leverage: ti.futures?.leverage || 1,
        positionMode: ti.futures?.positionSide || 'one_way',
        futuresType: ti.futures?.type || 'usdt_m',
        marginMode: ti.futures?.marginMode || 'cross',
      };

      const { jobId } = await backtestService.submit(payload);

      // create immediate UI feedback: queued state
      setLiveBacktest({
        jobId,
        status: 'queued',
        progress: 0,
        equityCurve: [],
        trades: [],
        startedAt: Date.now(),
        lastEventTs: Date.now(),
      });

      // Optional: open the dialog instantly if you prefer the dialog UX
      // setBacktestDialogOpen(true);

      if (!jobId) throw new Error('No jobId returned from backtest submit');

      attachBacktestListeners(jobId);

      // Optional polling fallback
      if (backtestPollRef.current) clearInterval(backtestPollRef.current);
      backtestPollRef.current = setInterval(async () => {
        try {
          const { status, detail, reason, workersOnline } = await backtestService.getStatus(jobId);

          // 'unknown' means the server has no record of this job — never
          // enqueued, or finished and expired. Polling it forever is what made
          // a dead backtest worker look like an ordinary queue wait: the UI sat
          // on "waiting for an available worker" indefinitely while nothing
          // existed to wait for. Stop, and say what the server said.
          if (status === 'unknown') {
            clearInterval(backtestPollRef.current);
            detachBacktestListeners(jobId);
            setIsBacktesting(false);
            setLiveBacktest(prev => prev ? {
              ...prev,
              status: 'failed',
              error: reason || 'This backtest is no longer tracked by the server.',
            } : prev);
            showToast('error', workersOnline === 0
              ? 'No backtest worker is running — start the backtest process, then re-run.'
              : (reason || 'This backtest is no longer tracked.'));
            return;
          }

          if (status === 'queued') {
            setLiveBacktest(prev => prev ? { ...prev, status: 'queued' } : prev);
          } else if (status === 'running') {
            setLiveBacktest(prev => prev ? { ...prev, status: 'running' } : prev);
          }
          if (status === 'completed') {
            clearInterval(backtestPollRef.current);
            if (detail) {
              setBacktestResults({
                metrics: detail.metrics || {},
                trades: detail.trades || [],
                equityCurve: detail.equity || [],
                backtest_config: buildRanConfig(),
              });
              setIsBacktesting(false);
              setBacktestProgress(100);
              // Popup suppressed: the completed report (returns + full trade list)
              // renders inline on the Backtest page below, so we no longer auto-open
              // the dialog. It's still reachable via "View full report".
              // setBacktestDialogOpen(true);
            }
            detachBacktestListeners(jobId);
          } else if (status === 'canceled') {
            clearInterval(backtestPollRef.current);
            detachBacktestListeners(jobId);
            setIsBacktesting(false);
            setLiveBacktest(prev => prev ? { ...prev, status: 'canceled' } : prev);
            return;
          }

          else if (status === 'failed') {
            clearInterval(backtestPollRef.current);
            detachBacktestListeners(jobId);
            setIsBacktesting(false);
            // Surface the ACTUAL reason (e.g. "Insufficient data for HFMF
            // (1h); got 17 (need 50)") instead of a generic alert — the
            // status endpoint now reads it off the job record even when the
            // TTL'd status hash was evicted.
            const why = detail?.error || reason || 'The backtest failed.';
            setLiveBacktest(prev => prev ? { ...prev, status: 'failed', error: why } : prev);
            showToast('error', why);
          }
        } catch {/* ignore transient errors */ }
      }, 5000);

    } catch (err) {
      console.error("Backtest enqueue failed:", err);
      setIsBacktesting(false);
    }
  };



  // unified close (clear flags + close dialog)
  function closeBacktestDialog() {
    setBacktestDialogOpen(false);
    setViewingSavedBacktestId(null);
    // optional: also clear results
    // setBacktestResults(null);
  }

  const botsById = useMemo(
    () => Object.fromEntries(bots.map(b => [b.id, b.name])),
    [bots]
  );

  // Scanner profiles are templates with no fixed symbol — nothing to backtest.
  // Bots they spawned are real bots and stay selectable.
  const backtestableBots = useMemo(() => bots.filter(isBacktestableBot), [bots]);

  // ── Draft lifecycle ──────────────────────────────────────────────────────
  // Seed the draft from whichever bot is selected. parseLogicToConditions is
  // the same round-trip the create form uses, so the panels show the bot's
  // real strategy rather than an empty builder.
  useEffect(() => {
    // A new bot means a new conversation — otherwise advice about the previous
    // bot's results would carry over as context for this one.
    setBtAiHistory([]);
    setBtAiResult(null);
    // Same rule for the tuner: Optimize's sweeps are measured on the selected
    // bot's symbol, so a different bot makes them a different symbol's numbers.
    // Only Optimize's — Explore's stash belongs to the tuning pick, which this
    // change says nothing about.
    invalidateSweepStash('optimize');
    if (btResearchTab !== 'explore') { setBtSweep(null); setBtSweepPlan(null); }
    if (!selectedBotForBacktest) { setBtDraft(null); return; }
    const bot = selectedBotForBacktest;
    // A backtest just opened from history seeds the panel with THAT run's
    // strategy so it can be tweaked and re-run; otherwise seed from the bot.
    const seed = btSeedFromRef.current;
    const useSeed = !!seed && String(seed.botId) === String(bot.id);
    btSeedFromRef.current = null;
    const rawBuy = (useSeed ? seed.buy : bot.logic?.buy) || '';
    const rawSell = (useSeed ? seed.sell : bot.logic?.sell) || '';
    // Short legs — carried so the Optimize builder shows and can edit all four
    // sides of a long+short bot (it used to read them from the bot verbatim,
    // so the builder showed two while LOADED STRATEGY showed four).
    const rawShortOpen = (useSeed ? seed.shortOpen : bot.logic?.shortOpen) || '';
    const rawShortClose = (useSeed ? seed.shortClose : bot.logic?.shortClose) || '';
    // The builder silently mangles anything it cannot represent — arithmetic on
    // an operand, or a conditional — because parseLogicToConditions returns an
    // empty operand rather than failing. When that would happen, keep the raw
    // string and open in expression mode instead of destroying the strategy.
    // The short legs join the same guard: an unrepresentable short leg also
    // forces expression mode, so it is never silently flattened.
    const builderSafe = canRepresentInBuilder(rawBuy) && canRepresentInBuilder(rawSell)
      && canRepresentInBuilder(rawShortOpen) && canRepresentInBuilder(rawShortClose);
    setBtDraft({
      botId: bot.id,
      timeframe: String((useSeed ? seed.timeframe : bot.interval) || '').toLowerCase(),
      mode: builderSafe ? 'builder' : 'expression',
      // Recorded so the UI can explain why the toggle moved on its own.
      forcedExpression: !builderSafe,
      rawBuy,
      rawSell,
      rawShortOpen,
      rawShortClose,
      buyConditions: builderSafe ? (parseLogicToConditions(rawBuy) || []) : [],
      sellConditions: builderSafe ? (parseLogicToConditions(rawSell) || []) : [],
      shortOpenConditions: builderSafe ? (parseLogicToConditions(rawShortOpen) || []) : [],
      shortCloseConditions: builderSafe ? (parseLogicToConditions(rawShortClose) || []) : [],
    });
  }, [selectedBotForBacktest]);

  const btDraftMarketType = useMemo(() => {
    try {
      const ti = JSON.parse(btActiveBot?.trade_info || '{}');
      return ti.marketType === 'futures' ? 'futures' : 'spot';
    } catch { return 'spot'; }
  }, [btActiveBot]);

  // Session variables and flatten-before-close only mean something where there
  // IS a session. Read from trade_info first, falling back to the column, the
  // same precedence used elsewhere for this pair.
  const btDraftIsStock = useMemo(
    () => isStockBot(btActiveBot),
    [btActiveBot]
  );

  // The category vocabularies don't overlap (crypto text[] vs stock facets) —
  // a value carried across an asset-class switch would silently match nothing.
  useEffect(() => { setBtUniCategory(''); }, [btDraftIsStock]);

  // The timeframe a research run will ACTUALLY use.
  //
  // Explore runs on its own setup timeframe, never the selected bot's interval —
  // a bot in Explore is a strategy seed, not the run target (same rule
  // btDraftLogic applies to the logic). Reading btDraft.timeframe there made the
  // Explore timeframe select cosmetic the moment a bot was selected, and left it
  // null with no bot at all.
  const btEffectiveTimeframe = btExploreStandalone
    ? String(btExploreTimeframe || '').toLowerCase()
    : String(btDraft?.timeframe || btActiveBot?.interval || '').toLowerCase();

  const btDraftIsTick = btEffectiveTimeframe === 'tick';

  // Which editor currently holds the strategy. Same split as btDraftLogic: in
  // Explore the logic lives in the explore editor, so reading btDraft.mode
  // there consults the wrong editor — and getting this wrong is destructive,
  // because the builder branch re-parses the expression and
  // parseLogicToConditions mangles anything it cannot represent.
  const btEffectiveMode = btExploreStandalone ? btExploreMode : (btDraft?.mode || 'builder');

  // A venue-TYPE change invalidates the symbol list. BTCUSDT cannot be tested
  // on Alpaca and AAPL cannot be tested on Binance, so symbols carried across a
  // crypto<->stock switch guarantee a batch of failures — the field, the tuning
  // pick and any results measured on the old type all go. Same-type switches
  // (binance.com -> mexc) keep everything: the tickers may still exist there,
  // and wiping work on a maybe is worse than letting a missing symbol fail
  // honestly. A ref seeded on first render, so mounting does not clear a
  // restored session.
  const btExploreTypeRef = useRef(null);
  useEffect(() => {
    const type = btExploreContext.asset_class;
    if (btExploreTypeRef.current === null) { btExploreTypeRef.current = type; return; }
    if (btExploreTypeRef.current === type) return;
    btExploreTypeRef.current = type;
    // Short legs only exist on stock venues — carried onto a crypto venue they
    // would ride into payloads for a venue that cannot short.
    if (type !== 'stock_equity' && (btExploreShortOpen || btExploreShortClose)) {
      setBtExploreShortOpen('');
      setBtExploreShortClose('');
    }
    if (btBatchSymbols || btTuneSymbol || btBatch) {
      setBtBatchSymbols('');
      setBtUniNote('');
      setBtTuneSymbol('');
      setBtBatch(null);
      setBtSweep(null);
      setBtSweepPlan(null);
      invalidateSweepStash('explore');
      showToast('info', type === 'stock_equity'
        ? 'Switched to a stock venue — the crypto symbol list was cleared.'
        : 'Switched to a crypto venue — the stock symbol list was cleared.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btExploreContext.asset_class]);

  // The symbol Explore is currently "about".
  //
  // Explore has no single symbol field — it has a LIST to test breadth across,
  // and a tuning pick that only exists after a batch. So the working symbol is
  // the tuned pick, else the first of the list, else nothing. Deploying needs
  // exactly one, and the header has to name the one that would be used rather
  // than leave the Start button ambiguous.
  const btWorkingSymbol = useMemo(() => {
    if (btTuneSymbol) return btTuneSymbol;
    const first = String(btBatchSymbols || '').split(/[,\s]+/).map(s => s.trim()).filter(Boolean)[0];
    return first || '';
  }, [btTuneSymbol, btBatchSymbols]);

  const btIndicators = useMemo(() => {
    if (btDraftIsTick) return TICK_INDICATORS;
    return btDraftMarketType === 'futures' ? FUTURES_INDICATORS : CANDLE_INDICATORS;
  }, [btDraftIsTick, btDraftMarketType]);

  // Mode-aware draft fields for a pair of logic strings.
  //
  // Every path that puts logic INTO the draft must go through this. The seeding
  // effect above already guards, but the advisor, the ranked-candidate loader
  // and Revert did not: parseLogicToConditions returns an empty operand for
  // anything with arithmetic or a conditional, so those paths would silently
  // swap the strategy for a mangled one that still looks plausible.
  const draftLogicFields = useCallback((buy, sell) => {
    const b = buy || '';
    const s = sell || '';
    const safe = canRepresentInBuilder(b) && canRepresentInBuilder(s);
    return {
      mode: safe ? 'builder' : 'expression',
      forcedExpression: !safe,
      rawBuy: b,
      rawSell: s,
      buyConditions: safe ? (parseLogicToConditions(b) || []) : [],
      sellConditions: safe ? (parseLogicToConditions(s) || []) : [],
    };
  }, []);

  // Logic the draft currently describes, compiled back to expression strings.
  // Single source of truth for "what logic does the draft describe". Every
  // consumer — backtest submit, Validate, Search & rank, breadth, deploy —
  // already reads this, so honouring the mode here is the whole integration.
  // ── Research funnel verdict ──
  //
  // The panels below report numbers; deciding what those numbers MEAN was
  // tribal knowledge. Two users looking at the same scan reached different
  // conclusions because nothing said "this is dead, stop here". This turns each
  // step into a verdict against a stated threshold, and names the single next
  // action — so the reasoning is in the product rather than in whoever set it up.
  //
  // Thresholds are the same ones the deployment gate enforces, deliberately:
  // a funnel that passes something the gate then rejects is worse than no funnel.
  const btDraftLogic = useMemo(() => {
    // Explore's strategy lives in the Explore editor, full stop — a selected
    // bot only seeds it through the copy dropdown. Every consumer reads
    // through here, so breadth, the tuner and the AI all act on what the
    // explore editor shows.
    // Short legs. Explore: the two dedicated fields (stock venues only —
    // the UI hides them elsewhere and crypto spot cannot short). Optimize:
    // shorts are not editable in the drawer, so they come from the loaded
    // bot verbatim — which is exactly what makes a backtest of a four-sided
    // bot simulate the strategy the bot actually trades.
    if (btResearchTab === 'explore') {
      const base = btExploreMode === 'expression'
        ? { buy: (btExploreBuy || '').trim(), sell: (btExploreSell || '').trim() }
        : { buy: buildLogicFromConditions(btExploreBuyConds), sell: buildLogicFromConditions(btExploreSellConds) };
      return { ...base, shortOpen: (btExploreShortOpen || '').trim(), shortClose: (btExploreShortClose || '').trim() };
    }
    const botShorts = {
      shortOpen: (selectedBotForBacktest?.logic?.shortOpen || '').trim(),
      shortClose: (selectedBotForBacktest?.logic?.shortClose || '').trim(),
    };
    if (!btDraft) return { buy: '', sell: '', ...botShorts };
    // Shorts are now editable in the Optimize builder too. Derive them the same
    // way as buy/sell (raw in expression mode, built from conditions in builder
    // mode); fall back to the bot's leg only when the draft has no short field
    // at all (older drafts seeded before this change).
    if (btDraft?.mode === 'expression') {
      return {
        buy: (btDraft.rawBuy || '').trim(),
        sell: (btDraft.rawSell || '').trim(),
        shortOpen: (btDraft.rawShortOpen ?? botShorts.shortOpen).trim(),
        shortClose: (btDraft.rawShortClose ?? botShorts.shortClose).trim(),
      };
    }
    return {
      buy: buildLogicFromConditions(btDraft.buyConditions),
      sell: buildLogicFromConditions(btDraft.sellConditions),
      shortOpen: btDraft.shortOpenConditions ? buildLogicFromConditions(btDraft.shortOpenConditions) : botShorts.shortOpen,
      shortClose: btDraft.shortCloseConditions ? buildLogicFromConditions(btDraft.shortCloseConditions) : botShorts.shortClose,
    };
  }, [btDraft, btResearchTab, selectedBotForBacktest, btExploreMode, btExploreBuy, btExploreSell, btExploreBuyConds, btExploreSellConds, btExploreShortOpen, btExploreShortClose]);

  // Dirty = the draft says something different from the saved bot. Compared on
  // compiled logic, not condition objects, so re-parsing noise doesn't count.
  //
  // Whitespace is stripped before comparing because the parse→build round-trip
  // is not byte-stable for multi-param indicators: a stored "MACD(12,26,9)"
  // rebuilds as "MACD(12, 26, 9)". Equivalent expressions, but a literal
  // compare would mark every such bot dirty the moment it was selected.
  const btDraftDirty = useMemo(() => {
    const bot = selectedBotForBacktest;
    if (!bot || !btDraft) return false;
    const sameTf = String(bot.interval || '').toLowerCase() === String(btDraft.timeframe || '').toLowerCase();
    const norm = (s) => String(s || '').replace(/\s+/g, '').toLowerCase();
    return !sameTf
      || norm(bot.logic?.buy) !== norm(btDraftLogic.buy)
      || norm(bot.logic?.sell) !== norm(btDraftLogic.sell)
      // Short legs are editable now, so an edit to either must count as dirty
      // or "save edited strategy" would silently drop it.
      || norm(bot.logic?.shortOpen) !== norm(btDraftLogic.shortOpen)
      || norm(bot.logic?.shortClose) !== norm(btDraftLogic.shortClose);
  }, [selectedBotForBacktest, btDraft, btDraftLogic]);

  // Stock backtests have no tick path (Alpaca's free feed has no tick history),
  // and Alpaca has no 3d/1M bars — keep the list to what the engine can serve.
  // Keyed off the ACTIVE bot, not the selection: in Explore the venue comes from
  // the setup exchange (btExploreContext), so keying this off the selection
  // offered tick for an Alpaca exploration and withheld it for a crypto one
  // whenever a stock bot happened to be selected.
  const btTimeframeOptions = useMemo(() => {
    const base = ['tick', '1m', '5m', '15m', '30m', '1h', '4h', '8h', '12h', '1d'];
    return getBacktestAssetClass(btActiveBot) === 'stock_equity'
      ? base.filter(t => t !== 'tick')
      : base;
  }, [btActiveBot]);

  // Alpaca has no tick history, so a venue switch has to take the timeframe with
  // it — otherwise 'tick' stays selected while the option is gone from the list,
  // and every run silently asks for data that does not exist.
  useEffect(() => {
    if (btExploreTimeframe === 'tick' && !btTimeframeOptions.includes('tick')) {
      setBtExploreTimeframe('1h');
    }
  }, [btTimeframeOptions, btExploreTimeframe]);

  // If a LIVE bot stops being backtestable (e.g. it was converted to a scanner
  // profile), drop it so the form doesn't hold a ghost.
  //
  // Only applies to bots that actually exist in the list. Opening a saved
  // backtest sets the selection to a bot reconstructed from the saved record —
  // which legitimately isn't in `bots` when its bot was deleted, or when the
  // record has no bot_id. An earlier version of this check tested membership of
  // backtestableBots alone and so nulled that reconstruction immediately,
  // blanking the name, symbol and logic of every historical result opened.
  useEffect(() => {
    if (!selectedBotForBacktest) return;
    const liveBot = bots.find(b => b.id === selectedBotForBacktest.id);
    if (liveBot && !isBacktestableBot(liveBot)) setSelectedBotForBacktest(null);
  }, [bots, selectedBotForBacktest]);

  const groupedBots = useMemo(() => {
    const manual = [];
    const scannerProfiles = [];
    const spawnedByProfile = new Map();   // profileId → [bots...]
    const orphanedSpawned = [];           // spawned bots whose parent was deleted

    for (const bot of bots) {
      if (bot.isScannerProfile) {
        scannerProfiles.push(bot);
      } else if (bot.spawnedFromProfileId) {
        const arr = spawnedByProfile.get(bot.spawnedFromProfileId) || [];
        arr.push(bot);
        spawnedByProfile.set(bot.spawnedFromProfileId, arr);
      } else {
        manual.push(bot);
      }
    }

    // Sweep up spawned bots whose parent doesn't exist (deleted scanner)
    for (const [profileId, children] of spawnedByProfile.entries()) {
      if (!scannerProfiles.find(p => p.id === profileId)) {
        orphanedSpawned.push(...children);
        spawnedByProfile.delete(profileId);
      }
    }

    return { manual, scannerProfiles, spawnedByProfile, orphanedSpawned };
  }, [bots]);

  const handleBacktestDateChange = (_evt, newValue) => {
    if (!Array.isArray(newValue)) return;
    let [s, e] = newValue;
    s = Math.max(rangeBounds.min, Math.min(s, rangeBounds.max));
    e = Math.max(rangeBounds.min, Math.min(e, rangeBounds.max));
    if (s > e) s = Math.max(rangeBounds.min, e - MS_DAY); // keep at least 1 day
    setBacktestDateRange([s, e]);
  };


  function parseBuySellHistory(raw) {
    try {
      if (typeof raw === 'object') return raw; // Already parsed
      return JSON.parse(raw);
    } catch (err) {
      console.warn("Failed to parse buy/sell history:", raw);
      return null;
    }
  }

  const startOfDayMs = (t) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const endOfDayMs = (t) => {
    const d = new Date(t);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  };

  const toMsEpoch = (t) => {
    const n = Number(t);
    if (!Number.isFinite(n)) return undefined;
    return n < 1e12 ? n * 1000 : n; // seconds → ms
  };


  // The config describing the run that just finished, for the inline report and
  // the "View full report" dialog.
  //
  // Without this the completion handlers set backtestResults with no
  // backtest_config, so the report's `savedCfg.strategy ?? selected.logic`
  // fell through to the BOT's logic. Loading a backtest from history, editing
  // the strategy and re-running therefore still showed the original logic —
  // the run was correct, only its description was stale.
  const buildRanConfig = () => {
    const ran = btLastRunRef.current;
    return {
      symbol: selectedBotForBacktest?.symbol,
      timeframe: ran?.timeframe || String(selectedBotForBacktest?.interval || '').toLowerCase(),
      start_time_ms: ran?.startMs ?? startOfDayMs(backtestDateRange[0]),
      end_time_ms: ran?.endMs ?? endOfDayMs(backtestDateRange[1]),
      initialCapital: Number(backtestParams.initialCapital) || 0,
      commission: Number(backtestParams.commission) || 0,
      slippage: Number(backtestParams.slippage) || 0,
      ...(buildRiskConfig() ? { risk: buildRiskConfig() } : {}),
      ...(ran ? {
        strategy: {
          buy: ran.buy, sell: ran.sell,
          ...(ran.shortOpen || ran.shortClose
            ? { short_open_logic: ran.shortOpen, short_close_logic: ran.shortClose }
            : {}),
        },
      } : {}),
    };
  };

  function buildSavePayload(bot, results) {
    const userId = Number(localStorage.getItem('userId'));
    // Prefer the snapshot of what the run actually used. Falls back to the
    // bot's stored strategy for a result that didn't come from this session's
    // run (e.g. re-saving an older one).
    const ran = (btLastRunRef.current && btLastRunRef.current.botId === bot?.id)
      ? btLastRunRef.current
      : null;
    const strategy = ran
      ? { buy: ran.buy, sell: ran.sell }
      : { buy: bot?.logic?.buy, sell: bot?.logic?.sell };
    // Match the resolution the enqueue path uses. A bot reconstructed from
    // history carries `timeframe` rather than `interval`, and the save API
    // rejects an empty string outright.
    const timeframe = (ran?.timeframe || bot?.interval || bot?.timeframe || '').toLowerCase();

    // The run CLAMPS the requested window to the bot's available data bounds and
    // records the result in btLastRunRef. Re-deriving it from backtestDateRange
    // here sent a window the run never used, and an inverted or non-finite range
    // failed validation with a bare 400 (endTime must be >= startTime).
    const rawStart = startOfDayMs(backtestDateRange[0]);
    const rawEnd = endOfDayMs(backtestDateRange[1]);
    const startMs = Number.isFinite(ran?.startMs) ? ran.startMs : rawStart;
    const endMs = Number.isFinite(ran?.endMs) ? ran.endMs : rawEnd;

    // The save API validates botId with Number.isInteger, and a bot
    // reconstructed from a saved backtest carries the id as a STRING (Postgres
    // serialises bigints that way), which rejected the entire payload. Send a
    // number, and omit it entirely rather than sending NaN when the id isn't
    // numeric — botId is optional, so a run still saves without it.
    const botIdNum = Number(bot?.id);
    return {
      userId,
      botId: Number.isFinite(botIdNum) && botIdNum > 0 ? botIdNum : undefined,
      bot_name: bot?.name || undefined,
      symbol: bot?.symbol || ran?.symbol || '',
      timeframe,
      start_time_ms: startMs,
      end_time_ms: endMs,
      strategy,
      config: {
        initialCapital: Number(backtestParams.initialCapital) || 0,
        commission: Number(backtestParams.commission) || 0,
        slippage: Number(backtestParams.slippage) || 0,
        ...(buildRiskConfig() ? { risk: buildRiskConfig() } : {}),
      },
      metrics: results?.metrics || {},
      equity_curve: results?.equityCurve || [],
      trades: results?.trades || [],
      backtest_config: {
        symbol: bot?.symbol || ran?.symbol || '',
        timeframe,
        // Same clamped window as above — the report reads this block, so a
        // mismatch here would caption the chart with a range that never ran.
        start_time_ms: startMs,
        end_time_ms: endMs,
        initialCapital: Number(backtestParams.initialCapital) || 0,
        commission: Number(backtestParams.commission) || 0,
        slippage: Number(backtestParams.slippage) || 0,
        ...(buildRiskConfig() ? { risk: buildRiskConfig() } : {}),
        strategy
      }
    };
  }
  // NEW: save handler
  async function handleSaveBacktest() {
    try {
      if (!selectedBotForBacktest || !backtestResults) return;
      const payload = buildSavePayload(selectedBotForBacktest, backtestResults);
      await backtestService.saveResult(payload);
      // refresh recent list after saving
      fetchRecentBacktests();
    } catch (err) {
      console.error('Save backtest failed:', err);
      alert('Failed to save backtest. Check logs.');
    }
  }


  // NEW: recent loader
  async function fetchRecentBacktests() {
    try {
      const userId = Number(localStorage.getItem('userId'));
      if (!Number.isFinite(userId)) return;
      // Fetch the full retained set; the list pages client-side. Asking for 10
      // meant the history showed a fraction of what was stored, and a saved run
      // that scrolled past ten was indistinguishable from one that was deleted.
      const items = await backtestService.getResults(60);
      setRecentBacktests(items || []);
      setBtHistoryPage(0);
    } catch (err) {
      console.error('Failed to load recent backtests:', err);
    }
  }

  useEffect(() => {
    if (activeTab !== 'backtest') return;
    (async () => {
      const userId = Number(localStorage.getItem('userId'));
      if (!Number.isFinite(userId)) return;

      try {
        const { active, jobId, status, progress } = await backtestService.getActive(userId);
        if (!active || !jobId) return;

        // Seed UI with what we know
        setLiveBacktest(prev => ({
          jobId,
          status: status || 'running',
          progress: Number(progress) || 0,
          equityCurve: prev?.equityCurve ?? [],
          trades: prev?.trades ?? [],
          startedAt: prev?.startedAt ?? Date.now(),
          lastEventTs: Date.now(),
        }));

        setBacktestProgress(Number(progress) || 0);  // 👈 seed the actual progress bar

        // Rejoin the room + attach handlers
        attachBacktestListeners(jobId);

        // Poll as fallback (same poll you already use)
        const pollId = setInterval(async () => {
          try {
            const { status: s, detail } = await backtestService.getStatus(jobId);
            if (s === 'completed' || s === 'canceled' || s === 'failed') {
              clearInterval(pollId);
              detachBacktestListeners(jobId);
              setIsBacktesting(false);
              if (s === 'completed' && detail) {
                setBacktestResults({
                  metrics: detail.metrics || {},
                  trades: detail.trades || [],
                  equityCurve: detail.equity || [],
                  backtest_config: buildRanConfig(),
                });
                // Popup suppressed — results render inline on the Backtest page.
                // setBacktestDialogOpen(true);
              }
              setLiveBacktest(prev => prev ? { ...prev, status: s } : prev);
            } else {
              setLiveBacktest(prev => prev ? { ...prev, status: s || prev.status } : prev);
            }
          } catch { }
        }, 5000);

        // Show the running section
        setIsBacktesting(true);
      } catch (e) {
        console.warn('active backtest lookup failed', e);
      }
    })();
  }, [activeTab]);


  useEffect(() => {
    if (activeTab === 'backtest') {
      fetchRecentBacktests();
    }
  }, [activeTab]);


  // 🔄 Replace your existing attachBacktestListeners with this version
  function attachBacktestListeners(jobId) {
    const channel = `backtest:events:${jobId}`;

    // 1) JOIN the room so the server will deliver roomed emits
    socket.emit('joinBacktest', jobId);

    // 2) Optional: raw logger for visibility during dev
    const rawLogger = (...args) => console.log('[RAW BACKTEST]', channel, ...args);
    socket.on(channel, rawLogger);

    // 3) Main handler (normalizes shapes + updates UI state)
    const handler = async (event) => {
      // Ensure container exists
      setLiveBacktest((prev) => {
        if (!prev || prev.jobId !== jobId) {
          return {
            jobId,
            progress: 0,
            equityCurve: [],
            trades: [],
            startedAt: Date.now(),
            lastEventTs: event.ts,
          };
        }
        return prev;
      });

      switch (event.type) {
        case 'start': {
          setBacktestProgress(0);
          setLiveBacktest(prev => prev ? { ...prev, status: 'running', lastEventTs: event.ts } : prev);

          break;
        }

        case 'progress': {
          const pctRaw = event.progress ?? event.percent;
          const pct = Math.max(0, Math.min(100, Number(pctRaw) || 0));
          setBacktestProgress(pct);
          setLiveBacktest(prev =>
            prev ? { ...prev, status: 'running', progress: pct, lastEventTs: event.ts } : prev
          );
          break;
        }

        case 'equity': {
          // Normalize to [t, e] tuples
          if (Array.isArray(event.points)) {
            const normalized = event.points
              .map((p) => (Array.isArray(p)
                ? [toMs(p[0]), Number(p[1])]
                : [toMs(p.t ?? p.time ?? p.ts), Number(p.v ?? p.equity)]
              ))
              .filter(([t, e]) => Number.isFinite(t) && Number.isFinite(e));

            setLiveBacktest((prev) =>
              prev ? { ...prev, equityCurve: [...prev.equityCurve, ...normalized], lastEventTs: event.ts } : prev
            );
          } else {
            const t = toMs(event.time ?? event.ts);
            const e = Number(event.equity);
            if (Number.isFinite(t) && Number.isFinite(e)) {
              setLiveBacktest((prev) =>
                prev ? { ...prev, equityCurve: [...prev.equityCurve, [t, e]], lastEventTs: event.ts } : prev
              );
            }
          }
          break;
        }

        case 'trade': {
          if (event.botData && Array.isArray(event.botData.history)) {
            const parsed = event.botData.history.map((h) => {
              let payload = h.buy_sell_history;
              try { payload = JSON.parse(payload); } catch { }
              return {
                time: toMs(h.date),
                side: payload.side,
                price: payload.price,
                qty: payload.quantity,
                pnl: Number.isFinite(payload.proceeds) && Number.isFinite(payload.cost)
                  ? (Number(payload.proceeds) - Number(payload.cost))
                  : undefined,
              };
            });
            setLiveBacktest((prev) =>
              prev ? { ...prev, trades: [...prev.trades, ...parsed], lastEventTs: event.ts } : prev
            );
          } else if (event.trade) {
            setLiveBacktest((prev) =>
              prev ? {
                ...prev,
                trades: [...prev.trades, {
                  ...event.trade,
                  time: toMs(event.trade.time ?? event.trade.ts ?? event.ts)
                }],
                lastEventTs: event.ts
              } : prev);
          }
          break;
        }

        case 'status': {
          // server might emit { type:'status', status:'canceled'|'running'|'queued'... }
          if (event.status === 'canceled') {
            setBacktestProgress((p) => (p < 100 ? p : 100));
            setIsBacktesting(false);
            setLiveBacktest(prev => prev ? { ...prev, status: 'canceled', lastEventTs: event.ts } : prev);
            socket.emit('leaveBacktest', jobId);
            detachBacktestListeners(jobId);
          }
          break;
        }

        case 'canceled': { // if you emit a dedicated event
          setIsBacktesting(false);
          setLiveBacktest(prev => prev ? { ...prev, status: 'canceled', lastEventTs: event.ts } : prev);
          socket.emit('leaveBacktest', jobId);
          detachBacktestListeners(jobId);
          break;
        }


        case 'trades': {
          // batch append; server sends { type:'trades', trades:[...] }
          const list = Array.isArray(event.trades) ? event.trades : [];
          if (list.length) {
            setLiveBacktest(prev =>
              prev
                ? { ...prev, trades: [...prev.trades, ...list.map(tr => ({ ...tr, time: toMs(tr.time ?? tr.t ?? tr.timestamp ?? event.ts) }))], lastEventTs: event.ts }
                : prev
            );
          }
          break;
        }

        case 'complete': {
          setBacktestProgress(100);
          setIsBacktesting(false);

          // prefer server’s final payload
          const normTime = (t) => toMs(t ?? event.ts);
          const tradesFromEvent = Array.isArray(event?.trades)
            ? event.trades.map(t => ({ ...t, time: normTime(t.time ?? t.t ?? t.timestamp) }))
            : [];
          const equityFromEvent = Array.isArray(event?.equity) ? event.equity : [];

          // take a synchronous snapshot of current live stream
          const current = liveBacktestRef.current;

          const tradesFromStream = Array.isArray(current?.trades) ? current.trades : [];
          const equityFromStream = Array.isArray(current?.equityCurve) ? current.equityCurve : [];

          const tradesFinal = tradesFromEvent.length ? tradesFromEvent : tradesFromStream;
          const equityFinal = equityFromEvent.length ? equityFromEvent : equityFromStream;

          const mergedMetrics = { ...(event?.metrics || {}) };
          mergedMetrics.totalTrades = Number.isFinite(mergedMetrics.totalTrades)
            ? mergedMetrics.totalTrades
            : tradesFinal.length;

          // update UI state
          setLiveBacktest(prev =>
            prev ? { ...prev, status: 'completed', progress: 100, lastEventTs: event.ts } : prev
          );
          setBacktestResults({ metrics: mergedMetrics, trades: tradesFinal, equityCurve: equityFinal, backtest_config: buildRanConfig() });

          // persist (fire-and-forget)
          try {
            const payload = buildSavePayload(
              selectedBotForBacktest,
              { metrics: mergedMetrics, trades: tradesFinal, equityCurve: equityFinal }
            );
            backtestService.saveResult(payload)
              .then(() => fetchRecentBacktests())
              // The API answers a rejected payload with { error, detail: [...] }
              // naming the offending fields. Logging only the AxiosError threw
              // that away and left a bare 400 to guess at.
              .catch(err => console.error(
                '[backtest] save FAILED',
                err?.response?.status,
                err?.response?.data?.detail || err?.response?.data?.error || err?.message,
                { sentSymbol: payload?.symbol, sentTimeframe: payload?.timeframe,
                  sentStart: payload?.start_time_ms, sentEnd: payload?.end_time_ms },
              ));
          } catch (err) {
            console.error('[backtest] save exception', err);
          }

          // Popup suppressed — the completed report renders inline on the Backtest
          // page (returns + full paginated trade list). Dialog stays available via
          // "View full report" and for opening saved backtests.
          // setBacktestDialogOpen(true);
          socket.emit('leaveBacktest', jobId);
          detachBacktestListeners(jobId);
          break;
        }


        case 'error': {
          console.error('[backtest:error]', event);
          setIsBacktesting(false);
          socket.emit('leaveBacktest', jobId);
          detachBacktestListeners(jobId);
          setLiveBacktest(prev => prev ? { ...prev, status: 'error', lastEventTs: event.ts } : prev);

          break;
        }

        default:
          break;
      }
    };

    socket.on(channel, handler);

    // Store BOTH handlers so we can off() both later
    backtestListenersRef.current.set(jobId, { channel, handlers: [handler, rawLogger] });
  }

  // eslint-disable-next-line no-unused-vars -- orphaned after the backtest report restyle
  function formatProfitLikeChipInput(trade) {
    const profitVal = trade?.profit ?? trade?.pnl ?? null; // string like "+3.2%" or number

    if (profitVal == null) {
      return { text: '—', isLoss: false };
    }

    if (typeof profitVal === 'string') {
      const trimmed = profitVal.trim();
      const isLoss = trimmed.startsWith('-') || Number(trimmed.replace(/[%,$]/g, '')) < 0;
      return { text: trimmed, isLoss };
    }

    // numeric pnl
    const n = Number(profitVal);
    if (!Number.isFinite(n)) return { text: '—', isLoss: false };
    const sign = n >= 0 ? '+' : '-';
    return { text: `${sign}$${Math.abs(n).toFixed(2)}`, isLoss: n < 0 };
  }


  function detachBacktestListeners(jobId) {
    const rec = backtestListenersRef.current.get(jobId);
    if (!rec) return;
    for (const h of rec.handlers || []) {
      socket.off(rec.channel, h);
    }
    backtestListenersRef.current.delete(jobId);
  }



  const extractIndicatorsFromLogic = (logicStr) => {
    const regex = /(tickRSI|tickVWAP|tickPrice|tickDelta|tickVolume|tickTradeVolume|SMA|EMA|RSI|MACD|Volume)\s*\(?\s*(\d+)?\s*,?\s*["']?(\w+)?["']?\s*\)?/g;

    const indicators = new Set();
    let match;
    while ((match = regex.exec(logicStr)) !== null) {
      const name = match[1];
      const period = parseInt(match[2]) || 0;
      const source = match[3] || 'tick';

      indicators.add(JSON.stringify({ name, period, source }));
    }

    return Array.from(indicators).map(s => JSON.parse(s));
  };

// Direct-submit path for the duplicate-bot retry. Skips all the form
  // validation in handleSave because the payload was already built and
  // validated on the first attempt — we just need to re-send it with
  // confirm_duplicate=true to bypass the server's soft-warning check.
  const submitBotPayloadDirect = async (payload, stopped, selectedExchange) => {
    try {
      const resp = await botApiV2.createBot(payload);
      const raw = resp.bot ?? resp;

      let tiSafe = {};
      try { tiSafe = JSON.parse(raw.trade_info || '{}'); } catch (_) { }

      const webhookSecret = (tiSafe.webhook?.secret || '').trim();

      const formattedBot = {
        id: raw.id,
        name: raw.name,
        symbol: tiSafe.symbol || '(from webhook)',
        interval: tiSafe.timeframe || (tiSafe.signalSource === 'tradingview' ? 'Webhook' : '—'),
        exchangeId: raw.exchange_id ?? raw.exchangeId ?? newBot.exchangeId ?? null,
        exchange: raw.exchange?.exchange || selectedExchange?.exchange || '',
        exchangeName: raw.exchange?.name || raw.exchange?.exchange || '—',
        trade_info: raw.trade_info,
        asset_class: raw.asset_class ?? null,   // see the fetch path — column, not just trade_info
        status: raw.stop_trade_ASSET ? 'paused' : 'active',
        stop_trade_ASSET: raw.stop_trade_ASSET,
        logic: {
          buy: raw.buy_logic,
          sell: raw.sell_logic,
          shortOpen: tiSafe.futures?.shortOpenLogic || tiSafe.short_open_logic || '',
          shortClose: tiSafe.futures?.shortCloseLogic || tiSafe.short_close_logic || '',
          profitStop: tiSafe.profitStop,
          lossStop: tiSafe.lossStop,
          maxLoss: tiSafe.maxDailyLoss,
          maxTrades: tiSafe.maxTradesPerDay,
          trailingStop: tiSafe.trailingStop,
          investment: tiSafe.totalInvestment,
        },
        stats: raw.statistics ? normalizeBotStats(raw.statistics) : undefined,
        statusMessage: raw.status || '',
        errorMessage: raw.error_message || '',
        signalSource: tiSafe.signalSource || 'logic',
        webhookUrl: tiSafe.signalSource === 'tradingview'
          ? buildTradingViewWebhookUrl(raw.id, webhookSecret)
          : '',
      };

      const message = stopped
        ? `Bot "${formattedBot.name}" created (paused). Activate it when ready.`
        : resp?.message || 'Bot created and activated!';
      showToast('success', message);

      setBots(prev => [...prev, formattedBot]);
      // Tell the onboarding checklist a bot now exists — nothing on this page
      // did, so "Create your first bot" stayed unticked until a reload.
      try { refreshStatus && refreshStatus({ fresh: true }); } catch (_) {}
      resetForm();
      setIsCreating(false);

      // A brand-new strategy has never been backtested — by definition, since it
      // did not exist until now. Rather than drop the user on the bots list to
      // find the Resume button refused, hand them straight to the backtest with
      // the new bot selected. Non-backtestable bots (webhook, scanner) go to the
      // list as before, because there is nothing for them to run.
      if (stopped && isBacktestableBot(formattedBot)) {
        setSelectedBotForBacktest(formattedBot);
        setActiveTab('backtest');
        // Land in OPTIMIZE. The bot now exists, so the research column must
        // show it — arriving with the tab still on Explore (where the user
        // came from) showed the exploration setup instead of the bot just
        // created, which read as "nothing happened". Same stash swap the tab
        // chips use, so any explore sweep results survive the move.
        if (btResearchTab !== 'optimize') {
          setBtResearchTab('optimize');
          swapSweepContext(sweepStashKey(btResearchTab, btSweepKind), sweepStashKey('optimize', btSweepKind));
        }
        setBtEditOpen(true);
        // Start the backtest rather than landing on a form with one obvious
        // button left to press. The bot itself stays PAUSED throughout — a
        // backtest is a simulation and changes nothing live; trading starts
        // only from the explicit Deploy-as-live-bot action, gate and all.
        runBacktest(formattedBot);
      } else {
        setActiveTab('bots');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to create bot.';
      showToast('error', msg);
      console.error('Error saving bot (duplicate retry):', err);
    }
  };

  const handleSave = async (stopped = false) => {    const email = getEmail();
    if (!email) {
      alert('No email found (localStorage.email). Please log in again.');
      return;
    }

    // ── Basic required fields ──────────────────────────────────────────────────
    if (!newBot.name || !newBot.exchangeId) {
      alert('Name and exchange are required.');
      return;
    }

if (newBot.signalSource !== 'tradingview' && !newBot.symbol) {
      alert('Symbol is required for logic-based bots.');
      return;
    }

    // Funding asset is always required (balance source for bot capital & P&L)
    if (!newBot.funding_asset || !String(newBot.funding_asset).trim()) {
      alert('Funding asset is required.');
      return;
    }

    // ── Position size validation (spot vs futures) ────────────────────────────
    const isFutures = newBot.marketType === 'futures';
    // hasSize check
    const hasSize = isFutures
      ? (newBot.futureSizeMode === 'fixed_notional'
        ? Number(newBot.fixedNotional) > 0
        : Number(newBot.marginPct) > 0)
      : Number(newBot.tradeAmount) > 0;  // ← was positionValue

    // saveDisabled check  
    

    if (!hasSize) {
      alert('Position Size must be greater than 0.');
      return;
    }

    // ── BETA CAP — bot notional limited to $1000 USDT ─────────────────────────
    const BETA_BOT_CAP_USDT = 1000;
    let notionalUsdt = 0;

    if (newBot.marketType === 'futures') {
      const leverage = Number(newBot.leverage) || 1;
      if (newBot.futureSizeMode === 'fixed_notional') {
        notionalUsdt = Number(newBot.fixedNotional) || 0;
      } else {
        // margin_pct: can't compute exact notional without account balance.
        // Block this mode during beta — force users to fixed_notional.
        alert(
          'Beta limit: percentage-based margin sizing is disabled. ' +
          'Please switch to "Fixed Notional" sizing and set a value up to $1000.'
        );
        return;
      }
    } else {
      // Spot
      const sizeValue = Number(newBot.tradeAmount || newBot.positionValue) || 0;
      const sizeMode = newBot.sizeMode || 'quote';
      if (sizeMode === 'quote') {
        notionalUsdt = sizeValue;
      } else {
        // base mode — convert via spot price snapshot if you have one
        // For client-side only, we'll let the server enforce. Estimate optimistically.
        notionalUsdt = 0; // server validates the precise notional
      }
    }

    if (notionalUsdt > BETA_BOT_CAP_USDT) {
      alert(
        `Beta limit: bot position size cannot exceed $${BETA_BOT_CAP_USDT} USDT notional. ` +
        `Current: $${notionalUsdt.toFixed(0)}. ` +
        (newBot.marketType === 'futures'
          ? 'Reduce notional or leverage.'
          : 'Reduce position size.')
      );
      return;
    }

    // ── Logic validation (non-webhook only) ───────────────────────────────────

    // ── Logic validation (non-webhook only) ───────────────────────────────────
    if (newBot.signalSource !== 'tradingview') {
      const isHedge = isFutures && newBot.positionSide === 'hedge';
      const isStockHedgeMode = isStockEquity && newBot.allowShorts;

      if (isWizExpression) {
        const sides = (isHedge || isStockHedgeMode)
          ? ['buy', 'sell', 'shortOpen', 'shortClose']
          : ['buy', 'sell'];

        // A syntax error here would be caught server-side, but only after the
        // bot is saved and started — much later than the user can connect it to
        // what they typed.
        const badSide = sides.find(k => validateLogicExpression(wizRawLogic[k]));
        if (badSide) {
          alert(`The ${badSide} expression is not valid: ${validateLogicExpression(wizRawLogic[badSide])}`);
          return;
        }

        if (!sides.some(k => (wizRawLogic[k] || '').trim())) {
          alert('Please write at least one entry or exit expression.');
          return;
        }
      } else {
        const hasLogic = (isHedge || isStockHedgeMode)
          ? (buyConditions.length || sellConditions.length ||
            shortOpenConditions.length || shortCloseConditions.length)
          : (buyConditions.length || sellConditions.length);

        if (!hasLogic) {
          alert('Please add at least one condition.');
          return;
        }
      }
    }

    // ── Futures validation ────────────────────────────────────────────────────
    if (isFutures) {
      const futuresErrors = validateFuturesConfig(newBot, userExchanges);
      if (futuresErrors.length > 0) {
        alert(futuresErrors.join('\n'));
        return;
      }
    }

    // ── Resolve assets ────────────────────────────────────────────────────────
    const userId = parseInt(localStorage.getItem('userId'), 10);
    const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
    const isHedge = isFutures && newBot.positionSide === 'hedge';

    const { base: BASE, quote: QUOTE } = getPairMeta({
      symbol: newBot.symbol,
      exchangeId: newBot.exchangeId,
      userExchanges,
      symbolsByExchange,
    });

    // ── Spot sizing — normalize field names ──────────────────────────
    const spotValue = newBot.positionValue || newBot.tradeAmount || 0;
    const spotMode = newBot.sizeMode || newBot.tradeMode || 'quote';

    // ── Order sizing (spot only — futures uses marginPct/fixedNotional) ────────
    const sizing = isFutures
      ? {
        mode: newBot.futureSizeMode || 'margin_pct',
        value: newBot.futureSizeMode === 'fixed_notional'
          ? Number(newBot.fixedNotional) || 0
          : Number(newBot.marginPct) || 0,
        assetType: newBot.futureSizeMode || 'margin_pct',
        assetSymbol: QUOTE,
        base_asset: newBot.baseAsset || BASE,
        base_precision: newBot.basePrecision ?? null,
        quote_asset: newBot.quoteAsset || QUOTE,
        quote_precision: newBot.quotePrecision ?? null,
        margin_asset: newBot.marginAsset ?? null,
      }
      : (() => {
        // ✅ PositionSizeSection always uses tradeAmount in BASE asset
        const s = buildOrderSizing(
          newBot.symbol,
          newBot.sizeMode || 'base', // ← respects toggle
          newBot.tradeAmount || 0,
          BASE, QUOTE
        );
        s.base_asset = newBot.baseAsset || BASE;
        s.base_precision = newBot.basePrecision ?? null;
        s.quote_asset = newBot.quoteAsset || QUOTE;
        s.quote_precision = newBot.quotePrecision ?? null;
        return s;
      })();

    // ── Build logic strings ───────────────────────────────────────────────────
    // Expression mode bypasses the condition model entirely — the raw string is
    // the strategy. Builder mode is unchanged.
    const buyLogicFinal = newBot.signalSource === 'tradingview'
      ? 'WEBHOOK_SIGNAL'
      : (isWizExpression ? (wizRawLogic.buy || '').trim() : buildLogicFromConditions(buyConditions));

    const sellLogicFinal = newBot.signalSource === 'tradingview'
      ? 'WEBHOOK_SIGNAL'
      : (isWizExpression ? (wizRawLogic.sell || '').trim() : buildLogicFromConditions(sellConditions));

    // Four-sided: futures hedge OR stock with Allow Shorts. One derivation for
    // both venues — the stock path used to rebuild from the condition arrays
    // unconditionally, silently ignoring expression mode.
    const wantsShortLogic = isHedge || (isStockEquity && newBot.allowShorts);
    const shortOpenLogicFinal = wantsShortLogic
      ? (isWizExpression ? (wizRawLogic.shortOpen || '').trim() : buildLogicFromConditions(shortOpenConditions))
      : '';

    const shortCloseLogicFinal = wantsShortLogic
      ? (isWizExpression ? (wizRawLogic.shortClose || '').trim() : buildLogicFromConditions(shortCloseConditions))
      : '';

    // ── Assemble tradeInfo ────────────────────────────────────────────────────
    const tradeInfo = {
      timeframe: newBot.signalSource === 'tradingview' ? '' : newBot.interval,
      symbol: newBot.symbol || '',
      exchange: selectedExchange?.exchange || '',

      // Spot investment (kept for backwards compat)
      totalInvestment: !isFutures && sizing.mode === 'quote' ? sizing.value : '',

      // Risk management — BOT level. profitStop/lossStop pause the whole bot
      // at a cumulative P&L threshold; they are not per-trade exits.
      profitStop: profitStop + '%',
      lossStop: lossStop + '%',
      maxDailyLoss: newBot.maxLoss,
      maxTradesPerDay: newBot.maxTrades,
      trailingStop: newBot.trailingStop,

      // Per-POSITION exits, in the exact block the backtest's precedence chain
      // reads (trade_info.risk) — so the bot backtests with the same exits it
      // trades with, and the live engine gets one unambiguous home for them.
      // Only set fields travel: a blank means "no such exit", and 0 would read
      // as a 0% stop.
      ...(() => {
        const n = (v) => { const x = Number(v); return Number.isFinite(x) && x > 0 ? x : null; };
        const risk = {
          ...(n(newBot.riskStopLossPct) ? { stopLossPct: n(newBot.riskStopLossPct) } : {}),
          ...(n(newBot.riskTakeProfitPct) ? { takeProfitPct: n(newBot.riskTakeProfitPct) } : {}),
          ...(n(newBot.trailingStop) ? { trailingStopPct: n(newBot.trailingStop) } : {}),
          ...(n(newBot.riskMaxBarsInTrade) ? { maxBarsInTrade: Math.floor(n(newBot.riskMaxBarsInTrade)) } : {}),
          ...(isStockEquity && n(newBot.riskFlattenBeforeCloseMin)
            ? { flattenBeforeCloseMin: Math.floor(n(newBot.riskFlattenBeforeCloseMin)) } : {}),
        };
        return Object.keys(risk).length ? { risk } : {};
      })(),

      // Sizing
      mode: sizing.mode,
      value: Number(sizing.value) || 0,
      asset_symbol: sizing.assetSymbol,
      orderSizing: sizing,

      // Indicators.
      //
      // In expression mode there are no condition rows to read, so this comes
      // off the logic strings instead. It is not optional: a backtest re-derives
      // its indicator list from the logic (backtest/simulateOHLCV.js), but a
      // LIVE bot computes only what this array names (file/runBotLogic.js
      // getBotIndicators). Leave it empty and the bot backtests correctly and
      // then never fires in production.
      indicators: newBot.signalSource === 'tradingview'
        ? []
        : isWizExpression
          ? extractIndicatorsFromLogic(
            buyLogicFinal,
            sellLogicFinal,
            shortOpenLogicFinal,
            shortCloseLogicFinal,
          )
          : extractIndicatorsFromConditions(
            buyConditions,
            sellConditions,
       ...((isHedge || (isStockEquity && newBot.allowShorts))
         ? [shortOpenConditions, shortCloseConditions]
         : []),
          ),

      // Signal source
      signalSource: newBot.signalSource,
      webhook: {
        enabled: newBot.signalSource === 'tradingview',
        provider: 'tradingview',
        secret: (newBot.webhookSecret || '').trim(),
        actionField: newBot.webhookActionField || 'action',
        buyValue: newBot.webhookBuyValue || 'BUY',
        sellValue: newBot.webhookSellValue || 'SELL',
      },

      // Market type
      marketType: newBot.marketType,

      // Futures config
      ...(isFutures && {
        futures: {
          type: newBot.futuresType,                    // 'usdt_m' | 'coin_m'
          leverage: Number(newBot.leverage) || 1,
          marginMode: newBot.marginMode,               // 'cross' | 'isolated'
          positionSide: newBot.positionSide,           // 'one_way' | 'hedge'
          reduceOnly: newBot.reduceOnly || false,

          // Position sizing
          sizeMode: newBot.futureSizeMode || 'margin_pct',
          marginPct: Number(newBot.marginPct) || 0,
          fixedNotional: Number(newBot.fixedNotional) || 0,
          maxConcurrentTrades: 1,

          // Hedge mode logic
          ...(isHedge && {
            shortOpenLogic: shortOpenLogicFinal,
            shortCloseLogic: shortCloseLogicFinal,
          }),
        },
      }),

      // Order execution
       orderExecution: {
        mode: newBot.orderExecution || 'market',
        limitOffsetPct: Number(newBot.limitOffsetPct) || 0.05,
        limitTimeoutSec: Number(newBot.limitTimeoutSec) || 10,
        postOnly: newBot.postOnly || false,
      },

// Phase B — asset class + stock-specific extras
      asset_class: assetClass,

      // Funding asset — balance source for bot capital & P&L.
      // Same field name & shape as scanner profiles for backend consistency.
      funding_asset: newBot.funding_asset || defaultFundingAssetForClass(assetClass),

      ...(isStockEquity && {
        alpaca: {
          // Options orders are RTH-only at Alpaca — extended hours is forced
          // off rather than silently ignored at order time.
          extended_hours: newBot.instrument === 'options' ? false : !!newBot.extendedHours,
        },
        // Stock shorts: trade_info.short_open_logic (top-level, snake_case) is
        // the CANONICAL durable home — the live engine and backtest both read
        // it. Same derivation as futures (shortOpenLogicFinal), so expression
        // mode is respected instead of silently rebuilding from the builder
        // arrays.
        ...(newBot.allowShorts && {
          short_open_logic: shortOpenLogicFinal,
          short_close_logic: shortCloseLogicFinal,
        }),
        // Options instrument switch — validated + normalized server-side
        // (services/optionsValidate.js); the backend fills defaults.
        ...(newBot.instrument === 'options' && {
          instrument: 'options',
          options: newBot.options || {},
        }),
      }),
    };

    // ── API payload ───────────────────────────────────────────────────────────
    const isStockHedge = isStockEquity && newBot.allowShorts;

    const v2Payload = {
      userId,
      name: newBot.name,
      exchange_id: newBot.exchangeId,
      buy_logic: buyLogicFinal,
      sell_logic: sellLogicFinal,
      ...((isHedge || isStockHedge) && {
        short_open_logic: shortOpenLogicFinal,
        short_close_logic: shortCloseLogicFinal,
      }),
trade_info: JSON.stringify(tradeInfo),
      asset_class: assetClass,   // ← mirror trade_info.asset_class to the column
      funding_asset: newBot.funding_asset || defaultFundingAssetForClass(assetClass),  // mirror to top-level (matches asset_class pattern)
      INIT_PRICE_ASSET: 100.0,
      stop_trade_ASSET: stopped,
      status: stopped ? 'paused' : '',
      error_message: '',
    };

    // ── Submit ────────────────────────────────────────────────────────────────

    const isRetry = v2Payload._isDuplicateRetry === true;
    if (isRetry) delete v2Payload._isDuplicateRetry;  // strip our local-only flag

    try {
      const resp = await botApiV2.createBot(v2Payload);
      const raw = resp.bot ?? resp;

      let tiSafe = {};
      try { tiSafe = JSON.parse(raw.trade_info || '{}'); } catch (_) { }

      const webhookSecret = (tiSafe.webhook?.secret || '').trim();

      const formattedBot = {
        id: raw.id,
        name: raw.name,
        symbol: tiSafe.symbol || '(from webhook)',
        interval: tiSafe.timeframe || (tiSafe.signalSource === 'tradingview' ? 'Webhook' : '—'),
        exchangeId: raw.exchange_id ?? raw.exchangeId ?? newBot.exchangeId ?? null,
        exchange: raw.exchange?.exchange || selectedExchange?.exchange || '',
        exchangeName: raw.exchange?.name || raw.exchange?.exchange || '—',
        trade_info: raw.trade_info,
        asset_class: raw.asset_class ?? null,   // see the fetch path — column, not just trade_info
        status: raw.stop_trade_ASSET ? 'paused' : 'active',
        stop_trade_ASSET: raw.stop_trade_ASSET,
        logic: {
          buy: raw.buy_logic,
          sell: raw.sell_logic,
          shortOpen: tiSafe.futures?.shortOpenLogic || tiSafe.short_open_logic || '',
          shortClose: tiSafe.futures?.shortCloseLogic || tiSafe.short_close_logic || '',
          profitStop: tiSafe.profitStop,
          lossStop: tiSafe.lossStop,
          maxLoss: tiSafe.maxDailyLoss,
          maxTrades: tiSafe.maxTradesPerDay,
          trailingStop: tiSafe.trailingStop,
          investment: tiSafe.totalInvestment,
        },
        stats: raw.statistics ? normalizeBotStats(raw.statistics) : undefined,
        statusMessage: raw.status || '',
        errorMessage: raw.error_message || '',
        signalSource: tiSafe.signalSource || 'logic',
        webhookUrl: tiSafe.signalSource === 'tradingview'
          ? buildTradingViewWebhookUrl(raw.id, webhookSecret)
          : '',
      };

      const message = stopped
        ? `Bot "${formattedBot.name}" created (paused). Activate it when ready.`
        : resp?.message || 'Bot created and activated!';
      showToast('success', message);

      setBots(prev => [...prev, formattedBot]);
      // Tell the onboarding checklist a bot now exists — nothing on this page
      // did, so "Create your first bot" stayed unticked until a reload.
      try { refreshStatus && refreshStatus({ fresh: true }); } catch (_) {}
      resetForm();
      setIsCreating(false);
      setActiveTab('bots');
    } catch (err) {
      // Soft-warning duplicate-bot path. Server returned 409 with a structured
      // payload describing which existing bot conflicts. We surface this as a
      // dialog so the user can confirm and resubmit with confirm_duplicate=true.
      // On the retry, the server skips the dedup check and creates the bot.
      if (
        !isRetry &&
        err?.response?.status === 409 &&
        err?.response?.data?.code === 'DUPLICATE_BOT'
      ) {
        setDuplicateBotConflict({
          conflict: err.response.data.conflict,
          pendingPayload: { ...v2Payload, confirm_duplicate: true, _isDuplicateRetry: true },
          stopped,   // capture so the retry preserves "Save (Paused)" vs "Save & Activate"
        });
        return;  // wait for the user's choice
      }

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to create bot.';
      showToast('error', msg);
      console.error('Error saving bot:', err);
    }
  };


  const handleNext = () => {
    // Skip Trading Logic step (step 1) for TradingView bots
    if (activeStep === 0 && newBot.signalSource === 'tradingview') {
      setActiveStep(2); // Jump to Risk Management
    } else {
      setActiveStep(s => s + 1);
    }
  };
  async function ensureFuturesSymbols(exchangeId, exchangeName, marketType) {
    const cacheKey = `${exchangeName}:${marketType}`;
    if (!cacheKey || futuresSymbolsByExchange[cacheKey]) return;

    try {
      setSymbolsLoading(true);
      setSymbolsError(null);

      const list = await symbolService.getFutures(exchangeName, marketType);

      if (list.length === 0) {
        setSymbolsError(`No ${marketType} symbols found for ${exchangeName}`);
      }

      const normalized = list.map(item => ({
        symbol: item.symbol || item.class_name || '',
        base_asset: item.base_asset ?? null,
        quote_asset: item.quote_asset ?? null,
        margin_asset: item.margin_asset ?? null,
        pair: item.pair ?? null,
        contract_type: item.contract_type ?? null,
        status: item.status ?? null,
        price_precision: item.price_precision ?? null,
        quantity_precision: item.quantity_precision ?? null,
        market_type: item.market_type ?? marketType,
      }));

      setFuturesSymbolsByExchange(prev => ({ ...prev, [cacheKey]: normalized }));
    } catch (e) {
      console.error('futures symbols fetch failed', e);
      const msg = e?.response?.data?.error || e?.message || 'Failed to load futures symbols';
      setSymbolsError(msg);
      showToast('error', msg);
    } finally {
      setSymbolsLoading(false);
    }
  }

  const handleBack = () => {
    // If on Risk Management and using TradingView, go back to Basic Config
    if (activeStep === 2 && newBot.signalSource === 'tradingview') {
      setActiveStep(0);
    } else {
      setActiveStep(s => s - 1);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setActiveStep(0);
    setActiveTab('create');
  };
  const handleCancelCreate = () => {
    setIsCreating(false);
    resetForm();
  };




  const resetForm = () => {
    setNewBot({
      name: '',
      exchangeId: null,
      interval: '1H',
      symbol: 'BTC/USDT',
      sizeMode: 'quote',
      positionValue: '',
      totalAmount: '',
      maxLoss: '5',
      maxTrades: '10',
      trailingStop: '2',
      riskStopLossPct: '',
      riskTakeProfitPct: '',
      riskMaxBarsInTrade: '',
      riskFlattenBeforeCloseMin: '',
      signalSource: 'logic',
      webhookSecret: '',
      webhookActionField: 'action',
      webhookBuyValue: 'BUY',
      webhookSellValue: 'SELL',
      // ✅ NEW: Reset futures fields
      marketType: 'spot',
      futuresType: 'usdt_m',
      leverage: 1,
      marginMode: 'cross',
      positionSide: 'one_way',
      reduceOnly: false,

      // ✅ NEW: Reset order execution fields
      orderExecution: 'market',
      limitOffsetPct: 0.05,
      limitTimeoutSec: 10,
      postOnly: false,
      futureSizeMode: 'margin_pct',
      marginPct: '',
      fixedNotional: '',
      maxConcurrentTrades: 1,
           marginAsset: undefined,
      baseAsset: undefined,
      quoteAsset: undefined,
      basePrecision: null,
      quotePrecision: null,
      contractType: null,

      // Phase B
// Phase B
      extendedHours: false,
      allowShorts: false,
      instrument: 'shares',
      options: null,

      // Funding asset reset to crypto default; auto-snaps to USD on Alpaca via effect
      funding_asset: 'USDT',
    });
    setShortOpenConditions([]);
    setShortCloseConditions([]);
    setBuyConditions([]);
    setSellConditions([]);
    setWizLogicMode('builder');
    setWizRawLogic({ buy: '', sell: '', shortOpen: '', shortClose: '' });
    setWizForcedExpression(false);
    setProfitStop('20');
    setLossStop('10');
    setActiveStep(0);
  };

  // Quick Bot: prefill the wizard with a ready-made preset (RSI Reversal on the
  // first exchange) + safe risk defaults and jump to the review step, so a
  // first-timer just confirms and activates. Reuses the normal handleSave path.
  // Decide WHICH exchange Quick Bot should use, then hand off to startQuickBot.
  //
  // This used to grab userExchanges[0] blindly, which meant an Alpaca account
  // sitting first in the list got prefilled with the crypto pair BTC/USDT — and
  // since the flow jumps straight to the review step, that was easy to confirm
  // without ever seeing it.
  const handleQuickBot = () => {
    if (!userExchanges?.length) {
      setToast({ open: true, severity: 'info', message: 'First create an exchange — opening the demo setup for you…' });
      // next=quickbot tells the portfolio page to send us back here once the
      // demo exchange exists, instead of stranding the user on that page.
      routerHistory.push('/profile/portfolio?onboard=demo&next=quickbot');
      return;
    }
    if (userExchanges.length === 1) { startQuickBot(userExchanges[0]); return; }
    // More than one venue and no basis to guess — ask.
    setQuickBotExchange(userExchanges[0]);
    setQuickBotPickerOpen(true);
  };

  // Prefill the wizard with a ready-made preset + safe risk defaults and jump to
  // the review step, so a first-timer just confirms and activates. Reuses the
  // normal handleSave path.
  const startQuickBot = (ex) => {
    if (!ex) return;
    const exchangeName = ex.exchange || ex.name;
    const tpl = SPOT_TEMPLATES[0]; // RSI Reversal — well-known starter strategy
    // RSI lives in CANDLE_INDICATORS, so the same starter template is valid for
    // equities; only the market and its funding currency differ.
    const isStock = getAssetClassFromExchange(ex) === 'stock_equity';
    resetForm();
    setNewBot((prev) => ({
      ...prev,
      name: `Quick Bot — ${tpl.name}`,
      exchangeId: ex.id,
      symbol: isStock ? 'AAPL' : 'BTC/USDT',
      // 1m so a demo bot actually shows activity soon after activation —
      // on 1H a first-timer could wait an hour to see anything happen.
      interval: '1m',
      signalSource: 'logic',
      orderExecution: 'market',
      sizeMode: 'quote',
      // PositionSizeSection binds to tradeAmount, and handleSave validates
      // tradeAmount only ("← was positionValue"). Quick Bot used to set just
      // positionValue, so the size box rendered empty and saving failed with
      // "Position Size must be greater than 0" — while the readiness rail
      // wrongly showed the step as done. Set both: tradeAmount is what the UI
      // and validation read, positionValue is still consulted as a fallback.
      tradeAmount: '100',
      positionValue: '100',
      // Set correctly up front so the asset-class auto-snap effect doesn't have
      // to correct a visible USDT -> USD flash on Alpaca.
      funding_asset: defaultFundingAssetForClass(isStock ? 'stock_equity' : 'crypto'),
    }));
    if (ex.id) ensureSymbols(ex.id, exchangeName);
    setBuyConditions(templateToConditions(tpl.buy));
    setSellConditions(templateToConditions(tpl.sell));
    // Quick Bot is condition rows by definition. Leaving a stale expression mode
    // on would save the old raw string instead of the preset.
    setWizLogicMode('builder');
    setWizRawLogic({ buy: '', sell: '', shortOpen: '', shortClose: '' });
    setWizForcedExpression(false);
    setProfitStop('20');
    setLossStop('10');
    setIsCreating(true);
    setActiveTab('create');
    setActiveStep(2); // jump to the review/risk step — confirm & activate
  };

  // Welcome-modal / checklist deep-link: ?quickbot=1 launches Quick Bot once.
  // Wait for the exchange list so we don't wrongly redirect a user who has one.
  useEffect(() => {
    if (!exchangesLoaded) return;
    const params = new URLSearchParams(rfLocation.search || '');
    if (params.get('quickbot') === '1' && !quickBotRanRef.current) {
      quickBotRanRef.current = true;
      handleQuickBot();
      routerHistory.replace('/profile/traderform');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangesLoaded, rfLocation.search]);

  // Shape the in-memory backtest result like a `backtests` row so the backend
  // can summarize it with the same code it uses for saved history. Returns null
  // when nothing has been run, in which case the advisor falls back to saved
  // history alone.
  const buildCurrentRunPayload = () => {
    if (!backtestResults?.metrics) return null;
    const ran = btLastRunRef.current;
    const cfg = safeParseMaybe(backtestResults.backtest_config) || {};
    return {
      symbol: cfg.symbol || selectedBotForBacktest?.symbol || '',
      timeframe: cfg.timeframe || ran?.timeframe || '',
      strategy: cfg.strategy || (ran ? { buy: ran.buy, sell: ran.sell } : {}),
      metrics: backtestResults.metrics,
      start_time_ms: cfg.start_time_ms ?? ran?.startMs ?? backtestDateRange[0],
      end_time_ms: cfg.end_time_ms ?? ran?.endMs ?? backtestDateRange[1],
    };
  };

  const askBacktestAdvisor = async () => {
    const bot = selectedBotForBacktest;
    if (!bot || !btDraft || !btAiPrompt.trim()) return;
    setBtAiLoading(true);
    setBtAiResult(null);
    try {
      let ti = {};
      try { ti = JSON.parse(bot.trade_info || '{}'); } catch { /* defaults below */ }
      const res = await btAiService.current.refineStrategy({
        refinement: btAiPrompt.trim(),
        mode: btSearchMode,
        currentBuyLogic: btDraftLogic.buy,
        currentSellLogic: btDraftLogic.sell,
        currentShortOpenLogic: btDraftLogic.shortOpen || '',
        currentShortCloseLogic: btDraftLogic.shortClose || '',
        interval: btDraft.timeframe,
        symbol: bot.symbol,
        marketType: btDraftMarketType,
        // 'hedge' is the channel the AI reads to emit short legs. Futures hedge
        // OR a stock bot that carries short legs (the four-sided case) — without
        // the stock arm the advisor only ever refined the two long legs.
        positionMode: ti.futures?.positionSide === 'hedge'
          ? 'hedge'
          : ((btDraftIsStock && (btDraftLogic.shortOpen || btDraftLogic.shortClose)) ? 'hedge' : 'one_way'),
        // Same precedence isStockBot uses. Drives whether the advisor is even
        // offered session variables and flatten-before-close for this bot.
        assetClass: ti.asset_class || bot.asset_class || null,
        currentRun: buildCurrentRunPayload(),
        history: btAiHistory,
      });
      setBtAiResult(res);
      // Record both sides so the next question continues this thread. The
      // assistant turn carries the proposed logic, not just the prose, so a
      // follow-up like "keep that entry but loosen the exit" resolves.
      const assistantText = [
        res?.reply || res?.explanation || '',
        res?.buyLogic ? `buyLogic: ${res.buyLogic}` : '',
        res?.sellLogic ? `sellLogic: ${res.sellLogic}` : '',
      ].filter(Boolean).join('\n');
      setBtAiHistory(h => [
        ...h,
        { role: 'user', content: btAiPrompt.trim() },
        ...(assistantText ? [{ role: 'assistant', content: assistantText }] : []),
      ].slice(-8));
      setBtAiPrompt('');   // ready for the follow-up
      if (!res?.success && res?.explanation) showToast('error', res.explanation);
    } catch (err) {
      console.error('Backtest advisor failed:', err);
      showToast('error', 'The AI advisor could not be reached.');
    } finally {
      setBtAiLoading(false);
    }
  };

  // Stop polling on unmount so a search left running does not keep firing
  // requests against a screen nobody is looking at.
  useEffect(() => () => { if (btSearchPollRef.current) clearInterval(btSearchPollRef.current); }, []);

  const runStrategySearch = async () => {
    const bot = selectedBotForBacktest;
    if (!bot || !btDraft || btSearchBusy) return;
    let ti = {};
    try { ti = JSON.parse(bot.trade_info || '{}'); } catch { /* defaults below */ }

    setBtSearchBusy(true);
    setBtSearch({ status: 'running', phase: 'generating' });
    try {
      const searchId = await btAiService.current.startStrategySearch({
        botId: bot.id,
        // The refine/explore toggle above this button was wired to the ADVISOR
        // only, never to the search — so the backend fell back to its 'refine'
        // default and the explore path had never once executed, whatever the
        // toggle showed. Refine and explore differ in brief set AND in candidate
        // count (4 vs 8), so omitting this silently halved the search as well.
        mode: btSearchMode,
        refinement: btAiPrompt.trim(),
        currentBuyLogic: btDraftLogic.buy,
        currentSellLogic: btDraftLogic.sell,
        currentShortOpenLogic: btDraftLogic.shortOpen || '',
        currentShortCloseLogic: btDraftLogic.shortClose || '',
        interval: btDraft.timeframe,
        symbol: bot.symbol,
        marketType: btDraftMarketType,
        positionMode: ti.futures?.positionSide === 'hedge'
          ? 'hedge'
          : ((btDraftIsStock && (btDraftLogic.shortOpen || btDraftLogic.shortClose)) ? 'hedge' : 'one_way'),
        assetClass: ti.asset_class || bot.asset_class || null,
        trade_info: bot.trade_info,
        isTick: (btDraft.timeframe || '') === 'tick',
        currentRun: buildCurrentRunPayload(),
        // Candidates are measured on the SAME window and costs as the run the
        // user is looking at — otherwise the ranking compares strategies to each
        // other on different data, which is worse than not ranking them at all.
        config: {
          initialCapital: Number(backtestParams.initialCapital) || 0,
          commission: Number(backtestParams.commission) || 0,
          slippage: Number(backtestParams.slippage) || 0,
          startTime: startOfDayMs(backtestDateRange[0]),
          endTime: endOfDayMs(backtestDateRange[1]),
        },
      });

      setBtSearch({ id: searchId, status: 'running', phase: 'generating' });

      if (btSearchPollRef.current) clearInterval(btSearchPollRef.current);
      btSearchPollRef.current = setInterval(async () => {
        try {
          const rec = await btAiService.current.getStrategySearch(searchId);
          setBtSearch({ id: searchId, ...rec });
          if (rec.status === 'completed' || rec.status === 'failed') {
            clearInterval(btSearchPollRef.current);
            btSearchPollRef.current = null;
            setBtSearchBusy(false);
            if (rec.status === 'failed') showToast('error', rec.error || 'Strategy search failed.');
          }
        } catch (err) {
          // A transient poll failure is not a failed search — keep polling and
          // let the timeout in the backend decide.
          console.warn('search poll failed:', err?.message || err);
        }
      }, 4000);
    } catch (err) {
      console.error('Strategy search failed to start:', err);
      showToast('error', err?.message || 'Could not start the strategy search.');
      setBtSearch({ status: 'failed', error: err?.message });
      setBtSearchBusy(false);
    }
  };

  // Explore's window, built at submit time.
  //
  // Tick does not scale in days. The lookback slider is 7–365 DAYS, and the tick
  // loader walks the whole span in 12h chunks with NO early exit once both
  // bounds are supplied (loadWithAutoExpand, userProvidedBoth) — so a 90-day
  // tick request is ~180 sequential aggTrade fetches per symbol, times every
  // symbol in a batch. deriveWindow's own tick ceiling is 12h; honour it here
  // rather than issuing a request that would take hours and rate-limit the
  // venue. End at `now` too: a tick window ending at end-of-today asks for
  // trades that have not happened.
  const BT_TICK_WINDOW_MS = 12 * 60 * 60 * 1000;
  // Timeframe axes travel as MINUTES (the server's surface sorts numerically)
  // but nobody thinks in minutes — the sweep inputs show and accept '1h, 2h,
  // 4h'. Mirrors INTERVAL_MINUTES in backtest/logicRewrite.js. Declared here,
  // above every consumer: the effective-window memo below and the sweep
  // helpers further down both read it, and a component-body const is TDZ
  // until its line runs.
  const TF_MINUTES = { '1m': 1, '3m': 3, '5m': 5, '15m': 15, '30m': 30, '1h': 60, '2h': 120, '4h': 240, '6h': 360, '8h': 480, '12h': 720, '1d': 1440, '3d': 4320, '1w': 10080 };
  const tfLabel = (mins) => Object.keys(TF_MINUTES).find(k => TF_MINUTES[k] === Number(mins)) || String(mins);
  const tfParse = (s) => {
    const t = String(s).trim().toLowerCase();
    if (TF_MINUTES[t] != null) return TF_MINUTES[t];
    const n = Number(t);
    return Number.isFinite(n) ? n : NaN;   // raw minutes still accepted
  };
  // Days are the slider's unit; BARS are the cost. 90 days is 2,160 bars at 1h
  // and 130,000 at 1m — the latter, times a 25-symbol batch, is what OOM-killed
  // the worker in a loop. Mirror of BACKTEST_BATCH_MAX_BARS server-side: the
  // server clamps regardless (a client cannot be trusted with the worker's
  // memory), this mirror exists so the slider can SAY the window that will
  // actually run instead of promising 90 days and measuring 35.
  const BT_MAX_BARS = 50000;
  const btExploreEffectiveDays = useMemo(() => {
    const tfMin = TF_MINUTES[btEffectiveTimeframe];
    if (!tfMin) return { days: btExLookbackDays, clamped: false };
    const capDays = Math.max(1, Math.floor((BT_MAX_BARS * tfMin) / 1440));
    return btExLookbackDays > capDays
      ? { days: capDays, clamped: true }
      : { days: btExLookbackDays, clamped: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btEffectiveTimeframe, btExLookbackDays]);

  const buildExploreWindow = () => {
    if (btEffectiveTimeframe === 'tick') {
      const end = Date.now();
      return { startTime: end - BT_TICK_WINDOW_MS, endTime: end };
    }
    const end = endOfTodayMs();
    return { startTime: end - btExploreEffectiveDays.days * MS_DAY, endTime: end };
  };

  useEffect(() => () => { if (btSweepPollRef.current) clearInterval(btSweepPollRef.current); }, []);

  // Default the tuning symbol to whatever breadth just crowned. Only when the
  // user has not already chosen one — silently overwriting their pick every time
  // a batch finishes would be its own bug.
  useEffect(() => {
    if (btTuneSymbol) return;
    const rows = btBatch?.summary?.bySymbol;
    if (!Array.isArray(rows) || !rows.length) return;
    const best = [...rows].sort((a, b) => (b.returnPct ?? -Infinity) - (a.returnPct ?? -Infinity))[0];
    if (best?.symbol) setBtTuneSymbol(best.symbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btBatch]);

  // ── Parameter sweep ───────────────────────────────────────────────────────
  // Ask the server which numbers are tunable. It never guesses client-side: the
  // parser that proposes the axes is the same one that rewrites the logic, so
  // what is offered here and what actually gets measured cannot drift apart.
  const loadSweepPlan = async (kind) => {
    const logic = btDraftLogic;
    try {
      const res = await backtestService.planSweep({
        kind,
        botId: btDraft?.botId,
        risk: buildRiskConfig(),
        strategy: {
          buy_logic: logic?.buy || '', sell_logic: logic?.sell || '',
          ...((logic?.shortOpen || logic?.shortClose)
            ? { short_open_logic: logic.shortOpen || '', short_close_logic: logic.shortClose || '' }
            : {}),
        },
        ...(kind === 'discover' ? { maxCandidates: Number(btSweepMaxCandidates) || 300 } : {}),
      });
      const axes = res?.axes || [];
      setBtSweepPlan({ ...res, kind });
      // Pre-tick the first two axes only. Every extra axis is more combinations
      // and more ways to be fooled by the best of N.
      const sel = {}, vals = {};
      // Seed values so the DEFAULT grid fits the cap. In Explore every axis is
      // live (a field with 2+ values is swept), and full 5-value ladders on a
      // two-indicator strategy multiply to tens of thousands — so the default
      // was a heavily sampled grid before the user touched anything. Greedy:
      // early axes keep their full ladder while the running product allows it,
      // later ones are trimmed to the values nearest base (base always kept),
      // and past the budget an axis seeds base alone — riding along fixed until
      // the user adds values back. Deliberately front-loaded rather than evenly
      // thinned: three full ladders beat six half-ladders for reading a
      // surface, and the axis order is the planner's, which puts the strategy's
      // own numbers first.
      //
      // Interval axes seed in interval notation ('1h, 2h, 4h'), everything else
      // as numbers — axisValuesToText and tfParse are the matched pair.
      const budget = Number(res?.cap) || (kind === 'risk' ? 1000 : 400);
      let product = 1;
      axes.forEach((a, i) => {
        sel[a.key] = i < 2;
        const full = a.values || [];
        let keep = full;
        if (product * full.length > budget) {
          const room = Math.max(1, Math.floor(budget / product));
          const base = a.baseValue;
          keep = [...full]
            .sort((x, y) => (x === base ? -1 : y === base ? 1 : Math.abs(x - base) - Math.abs(y - base)))
            .slice(0, room)
            .sort((x, y) => x - y);
        }
        product *= Math.max(1, keep.length);
        vals[a.key] = axisValuesToText({ ...a, values: keep });
      });
      setBtSweepSel(sel);
      setBtSweepVals(vals);
    } catch (err) {
      console.error('sweep plan failed:', err);
      showToast('error', err?.response?.data?.error || err?.message || 'Could not plan the sweep.');
    }
  };

  // Parse the editable ladder back into numbers. Invalid entries are dropped
  // rather than sent — the server would reject the whole grid for one typo.
  // Explore's HYPERPARAMETERS grid has no checkboxes: every field is eligible,
  // and the ≥2-distinct-values filter below decides what is actually swept —
  // comma-separated values; a single value rides along fixed.
  const isIntervalAxis = (a) => a?.kind === 'interval';
  const axisValuesToText = (a) =>
    (a.values || []).map(v => (isIntervalAxis(a) ? tfLabel(v) : v)).join(', ');

  const sweepAxesPayload = () => (btSweepPlan?.axes || [])
    .filter(a => (btResearchTab === 'explore' ? true : btSweepSel[a.key]))
    .map(a => ({
      key: a.key,
      values: String(btSweepVals[a.key] ?? '')
        .split(',')
        .map(s => (isIntervalAxis(a) ? tfParse(s) : Number(String(s).trim())))
        .filter(v => Number.isFinite(v) && v >= 0),
    }))
    .filter(a => new Set(a.values).size >= 2);

  const btSweepCombos = useMemo(() => {
    // Discovery does not build a grid — the count is however many candidates the
    // server will generate, bounded by the cap it reported.
    if (btSweepKind === 'discover') {
      return Math.min(Number(btSweepMaxCandidates) || 0, btSweepPlan?.cap || 400);
    }
    // Mirrors estimateCombos on the server: the CROSS PRODUCT. The old mirror
    // summed (coordinate-wise); leaving it would have under-reported the grid
    // by orders of magnitude against a server that now multiplies.
    const axes = sweepAxesPayload();
    return axes.reduce((n, a) => n * Math.max(1, new Set(a.values).size), 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [btSweepPlan, btSweepSel, btSweepVals, btSweepKind, btSweepMaxCandidates]);

  // AI ASSIST: describe → generate → land in the editor. The routing rule is the
  // same one the copy-from-bot dropdown uses: builder only when the builder can
  // represent the logic without rewriting it, expression otherwise. The
  // assistant's answer must go through the same door as a hand-typed one.
  const btNlExamples = [
    'Buy when RSI is oversold and price is above 50 SMA',
    'Golden cross: 50 SMA crosses above 200 SMA',
    'MACD bullish crossover with high volume confirmation',
    'Mean reversion: RSI below 25 buy, above 75 sell',
    'Trend following: price above EMA 20 and MACD positive',
  ];

  const runNlAssist = async (promptOverride) => {
    const prompt = String(promptOverride ?? btNlPrompt).trim();
    if (!prompt || btNlBusy) return;
    setBtNlBusy(true);
    setBtNlNote(null);
    try {
      const cur = btDraftLogic;
      // A shortable stock exploration must ask the AI for four legs — otherwise
      // "find a strategy" on a long+short bot came back long-only.
      const exploreWantsShorts = btDraftIsStock
        && (btExploreShortOpen || btExploreShortClose || cur?.shortOpen || cur?.shortClose);
      const res = await btAiService.current.generateStrategy(prompt, {
        interval: btEffectiveTimeframe || '1h',
        marketType: btDraftMarketType,
        positionMode: exploreWantsShorts ? 'hedge' : 'one_way',
        assetClass: btDraftIsStock ? 'stock_equity' : null,
        existingBuyLogic: cur?.buy || '',
        existingSellLogic: cur?.sell || '',
        existingShortOpenLogic: cur?.shortOpen || '',
        existingShortCloseLogic: cur?.shortClose || '',
      });
      // The advisor can answer in prose instead of emitting a strategy; show
      // the reply rather than silently discarding it as empty logic.
      if (res.conversational && res.reply) {
        setBtNlNote({ tone: 'info', text: res.reply });
        return;
      }
      if (!res.success || (!res.buyLogic && !res.sellLogic)) {
        setBtNlNote({ tone: 'error', text: res.explanation || 'The assistant returned no strategy. Try describing the entry and exit separately.' });
        return;
      }
      const cb = res.buyLogic || '';
      const cs = res.sellLogic || '';
      // Explore's strategy lives in ONE place, the explore editor — a selected
      // bot only seeds it. The assistant writes there, through the same
      // builder-vs-expression door as a hand-typed strategy, so every check
      // below acts on exactly what is on screen.
      const safe = canRepresentInBuilder(cb) && canRepresentInBuilder(cs);
      setBtExploreBuy(cb);
      setBtExploreSell(cs);
      setBtExploreMode(safe ? 'builder' : 'expression');
      setBtExploreBuyConds(safe ? (parseLogicToConditions(cb) || []) : []);
      setBtExploreSellConds(safe ? (parseLogicToConditions(cs) || []) : []);
      // Four-sided result: the short legs are expression fields in Explore, so
      // write them straight through (empty when the AI returned none).
      if (res.shortOpenLogic || res.shortCloseLogic) {
        setBtExploreShortOpen(res.shortOpenLogic || '');
        setBtExploreShortClose(res.shortCloseLogic || '');
      }
      setBtNlPrompt('');
      setBtNlNote({
        tone: 'ok',
        text: res.explanation || 'Strategy loaded into the editor below.',
        warnings: Array.isArray(res.warnings) ? res.warnings : [],
      });
    } catch (err) {
      setBtNlNote({ tone: 'error', text: err?.message || 'The assistant is unavailable right now.' });
    } finally {
      setBtNlBusy(false);
    }
  };

  // Apply a leaderboard row to the draft. Split out of the row's onClick so the
  // consequential-load confirm can call the exact same code path — a second copy
  // would be a second chance for the two to drift.
  const applyLeaderboardRow = (r, { flaggedNoise = false } = {}) => {
    if (!r) return;
    if (r.risk) {
      setBacktestParams((p) => ({
        ...p,
        stopLossPct: r.risk.stopLossPct || '',
        takeProfitPct: r.risk.takeProfitPct || '',
        trailingStopPct: r.risk.trailingStopPct || '',
        maxBarsInTrade: r.risk.maxBarsInTrade || '',
        flattenBeforeCloseMin: r.risk.flattenBeforeCloseMin || '',
      }));
    }
    // Indicator and discovery sweeps both produce new logic — a rewrite in one
    // case, a whole candidate in the other. A risk sweep carries the unchanged
    // strings along and must not flip the builder into expression mode.
    // Destination follows btDraftLogic's split: the explore editor in Explore
    // (its strategy lives there, full stop), the bot draft in Optimize.
    if (['indicator', 'discover'].includes(btSweep?.kind) && (r.buyLogic || r.sellLogic)) {
      if (btResearchTab === 'explore') {
        const cb = r.buyLogic ?? btExploreBuy;
        const cs = r.sellLogic ?? btExploreSell;
        setBtExploreBuy(cb);
        setBtExploreSell(cs);
        setBtExploreMode('expression');
      } else {
        setBtDraft((d) => (d ? {
          ...d,
          mode: 'expression',
          rawBuy: r.buyLogic ?? d.rawBuy,
          rawSell: r.sellLogic ?? d.rawSell,
        } : d));
      }
    }
    setBtPendingLoad(null);
    // Handoff copy, verbatim: the noise path names the flag and the condition
    // for going live; the safe path is plain.
    setBtLoadedMsg(flaggedNoise
      ? 'Loaded into bot draft with a NOISE flag; validation required before it can go live.'
      : 'Loaded into bot draft.');
    showToast('success', 'Loaded into the bot draft — re-run the backtest to confirm.');
  };

  // Switch the tuner between risk / indicator / discover WITHOUT losing work.
  //
  // The chips used to clear the plan and the results on every click, so a
  // finished sweep was destroyed by glancing at another kind — switch to
  // INDICATORS and back, and the risk grid you had just waited minutes for was
  // gone. Each kind now keeps its own {results, plan, selections, values} in
  // the stash; a switch stores the outgoing kind and restores the incoming
  // one, and only plans afresh when there is nothing to restore.
  // One implementation for both switches — kind chips and research tabs move
  // through the same stash, differing only in which half of the key changes.
  //
  // `planKind` doubles as the auto-plan switch. A KIND click passes it:
  // loadSweepPlan closes over this render's btDraftLogic, and the kind switch
  // does not change which tab's logic that is, so planning is safe. A TAB
  // switch must NOT pass it — the closure still holds the OUTGOING tab's
  // strategy, so planning here would offer Optimize axes computed from
  // Explore's editor (or vice versa). The restored tab shows its stashed plan
  // or the "find tunable parameters" state instead.
  const swapSweepContext = (fromKey, toKey, planKind = null) => {
    btSweepStashRef.current[fromKey] = {
      sweep: btSweep, plan: btSweepPlan, sel: btSweepSel, vals: btSweepVals,
    };
    btSweepCtxRef.current = toKey;
    const saved = btSweepStashRef.current[toKey];
    setBtSweep(saved?.sweep || null);
    setBtSweepPlan(saved?.plan || null);
    setBtSweepSel(saved?.sel || {});
    setBtSweepVals(saved?.vals || {});
    if (!saved?.plan && planKind) loadSweepPlan(planKind);
  };

  const switchSweepKind = (k) => {
    if (k === btSweepKind) return;
    setBtSweepKind(k);
    swapSweepContext(sweepStashKey(btResearchTab, btSweepKind), sweepStashKey(btResearchTab, k), k);
  };

  // The stash holds results measured ON a symbol (and, in Explore, on the
  // strategy in the editor). When that context changes, the affected TAB's
  // stashed results describe something no longer on screen — but only that
  // tab's: changing Explore's tuning symbol says nothing about the bot
  // Optimize is tuning, and wiping both was itself a cross-contamination.
  const invalidateSweepStash = (tab) => {
    if (!tab) { btSweepStashRef.current = {}; return; }
    for (const key of Object.keys(btSweepStashRef.current)) {
      if (key.startsWith(`${tab}:`)) delete btSweepStashRef.current[key];
    }
  };

  const runSweep = async () => {
    const isDiscover = btSweepKind === 'discover';
    const axes = sweepAxesPayload();
    // Discovery generates its own candidates, so requiring axes here would make
    // the mode unreachable from the UI exactly the way the risk-field whitelist
    // once made the indicator mode unreachable on the server.
    if (!isDiscover && !axes.length) { showToast('error', 'Pick at least one parameter with two or more values.'); return; }
    const bot = bots.find(b => String(b.id) === String(btDraft?.botId));
    // Explore tunes a strategy that may not belong to a bot yet — that is the
    // whole point of finding one there — so it needs a symbol, not a bot, and
    // its venue comes from the exploration setup (btActiveBot is the explore
    // context there), never from whichever bot seeded the strategy.
    const exploring = btResearchTab === 'explore';
    const src = exploring ? btActiveBot : bot;
    const sweepSymbol = exploring ? btTuneSymbol : bot?.symbol;
    if (!exploring && !bot) { showToast('error', 'Select a bot first.'); return; }
    if (!sweepSymbol) { showToast('error', 'Pick a symbol to tune.'); return; }

    setBtSweepBusy(true);
    setBtSweep({ status: 'running', phase: 'queued' });
    // Reset the per-run UI: a previous run's confirmation or pending load must
    // not survive into a new one.
    setBtSweepStartedAt(Date.now());
    setBtPendingLoad(null);
    setBtLoadedMsg('');
    try {
      const logic = btDraftLogic;
      const { sweepId } = await backtestService.startSweep({
        kind: btSweepKind,
        botId: exploring ? null : (btDraft?.botId ?? null),
        symbol: sweepSymbol,
        timeframe: btEffectiveTimeframe || '1h',
        isTick: btEffectiveTimeframe === 'tick',
        assetClass: src?.asset_class,
        trade_info: src?.trade_info,
        strategy: {
          buy_logic: logic?.buy || '', sell_logic: logic?.sell || '',
          ...((logic?.shortOpen || logic?.shortClose)
            ? { short_open_logic: logic.shortOpen || '', short_close_logic: logic.shortClose || '' }
            : {}),
        },
        axes,
        ...(isDiscover ? { maxCandidates: Number(btSweepMaxCandidates) || 300 } : {}),
        // Same window and costs as the run on screen — otherwise the sweep
        // ranks parameterisations against each other on different data. In
        // Explore that window is the lookback slider, so tuning measures the
        // same span breadth just crowned a winner on.
        config: {
          initialCapital: Number(backtestParams.initialCapital) || 0,
          commission: Number(backtestParams.commission) || 0,
          slippage: Number(backtestParams.slippage) || 0,
          ...(btResearchTab === 'explore'
            ? buildExploreWindow()
            : { startTime: startOfDayMs(backtestDateRange[0]), endTime: endOfDayMs(backtestDateRange[1]) }),
          risk: buildRiskConfig(),
        },
      });

      setBtSweep({ id: sweepId, status: 'running', phase: 'queued' });
      // The tab+kind this sweep belongs to, fixed at start. The user is free
      // to switch either while it runs; results must follow the sweep's
      // context, not whichever view happens to be open when a poll returns.
      const ctxAtStart = sweepStashKey(btResearchTab, btSweepKind);
      if (btSweepPollRef.current) clearInterval(btSweepPollRef.current);
      btSweepPollRef.current = setInterval(async () => {
        try {
          const rec = await backtestService.getSweep(sweepId);
          const full = { id: sweepId, ...rec };
          // Movement marker for the stall watchdog: phase or count changed.
          const mark = `${rec.phase || ''}:${rec.completed ?? ''}`;
          if (btSweepProgressRef.current.mark !== mark) {
            btSweepProgressRef.current = { mark, at: Date.now() };
          }
          if (btSweepCtxRef.current === ctxAtStart) {
            setBtSweep(full);
          } else {
            // Finished (or progressed) off-screen: keep it in the stash so
            // switching back shows it, without stomping the visible view.
            btSweepStashRef.current[ctxAtStart] = {
              ...(btSweepStashRef.current[ctxAtStart] || {}),
              sweep: full,
            };
          }
          // `partial` is terminal too: the grid stopped early and the result is
          // honest about it rather than presenting a truncated leaderboard.
          if (['completed', 'partial', 'failed'].includes(rec.status)) {
            clearInterval(btSweepPollRef.current);
            btSweepPollRef.current = null;
            setBtSweepBusy(false);
            if (rec.status === 'failed') showToast('error', rec.error || 'Parameter sweep failed.');
          }
        } catch (err) {
          // A transient poll failure is not a failed sweep.
          console.warn('sweep poll failed:', err?.message || err);
        }
      }, 4000);
    } catch (err) {
      console.error('sweep failed to start:', err);
      const msg = err?.response?.data?.error || err?.message || 'Could not start the sweep.';
      showToast('error', msg);
      setBtSweep({ status: 'failed', error: msg });
      setBtSweepBusy(false);
    }
  };

  // Fill a symbol field from the ranked universe. `setter` is the target field's
  // state setter, so the same control drives both the scan and breadth panels.
  // Fill a symbol field from the scanner's universe resolver.
  //
  // Uses POST /api/scanner/universe/preview rather than a bespoke query: it
  // reads Redis-cached symbol metadata that already carries 24h volume, so it
  // needs no live exchange call. The first version of this hit the Symbol table
  // (fees but no volume) and then called Binance directly for ranking, which
  // answers 451 from here — it could not rank at all.
  const fillSymbolsFrom = async (setter) => {
    if (btUniBusy) return;
    setBtUniBusy(true);
    setBtUniNote('');
    try {
      // btActiveBot, not the selection. In Explore the venue is the setup
      // exchange, and reading the selection here meant Alpaca explorations sent
      // no exchange at all (or a crypto bot's) — the server then defaulted to
      // binance.com and filled the field with crypto pairs the exploration
      // cannot trade. btDraftIsStock already reads through btActiveBot, so the
      // picker's own controls and the request it builds had drifted apart.
      const bot = btActiveBot;
      const useCategory = !!btUniCategory;
      const filter = {
        type: useCategory ? 'category' : 'top_volume',
        ...(useCategory ? { category: btUniCategory } : {}),
        limit: btUniCount,
        // Only crypto has a quote asset. The selector is hidden for stock bots,
        // but hiding it left btUniQuote at its 'USDT' default and the value was
        // still sent — so the server filtered 13,631 USD-quoted equities by USDT
        // and returned nothing. Hiding an input is not the same as not sending
        // it; the server ignores it for equity venues too.
        ...(btDraftIsStock || btUniQuote === 'ALL' ? {} : { quote: btUniQuote }),
        ...(btDraftIsStock && btUniShortable ? { shortable: true } : {}),
        // Fees are not an axis for stocks (commission-free venue, null fee
        // rows) — a zero-fee mode left over from a crypto bot would empty the
        // list. The fee selector is hidden for stocks; force 'any' too.
        fee_filter: btDraftIsStock ? 'any' : btUniFee,
        min_volume: 0,
      };
      // Prefer the bot's own venue so the list matches what it can actually
      // trade; the server defaults to binance.com when neither is supplied.
      const exName = bot?.exchange || '';
      if (exName) filter.exchange = String(exName).toLowerCase();
      else if (bot?.exchangeId) filter.exchange_id = Number(bot.exchangeId);

      // The scanner cache is populated by a metadata cron that lives outside
      // these two services, so it is legitimately cold on a fresh Redis. Fall
      // back to the DB-backed resolver rather than leaving the button dead —
      // that one ranks via Binance.US, which is slower but always available.
      let syms = [];
      let source = '24h volume';
      try {
        const data = await scannerApi.universe.preview(filter);
        syms = (data?.symbols || []).map(x => (typeof x === 'string' ? x : x.symbol)).filter(Boolean);
        // Say what the order actually is. The server ranks only where it has
        // volume — binance.com is geo-blocked from some regions and equity
        // volume covers just the most-active names — and an unranked list
        // presented as "top by volume" is worse than one labelled honestly.
        if (data?.rankedBy === 'volume') {
          const unit = data.volumeUnit === 'shares' ? 'share volume' : '24h volume';
          source = data.volumePartial
            ? `${unit} (only ${data.rankedCount} ranked; rest unranked)`
            : unit;
        } else {
          source = 'listing order — no volume data for this venue';
        }
      } catch (primaryErr) {
        console.warn('scanner universe unavailable, falling back:', primaryErr?.response?.status || primaryErr?.message);
        // botId, not exchangeId: a bot's exchange_id is the user's exchange
        // CONNECTION, while Symbols.exchange_id is the shared catalog. The
        // backend joins through exchanges.exchange_global_id to bridge them.
        let ti = {};
        try { ti = JSON.parse(bot?.trade_info || '{}'); } catch { /* defaults */ }
        const alt = await backtestService.getSymbolUniverse({
          botId: bot?.id ?? undefined,
          // An exploration has no bot to resolve the venue through, so send the
          // name. Without it this path applied no exchange filter at all and
          // answered an Alpaca exploration with the whole catalog.
          exchange: exName || undefined,
          assetClass: ti.asset_class || bot?.asset_class || undefined,
          limit: btUniCount, rank: 'volume', feeFilter: btUniFee,
          ...(useCategory ? { category: btUniCategory } : {}),
          ...(btDraftIsStock && btUniShortable ? { shortable: 'true' } : {}),
          // Same guard as the primary path: the quote selector is hidden for an
          // equity venue but its state stays at 'USDT', and sending that filters
          // every USD-quoted equity down to nothing.
          ...(btDraftIsStock || btUniQuote === 'ALL' ? {} : { quote: btUniQuote }),
        });
        syms = alt?.symbols || [];
        source = `${alt?.rankedBy || 'listing order'} (fallback)`;
      }

      // Tertiary fallback: both universe endpoints read the main backend's
      // Symbols table + ranking cache, which can be empty (e.g. before the
      // catalog is seeded) — that is what made an Alpaca fill fail with
      // "no symbols". The symbol catalog (symbolService) is populated
      // independently, so pull the plain list from there and take the first N.
      // Unranked (listing order), but a usable list beats an empty field.
      if (!syms.length && exName) {
        try {
          const rows = await symbolService.get(exName, 'objects');
          syms = (Array.isArray(rows) ? rows : [])
            .map(r => (typeof r === 'string' ? r : (r.class_name || r.symbol)))
            .filter(Boolean)
            .slice(0, btUniCount);
          if (syms.length) source = 'catalog (listing order — ranking unavailable)';
        } catch (catErr) {
          console.warn('symbol catalog fallback failed:', catErr?.message || catErr);
        }
      }

      if (!syms.length) {
        setBtUniNote(btDraftIsStock
          ? 'No symbols available for this venue yet — the symbol catalog may not be seeded.'
          : 'No symbols matched — try a different quote asset or fee filter.');
        return;
      }
      setter(syms.join(', '));
      setBtUniNote(`${syms.length} by ${source}`);
    } catch (err) {
      console.error('symbol universe failed:', err);
      // The cache is cron-populated, so "not built yet" is a real and
      // actionable answer rather than a generic failure.
      const msg = err?.response?.data?.error || err?.message || 'Could not load symbols.';
      setBtUniNote(msg);
    } finally {
      setBtUniBusy(false);
    }
  };

  useEffect(() => () => { if (btValPollRef.current) clearInterval(btValPollRef.current); }, []);

  const validateExpression = validateLogicExpression;

  // Compact picker rendered above each symbol field.
  //
  // Styles are declared here rather than reusing the render block's `inputSx`:
  // that const is scoped inside the JSX below, so referencing it from a helper
  // defined at component level is a build error. Same visual tokens, local copy.
  // Shared by the config Paper AND the research Paper (§7a2). These lived
  // inside the config Paper's IIFE until the research column moved out of it —
  // the build caught the dangling references, so they are component-scope now.
  const labelSx = { display: 'block', fontFamily: AT_MONO, fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, mb: '7px' };
  const inputSx = { width: '100%', fontFamily: AT_MONO, fontSize: '15px', color: AT.bright, background: AT.inner, border: '1px solid rgba(255,255,255,.1)', borderRadius: '9px', px: '15px', py: '12px', outline: 'none', '&:focus': { borderColor: 'rgba(77,143,199,.5)' } };

  const pickerSelectSx = {
    fontFamily: AT_MONO, fontSize: '11.5px', color: AT.bright, background: AT.inner,
    border: '1px solid rgba(255,255,255,.1)', borderRadius: '7px', px: '8px', py: '5px',
    outline: 'none', width: 'auto', '&:focus': { borderColor: 'rgba(77,143,199,.5)' },
  };

  const renderSymbolPicker = (setter) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', mb: '8px' }}>
      <Mono sx={{ fontSize: '10px', letterSpacing: '.08em', color: AT.muted2 }}>TOP BY VOLUME</Mono>
      {/* Quote asset is a crypto concept — an equity has none, and offering
          USDT on a stock bot is what made the picker look broken. */}
      {!btDraftIsStock && (
        <Box component="select" value={btUniQuote} onChange={(e) => setBtUniQuote(e.target.value)}
          sx={pickerSelectSx}>
          {/* ALL = no quote filter, so a fee filter can be applied across every
              quote asset at once rather than one at a time. */}
          {['ALL', 'USDT', 'USDC', 'BTC', 'ETH'].map(q => (
            <option key={q} value={q}>{q === 'ALL' ? 'All quotes' : q}</option>
          ))}
        </Box>
      )}
      {/* Fees are a crypto axis only — Alpaca is commission-free and stock
          rows carry null fees, so every zero-fee mode matches nothing. */}
      {!btDraftIsStock && (
        <Box component="select" value={btUniFee} onChange={(e) => setBtUniFee(e.target.value)}
          sx={pickerSelectSx}>
          {SYMBOL_FEE_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </Box>
      )}
      {/* Category vocabulary is per asset class: crypto uses the curated
          categories text[]; stocks use real facets (ETF / listing venue,
          sectors once enriched) — the server resolves each accordingly. */}
      <Box component="select" value={btUniCategory} onChange={(e) => setBtUniCategory(e.target.value)}
        sx={pickerSelectSx}>
        {(btDraftIsStock ? STOCK_UNIVERSE_CATEGORIES : SYMBOL_CATEGORIES).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </Box>
      {/* Only ~38% of the equity catalog can be shorted. Without this a
          short strategy gets a universe where most names silently degrade
          to long-only. It doubles as a liquidity filter: brokers only make
          established names easy to borrow, which keeps the penny stocks
          that dominate share-volume ranking out of the list. */}
      {btDraftIsStock && (
        <Box
          component="button"
          onClick={() => setBtUniShortable(v => !v)}
          title="Restrict to names that are shortable and easy-to-borrow"
          sx={{
            fontFamily: AT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em',
            px: '10px', py: '5px', borderRadius: '7px', cursor: 'pointer',
            color: btUniShortable ? '#06121f' : AT.muted,
            background: btUniShortable ? AT.accent : 'transparent',
            border: `1px solid ${btUniShortable ? AT.accent : 'rgba(255,255,255,.12)'}`,
          }}>
          SHORTABLE
        </Box>
      )}
      <Box component="select" value={btUniCount} onChange={(e) => setBtUniCount(Number(e.target.value))}
        sx={pickerSelectSx}>
        {[5, 10, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
      </Box>
      <Box component="button" onClick={() => fillSymbolsFrom(setter)} disabled={btUniBusy}
        sx={{ fontFamily: AT_SANS, fontSize: '11.5px', fontWeight: 600, color: '#9cc3e4', background: 'transparent',
              border: '1px solid rgba(77,143,199,.3)', px: '11px', py: '5px', borderRadius: '7px',
              cursor: btUniBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
        {btUniBusy ? 'Loading…' : 'Fill'}
      </Box>
      {btUniNote && <Mono sx={{ fontSize: '10.5px', color: AT.muted }}>{btUniNote}</Mono>}
    </Box>
  );

  // Carry a discovered symbol forward into the tuning step.
  //
  // The tuning card already reads btTuneSymbol, but the only way to set it was
  // a select buried below the results — so a discovery row's single visible
  // action was "Start a bot here", which skips tuning entirely. Deploying is
  // the LAST thing you should be able to do with a winner and the only thing
  // the results offered.
  //
  // Clearing the plan and the previous sweep is not optional: both are measured
  // ON a symbol, so leaving them would show the old symbol's axes and results
  // under the new symbol's heading.
  const tuneDiscoveredSymbol = (symbol) => {
    if (!symbol) return;
    setBtTuneSymbol(symbol);
    setBtSweepPlan(null);
    setBtSweep(null);
    invalidateSweepStash('explore');
    setTimeout(() => {
      try { btTunePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) { /* not fatal */ }
    }, 60);
    showToast('success', `${symbol} loaded for tuning. Pick what to vary, then run the sweep.`);
  };

  // Funding carry is not a backtest: there is nothing to simulate, only a
  // published payment schedule to measure. So this is a straight read, not a
  // queued job like the scan and the batch.
  const createBotForSymbol = (symbol) => {
    const bot = btActiveBot;   // standalone exploration has no selection to read from
    if (!bot || !symbol) return;
    let ti = {};
    try { ti = JSON.parse(bot.trade_info || '{}'); } catch { /* defaults below */ }

    resetForm();
    setNewBot(prev => ({
      ...prev,
      // The timeframe the symbol was actually tested on, which in Explore is the
      // setup's — not the interval of whichever bot seeded the strategy.
      name: `${symbol} — ${btEffectiveTimeframe || bot.interval || ''}`.trim(),
      symbol,
      exchangeId: bot.exchangeId ?? bot.exchange_id ?? prev.exchangeId,
      interval: btEffectiveTimeframe || bot.interval || prev.interval,
      marketType: ti.marketType || 'spot',
      signalSource: 'logic',
    }));

    // The tuned RISK travels too. Without this, "start a bot with the explored
    // parameters" carried the strategy and silently left the wizard's default
    // 20/10 stops behind — so the bot that got deployed was not the thing the
    // sweep measured. Set after resetForm, which restores those defaults.
    //
    // backtestParams is the sweep's vocabulary (percentages named for what they
    // do); the wizard's is profitStop/lossStop/trailingStop. Only carry a value
    // that is actually set: a blank in the sweep means "no such exit", and
    // writing 0 into the wizard would read as a 0% stop.
    const carryRisk = (v, set) => {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) set(String(n));
    };
    // Every backtest risk exit now has a wizard home: the per-position fields
    // land in trade_info.risk, which both the backtest and the live engine
    // read. (An earlier version toasted that maxBars/flatten were
    // simulation-only; that stopped being true when the live engine grew
    // per-position exits.)
    const carry = {};
    const cv = (v) => { const x = Number(v); return Number.isFinite(x) && x > 0 ? String(x) : null; };
    if (cv(backtestParams.stopLossPct)) carry.riskStopLossPct = cv(backtestParams.stopLossPct);
    if (cv(backtestParams.takeProfitPct)) carry.riskTakeProfitPct = cv(backtestParams.takeProfitPct);
    if (cv(backtestParams.trailingStopPct)) carry.trailingStop = cv(backtestParams.trailingStopPct);
    if (cv(backtestParams.maxBarsInTrade)) carry.riskMaxBarsInTrade = String(Math.floor(Number(backtestParams.maxBarsInTrade)));
    if (cv(backtestParams.flattenBeforeCloseMin)) carry.riskFlattenBeforeCloseMin = String(Math.floor(Number(backtestParams.flattenBeforeCloseMin)));
    if (Object.keys(carry).length) setNewBot(prev => ({ ...prev, ...carry }));

    // The strategy travels with the symbol, in whichever form it is held.
    // btEffectiveMode, not btDraft.mode — in Explore the strategy lives in the
    // explore editor and btDraft is routinely null, which sent every expression
    // strategy down the builder branch to be re-parsed and mangled.
    if (btEffectiveMode === 'expression') {
      setWizRawLogic({ buy: btDraftLogic.buy, sell: btDraftLogic.sell, shortOpen: '', shortClose: '' });
      setWizLogicMode('expression');
      setWizForcedExpression(true);
    } else {
      setBuyConditions(parseLogicToConditions(btDraftLogic.buy) || []);
      setSellConditions(parseLogicToConditions(btDraftLogic.sell) || []);
    }

    setIsCreating(true);
    setActiveTab('create');
    setActiveStep(0);
    showToast('success', `Started a new bot for ${symbol} with the tested strategy.`);
  };

  useEffect(() => () => { if (btBatchPollRef.current) clearInterval(btBatchPollRef.current); }, []);

  const runSymbolBatch = async () => {
    const bot = btActiveBot;
    // Explore legitimately has no draft — that is the point of exploring before
    // you own a bot. Requiring one here made "Test breadth" a dead button in the
    // standalone case, with no error to explain it.
    if (!bot || (!btExploreStandalone && !btDraft) || btBatchBusy) return;
    const symbols = btBatchSymbols.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    if (!symbols.length) { showToast('error', 'Add at least one symbol.'); return; }
    if (!btDraftLogic.buy && !btDraftLogic.sell) { showToast('error', 'The draft has no logic to test.'); return; }

    let ti = {};
    try { ti = JSON.parse(bot.trade_info || '{}'); } catch { /* defaults below */ }

    setBtBatchBusy(true);
    setBtBatch({ status: 'running', phase: 'queued' });
    try {
      const { searchId, symbolsAccepted, maxSymbols } = await backtestService.startBatch({
        botId: bot.id,
        symbols,
        strategy: {
          buy: btDraftLogic.buy, sell: btDraftLogic.sell,
          ...((btDraftLogic.shortOpen || btDraftLogic.shortClose)
            ? { short_open_logic: btDraftLogic.shortOpen, short_close_logic: btDraftLogic.shortClose }
            : {}),
        },
        interval: btEffectiveTimeframe,
        marketType: btDraftMarketType,
        assetClass: ti.asset_class || bot.asset_class || null,
        trade_info: bot.trade_info,
        isTick: btEffectiveTimeframe === 'tick',
        // Untouched costs mean "use the venue's numbers" — so let the server
        // also apply per-symbol catalog fees (zero-fee pairs charged 0). The
        // moment the user types their own commission, their number is the
        // number, for every symbol.
        autoFees: !btCostsAreCustom,
        // Costs from the run on screen; the WINDOW is Explore's own lookback
        // slider when exploring — sharing optimize's range meant changing one
        // experiment silently changed the other.
        config: {
          initialCapital: Number(backtestParams.initialCapital) || 0,
          commission: Number(backtestParams.commission) || 0,
          slippage: Number(backtestParams.slippage) || 0,
          ...(btResearchTab === 'explore'
            ? buildExploreWindow()
            : { startTime: startOfDayMs(backtestDateRange[0]), endTime: endOfDayMs(backtestDateRange[1]) }),
          ...(buildRiskConfig() ? { risk: buildRiskConfig() } : {}),
        },
      });
      if (symbolsAccepted < symbols.length) {
        showToast('info', `Testing the first ${symbolsAccepted} of ${symbols.length} symbols (limit ${maxSymbols}).`);
      }
      setBtBatch({ id: searchId, status: 'running', phase: 'queued' });
      setBtBatchStartedAt(Date.now());

      if (btBatchPollRef.current) clearInterval(btBatchPollRef.current);
      btBatchPollRef.current = setInterval(async () => {
        try {
          const rec = await backtestService.getBatch(searchId);
          setBtBatch({ id: searchId, ...rec });
          // 'canceled' is terminal too — the cancel endpoint stamps the record,
          // and polling past it would spin forever on a batch nobody is running.
          if (['completed', 'failed', 'canceled'].includes(rec.status)) {
            clearInterval(btBatchPollRef.current);
            btBatchPollRef.current = null;
            setBtBatchBusy(false);
            if (rec.status === 'failed') showToast('error', rec.error || 'Batch failed.');
          }
        } catch (err) {
          console.warn('batch poll failed:', err?.message || err);
        }
      }, 4000);
    } catch (err) {
      console.error('Batch failed to start:', err);
      showToast('error', err?.response?.data?.error || err?.message || 'Could not start the batch.');
      setBtBatch({ status: 'failed', error: err?.message });
      setBtBatchBusy(false);
    }
  };

  const applySearchCandidate = (cand) => {
    if (!cand) return;
    setBtDraft(d => d ? { ...d, ...draftLogicFields(cand.buyLogic, cand.sellLogic) } : d);
    if (cand.risk) {
      const fld = (v) => (Number(v) > 0 ? String(v) : '');
      setBacktestParams(p => ({
        ...p,
        stopLossPct: fld(cand.risk.stopLossPct),
        takeProfitPct: fld(cand.risk.takeProfitPct),
        trailingStopPct: fld(cand.risk.trailingStopPct),
        maxBarsInTrade: fld(cand.risk.maxBarsInTrade),
        flattenBeforeCloseMin: fld(cand.risk.flattenBeforeCloseMin),
      }));
      setBtAdvOpen(true);
    }
    setBtEditOpen(true);
    showToast('success', `Loaded rank #${cand.rank} into the editor — run it to confirm.`);
  };

  // Write the suggestion into the draft. Deliberately a separate, explicit step.
  const applyAdvisorSuggestion = () => {
    if (!btAiResult) return;
    const { buyLogic, sellLogic, risk } = btAiResult;
    if (!buyLogic && !sellLogic && !risk) return;
    if (buyLogic || sellLogic) {
      setBtDraft(d => {
        if (!d) return d;
        // A one-sided suggestion keeps the other side as it stands, so resolve
        // both to strings first rather than mixing a raw string with rows.
        const held = (raw, conds) => (d.mode === 'expression' ? (raw || '') : buildLogicFromConditions(conds));
        return {
          ...d,
          ...draftLogicFields(
            buyLogic || held(d.rawBuy, d.buyConditions),
            sellLogic || held(d.rawSell, d.sellConditions),
          ),
        };
      });
    }
    // Risk exits are backtest config, not part of the logic draft, so they go
    // to backtestParams. A proposed 0 means "turn this one off" — blanking the
    // field is the right translation, not leaving the previous value behind.
    if (risk) {
      const fld = (v) => (Number(v) > 0 ? String(v) : '');
      setBacktestParams(p => ({
        ...p,
        stopLossPct: fld(risk.stopLossPct),
        takeProfitPct: fld(risk.takeProfitPct),
        trailingStopPct: fld(risk.trailingStopPct),
        maxBarsInTrade: fld(risk.maxBarsInTrade),
        flattenBeforeCloseMin: fld(risk.flattenBeforeCloseMin),
      }));
      setBtAdvOpen(true);   // show what just changed
    }
    setBtAiResult(null);
    setBtAiPrompt('');
    showToast('success', risk
      ? 'Applied logic and risk exits — run a backtest to see the effect.'
      : 'Applied to the draft — run a backtest to see the effect.');
  };

  // Persist the backtest-page draft onto the bot and make sure it's running.
  //
  // The strategy lives in the buy_logic/sell_logic COLUMNS and the interval in
  // trade_info.timeframe, so both go in one PUT. The backend publishes
  // BOT_UPDATED on save, which rewrites the bot's live Redis state — so an
  // already-running bot picks up the new strategy without a restart. That's
  // also why we confirm first when the bot is live: it may be holding an open
  // position taken under the old rules.
  const deployDraft = async (force = false) => {
    const bot = selectedBotForBacktest;
    if (!bot || !btDraft || btDraft.botId !== bot.id) return;

    // Mode-aware: in expression mode the condition arrays are empty and the
    // strategy is the raw string, so building from the rows refused to deploy a
    // draft that plainly had logic.
    const buy = btDraftLogic.buy;
    const sell = btDraftLogic.sell;
    if (!buy && !sell) {
      showToast('error', 'Add at least one entry or exit rule before deploying.');
      return;
    }
    if (!btDraft.timeframe) {
      showToast('error', 'Pick a timeframe before deploying.');
      return;
    }

    // ── Gate ──
    // Checked before anything else, including the already-live confirm: a
    // strategy that has not earned deployment should not reach a dialog whose
    // only question is "are you sure you want to update the live bot".
    // `force` carries an override reason once the user has supplied one.
    if (!force) {
      setBtGateChecking(true);
      try {
        const g = await backtestService.getDeploymentGate({ botId: bot.id, buy, sell });
        if (!g.passed) { setBtGate(g); setBtGateReason(''); return; }
        setBtGate(null);
      } catch (err) {
        // A gate that cannot be evaluated must not silently wave the deploy
        // through — that is the failure mode the gate exists to prevent.
        console.error('gate check failed:', err);
        showToast('error', 'Could not verify this strategy before deploying. Try again.');
        return;
      } finally {
        setBtGateChecking(false);
      }
    }

    const wasLive = bot.status === 'active';
    if (wasLive && !force) { setBtDeployConfirm(bot); return; }

    setBtDeploying(true);
    try {
      let ti = {};
      try { ti = JSON.parse(bot.trade_info || '{}'); } catch { /* rebuild below */ }
      const nextTradeInfo = {
        ...ti,
        timeframe: btDraft.timeframe,
        // Recomputed from the logic being deployed. Spreading `ti` alone kept
        // the PREVIOUS strategy's indicator list, and nothing server-side
        // recomputes it on update — so changing RSI(14) to EMA(50) and
        // deploying produced a live bot that still computed RSI and never
        // computed EMA, referencing a series nothing was calculating. Silent:
        // the backtest re-derives indicators from the logic string, so it kept
        // looking correct right up until the bot traded.
        //
        // Derived from the strings rather than the condition rows so it is
        // identical in builder and expression mode. Short logic is not editable
        // in this panel, so it comes from the bot and must still be included or
        // deploying would drop a hedge bot's short-side indicators.
        indicators: extractIndicatorsFromLogic(
          buy,
          sell,
          selectedBotForBacktest?.logic?.shortOpen || '',
          selectedBotForBacktest?.logic?.shortClose || '',
        ),
        // Auditable record of a deliberate bypass: which checks failed, why the
        // user overrode them, and when.
        ...(btGate && !btGate.passed && btGateReason.trim()
          ? {
              deployment_override: {
                at: new Date().toISOString(),
                reason: btGateReason.trim(),
                failedChecks: (btGate.checks || []).filter(c => !c.passed).map(c => c.id),
              },
            }
          : {}),
      };

      const updated = await tradingBotService.update(bot.id, {
        buy_logic: buy,
        sell_logic: sell,
        trade_info: JSON.stringify(nextTradeInfo),
        // Deploying means "run this" — clear the pause and any stale error/guard
        // state, mirroring what toggleBotStatus does on resume.
        stop_trade_ASSET: false,
        status: '',
        error_message: '',
        guard_tag: '', guard_reason: '', guard_at: '', guard_side: '', guard_price: '',
      });

      const nextBot = {
        ...bot,
        interval: btDraft.timeframe,
        trade_info: updated?.trade_info ?? JSON.stringify(nextTradeInfo),
        logic: { ...bot.logic, buy, sell },
        status: 'active',
        stop_trade_ASSET: false,
        errorMessage: '',
      };
      setBots(prev => prev.map(b => (b.id === bot.id ? nextBot : b)));
      setSelectedBotForBacktest(nextBot);
      setBtDeployConfirm(null);
      showToast('success', wasLive
        ? `"${bot.name}" updated — the live bot is now running the new strategy.`
        : `"${bot.name}" deployed and running.`);
    } catch (err) {
      console.error('Failed to deploy bot changes:', err);
      showToast('error', err?.response?.data?.error || 'Failed to deploy changes.');
    } finally {
      setBtDeploying(false);
    }
  };

  // Draft a strategy from a plain-language description, then open the wizard
  // with the conditions already populated. Symbol/exchange are still chosen on
  // step 0 — the generated logic is symbol-agnostic (an RSI threshold reads the
  // same on any pair), so asking for them up front would add friction here for
  // no gain in strategy quality.
  const startAiKickoff = async () => {
    const text = aiKickoffPrompt.trim();
    if (!text) return;
    setAiKickoffLoading(true);
    try {
      const res = await btAiService.current.generateStrategy(text, {
        interval: '1H',
        marketType: 'spot',
        positionMode: 'one_way',
      });

      // The advisor may answer conversationally instead of emitting logic
      // (e.g. "what is RSI?"). Surface that rather than opening an empty wizard.
      if (res?.conversational && res.reply) {
        showToast('info', res.reply.slice(0, 240));
        return;
      }
      if (!res?.buyLogic && !res?.sellLogic) {
        showToast('error', res?.explanation || 'The AI could not draft a strategy from that. Try describing entry and exit conditions.');
        return;
      }

      resetForm();
      // The advisor now knows the arithmetic/conditional syntax, so what comes
      // back may not fit the builder. Parsing it anyway does not merely look
      // wrong — parseLogicToConditions returns an empty operand for what it
      // cannot match, so the bot would be built from a different strategy than
      // the one proposed.
      if (!canRepresentInBuilder(res.buyLogic || '') || !canRepresentInBuilder(res.sellLogic || '')) {
        setWizRawLogic({ buy: res.buyLogic || '', sell: res.sellLogic || '', shortOpen: '', shortClose: '' });
        setWizLogicMode('expression');
        setWizForcedExpression(true);
      } else {
        setBuyConditions(parseLogicToConditions(res.buyLogic || '') || []);
        setSellConditions(parseLogicToConditions(res.sellLogic || '') || []);
      }
      setNewBot(prev => ({ ...prev, name: text.slice(0, 60), signalSource: 'logic' }));
      setIsCreating(true);
      setActiveTab('create');
      setActiveStep(0);
      setAiKickoffPrompt('');
      showToast('success', 'Strategy drafted — set the market and position size, then review.');
    } catch (err) {
      console.error('AI kickoff failed:', err);
      showToast('error', 'The AI advisor could not be reached.');
    } finally {
      setAiKickoffLoading(false);
    }
  };

  // The create wizard's interval dropdown is mixed-case ('1m' but '1H'/'1D');
  // the draft timeframe is lowercased. Snap back to the option's own casing so
  // the prefilled <select> actually matches an entry instead of showing blank.
  const toFormInterval = (tf) => {
    const opts = ['tick', '1m', '5m', '15m', '30m', '1H', '4H', '8H', '12H', '1D'];
    const hit = opts.find(o => o.toLowerCase() === String(tf || '').toLowerCase());
    return hit || tf || '1H';
  };

  // Fork the backtested strategy into a BRAND NEW bot instead of overwriting the
  // one it came from.
  //
  // Rather than a bespoke mini-form, this drops the user into the normal create
  // wizard with everything already known filled in — market, venue, timeframe
  // and the backtested entry/exit conditions. Position size is deliberately
  // left blank: staking real money is the one decision that shouldn't be
  // inherited silently. Going through the wizard also means the new bot gets
  // every existing guard for free (position-size validation, the $1000 beta
  // notional cap, funding-asset checks, futures validation and the duplicate-bot
  // warning) rather than a second, weaker path around them.
  const deployDraftAsNewBot = () => {
    const bot = selectedBotForBacktest;
    if (!bot || !btDraft || btDraft.botId !== bot.id) return;

    let ti = {};
    try { ti = JSON.parse(bot.trade_info || '{}'); } catch { /* fall back to defaults */ }

    const isFut = ti.marketType === 'futures';
    const assetCls = getBacktestAssetClass(bot);

    resetForm();
    setNewBot(prev => ({
      ...prev,
      name: `${bot.name} (backtested)`,
      exchangeId: bot.exchangeId ?? null,
      symbol: bot.symbol || prev.symbol,
      interval: toFormInterval(btDraft.timeframe),
      signalSource: 'logic',

      marketType: isFut ? 'futures' : 'spot',
      futuresType: ti.futures?.type || prev.futuresType,
      leverage: Number(ti.futures?.leverage) || 1,
      marginMode: ti.futures?.marginMode || prev.marginMode,
      positionSide: ti.futures?.positionSide || prev.positionSide,
      futureSizeMode: ti.futures?.sizeMode || prev.futureSizeMode,

      orderExecution: ti.orderExecution?.mode || 'market',
      limitOffsetPct: ti.orderExecution?.limitOffsetPct ?? prev.limitOffsetPct,
      limitTimeoutSec: ti.orderExecution?.limitTimeoutSec ?? prev.limitTimeoutSec,
      postOnly: ti.orderExecution?.postOnly ?? prev.postOnly,

      funding_asset: ti.funding_asset || defaultFundingAssetForClass(assetCls),
      maxLoss: ti.maxDailyLoss != null ? String(ti.maxDailyLoss) : prev.maxLoss,
      maxTrades: ti.maxTradesPerDay != null ? String(ti.maxTradesPerDay) : prev.maxTrades,
      trailingStop: ti.trailingStop != null ? String(ti.trailingStop) : prev.trailingStop,

      extendedHours: !!ti.alpaca?.extended_hours,
      allowShorts: !!ti.short_open_logic,

      // Instrument travels with the strategy: deploying a draft from an
      // options bot must produce an OPTIONS bot, not silently downgrade to
      // shares. The wizard section renders pre-filled from these.
      instrument: ti.instrument === 'options' ? 'options' : 'shares',
      options: ti.instrument === 'options' ? (ti.options || {}) : null,

      // ── Left blank on purpose: the user states the new bot's stake. ──
      sizeMode: ti.sizeMode || prev.sizeMode,
      positionValue: '',
      totalAmount: '',
      marginPct: '',
      fixedNotional: '',
    }));

    // Copy the condition objects so editing the wizard doesn't mutate the draft
    // still shown on the backtest page.
    setBuyConditions((btDraft.buyConditions || []).map(c => ({ ...c })));
    setSellConditions((btDraft.sellConditions || []).map(c => ({ ...c })));

    // The reconfigure panel only edits long entry/exit, so short logic isn't in
    // the draft. Carry the source bot's across verbatim — otherwise forking a
    // hedge (or shorting stock) bot would silently drop half its strategy while
    // leaving hedge mode switched on.
    const shortOpenRaw = bot.logic?.shortOpen || '';
    const shortCloseRaw = bot.logic?.shortClose || '';
    setShortOpenConditions(parseLogicToConditions(shortOpenRaw) || []);
    setShortCloseConditions(parseLogicToConditions(shortCloseRaw) || []);

    // Carry the draft's logic mode across. Deploying an expression-mode draft
    // through a builder-mode wizard would save whatever the empty condition
    // rows build — i.e. lose the strategy that was just backtested.
    const draftIsExpression = btDraft?.mode === 'expression';
    const shortsNeedExpression = !canRepresentInBuilder(shortOpenRaw) || !canRepresentInBuilder(shortCloseRaw);
    if (draftIsExpression || shortsNeedExpression) {
      setWizRawLogic({
        buy: draftIsExpression ? (btDraft.rawBuy || '') : buildLogicFromConditions(btDraft.buyConditions || []),
        sell: draftIsExpression ? (btDraft.rawSell || '') : buildLogicFromConditions(btDraft.sellConditions || []),
        shortOpen: shortOpenRaw,
        shortClose: shortCloseRaw,
      });
      setWizLogicMode('expression');
      setWizForcedExpression(true);
    } else {
      setWizLogicMode('builder');
      setWizRawLogic({ buy: '', sell: '', shortOpen: '', shortClose: '' });
      setWizForcedExpression(false);
    }
    if (ti.profitStop != null) setProfitStop(String(ti.profitStop));
    if (ti.lossStop != null) setLossStop(String(ti.lossStop));

    if (bot.exchangeId) ensureSymbols(bot.exchangeId, bot.exchangeName || bot.exchange);

    setIsCreating(true);
    setActiveTab('create');
    setActiveStep(0);
    showToast('info', 'Strategy, market and timeframe carried over from the backtest — set how much to trade, then review risk.');
  };

  const toggleBotStatus = async (botId) => {
    const botToUpdate = bots.find(b => b.id === botId);
    if (!botToUpdate) return;

    const currentlyPaused = botToUpdate.stop_trade_ASSET === true;
    const newStopStatus = !currentlyPaused;
    const goingLive = currentlyPaused;   // resuming a paused bot puts real money at risk

    // ── No live capital on untested logic ──
    //
    // The deployment gate only guarded the reconfigure-and-deploy path, so the
    // ordinary Resume button could put a strategy live that had never been
    // backtested at all. Only the "has this exact logic been tested" check is
    // enforced here; the rest of the gate (breadth, holdout, random-entry
    // percentile) stays advisory on deploy, because a hard block on those would
    // stop users resuming a bot they had merely paused.
    //
    // Bots that cannot be backtested are exempt by construction, not by
    // exception: webhook/TradingView bots take their signals from outside the
    // engine and scanner profiles are not bots at all, which is exactly what
    // isBacktestableBot already encodes.
    if (goingLive && isBacktestableBot(botToUpdate)) {
      try {
        const g = await backtestService.getDeploymentGate({
          botId,
          buy: botToUpdate.logic?.buy ?? botToUpdate.buy_logic ?? '',
          sell: botToUpdate.logic?.sell ?? botToUpdate.sell_logic ?? '',
        });
        const tested = (g.checks || []).find(c => c.id === 'tested');
        if (tested && !tested.passed) {
          setBtDeployBlocked({ bot: botToUpdate, gate: g });
          return;
        }
      } catch (err) {
        // A gate that cannot be evaluated must not wave the bot through — that
        // is the failure mode the gate exists to prevent.
        console.error('pre-activation gate check failed:', err);
        showToast('error', 'Could not verify this bot has been backtested. Try again.');
        return;
      }
    }

    try {
      await tradingBotService.update(botId, {
        stop_trade_ASSET: newStopStatus,
        error_message: '',
        status: '',
        // ✅ Clear guard fields on resume
        guard_tag: '',
        guard_reason: '',
        guard_at: '',
        guard_side: '',
        guard_price: '',
      });

      setBots(prevBots =>
        prevBots.map(bot =>
          bot.id === botId
            ? {
              ...bot,
              stop_trade_ASSET: newStopStatus,
              status: newStopStatus ? 'paused' : 'active',
              // ✅ Clear guard state locally on resume
              ...(newStopStatus ? {} : {
                guardTag: '',
                guardReason: '',
                guardAt: '',
                guardSide: '',
                guardPrice: '',
                errorMessage: '',
              })
            }
            : bot
        )
      );
    } catch (error) {
      console.error('Failed to toggle bot status:', error);
    }
  };

  const deleteBot = async (id) => {
    try {
      await tradingBotService.delete(id);
      setBots(bots.filter(bot => bot.id !== id));
    } catch (err) {
      console.error('Failed to delete bot:', err);
    }
  };

  // Lazy-load one page of a bot's history (newest-first) and merge it into
  // liveBotHistories. reset=true starts from offset 0 (on expand); otherwise it
  // pages older entries. Deduped + ordered by mergeHistoryEntries, so overlap
  // with socket-fed live entries is harmless.
  const loadBotHistoryPage = async (botId, { reset = false } = {}) => {
    const cur = historyPaging[botId] || { offset: 0, total: null, loading: false, done: false };
    if (cur.loading) return;
    if (!reset && cur.done) return;
    const offset = reset ? 0 : cur.offset;

    setHistoryPaging(prev => ({ ...prev, [botId]: { ...(prev[botId] || {}), offset, loading: true, done: false } }));
    try {
      const res = await tradingBotService.getBotHistory(botId, { limit: HISTORY_PAGE_SIZE, offset });
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
      const total = Number.isFinite(res?.total) ? res.total : null;
      const mapped = mapHistoryRows(items);

      setLiveBotHistories(prev => ({ ...prev, [botId]: mergeHistoryEntries(prev[botId] || [], mapped) }));

      const nextOffset = offset + items.length;
      const done = (total != null && nextOffset >= total) || items.length < HISTORY_PAGE_SIZE;
      setHistoryPaging(prev => ({ ...prev, [botId]: { offset: nextOffset, total, loading: false, done } }));
    } catch (err) {
      console.error(`[history] load page failed for bot ${botId}:`, err?.message || err);
      setHistoryPaging(prev => ({ ...prev, [botId]: { ...(prev[botId] || { offset, total: null, done: false }), loading: false } }));
    }
  };

  const toggleExpandBot = (id) => {
    const willExpand = expandedBot !== id;
    setExpandedBot(willExpand ? id : null);
    // On first expand of a bot, fetch its first history page. The socket already
    // seeded the most recent entries; this fills in a full first page and enables
    // "Load more" for older history.
    if (willExpand && !historyPaging[id]) {
      loadBotHistoryPage(id, { reset: true });
    }
  };

  const getBotTransactions = (botId) => {
    return mockTransactions.filter(tx => tx.botId === botId);
  };

  // ─── Bot card renderer (used in both Manual and Scanner tabs) ─────────────
  function renderBotCard(bot) {
    return (
      <Card key={bot.id} elevation={0} sx={{
        bgcolor: AT.card,
        borderRadius: '14px',
        border: `1px solid ${expandedBot === bot.id ? 'rgba(77,143,199,.35)' : AT.hairline}`,
        boxShadow: '0 1px 2px rgba(0,0,0,.22)',
        transition: 'border-color .2s',
        '&:hover': { borderColor: 'rgba(77,143,199,.35)' }
      }}>
        <CardContent sx={{ p: '16px !important' }}>
          {/* ✅ Status Alert Banner */}
          {(() => {
            const statusDisplay = getTradingStatusDisplay(bot);
            if (!statusDisplay) return null;
            return (
              <Alert severity={statusDisplay.color} icon={statusDisplay.icon} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>{statusDisplay.label}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>{statusDisplay.description}</Typography>
                    {(statusDisplay.guardAt || statusDisplay.guardPrice) && (
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                        {statusDisplay.guardAt && <Typography variant="caption" color="text.secondary"><strong>Since:</strong> {new Date(statusDisplay.guardAt).toLocaleString()}</Typography>}
                        {statusDisplay.guardPrice && <Typography variant="caption" color="text.secondary"><strong>At Price:</strong> ${Number(statusDisplay.guardPrice).toLocaleString()}</Typography>}
                        {statusDisplay.guardSide && <Typography variant="caption" color="text.secondary"><strong>Blocked:</strong> {statusDisplay.guardSide}</Typography>}
                      </Stack>
                    )}
                  </Box>
                </Box>
              </Alert>
            );
          })()}

          {/* Bot Header Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleExpandBot(bot.id)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton size="small">
                {expandedBot === bot.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{bot.name}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ExchangeBadge type={bot.exchange} label={bot.exchangeName || bot.exchange} />
                  <FuturesBadge bot={bot} />
                  <OrderTypeBadge bot={bot} />
                  {/* Phase A: stock-equity badges (market hours, PDT, halts) */}
                  <StockStatusBadgeRow
                    bot={bot}
                    accountStatus={getAccountStatusForBot(bot, alpacaStatus)}
                  />

                  {/* Spawned chip */}
                  {bot.spawnedFromProfileId && bot.spawnedFromMeta && (
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="caption" fontWeight={600}>Spawned by {bot.spawnedFromMeta.profile_name}</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>Triggered at ${Number(bot.spawnedFromMeta.trigger_event?.triggered_price || 0).toLocaleString()}</Typography>
                          {bot.spawnedFromMeta.trigger_event?.strength != null && <Typography variant="caption" sx={{ display: 'block' }}>Strength: {Number(bot.spawnedFromMeta.trigger_event.strength).toFixed(2)}</Typography>}
                          {bot.spawnedFromMeta.auto_terminate_at && <Typography variant="caption" sx={{ display: 'block' }}>Auto-terminates: {new Date(bot.spawnedFromMeta.auto_terminate_at).toLocaleString()}</Typography>}
                        </Box>
                      }
                    >
                      <Chip size="small" icon={<ShowChart sx={{ fontSize: 12 }} />} label="Spawned" variant="outlined" sx={{ height: 22, bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#4d8fc7', borderColor: '#4d8fc7', '& .MuiChip-icon': { color: '#4d8fc7' } }} />
                    </Tooltip>
                  )}

                  {bot.signalSource === 'tradingview' && (
                    <Tooltip title="Receives signals from TradingView webhooks">
                      <Chip size="small" icon={<img src="https://www.tradingview.com/static/images/favicon.ico" alt="TV" style={{ width: 14, height: 14 }} />} label="Webhook" variant="outlined" sx={{ height: 22, '& .MuiChip-icon': { ml: 0.5 } }} />
                    </Tooltip>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    {bot.symbol} • {bot.interval}
                    {(() => { try { return (typeof bot.trade_info === 'string' ? JSON.parse(bot.trade_info) : bot.trade_info)?.instrument === 'options'; } catch { return false; } })() && (
                      <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, borderRadius: '5px', fontSize: '10px', fontWeight: 700, letterSpacing: '.05em', color: '#b58cf5', background: 'rgba(150,100,240,.14)', border: '1px solid rgba(150,100,240,.35)' }}>
                        OPTIONS
                      </Box>
                    )}
                  </Typography>
                  {bot.currentPrice && (
                    <Typography variant="body2" color="text.secondary">
                      • {Number(bot.currentPrice).toLocaleString()} {getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange }).quote}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(() => {
                const sevHex = { success: AT.positive, error: AT.negative, warning: AT.amber, info: AT.accent, default: AT.muted };
                const sd = getTradingStatusDisplay(bot);
                const label = sd ? sd.label : (bot.status === 'active' ? 'Active' : (bot.status || 'Unknown'));
                const c = sevHex[sd ? sd.color : (bot.status === 'active' ? 'success' : 'default')] || AT.muted;
                const pill = (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', fontWeight: 600, color: c, background: `${c}1f`, border: `1px solid ${c}59`, px: '12px', py: '6px', borderRadius: '8px', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c, flex: 'none' }} />{label}
                  </Box>
                );
                return sd
                  ? <Tooltip title={<Box><Typography variant="body2" fontWeight={600}>{sd.label}</Typography><Typography variant="caption">{sd.description}</Typography></Box>}>{pill}</Tooltip>
                  : pill;
              })()}

              {bot.signalSource !== 'tradingview' && (() => {
                const check = gateBacktest(bot);
                return (
                  <Tooltip title={check.supported ? 'Run Backtest' : check.message}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); runBacktest(bot); }}
                        color="info"
                        disabled={!check.supported}
                      >
                        <BarChart fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              })()}
              <Tooltip title={bot.status === 'active' ? 'Pause Bot' : 'Resume Bot'}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleBotStatus(bot.id); }} color={bot.status === 'active' ? 'warning' : 'success'}>
                  {bot.status === 'active' ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Collapse in={expandedBot === bot.id}>
            <Box sx={{ mt: 3, pl: 2, pr: 1 }}>
              {(() => {
                const ti = (() => { try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; } })();
                const isFuturesBot = ti.marketType === 'futures';
                const isStockRow = (ti.asset_class || bot.asset_class) === 'stock_equity';
                const isHedgeBot = (isFuturesBot && ti.futures?.positionSide === 'hedge') ||
                                   (isStockRow && (ti.short_open_logic || ti.futures?.shortOpenLogic));
                const { quote: QUOTE } = getPairMeta({
                  symbol: bot.symbol,
                  exchangeId: bot.exchangeId,
                  userExchanges,
                  symbolsByExchange,
                });

                return (
                  <>
                    {/* Phase A: Pending order progress bar (partial fills) */}
                    <PendingOrderProgress bot={bot} quote={QUOTE} />

                    {/* SECTION 1: Strategy & Risk */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, height: '100%', bgcolor: '#0f1218' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Functions sx={{ color: '#4d8fc7', fontSize: 18 }} />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Trading Strategy</Typography>
                          </Box>
                          <Stack spacing={2}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <TrendingUp sx={{ color: '#46b88a', fontSize: 16 }} />
                                <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>{isFuturesBot ? 'OPEN LONG' : 'BUY CONDITION'}</Typography>
                              </Box>
                              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.buy || 'No conditions set'}</Typography>
                              </Paper>
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <TrendingDown sx={{ color: '#d97b72', fontSize: 16 }} />
                                <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>{isFuturesBot ? 'CLOSE LONG' : 'SELL CONDITION'}</Typography>
                              </Box>
                              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.sell || 'No conditions set'}</Typography>
                              </Paper>
                            </Box>
                            {isHedgeBot && (
                              <>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                    <TrendingDown sx={{ color: '#d9a441', fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>OPEN SHORT</Typography>
                                  </Box>
                                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.shortOpen || 'No conditions set'}</Typography>
                                  </Paper>
                                </Box>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                    <TrendingUp sx={{ color: '#4d8fc7', fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>CLOSE SHORT</Typography>
                                  </Box>
                                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.shortClose || 'No conditions set'}</Typography>
                                  </Paper>
                                </Box>
                              </>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, height: '100%', bgcolor: '#0f1218' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <WarningAmber sx={{ color: '#d9a441', fontSize: 18 }} />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Risk Management</Typography>
                          </Box>
                          <Grid container spacing={1.5}>
                            {(() => {
                              const tiInner = (() => { try { return bot.trade_info ? JSON.parse(bot.trade_info) : {}; } catch { return {}; } })();
                              const { base: BASE, quote: QUOTE } = getPairMeta({ symbol: tiInner.symbol || bot.symbol, exchangeId: bot.exchange_id || bot.exchangeId || newBot.exchangeId, userExchanges, symbolsByExchange });
                              const os = tiInner.orderSizing || { mode: tiInner.mode || 'quote', value: tiInner.value ?? tiInner.totalInvestment, assetSymbol: tiInner.asset_symbol };
                              const unit = os.assetSymbol || (os.mode === 'base' ? BASE : QUOTE) || '—';
                              const rawVal = Number(os.value);
                              const safeVal = Number.isFinite(rawVal) ? rawVal : 0;

                              const riskItems = [
                                { icon: <AttachMoney sx={{ fontSize: 20 }} />, label: 'Position Size', value: os.mode === 'quote' ? `${safeVal.toLocaleString()} ${unit}` : `${safeVal.toLocaleString(undefined, { maximumFractionDigits: 8 })} ${unit}` },
                                { icon: <TrendingUp sx={{ fontSize: 20 }} />, label: 'Take Profit', value: bot.logic?.profitStop || '—' },
                                { icon: <TrendingDown sx={{ fontSize: 20 }} />, label: 'Stop Loss', value: bot.logic?.lossStop || '—' },
                                { icon: <Timeline sx={{ fontSize: 20 }} />, label: 'Trailing Stop', value: bot.logic?.trailingStop ? `${bot.logic.trailingStop}%` : '—' },
                                { icon: <BarChart sx={{ fontSize: 20 }} />, label: 'Max Daily Loss', value: bot.logic?.maxLoss ? `${bot.logic.maxLoss}%` : '—' },
                                { icon: <History sx={{ fontSize: 20 }} />, label: 'Max Trades/Day', value: bot.logic?.maxTrades || '—' },
                                ...(tiInner.marketType === 'futures' ? [
                                  { icon: <TrendingUp sx={{ fontSize: 20 }} />, label: 'Leverage', value: `${tiInner.futures?.leverage || 1}x` },
                                  { icon: <AccountBalance sx={{ fontSize: 20 }} />, label: 'Margin Mode', value: (tiInner.futures?.marginMode || 'cross').charAt(0).toUpperCase() + (tiInner.futures?.marginMode || 'cross').slice(1) },
                                  { icon: <ShowChart sx={{ fontSize: 20 }} />, label: 'Position Mode', value: tiInner.futures?.positionSide === 'hedge' ? 'Hedge' : 'One-Way' },
                                ] : []),
                                ...(tiInner.orderExecution?.mode && tiInner.orderExecution.mode !== 'market' ? [
                                  { icon: <AttachMoney sx={{ fontSize: 20 }} />, label: 'Order Type', value: ORDER_EXECUTION_MODES[tiInner.orderExecution.mode]?.label || tiInner.orderExecution.mode },
                                ] : [])
                              ];

                              return riskItems.map((item, idx) => (
                                <Grid item xs={6} sm={4} key={idx}>
                                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#070a0f', border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', minHeight: 85, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', '&:hover': { borderColor: 'rgba(255,255,255,0.1)' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                      <Box sx={{ color: '#9aa6bd' }}>{item.icon}</Box>
                                      <Typography variant="caption" sx={{ color: '#9aa6bd', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', fontWeight: 500 }}>{item.label}</Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#f2f6ff', fontFamily: '"SF Mono", Monaco, Consolas, monospace', fontSize: '1rem', fontWeight: 600 }}>{item.value}</Typography>
                                  </Box>
                                </Grid>
                              ));
                            })()}
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>

                    <FuturesInfoPanel bot={bot} futuresData={futuresInfo[bot.id]} />

                    {/* SECTION 1b: Total Value chart — derive a value from whatever
                        the bot has so it shows for all bots, not just synced ones. */}
                    {(() => {
                      const stat = bot.stats || {};
                      let ti = {}; try { ti = JSON.parse(bot.trade_info || '{}'); } catch (_) {}
                      const pick = (...vals) => { for (const v of vals) { const n = Number(v); if (Number.isFinite(n) && n > 0) return n; } return undefined; };
                      const cfg = pick(ti.positionValue, ti.totalAmount, ti.tradeAmount, bot.amount, bot.totalAmount);
                      const posVal = (Number.isFinite(Number(stat.PositionQty)) && Number.isFinite(Number(stat.MarkPrice))) ? Number(stat.PositionQty) * Number(stat.MarkPrice) : 0;
                      const cashEq = Number.isFinite(Number(stat.CashQuote)) ? Number(stat.CashQuote) + posVal : undefined;
                      const realizedEq = (Number.isFinite(Number(stat.InitialCapital)) && Number.isFinite(Number(stat.RealizedPnL))) ? Number(stat.InitialCapital) + Number(stat.RealizedPnL) : undefined;
                      const equity = pick(stat.Equity, cashEq, realizedEq, cfg);
                      if (equity == null) return null;
                      const initial = pick(stat.InitialCapital, cfg, equity);
                      return <BotEquityChart initial={initial} equity={equity} />;
                    })()}

                    {/* SECTION 2: Live Statistics */}
                    {bot.stats && (
                      <Paper elevation={0} sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, bgcolor: '#0f1218' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <AccountBalance sx={{ color: '#4d8fc7', fontSize: 18 }} />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Live Statistics</Typography>
                          </Box>
                          {bot.stats.UpdatedAt && (
                            <Chip size="small" variant="outlined" label={`Updated: ${new Date(bot.stats.UpdatedAt).toLocaleString()}`} sx={{ height: 24, fontSize: '0.7rem', color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)' }} />
                          )}
                        </Box>

                        {(() => {
                          const baseStats = bot.stats || {};
                          // Real server stats are present iff UpdatedAt is set (an
                          // empty/never-synced statistics object normalizes to all-undefined).
                          // Prefer them; only recompute from history when absent — so a
                          // paginated (partial) history can't skew the totals.
                          const hasServerStats = !!(bot.stats && bot.stats.UpdatedAt);
                          const computed = hasServerStats ? null : computeStatsFromHistory(liveBotHistories[bot.id]);
                          const s = computed ? { ...baseStats, ...computed } : baseStats;
                          const isProfit = (s.RealizedPnL || 0) >= 0;
                          const totalReturnPct = isNum(s.Equity) && isNum(s.InitialCapital) && Number(s.InitialCapital) !== 0
                            ? ((Number(s.Equity) - Number(s.InitialCapital)) / Number(s.InitialCapital)) * 100
                            : null;
                          const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                          const fmtAsset = (v) => {
                            if (!isNum(v)) return '—';
                            const n = Number(v);
                            const digits = Math.abs(n) < 1 ? 4 : 2;
                            const sign = n < 0 ? '-' : '';
                            return `${sign}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${QUOTE || ''}`;
                          };
                          const statGroups = [
                            {
                              title: 'Capital', icon: <AccountBalance sx={{ fontSize: 16 }} />, items: [
                                { label: 'Initial', value: fmtAsset(s.InitialCapital) },
                                { label: 'Equity', value: fmtAsset(s.Equity), highlight: true },
                                { label: 'Cash', value: fmtAsset(s.CashQuote) },
                              ]
                            },
                            {
                              title: 'Position', icon: <ShowChart sx={{ fontSize: 16 }} />, items: [
                                { label: 'Quantity', value: isNum(s.PositionQty) ? Number(s.PositionQty).toLocaleString(undefined, { maximumFractionDigits: 8 }) : '—' },
                                { label: 'Entry Price', value: fmtAsset(s.EntryPrice) },
                                { label: 'Mark Price', value: fmtAsset(s.MarkPrice) },
                              ]
                            },
                            {
                              title: 'Profit & Loss', icon: <AttachMoney sx={{ fontSize: 16 }} />, isProfit, items: [
                                { label: 'Realized P&L', value: fmtAsset(s.RealizedPnL), highlight: true },
                                { label: 'Total Return', value: isNum(totalReturnPct) ? fmtPct(totalReturnPct) : '—' },
                                { label: 'Max Drawdown', value: isNum(s.MaxDrawdownPct) ? fmtPct(s.MaxDrawdownPct) : '—' },
                              ]
                            },
                            {
                              title: 'Performance', icon: <BarChart sx={{ fontSize: 16 }} />, items: [
                                { label: 'Trades', value: isNum(s.Trades) ? Number(s.Trades).toLocaleString() : '0' },
                                { label: 'Win Rate', value: isNum(s.WinRatePct) ? fmtPct(s.WinRatePct) : '—' },
                                { label: 'Profit Factor', value: fmtRatio(s.ProfitFactor) },
                              ]
                            },
                          ];

                          return (
                            <Grid container spacing={2}>
                              {statGroups.map((group, gIdx) => (
                                <Grid item xs={6} md={3} key={gIdx}>
                                  <Box sx={{ p: 2, bgcolor: '#070a0f', borderRadius: 2, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', height: '100%', '&:hover': { borderColor: 'rgba(255,255,255,0.1)' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                                      <Box sx={{ color: '#9aa6bd' }}>{group.icon}</Box>
                                      <Typography variant="caption" fontWeight={600} sx={{ color: '#9aa6bd', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>{group.title}</Typography>
                                    </Box>
                                    <Stack spacing={1}>
                                      {group.items.map((item, iIdx) => (
                                        <Box key={iIdx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                          <Typography variant="caption" sx={{ color: '#9aa6bd' }}>{item.label}</Typography>
                                          <Typography variant="body2" fontWeight={item.highlight ? 700 : 500} sx={{ color: item.highlight && group.title === 'Profit & Loss' ? (group.isProfit ? '#46b88a' : '#d97b72') : '#f2f6ff', fontFamily: 'monospace' }}>
                                            {item.value}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          );
                        })()}

                        {/* Win/Loss breakdown */}
                        {(() => {
                          const baseStats = bot.stats || {};
                          // Real server stats are present iff UpdatedAt is set (an
                          // empty/never-synced statistics object normalizes to all-undefined).
                          // Prefer them; only recompute from history when absent — so a
                          // paginated (partial) history can't skew the totals.
                          const hasServerStats = !!(bot.stats && bot.stats.UpdatedAt);
                          const computed = hasServerStats ? null : computeStatsFromHistory(liveBotHistories[bot.id]);
                          const s = computed ? { ...baseStats, ...computed } : baseStats;
                          if (s.Wins === undefined && s.Losses === undefined) return null;
                          const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                          const fmtAsset = (v) => {
                            if (!isNum(v)) return '—';
                            const n = Number(v);
                            const digits = Math.abs(n) < 1 ? 4 : 2;
                            const sign = n < 0 ? '-' : '';
                            return `${sign}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${QUOTE || ''}`;
                          };
                          return (
                            <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Chip icon={<CheckCircle sx={{ fontSize: 16, color: '#46b88a !important' }} />} label={`${s.Wins || 0} Wins`} size="small" sx={{ bgcolor: '#052e16', color: '#46b88a', borderColor: '#065f46', border: '1px solid' }} />
                              <Chip icon={<Cancel sx={{ fontSize: 16, color: '#d97b72 !important' }} />} label={`${s.Losses || 0} Losses`} size="small" sx={{ bgcolor: '#2a0a0a', color: '#d97b72', borderColor: '#991b1b', border: '1px solid' }} />
                              {s.GrossProfit !== undefined && <Chip label={`Gross Profit: ${fmtAsset(s.GrossProfit)}`} size="small" sx={{ bgcolor: '#065f46', color: '#46b88a' }} />}
                              {s.GrossLoss !== undefined && <Chip label={`Gross Loss: ${fmtAsset(s.GrossLoss)}`} size="small" sx={{ bgcolor: '#991b1b', color: '#d97b72' }} />}
                            </Box>
                          );
                        })()}

                        {/* Phase A: Hedge position panel — renders only when shortPositionQty > 0 */}
                        <HedgePositionPanel
                          stats={bot.stats}
                          quote={QUOTE}
                          markPrice={bot.currentPrice || bot.stats?.MarkPrice}
                        />

                        {/* Phase A: Buying Power chip for stock_equity bots */}
                        {isStockRow && (() => {
                          const acct = getAccountStatusForBot(bot, alpacaStatus);
                          if (!acct) return null;
                          return (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                              <BuyingPowerChip
                                buyingPower={acct.buyingPower}
                                cash={acct.cash}
                                equity={acct.equity}
                              />
                            </Box>
                          );
                        })()}
                      </Paper>
                    )}

                    {/* SECTION 3: Webhook Display (TradingView only) */}
                    {bot.signalSource === 'tradingview' && bot.webhookUrl && (
                      <Paper elevation={0} sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, bgcolor: '#0f1218' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src="https://www.tradingview.com/static/images/favicon.ico" alt="TradingView" style={{ width: 18, height: 18 }} />
                          </Box>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>TradingView Webhook</Typography>
                          <Chip size="small" label="External Signals" color="info" variant="outlined" sx={{ ml: 'auto' }} />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#9aa6bd', mb: 0.5, display: 'block' }}>WEBHOOK URL</Typography>
                          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#4d8fc7', flex: 1, wordBreak: 'break-all' }}>{bot.webhookUrl}</Typography>
                            <Tooltip title="Copy webhook URL">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(bot.webhookUrl); showToast('success', 'Webhook URL copied!'); }} sx={{ color: '#9aa6bd' }}>
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Paper>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9aa6bd', mb: 0.5, display: 'block' }}>TRADINGVIEW ALERT MESSAGE</Typography>
                          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5, position: 'relative' }}>
                            <IconButton size="small" onClick={(e) => {
                              e.stopPropagation();
                              const tiLocal = (() => { try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; } })();
                              const actionField = tiLocal.webhook?.actionField || 'action';
                              const template = JSON.stringify({ [actionField]: '{{strategy.order.action}}', symbol: '{{ticker}}', price: '{{close}}' }, null, 2);
                              navigator.clipboard.writeText(template);
                              showToast('success', 'Alert template copied!');
                            }} sx={{ position: 'absolute', top: 4, right: 4, color: '#9aa6bd' }}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#a5d6a7', m: 0, whiteSpace: 'pre-wrap' }}>
                              {(() => {
                                const tiLocal = (() => { try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; } })();
                                const actionField = tiLocal.webhook?.actionField || 'action';
                                return `{
  "${actionField}": "{{strategy.order.action}}",
  "symbol": "{{ticker}}",
  "price": "{{close}}"
}`;
                              })()}
                            </Typography>
                          </Paper>
                        </Box>
                      </Paper>
                    )}

                    {/* SECTION 4: Trade History (with indicators) */}
                    <Paper elevation={0} sx={{ mt: 3, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', bgcolor: '#0f1218' }}>
                      <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#070a0f', borderBottom: '1px solid', borderColor: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <History sx={{ color: '#9aa6bd', fontSize: 20 }} />
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Trade History</Typography>
                        </Box>
                        {liveBotHistories[bot.id]?.length > 0 && (
                          <Chip size="small" variant="outlined" label={`${liveBotHistories[bot.id].length} entries`} sx={{ height: 24, color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)' }} />
                        )}
                      </Box>

                      {liveBotHistories[bot.id] && liveBotHistories[bot.id].length > 0 ? (
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Side</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Symbol</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Quantity</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Value</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Fee</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Type</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {liveBotHistories[bot.id].slice().reverse().map((entry, idx) => {
                                const parsed = parseBuySellHistory(entry.text);
                                if (!parsed) return null;

                                // Reconciliation
                                if (parsed.type === 'reconciliation') {
                                  const isCritical = parsed.critical;
                                  const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                                  return (
                                    <TableRow key={idx} sx={{ bgcolor: isCritical ? 'rgba(251, 191, 36, 0.08)' : 'rgba(56, 189, 248, 0.06)', borderLeft: '3px solid', borderColor: isCritical ? '#d9a441' : '#4d8fc7' }}>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd' }}>{entry.timestamp}</Typography></TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label="SYNC" size="small" sx={{ fontWeight: 600, minWidth: 70, bgcolor: isCritical ? '#78350f' : '#0c4a6e', color: isCritical ? '#d9a441' : '#4d8fc7', border: '1px solid', borderColor: isCritical ? '#d9a441' : '#4d8fc7' }} />
                                      </TableCell>
                                      <TableCell colSpan={5} sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Typography variant="body2" sx={{ color: '#e8edf7', fontWeight: 500 }}>{parsed.buy_sell_history}</Typography>
                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                                          {parsed.notional && <Typography variant="caption" sx={{ color: '#9aa6bd' }}>Notional: {parsed.notional} {QUOTE || ''}</Typography>}
                                          {parsed.diff_pct && <Typography variant="caption" sx={{ color: '#9aa6bd' }}>Drift: {parsed.diff_pct}%</Typography>}
                                          {parsed.source && <Typography variant="caption" sx={{ color: '#9aa6bd' }}>Source: {parsed.source === 'binance_sync' ? 'Binance' : parsed.source}</Typography>}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label={isCritical ? 'Critical' : 'Auto-Fixed'} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: isCritical ? '#78350f' : '#0f1218', color: isCritical ? '#d9a441' : '#4d8fc7', border: '1px solid', borderColor: isCritical ? '#d9a441' : '#4d8fc7' }} />
                                      </TableCell>
                                    </TableRow>
                                  );
                                }

                                // Order lifecycle (pending/cancelled/expired)
                                if (parsed.type === 'order_pending' || parsed.type === 'order_cancelled' || parsed.type === 'order_expired') {
                                  const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                                  const config = {
                                    order_pending: { label: '⏳ PENDING', bgColor: 'rgba(251, 191, 36, 0.06)', borderColor: '#d9a441', chipBg: '#78350f', chipColor: '#d9a441', statusLabel: 'Awaiting Fill' },
                                    order_cancelled: { label: '✕ CANCELLED', bgColor: 'rgba(156, 163, 175, 0.06)', borderColor: '#6f7c96', chipBg: 'rgba(255,255,255,0.1)', chipColor: '#9aa6bd', statusLabel: 'Cancelled' },
                                    order_expired: { label: '⏱ EXPIRED', bgColor: 'rgba(156, 163, 175, 0.06)', borderColor: '#6f7c96', chipBg: 'rgba(255,255,255,0.1)', chipColor: '#9aa6bd', statusLabel: 'Expired' },
                                  };
                                  const c = config[parsed.type];
                                  const sideUpper = String(parsed.side || '').toUpperCase();
                                  const isBuy = sideUpper.includes('BUY') || sideUpper.includes('LONG');
                                  return (
                                    <TableRow key={idx} sx={{ bgcolor: c.bgColor, borderLeft: '3px solid', borderLeftColor: c.borderColor }}>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd' }}>{entry.timestamp}</Typography></TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Chip label={parsed.side || '—'} size="small" sx={{ fontWeight: 600, minWidth: 50, fontSize: '0.65rem', bgcolor: isBuy ? '#46b88a' : '#d97b72', color: '#ffffff' }} />
                                          <Chip label={c.label} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: c.chipBg, color: c.chipColor, border: '1px solid', borderColor: c.borderColor }} />
                                        </Stack>
                                      </TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ color: '#e8edf7' }}>{parsed.symbol || bot.symbol}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#f2f6ff' }}>{parsed.price ? `${Number(parsed.price).toLocaleString()} ${QUOTE || ''}` : '—'}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#e8edf7' }}>{parsed.quantity ? Number(parsed.quantity).toFixed(6) : '—'}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd', fontFamily: 'monospace' }}>{parsed.order_id ? `#${String(parsed.order_id).slice(-6)}` : '—'}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#6f7c96', fontStyle: 'italic' }}>{parsed.reason || c.statusLabel}</Typography></TableCell>
                                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label="Limit" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#0f1218', color: '#4d8fc7', border: '1px solid', borderColor: '#3f6fb0' }} />
                                      </TableCell>
                                    </TableRow>
                                  );
                                }

                                // ─── Regular trade row ─────────────────────────────
                                const value = parsed.side === 'BUY' ? parseFloat(parsed.cost || 0) : parseFloat(parsed.proceeds || 0);
                                const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                                const hasIndicators = parsed.indicators && typeof parsed.indicators === 'object' && Object.keys(parsed.indicators).length > 0;

                                return (
                                  <React.Fragment key={idx}>
                                    <TableRow sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }, bgcolor: idx % 2 === 0 ? '#0f1218' : '#070a0f', '& td': hasIndicators ? { borderBottom: 'none' } : {} }}>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd' }}>{entry.timestamp}</Typography></TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label={parsed.side} size="small" sx={{ fontWeight: 600, minWidth: 50, bgcolor: parsed.side === 'BUY' ? '#46b88a' : '#d97b72', color: '#ffffff' }} />
                                      </TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" fontWeight={500} sx={{ color: '#e8edf7' }}>{parsed.symbol}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#f2f6ff' }}>{parseFloat(parsed.price).toLocaleString()} {QUOTE || ''}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#e8edf7' }}>{parseFloat(parsed.quantity).toFixed(6)}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', color: parsed.side === 'BUY' ? '#d97b72' : '#46b88a' }}>
                                          {parsed.side === 'BUY' ? '-' : '+'}{value.toFixed(2)} {QUOTE || ''}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#9aa6bd', fontSize: '0.75rem' }}>{parsed.fee ? `${Number(parsed.fee).toFixed(4)} ${QUOTE || ''}` : '—'}</Typography></TableCell>
                                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label={parsed.simulated ? 'Paper' : 'Live'} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: parsed.simulated ? '#0f1218' : '#052e16', color: parsed.simulated ? '#4d8fc7' : '#46b88a', border: '1px solid', borderColor: parsed.simulated ? '#4d8fc7' : '#46b88a' }} />
                                      </TableCell>
                                    </TableRow>

                                    {hasIndicators && (
                                      <TableRow sx={{ bgcolor: idx % 2 === 0 ? '#0f1218' : '#070a0f' }}>
                                        <TableCell colSpan={8} sx={{ borderColor: 'rgba(255,255,255,0.06)', py: 1, px: 2 }}>
                                          <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#070a0f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                              <Functions sx={{ color: '#4d8fc7', fontSize: 13 }} />
                                              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: '#9aa6bd', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                                                Indicators at signal
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                              {Object.entries(parsed.indicators).map(([key, val]) => {
                                                const m = key.match(/^([A-Za-z_][A-Za-z0-9_]*)_(\d+)_(?:tick|\w+)$/);
                                                const name = m?.[1] || key;
                                                const period = m?.[2];
                                                const displayName = period && period !== '0' ? `${name}(${period})` : name;
                                                const isNull = val == null || !Number.isFinite(Number(val));
                                                return (
                                                  <Typography key={key} sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#e8edf7' }}>
                                                    <span style={{ color: '#4d8fc7' }}>{displayName}</span>
                                                    <span style={{ color: '#6f7c96' }}> = </span>
                                                    <span style={{ color: isNull ? '#6f7c96' : '#46b88a' }}>
                                                      {isNull ? 'NaN' : Number(val).toFixed(4)}
                                                    </span>
                                                  </Typography>
                                                );
                                              })}
                                            </Box>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                          <History sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
                          <Typography variant="body2" sx={{ color: '#9aa6bd' }}>
                            {historyPaging[bot.id]?.loading ? 'Loading trade history…' : 'No trade history yet'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6f7c96' }}>Trades will appear here once the bot executes orders</Typography>
                        </Box>
                      )}

                      {/* Paginated "Load more" — pages older history on demand */}
                      {liveBotHistories[bot.id]?.length > 0 && !historyPaging[bot.id]?.done && (
                        <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.06)' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={historyPaging[bot.id]?.loading}
                            onClick={() => loadBotHistoryPage(bot.id)}
                            sx={{ color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)', textTransform: 'none' }}
                          >
                            {historyPaging[bot.id]?.loading ? 'Loading…' : 'Load older trades'}
                          </Button>
                          {Number.isFinite(historyPaging[bot.id]?.total) && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6f7c96' }}>
                              Showing {liveBotHistories[bot.id].length} of {historyPaging[bot.id].total}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>

                    {/* SECTION 5: Action Buttons */}
                    {/* SECTION 5: Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, mb: 1 }}>
                      {bot.signalSource !== 'tradingview' && (() => {
                        const check = gateBacktest(bot);
                        return (
                          <Tooltip title={check.supported ? '' : check.message} arrow>
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<BarChart />}
                                disabled={!check.supported}
                                onClick={(e) => { e.stopPropagation(); runBacktest(bot); }}
                                sx={{
                                  borderRadius: 1.5,
                                  borderColor: 'rgba(255,255,255,0.1)',
                                  color: '#e8edf7',
                                  '&:hover': { borderColor: 'rgba(255,255,255,0.18)', bgcolor: 'rgba(255, 255, 255, 0.05)' },
                                  '&.Mui-disabled': { color: '#6f7c96', borderColor: 'rgba(255,255,255,0.06)' },
                                }}
                              >
                                Backtest
                              </Button>
                            </span>
                          </Tooltip>
                        );
                      })()}

                      {/* Had no onClick at all — it rendered and did nothing.
                          Opens the bot in the Backtest tab's reconfigure panel,
                          which is the one working edit path: change timeframe /
                          entry / exit, re-test, then deploy back onto the bot. */}
                      <Button variant="outlined" size="small" startIcon={<Edit />}
                        onClick={() => {
                          setSelectedBotForBacktest(bot);
                          setBtEditOpen(true);
                          setActiveTab('backtest');
                          setTimeout(() => {
                            try { backtestPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
                          }, 60);
                        }}
                        sx={{
                          borderRadius: 1.5, borderColor: 'rgba(255,255,255,0.1)', color: '#e8edf7',
                          '&:hover': { borderColor: 'rgba(255,255,255,0.18)', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                        }}>
                        Edit
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<Delete />}
                        onClick={() => setConfirmDeleteBotId(bot.id)}
                        sx={{
                          borderRadius: 1.5, borderColor: 'rgba(255,255,255,0.1)', color: '#d97b72',
                          '&:hover': { borderColor: '#d97b72', bgcolor: 'rgba(220, 38, 38, 0.1)' }
                        }}>
                        Delete
                      </Button>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  }

  // ─── Scanner profile group (collapsible header + child bots) ──────────────
  function renderScannerProfileGroup(profile, children) {
    const isExpanded = expandedScannerProfile === profile.id;
    const cfg = profile.scannerConfig || {};
    const isPaused = cfg.paused === true;
    const triggerExpr = cfg.trigger?.expression || cfg.trigger?._preset?.kind || '—';
    const universe = cfg.universe?.type || '—';

    return (
      <Paper
        key={`profile-${profile.id}`}
        variant="outlined"
        sx={{
          borderColor: 'rgba(255,255,255,0.1)',
          borderWidth: 1,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#0f1218',
        }}
      >
        <Box
          onClick={() => setExpandedScannerProfile(isExpanded ? null : profile.id)}
          sx={{
            p: 2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: '#070a0f',
            borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.06)' : 'none',
            '&:hover': { bgcolor: '#0f1218' },
          }}
        >
          <IconButton size="small">
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>

          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: isPaused ? 'rgba(255,255,255,0.06)' : 'rgba(37, 99, 235, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShowChart sx={{ color: isPaused ? '#9aa6bd' : '#4d8fc7', fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }} noWrap>
                {profile.name}
              </Typography>
              <Chip
                size="small"
                label="Scanner Profile"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#1e3a8a',
                  color: '#9cc3e4',
                  border: '1px solid #4d8fc7',
                }}
              />
              {isPaused && (
                <Chip
                  size="small"
                  label="Paused"
                  icon={<Pause sx={{ fontSize: 12 }} />}
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: '#451a03',
                    color: '#d9a441',
                  }}
                />
              )}
            </Box>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                <strong>Universe:</strong> {universe}
                {cfg.universe?.limit && ` (limit ${cfg.universe.limit})`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                <strong>Trigger:</strong>{' '}
                {triggerExpr.length > 50 ? triggerExpr.slice(0, 50) + '…' : triggerExpr}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#f2f6ff', lineHeight: 1 }}>
                {children.length}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                {children.length === 1 ? 'spawned bot' : 'spawned bots'}
              </Typography>
            </Box>
            {cfg.max_concurrent_spawned && (
              <Typography variant="caption" sx={{ color: '#6f7c96' }}>
                / {cfg.max_concurrent_spawned} max
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
            <Tooltip title="Open in Scanner">
              <IconButton
                size="small"
                onClick={() => window.location.href = `/scanner?profile=${profile.id}`}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, bgcolor: '#0f1218' }}>
            {children.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" sx={{ color: '#9aa6bd' }}>
                  No bots spawned yet
                </Typography>
                <Typography variant="caption" sx={{ color: '#6f7c96' }}>
                  Bots will appear here when the trigger fires.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {children.map(bot => renderBotCard(bot))}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  }



  /*
    function parseBuySellHistory(raw) {
    try {
      // Clean quotes and backslashes
      const decoded = raw.replace(/\\+/g, '').replace(/"+/g, '"');
      
      const symbolMatch = decoded.match(/Symbol (\w+)/i);
      const sideMatch = decoded.match(/\b(BUY|SELL)\b/);
      const totalPriceMatch = decoded.match(/TOTAL PRICE ([\d.]+)/i);
      const avgPriceMatch = decoded.match(/AVERAGE PRICE ([\d.]+)/i);
      const timeMatch = decoded.match(/AT TIME (.+?) FOR/);
      const ruleMatch = decoded.match(/INFO "(.*)"/);
  
      return {
        symbol: symbolMatch?.[1] || 'N/A',
        side: sideMatch?.[1] || 'N/A',
        totalPrice: totalPriceMatch?.[1] || '0',
        avgPrice: avgPriceMatch?.[1] || '0',
        time: timeMatch?.[1] || 'Unknown Time',
        rule: ruleMatch?.[1] || '',
      };
    } catch (err) {
      console.warn("Failed to parse buy/sell history:", raw);
      return null;
    }
  }
  */

  // ... (keep all other existing functions like handleNext, handleBack, etc.)

  return (
    <ThemeProvider theme={AT_THEME}>
    <Box sx={{ minHeight: '100vh', bgcolor: AT.bg, color: AT.text, fontFamily: AT_SANS, '@keyframes clp': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } } }}>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
      <Toolbar button={<Logout />} />

      <Box sx={{ maxWidth: '1280px', mx: 'auto', px: { xs: 2.5, md: '40px' }, py: 4.5 }}>
        {/* Page header — eyebrow + engine status + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '12px', letterSpacing: '.2em', color: AT.accent }}>AUTOTRADER</Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: AT_MONO, fontSize: '10px', letterSpacing: '.12em', color: AT.muted }}>
            <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: AT.positive, animation: 'clp 2.6s ease-in-out infinite' }} />ENGINE ONLINE
          </Box>
        </Box>
        <Typography component="h1" sx={{ fontFamily: AT_SANS, fontSize: '32px', fontWeight: 700, letterSpacing: '-.02em', color: AT.bright, m: '0 0 6px' }}>
          Trading Bot Manager
        </Typography>
        <Typography sx={{ fontSize: '15px', color: AT.body, m: '0 0 26px' }}>
          Create, monitor and tune automated strategies running across your exchanges.
        </Typography>

        {/* Top tabs — icon + label with accent underline (handoff §3) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', borderBottom: `1px solid rgba(255,255,255,.07)`, mb: 3, flexWrap: 'wrap' }}>
          {[
            { key: 'bots', label: 'My Bots', icon: <Settings sx={{ fontSize: 17 }} /> },
            { key: 'create', label: 'Create Bot', icon: <AddCircleOutline sx={{ fontSize: 17 }} />, onClick: handleCreate },
            { key: 'backtest', label: 'Backtesting', icon: <History sx={{ fontSize: 17 }} /> },
            { key: 'advisor', label: 'AI Advisor', icon: <ShowChart sx={{ fontSize: 17 }} /> },
          ].map((t) => {
            const on = activeTab === t.key;
            return (
              <Box key={t.key} onClick={() => { setActiveTab(t.key); if (t.onClick) t.onClick(); }}
                sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '18px', py: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                  color: on ? '#fff' : AT.body, borderBottom: `2px solid ${on ? AT.accent : 'transparent'}`, mb: '-1px',
                  transition: 'color .15s', '& svg': { color: on ? AT.accent : AT.muted }, '&:hover': { color: on ? '#fff' : AT.text } }}>
                {t.icon} {t.label}
              </Box>
            );
          })}
        </Box>

        {activeTab === 'create' && isCreating && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 332px' }, gap: '16px', alignItems: 'start', mb: 4 }}>
                <Box sx={{ minWidth: 0, bgcolor: AT.card, border: `1px solid ${AT.hairline}`, borderRadius: '14px', boxShadow: '0 1px 2px rgba(0,0,0,.22)', p: { xs: '20px', md: '24px' } }}>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label, index) => {
                  const isSkipped = index === 1 && newBot.signalSource === 'tradingview';
                  return (
                    <Step key={label} completed={isSkipped ? false : undefined}>
                      <StepLabel
                        optional={isSkipped ? (
                          <Typography variant="caption" color="text.secondary">
                            (Using TradingView)
                          </Typography>
                        ) : null}
                        sx={isSkipped ? { opacity: 0.5 } : {}}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              {/* (Strategy summary now lives in the persistent right rail — §8a) */}

              {activeStep === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bot Name"
                      value={newBot.name}
                      onChange={e => setNewBot({ ...newBot, name: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ExchangeSelect
                      exchanges={userExchanges}
                      value={exchangeValue}               // <— stable reference
                      disabled={!userExchanges?.length}
                      onChange={(ex) => {
                        setNewBot(prev => ({ ...prev, exchangeId: ex?.id ?? null }));
                        if (ex?.id) {
                          const exchangeName = ex.exchange || ex.name;
                          ensureSymbols(ex.id, exchangeName);
                        }
                      }}
                    />
{/* Phase B — show asset class derived from exchange */}
                    {newBot.exchangeId && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssetClassChip assetClass={assetClass} />
                      </Box>
                    )}
                  </Grid>

                  {/* Funding Asset — balance source for bot capital & P&L.
                      Crypto: USDT/USDC/FDUSD/BUSD/BTC/ETH. Stocks: USD only (locked). */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!newBot.exchangeId || isStockEquity}>
                      <InputLabel>Funding Asset</InputLabel>
                      <Select
                        value={newBot.funding_asset || defaultFundingAssetForClass(assetClass)}
                        label="Funding Asset"
                        onChange={(e) => setNewBot(prev => ({ ...prev, funding_asset: e.target.value }))}
                      >
                        {getFundingAssetsForClass(assetClass).map(a => (
                          <MenuItem key={a} value={a}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Typography variant="body2" fontWeight={500}>{a}</Typography>
                              {/* TODO: live balance once GET /api/exchange/:id/balances ships */}
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                      {isStockEquity
                        ? 'Alpaca accounts are USD-funded. Bot capital and P&L are measured in USD.'
                        : !newBot.exchangeId
                          ? 'Select an exchange first.'
                          : `Bot capital, P&L, and equity measured in ${newBot.funding_asset || 'USDT'}. If the pair quotes in a different asset, the bot will auto-convert at startup.`}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>


                    <TextField
                      label="Search symbol"
                      placeholder="e.g. BTC/USDT or btcusdt"
                      value={symbolSearch}
                      onChange={(e) => setSymbolSearch(e.target.value)}
                      fullWidth
                      sx={{ mb: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                        endAdornment: symbolSearch ? (
                          <IconButton
                            size="small"
                            onClick={() => setSymbolSearch('')}
                            aria-label="Clear search"
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        ) : null
                      }}
                    />

                    {/* Stocks get a category filter (sector / listing venue /
                        ETF); crypto keeps the zero-fee filter. Fees are not a
                        meaningful axis on commission-free Alpaca. */}
                    {isStockEquity && stockCategoryChips.length > 1 && (
                      <Box sx={{ mb: 1.25 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {stockCategoryChips.map((opt) => {
                            const active = symbolStockCategory === opt.value;
                            return (
                              <Box
                                key={opt.value}
                                onClick={() => setSymbolStockCategory(opt.value)}
                                sx={{
                                  fontFamily: AT_MONO, fontSize: '11px', fontWeight: 600,
                                  px: '10px', py: '5px', borderRadius: '7px', cursor: 'pointer',
                                  whiteSpace: 'nowrap', transition: 'all .15s ease',
                                  color: active ? AT.accentText : AT.body,
                                  background: active ? 'rgba(77,143,199,.12)' : 'rgba(255,255,255,.04)',
                                  border: `1px solid ${active ? 'rgba(77,143,199,.38)' : 'rgba(255,255,255,.1)'}`,
                                }}
                              >
                                {opt.label}
                              </Box>
                            );
                          })}
                        </Box>
                        {symbolStockCategory !== 'any' && (
                          <Typography sx={{ fontSize: '11.5px', color: '#8593ad', mt: '7px' }}>
                            {`${filteredSymbolOptions.length} matching ${filteredSymbolOptions.length === 1 ? 'symbol' : 'symbols'}.`}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {/* Fee filter — narrows the list to zero-fee pairs. Same
                        modes as the scanner's universe fee filter. */}
                    {!isStockEquity && (
                    <Box sx={{ mb: 1.25 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {SYMBOL_FEE_FILTERS.map((opt) => {
                          const active = symbolFeeFilter === opt.value;
                          return (
                            <Tooltip key={opt.value} title={opt.desc} arrow placement="top">
                              <Box
                                onClick={() => setSymbolFeeFilter(opt.value)}
                                sx={{
                                  fontFamily: AT_MONO, fontSize: '11px', fontWeight: 600,
                                  px: '10px', py: '5px', borderRadius: '7px', cursor: 'pointer',
                                  whiteSpace: 'nowrap', transition: 'all .15s ease',
                                  color: active ? AT.accentText : AT.body,
                                  background: active ? 'rgba(77,143,199,.12)' : 'rgba(255,255,255,.04)',
                                  border: `1px solid ${active ? 'rgba(77,143,199,.38)' : 'rgba(255,255,255,.1)'}`,
                                }}
                              >
                                {opt.label}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                      {symbolFeeFilter !== 'any' && (
                        <Typography sx={{ fontSize: '11.5px', color: symbolsWithFeeData === 0 ? '#d9a441' : '#8593ad', mt: '7px' }}>
                          {symbolsWithFeeData === 0
                            ? 'No fee data recorded for this exchange’s symbols — this filter can’t match anything yet.'
                            : `${filteredSymbolOptions.length} matching ${filteredSymbolOptions.length === 1 ? 'symbol' : 'symbols'}. Symbols with no recorded fee are excluded.`}
                        </Typography>
                      )}
                    </Box>
                    )}

                     <Autocomplete
                      loading={symbolsLoading}
                      options={filteredSymbolOptions}
                      getOptionLabel={(opt) => {
                        if (opt.base_asset && opt.quote_asset) {
                          return `${opt.base_asset}/${opt.quote_asset}`;
                        }
                        return compactToPretty(opt.symbol);
                      }}
                      value={
                        filteredSymbolOptions.find(o => o.symbol === newBot.symbol) || null
                      }
                      // Phase B — render shortability badge inline for stock_equity
                      renderOption={(props, option) => {
                        const label = option.base_asset && option.quote_asset
                          ? `${option.base_asset}/${option.quote_asset}`
                          : compactToPretty(option.symbol);
                        if (!isStockEquity) {
                          return <li {...props}>{label}</li>;
                        }
                        const sym = String(option.symbol || '').toUpperCase();
                        const status = shortability.get(sym);
                        return (
                          <li {...props} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span>{label}</span>
                            <ShortabilityBadge status={status} compact />
                          </li>
                        );
                      }}
                      // Phase B — when the dropdown is open, batch-fetch shortability for visible symbols
                      onOpen={() => {
                        if (!isStockEquity) return;
                        const syms = (filteredSymbolOptions || []).slice(0, 50).map(o => String(o.symbol || '').toUpperCase()).filter(Boolean);
                        if (syms.length) shortability.lookup(syms);
                      }}
                      onChange={(_e, val) => {
                        if (!val) {
                          setNewBot(prev => ({
                            ...prev,
                            symbol: '',
                            baseAsset: undefined,
                            basePrecision: undefined,
                            quoteAsset: undefined,
                            quotePrecision: undefined,
                            marginAsset: undefined,
                          }));
                          return;
                        }

                        const symbol = (val.symbol || val.class_name || '').toUpperCase();
                        let baseAsset = val.base_asset;
                        let quoteAsset = val.quote_asset;

                        // For futures COIN-M, margin asset is the base asset (e.g. BTC)
                        // For futures USDT-M, margin asset is USDT
                        const marginAsset = val.margin_asset ?? quoteAsset ?? null;

                        if (baseAsset && !quoteAsset) {
                          quoteAsset = symbol.replace(new RegExp(`^${baseAsset}`, 'i'), '') || symbol.slice(-4);
                        }
                        if (!baseAsset || !quoteAsset) {
                          const [fb, fq] = splitPair(symbol);
                          baseAsset = baseAsset || fb;
                          quoteAsset = quoteAsset || fq;
                        }

                        setNewBot(prev => ({
                          ...prev,
                          symbol,
                          baseAsset,
                          basePrecision: val.base_precision ?? val.quantity_precision ?? val.price_precision ?? null,
                          quoteAsset,
                          quotePrecision: val.quote_precision ?? 2,
                          marginAsset,
                          // carry through futures metadata so Order Size In label is accurate
                          ...(prev.marketType === 'futures' && {
                            contractType: val.contract_type ?? null,
                          }),
                        }));
                      }}

                      noOptionsText={
                        newBot.exchangeId
                          ? (symbolsLoading ? 'Loading symbols…' : 'No matching symbols')
                          : 'Select an exchange first'
                      }
                      ListboxProps={{ style: { maxHeight: 320 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Trading Pair"
                          helperText={
                            symbolsError
                              ? symbolsError
                              : newBot.exchangeId
                                ? (() => {
                                  const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
                                  const exchangeName = selectedExchange?.exchange || selectedExchange?.name || newBot.exchangeId;

                                  if (newBot.marketType === 'futures') {
                                    const marketType = newBot.futuresType === 'coin_m' ? 'coinm_futures' : 'usdm_futures';
                                    const cacheKey = `${exchangeName}:${marketType}`;
                                    const count = (futuresSymbolsByExchange[cacheKey] || []).length;
                                    return `Loaded ${count} ${marketType === 'coinm_futures' ? 'COIN-M' : 'USDT-M'} futures symbols`;
                                  }

                                  const cacheKey = exchangeName;
                                  return `Loaded ${(symbolsByExchange[cacheKey] || []).length} symbols`;
                                })()
                                : 'Select an exchange to load symbols'
                          }
                          fullWidth
                        />
                      )}
                    />
<SymbolFeeDisplay
                      symbol={newBot.symbol}
                      exchangeId={newBot.exchangeId}
                      userExchanges={userExchanges}
                      symbolsByExchange={newBot.marketType === 'futures' ? futuresSymbolsByExchange : symbolsByExchange}
                      orderExecutionMode={newBot.orderExecution}
                    />

                    {/* Funding-asset vs pair-quote mismatch warning.
                        Stock_equity is always USD-funded so no warning ever applies.
                        For crypto, surface the conversion cost up front. */}
                    {!isStockEquity && newBot.symbol && newBot.funding_asset && (() => {
                      const { quote } = getPairMeta({
                        symbol: newBot.symbol,
                        exchangeId: newBot.exchangeId,
                        userExchanges,
                        symbolsByExchange: newBot.marketType === 'futures' ? futuresSymbolsByExchange : symbolsByExchange,
                      });
                      const mm = describeFundingMismatchForPair(newBot.funding_asset, quote);
                      if (!mm) return null;
                      return (
                        <Alert severity={mm.severity} icon={<WarningAmber fontSize="small" />} sx={{ mt: 1.5 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{mm.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{mm.body}</Typography>
                        </Alert>
                      );
                    })()}
                  </Grid>


                  {/* ✅ Time Interval - ONLY FOR LOGIC-BASED BOTS */}
                  {newBot.signalSource !== 'tradingview' && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Time Interval</InputLabel>
                        <Select
                          value={newBot.interval}
                          onChange={e => setNewBot({ ...newBot, interval: e.target.value })}
                          label="Time Interval"
                        >
                          {['tick', '1m', '5m', '15m', '30m', '1H', '4H', '8H', '12H', '1D'].map(i => (
                            <MenuItem key={i} value={i}>{i}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}


                  {/* Signal Source Section - Enhanced TradingView Integration */}
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        borderColor: newBot.signalSource === 'tradingview' ? 'primary.main' : 'divider',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <ShowChart color="primary" />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Signal Source
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {/* Signal Mode Toggle */}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Paper
                              variant="outlined"
                              onClick={() => setNewBot(prev => ({ ...prev, signalSource: 'logic' }))}
                              sx={{
                                flex: 1,
                                p: 2,
                                cursor: 'pointer',
                                borderColor: newBot.signalSource === 'logic' ? 'primary.main' : 'divider',
                                borderWidth: newBot.signalSource === 'logic' ? 2 : 1,
                                bgcolor: newBot.signalSource === 'logic' ? 'action.selected' : 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.light' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Functions color={newBot.signalSource === 'logic' ? 'primary' : 'action'} />
                                <Typography variant="subtitle2" fontWeight={600}>
                                  Internal Logic
                                </Typography>
                                {newBot.signalSource === 'logic' && (
                                  <Chip size="small" label="Selected" color="primary" sx={{ ml: 'auto' }} />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Use Buy/Sell rules defined in Step 2. Bot evaluates conditions automatically.
                              </Typography>
                            </Paper>

                            <Paper
                              variant="outlined"
                              onClick={() => setNewBot(prev => ({
                                ...prev,
                                signalSource: 'tradingview',
                                webhookSecret: prev.webhookSecret || Array.from(crypto.getRandomValues(new Uint8Array(16))).map(b => b.toString(16).padStart(2, '0')).join(''),
                              }))}
                              sx={{
                                flex: 1,
                                p: 2,
                                cursor: 'pointer',
                                borderColor: newBot.signalSource === 'tradingview' ? 'primary.main' : 'divider',
                                borderWidth: newBot.signalSource === 'tradingview' ? 2 : 1,
                                bgcolor: newBot.signalSource === 'tradingview' ? 'action.selected' : 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.light' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <img
                                  src="https://www.tradingview.com/static/images/favicon.ico"
                                  alt="TradingView"
                                  style={{ width: 20, height: 20 }}
                                />
                                <Typography variant="subtitle2" fontWeight={600}>
                                  TradingView Webhook
                                </Typography>
                                {newBot.signalSource === 'tradingview' && (
                                  <Chip size="small" label="Selected" color="primary" sx={{ ml: 'auto' }} />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Receive BUY/SELL signals from TradingView alerts via webhook.
                              </Typography>
                            </Paper>
                          </Box>
                        </Grid>

                        {/* TradingView Configuration */}
                        {newBot.signalSource === 'tradingview' && (
                          <>
                            <Grid item xs={12}>
                              <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                  <strong>How it works:</strong> Create an alert in TradingView, set the webhook URL below,
                                  and configure the alert message to send JSON with your action field.
                                </Typography>
                              </Alert>
                            </Grid>

                            {/* Webhook URL Preview */}
                            <Grid item xs={12}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  bgcolor: 'grey.900',
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    WEBHOOK URL (copy after creating bot)
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label="Template"
                                    variant="outlined"
                                    color="warning"
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    color: 'primary.light',
                                    wordBreak: 'break-all',
                                    p: 1,
                                    bgcolor: 'grey.800',
                                    borderRadius: 1,
                                  }}
                                >
                                  {buildTradingViewWebhookUrl("BOT_ID", newBot.webhookSecret)}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  ⚠️ BOT_ID will be replaced with the actual bot ID after creation
                                </Typography>
                              </Paper>
                            </Grid>

                            {/* Secret & Field Configuration */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Webhook Secret"
                                value={newBot.webhookSecret || ''}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookSecret: e.target.value }))}
                                fullWidth
                                placeholder="e.g., my-secret-key-123"
                                helperText="Optional. Must match the 'secret' in your TradingView URL."
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Visibility fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Action Field Name"
                                value={newBot.webhookActionField || 'action'}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookActionField: e.target.value }))}
                                fullWidth
                                helperText="JSON field name that contains BUY/SELL value"
                              />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <TextField
                                label="BUY Signal Value"
                                value={newBot.webhookBuyValue || 'BUY'}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookBuyValue: e.target.value }))}
                                fullWidth
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <TextField
                                label="SELL Signal Value"
                                value={newBot.webhookSellValue || 'SELL'}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookSellValue: e.target.value }))}
                                fullWidth
                                size="small"
                              />
                            </Grid>

                            {/* TradingView Alert Message Template */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                TradingView Alert Message Template
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  bgcolor: 'grey.900',
                                  borderRadius: 1,
                                  position: 'relative',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const template = JSON.stringify({
                                      [newBot.webhookActionField || 'action']: '{{strategy.order.action}}',
                                      symbol: '{{ticker}}',
                                      price: '{{close}}',
                                      time: '{{time}}',
                                    }, null, 2);
                                    navigator.clipboard.writeText(template);
                                    showToast('success', 'Alert template copied to clipboard!');
                                  }}
                                  sx={{ position: 'absolute', top: 8, right: 8 }}
                                >
                                  <Tooltip title="Copy to clipboard">
                                    <ContentCopy fontSize="small" />
                                  </Tooltip>
                                </IconButton>
                                <Typography
                                  component="pre"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    color: '#a5d6a7',
                                    m: 0,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {`{
  "${newBot.webhookActionField || 'action'}": "{{strategy.order.action}}",
  "symbol": "{{ticker}}",
  "price": "{{close}}",
  "time": "{{time}}"
}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                                  Copy this into TradingView's alert message field. The <code>{'{{strategy.order.action}}'}</code> will be
                                  replaced with "buy" or "sell" automatically.
                                </Typography>
                              </Paper>
                            </Grid>

                            {/* Setup Instructions */}
                            <Grid item xs={12}>
                              <Accordion
                                sx={{
                                  bgcolor: 'background.default',
                                  '&:before': { display: 'none' },
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                  <Typography variant="subtitle2">
                                    📖 TradingView Setup Instructions
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Stack spacing={1.5}>
                                    <Typography variant="body2">
                                      <strong>1.</strong> Create your bot here first to get the actual webhook URL
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>2.</strong> In TradingView, create a strategy or indicator with alerts
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>3.</strong> Click "Create Alert" on your chart
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>4.</strong> In the alert settings:
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                      <li>
                                        <Typography variant="body2">
                                          Check "Webhook URL" and paste your bot's webhook URL
                                        </Typography>
                                      </li>
                                      <li>
                                        <Typography variant="body2">
                                          Set "Message" to the JSON template above
                                        </Typography>
                                      </li>
                                    </Box>
                                    <Typography variant="body2">
                                      <strong>5.</strong> Save the alert - signals will now trigger your bot!
                                    </Typography>
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                      <Typography variant="caption">
                                        TradingView webhook alerts require a paid TradingView plan (Pro, Pro+, or Premium).
                                      </Typography>
                                    </Alert>
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>



                  {/* ✅ NEW: Insert FuturesConfig here }
                  <FuturesConfigSection
                    newBot={newBot}
                    setNewBot={setNewBot}
                    userExchanges={userExchanges}
                  />
                  */}


                 {/* ✅ NEW: Insert OrderExecution here */}
                  <OrderExecutionSection
                    newBot={newBot}
                    setNewBot={setNewBot}
                  />

                  {/* Options instrument switch (self-gates to stock_equity) */}
                  <Grid item xs={12}>
                    <OptionsInstrumentSection
                      assetClass={assetClass}
                      value={{ instrument: newBot.instrument, options: newBot.options }}
                      onChange={(patch) => setNewBot(prev => ({ ...prev, ...patch }))}
                      optionsApprovedLevel={alpacaStatus?.statusByExchangeId?.[newBot.exchangeId]?.account?.optionsApprovedLevel
                        ?? alpacaStatus?.statusByExchangeId?.[newBot.exchangeId]?.optionsApprovedLevel
                        ?? null}
                    />
                  </Grid>

                  {/* Phase B — Extended hours toggle (self-gates to stock_equity).
                      Hidden for options bots: Alpaca options orders are RTH-only. */}
                  {newBot.instrument !== 'options' && (
                  <Grid item xs={12}>
                    <ExtendedHoursToggle
                      assetClass={assetClass}
                      value={newBot.extendedHours}
                      orderExecutionMode={newBot.orderExecution}
                      onChange={(v) => setNewBot(prev => ({ ...prev, extendedHours: v }))}
                    />
                  </Grid>
                  )}

                  <Grid item xs={12}>
                    <PositionSizeSection
                      newBot={newBot}
                      setNewBot={setNewBot}
                      marketType={newBot.marketType}
                      leverage={newBot.leverage}
                      baseAsset={newBot.baseAsset}
                      quoteAsset={newBot.quoteAsset}
                    />
                  </Grid>
                </Grid>
              )}


               {activeStep === 1 && (
                <Box sx={{ minHeight: 500 }}>  {/* Add minHeight */}
                  {/* Phase B — Allow Shorts toggle for stock_equity (mirrors futures hedge mode) */}
                  {isStockEquity && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        onClick={() => setNewBot(prev => ({ ...prev, allowShorts: !prev.allowShorts }))}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                      >
                        <Chip
                          label={newBot.allowShorts ? 'ON' : 'OFF'}
                          size="small"
                          color={newBot.allowShorts ? 'info' : 'default'}
                          variant={newBot.allowShorts ? 'filled' : 'outlined'}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>Allow Shorts</Typography>
                          <Typography variant="caption" color="text.secondary">
                              Enable short-side conditions. The symbol must be easy-to-borrow (ETB) and your Alpaca account must be a margin account with shorting enabled.

                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  <StrategyBuilder
                    buyConditions={buyConditions}
                    sellConditions={sellConditions}
                    onBuyConditionsChange={setBuyConditions}
                    onSellConditionsChange={setSellConditions}

                    // ✅ Hedge mode short conditions
                    shortOpenConditions={shortOpenConditions}
                    shortCloseConditions={shortCloseConditions}
                    onShortOpenConditionsChange={setShortOpenConditions}
                    onShortCloseConditionsChange={setShortCloseConditions}

                    // ✅ Market context
                    isStock={isStockEquity}
                    marketType={newBot.marketType}
                    positionMode={
                      isStockEquity
                        ? (newBot.allowShorts ? 'hedge' : 'one_way')
                        : newBot.positionSide
                    }
                    assetClass={assetClass}

                    isTickMode={isTickInterval}
                    profitStop={profitStop}
                    lossStop={lossStop}
                    onProfitStopChange={setProfitStop}
                    onLossStopChange={setLossStop}

                    // Raw-expression escape hatch — multipliers and if/else.
                    // Passing these is what makes the toggle appear at all.
                    logicMode={wizLogicMode}
                    onLogicModeChange={setWizLogicMode}
                    rawLogic={wizRawLogic}
                    onRawLogicChange={setWizRawLogic}
                    forcedExpression={wizForcedExpression}
                  />
                  {/* Remove the Grid container wrapper - StrategyBuilder has its own layout */}
                </Box>
              )}


              {activeStep === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max Daily Loss (%)"
                      value={newBot.maxLoss}
                      onChange={e => setNewBot({ ...newBot, maxLoss: e.target.value })}
                      fullWidth
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max Trades Per Day"
                      value={newBot.maxTrades}
                      onChange={e => setNewBot({ ...newBot, maxTrades: e.target.value })}
                      fullWidth
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Trailing Stop (%)"
                      value={newBot.trailingStop}
                      onChange={e => setNewBot({ ...newBot, trailingStop: e.target.value })}
                      fullWidth
                      type="number"
                    />
                  </Grid>
                  {/* Per-position exits, matching the backtest's risk config field
                      for field. These travel as trade_info.risk — a block the
                      backtest already reads (simulateOHLCV's precedence chain), so
                      a bot created here backtests with exactly the exits it will
                      trade with. Distinct from Profit/Loss Stop above the builder,
                      which pause the whole BOT at a cumulative P&L level. */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Per-trade exits
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Applied to each position, measured from its entry price. Blank = disabled.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Stop Loss (% per trade)"
                      value={newBot.riskStopLossPct}
                      onChange={e => setNewBot({ ...newBot, riskStopLossPct: e.target.value })}
                      fullWidth type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Take Profit (% per trade)"
                      value={newBot.riskTakeProfitPct}
                      onChange={e => setNewBot({ ...newBot, riskTakeProfitPct: e.target.value })}
                      fullWidth type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max Bars in Trade"
                      value={newBot.riskMaxBarsInTrade}
                      onChange={e => setNewBot({ ...newBot, riskMaxBarsInTrade: e.target.value })}
                      helperText={`Exit after this many ${newBot.interval || ''} bars in a position`}
                      fullWidth type="number"
                    />
                  </Grid>
                  {isStockEquity && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Flatten Before Close (min)"
                        value={newBot.riskFlattenBeforeCloseMin}
                        onChange={e => setNewBot({ ...newBot, riskFlattenBeforeCloseMin: e.target.value })}
                        helperText="Close any open position this many minutes before the session ends"
                        fullWidth type="number"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Strategy Summary
                      </Typography>
                      <Stack spacing={1}>
                         <Typography variant="body2"><strong>Name:</strong> {newBot.name || 'Untitled'}</Typography>
                        <Typography variant="body2"><strong>Pair:</strong> {newBot.symbol || '(from webhook)'}</Typography>
                        <Typography variant="body2">
                          <strong>Market:</strong>{' '}
                          {isStockEquity
                            ? `Stock Equity${newBot.extendedHours ? ' (Extended Hours)' : ''}${newBot.allowShorts ? ' • Long+Short' : ''}`
                            : (newBot.marketType === 'futures' ? `Futures ${newBot.leverage}x` : 'Spot')}
                        </Typography>
                        <Typography variant="body2"><strong>Signal:</strong> {newBot.signalSource === 'tradingview' ? 'TradingView Webhook' : 'Internal Logic'}</Typography>

                        {newBot.signalSource !== 'tradingview' && (
                          <>
                            {/* Must read the same source handleSave will, or the
                                review step shows "None" for an expression bot
                                and confirms a strategy that isn't the one saved. */}
                            <Typography variant="body2">
                              <strong>{newBot.marketType === 'futures' && newBot.positionSide === 'hedge' ? 'Open Long:' : 'Buy Logic:'}</strong>{' '}
                              {(isWizExpression ? wizRawLogic.buy : buildLogicFromConditions(buyConditions)) || 'None'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{newBot.marketType === 'futures' && newBot.positionSide === 'hedge' ? 'Close Long:' : 'Sell Logic:'}</strong>{' '}
                              {(isWizExpression ? wizRawLogic.sell : buildLogicFromConditions(sellConditions)) || 'None'}
                            </Typography>
                            {((newBot.marketType === 'futures' && newBot.positionSide === 'hedge') ||
                              (isStockEquity && newBot.allowShorts)) && (
                              <>
                                <Typography variant="body2"><strong>Open Short:</strong> {(isWizExpression ? wizRawLogic.shortOpen : buildLogicFromConditions(shortOpenConditions)) || 'None'}</Typography>
                                <Typography variant="body2"><strong>Close Short:</strong> {(isWizExpression ? wizRawLogic.shortClose : buildLogicFromConditions(shortCloseConditions)) || 'None'}</Typography>
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* Webhook Configuration Section - Only for TradingView bots */}



              {/* Phase B — PDT warning for stock_equity, only on the final step */}
              {activeStep === steps.length - 1 && (
                <ShortEligibilityWarning
                  assetClass={assetClass}
                  allowShorts={newBot.allowShorts}
                  accountStatus={accountStatusForSelected}
                />
              )}


              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  onClick={activeStep === 0 ? handleCancelCreate : handleBack}
                  variant="outlined"
                >
                  {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>
                <Box>
                  {activeStep < steps.length - 1 && (
                    <Button
                      onClick={handleNext}
                      variant="contained"
                      disableElevation
                      sx={{ ml: 1, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'linear-gradient(180deg,#436686,#395a78)' } }}
                    >
                      Next
                    </Button>
                  )}
                  {activeStep === steps.length - 1 && (
                    <>
                      <Button
                        onClick={() => handleSave(true)}
                        variant="outlined"
                        sx={{ ml: 1, borderColor: 'grey.600', color: 'grey.300', textTransform: 'none', '&:hover': { borderColor: 'grey.500', bgcolor: 'rgba(255,255,255,0.05)' } }}
                      >
                        Save (Paused)
                      </Button>
                      {botReadiness.ready ? (
                        // A new strategy cannot have been backtested, so there is
                        // no honest "activate now" for one. Bots whose signals come
                        // from outside the engine (webhook) have nothing to
                        // backtest and keep the direct path.
                        newBot.signalSource === 'tradingview' ? (
                          <Button
                            onClick={() => handleSave(false)}
                            variant="contained"
                            disableElevation
                            sx={{ ml: 1, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'linear-gradient(180deg,#436686,#395a78)' } }}
                          >
                            Save &amp; Activate
                          </Button>
                        ) : (
                          <Tooltip title="New strategies are saved paused. You will be taken to the backtest — activate once it has been tested." arrow>
                            <Button
                              onClick={() => handleSave(true)}
                              variant="contained"
                              disableElevation
                              sx={{ ml: 1, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'linear-gradient(180deg,#436686,#395a78)' } }}
                            >
                              Save &amp; backtest
                            </Button>
                          </Tooltip>
                        )
                      ) : (
                        <Tooltip title={`Next: ${botReadiness.firstMissing?.label || 'finish the remaining steps'}`} arrow>
                          <Button
                            onClick={() => botReadiness.firstMissing && setActiveStep(botReadiness.firstMissing.step)}
                            variant="outlined"
                            sx={{ ml: 1, textTransform: 'none', color: AT.muted, borderColor: 'rgba(255,255,255,.14)' }}
                          >
                            Finish setup to activate
                          </Button>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
              </Box>
                </Box>
                <Box sx={{ position: { md: 'sticky' }, top: 16, alignSelf: 'flex-start' }}>
                  <StrategyRail
                    readiness={botReadiness}
                    newBot={newBot}
                    buyConditions={buyConditions}
                    sellConditions={sellConditions}
                    expressionMode={isWizExpression}
                    profitStop={profitStop}
                    lossStop={lossStop}
                    exchangeLabel={(() => { const e = userExchanges.find((x) => x.id === newBot.exchangeId); return e ? (e.name || e.exchange) : ''; })()}
                    onJump={(step) => setActiveStep(step)}
                  />
                </Box>
              </Box>
        )}

        {activeTab === 'bots' && (
          <Box>
            {/* AI-first entry: the primary way to create a bot. Manual and
                Quick Bot remain below as secondary paths. */}
            {!isCreating && (
              <Paper elevation={0} sx={{ p: '20px 22px', mb: 2, border: '1px solid rgba(77,143,199,.28)', borderRadius: '14px', bgcolor: 'rgba(77,143,199,.06)' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: AT.bright, mb: '4px' }}>
                  Describe your strategy
                </Typography>
                <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <Box component="input"
                    value={aiKickoffPrompt}
                    onChange={(e) => setAiKickoffPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !aiKickoffLoading) startAiKickoff(); }}
                    placeholder='e.g. "buy oversold bounces on rising volume, exit when momentum fades"'
                    sx={{ flex: 1, minWidth: 260, fontFamily: AT_SANS, fontSize: '14px', color: AT.bright, background: AT.inner, border: '1px solid rgba(255,255,255,.1)', borderRadius: '9px', px: '15px', py: '12px', outline: 'none', '&:focus': { borderColor: 'rgba(77,143,199,.5)' } }} />
                  <Box component="button"
                    disabled={aiKickoffLoading || !aiKickoffPrompt.trim()}
                    onClick={startAiKickoff}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: AT_SANS, fontSize: '14px', fontWeight: 600, color: '#e7eef6', background: (aiKickoffLoading || !aiKickoffPrompt.trim()) ? 'rgba(255,255,255,.08)' : 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '22px', py: '12px', borderRadius: '9px', cursor: (aiKickoffLoading || !aiKickoffPrompt.trim()) ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                    {aiKickoffLoading ? 'Drafting…' : 'Build with AI'}
                  </Box>
                </Box>
              </Paper>
            )}

            {!isCreating && (
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  disableElevation
                  data-tour="create-bot"
                  startIcon={<Bolt />}
                  onClick={handleQuickBot}
                  sx={{ color: '#06121f', bgcolor: AT.accent, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#5a9bd0' } }}
                >
                  Quick Bot
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={handleCreate}
                  sx={{ textTransform: 'none' }}
                >
                  Create New Bot
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<History />}
                  onClick={() => setActiveTab('backtest')}
                  sx={{ textTransform: 'none' }}
                >
                  Backtesting
                </Button>
              </Stack>
            )}

            {bots.length === 0 ? (
              <Paper elevation={0} sx={{
                p: 4,
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 2
              }}>
                <Typography variant="h6" gutterBottom>No trading bots created yet</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Launch a ready-made strategy with Quick Bot, or build your own from scratch
                </Typography>
                <Stack direction="row" spacing={1.5} justifyContent="center">
                  <Button
                    variant="contained"
                    disableElevation
                    data-tour="create-bot"
                    startIcon={<Bolt />}
                    onClick={handleQuickBot}
                    sx={{ color: '#06121f', bgcolor: AT.accent, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#5a9bd0' } }}
                  >
                    Quick Bot
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutline />}
                    onClick={handleCreate}
                    sx={{ textTransform: 'none' }}
                  >
                    Build manually
                  </Button>
                </Stack>
              </Paper>

            ) : (
              <>
                {/* Manual / Scanner sub-tabs (handoff §5) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '26px', borderBottom: `1px solid rgba(255,255,255,.07)`, mb: 2.5, flexWrap: 'wrap' }}>
                  {(() => {
                    const onManual = botListTab === 'manual';
                    const onScanner = botListTab === 'scanner';
                    const scN = groupedBots.scannerProfiles.length;
                    return (
                      <>
                        <Box onClick={() => setBotListTab('manual')} sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '2px', pb: '12px', cursor: 'pointer', fontSize: '14.5px', fontWeight: 600, color: onManual ? '#fff' : AT.body, borderBottom: `2px solid ${onManual ? AT.accent : 'transparent'}`, mb: '-1px' }}>
                          Manual Bots
                          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '12px', fontWeight: 600, color: '#cdd6e8', background: 'rgba(255,255,255,.08)', px: '9px', py: '2px', borderRadius: '999px' }}>{groupedBots.manual.length}</Box>
                        </Box>
                        <Box onClick={() => setBotListTab('scanner')} sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '2px', pb: '12px', cursor: 'pointer', fontSize: '14.5px', fontWeight: 600, color: onScanner ? '#fff' : AT.body, borderBottom: `2px solid ${onScanner ? AT.accent : 'transparent'}`, mb: '-1px' }}>
                          Scanner Bots
                          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11.5px', fontWeight: 600, color: '#9cc3e4', background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)', px: '9px', py: '2px', borderRadius: '999px' }}>{scN} {scN === 1 ? 'Profile' : 'Profiles'}</Box>
                        </Box>
                      </>
                    );
                  })()}
                </Box>

                {/* Manual Bots tab */}
                {botListTab === 'manual' && (
                  <Stack spacing={2}>
                    {groupedBots.manual.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{ p: 4, textAlign: 'center', borderColor: 'rgba(255,255,255,0.06)', bgcolor: '#0f1218' }}
                      >
                        <Settings sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
                        <Typography variant="body1" sx={{ color: '#e8edf7' }}>
                          No manual bots yet
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                          Manually-created bots will appear here. Bots spawned by scanners are in the Scanner Bots tab.
                        </Typography>
                      </Paper>
                    ) : (
                      groupedBots.manual.map(bot => renderBotCard(bot))
                    )}
                  </Stack>
                )}

                {/* Scanner Bots tab */}
                {botListTab === 'scanner' && (
                  <Stack spacing={2}>
                    {groupedBots.scannerProfiles.length === 0 && groupedBots.orphanedSpawned.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{ p: 4, textAlign: 'center', borderColor: 'rgba(255,255,255,0.06)', bgcolor: '#0f1218' }}
                      >
                        <ShowChart sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
                        <Typography variant="body1" sx={{ color: '#e8edf7' }}>
                          No scanner profiles yet
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9aa6bd', display: 'block', mb: 2 }}>
                          Create a scanner profile to automatically spawn bots when conditions are met.
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.location.href = '/scanner'}
                          sx={{ borderColor: 'rgba(255,255,255,0.1)', color: '#e8edf7' }}
                        >
                          Open Scanner
                        </Button>
                      </Paper>
                    ) : (
                      <>
                        {groupedBots.scannerProfiles.map(profile =>
                          renderScannerProfileGroup(profile, groupedBots.spawnedByProfile.get(profile.id) || [])
                        )}

                        {groupedBots.orphanedSpawned.length > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderColor: 'rgba(255,255,255,0.1)',
                              bgcolor: '#0f1218',
                              borderStyle: 'dashed',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <WarningAmber sx={{ color: '#d9a441', fontSize: 20 }} />
                              <Typography variant="subtitle2" sx={{ color: '#d9a441' }}>
                                Orphaned spawned bots ({groupedBots.orphanedSpawned.length})
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#9aa6bd', ml: 1 }}>
                                Parent scanner profile was deleted
                              </Typography>
                            </Box>
                            <Stack spacing={1.5}>
                              {groupedBots.orphanedSpawned.map(bot => renderBotCard(bot))}
                            </Stack>
                          </Paper>
                        )}
                      </>
                    )}
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}

        {activeTab === 'backtest' && (
          <Box>
            <Paper elevation={0} sx={{ p: '20px 22px', mb: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', bgcolor: AT.card }}>
              {(() => {
                const { quote: BT_QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
                const btDays = Math.max(1, Math.round((backtestDateRange[1] - backtestDateRange[0]) / MS_DAY));
                const btCaption = `${format(new Date(backtestDateRange[0]), 'MMM d, yyyy')} – ${format(new Date(backtestDateRange[1]), 'MMM d, yyyy')} · ${btDays} days`;
                const check = selectedBotForBacktest ? gateBacktest(selectedBotForBacktest) : { supported: true, unsupported: [] };
                const disabled = !selectedBotForBacktest || isBacktesting || !check.supported;
                return (
                  <>
                    {btResearchTab !== 'explore' && (<>
                    {/* compact config bar (§7a) */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '18px', flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Box component="label" sx={labelSx}>BACKTEST</Box>
                        {/* MUI Select rather than a native <select>: native
                            <option> can only hold text, so it couldn't show the
                            venue logo / account chip the bot and scanner
                            pickers use. Styled to match inputSx. */}
                        <Select
                          value={selectedBotForBacktest?.id ?? ''}
                          onChange={(e) => {
                            const id = e.target.value;
                            const hit = backtestableBots.find(b => String(b.id) === String(id));
                            if (hit) setSelectedBotForBacktest(hit);
                          }}
                          displayEmpty
                          renderValue={(val) => {
                            const bot = String(selectedBotForBacktest?.id ?? '') === String(val)
                              ? selectedBotForBacktest
                              : backtestableBots.find(b => String(b.id) === String(val));
                            if (!bot) return 'Select a bot…';
                            const ex = userExchanges.find(e => e.id === bot.exchangeId);
                            const venue = baseExchangeLabel(ex || { exchange: bot.exchange });
                            return [
                              `${bot.name}${bot.symbol ? ` (${bot.symbol})` : ''}`,
                              venue,
                              ex ? getAccountMeta(ex).chipLabel : null,
                            ].filter(Boolean).join(' · ');
                          }}
                          MenuProps={{ PaperProps: { sx: { bgcolor: AT.card, border: '1px solid rgba(255,255,255,.1)', '& .MuiMenuItem-root': { color: AT.body } } } }}
                          sx={{
                            ...inputSx, p: 0, fontFamily: AT_SANS, fontWeight: 600,
                            '& .MuiSelect-select': { p: '12px 15px' },
                            '& fieldset': { border: 'none' },
                            '& .MuiSelect-icon': { color: AT.muted },
                          }}
                        >
                          {/* A bot reconstructed from a saved backtest may not be in
                              the live list (deleted, or the record has no bot_id).
                              Without its own entry the select renders blank and the
                              page looks like nothing loaded. */}
                          {selectedBotForBacktest
                            && !backtestableBots.some(b => String(b.id) === String(selectedBotForBacktest.id))
                            && (
                              <MenuItem value={selectedBotForBacktest.id ?? ''}>
                                <ExchangeOptionRow
                                  dark
                                  exchange={userExchanges.find(e => e.id === selectedBotForBacktest.exchangeId) || { exchange: selectedBotForBacktest.exchange }}
                                  primary={`${selectedBotForBacktest.name}${selectedBotForBacktest.symbol ? ` (${selectedBotForBacktest.symbol})` : ''}`}
                                  secondaryExtra="from saved backtest"
                                />
                              </MenuItem>
                            )}
                          {backtestableBots.map(b => (
                            <MenuItem key={b.id} value={b.id}>
                              <ExchangeOptionRow
                                dark
                                exchange={userExchanges.find(e => e.id === b.exchangeId) || { exchange: b.exchange }}
                                primary={`${b.name}${b.symbol ? ` (${b.symbol})` : ''}`}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      <Box>
                        <Box component="label" sx={labelSx}>PERIOD</Box>
                        <Box sx={{ display: 'flex', gap: '6px' }}>
                          {['1M', '3M', '6M', '1Y', 'YTD'].map((k) => {
                            const on = btRange === k;
                            return (
                              <Box key={k} onClick={() => {
                                setBtRange(k);
                                const end = endOfTodayMs();
                                const start = k === 'YTD' ? new Date(new Date(end).getFullYear(), 0, 1).getTime() : monthsAgoMs({ '1M': 1, '3M': 3, '6M': 6, '1Y': 12 }[k]);
                                setBacktestDateRange([Math.max(rangeBounds.min, start), Math.min(rangeBounds.max, end)]);
                              }} sx={{ fontFamily: AT_MONO, fontSize: '12px', fontWeight: 600, px: '13px', py: '9px', borderRadius: '8px', cursor: 'pointer', transition: 'all .15s ease', color: on ? AT.accentText : AT.body, background: on ? 'rgba(77,143,199,.12)' : AT.inner, border: `1px solid ${on ? 'rgba(77,143,199,.38)' : 'rgba(255,255,255,.1)'}` }}>{k}</Box>
                            );
                          })}
                        </Box>
                      </Box>
                      <Box sx={{ width: 150 }}>
                        <Box component="label" sx={labelSx}>CAPITAL ({BT_QUOTE || 'USD'})</Box>
                        <Box component="input" type="number" value={backtestParams.initialCapital}
                          onChange={(e) => setBacktestParams((p) => ({ ...p, initialCapital: Number(e.target.value) || 0 }))}
                          sx={inputSx} />
                      </Box>
                      {/* Explore does not run single backtests — its results paper is
                          hidden there, so a Run button would produce nothing visible.
                          A bot in Explore is a strategy seed; breadth does the testing. */}
                      {btResearchTab !== 'explore' && (
                      <Tooltip title={!selectedBotForBacktest ? 'Select a bot first' : (check.supported ? '' : check.message)} arrow>
                        <Box component="span" sx={{ display: 'inline-block' }}>
                          <Box component="button" disabled={disabled}
                            onClick={() => selectedBotForBacktest && runBacktest(selectedBotForBacktest)}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: '9px', fontFamily: AT_SANS, fontSize: '14px', fontWeight: 600, color: '#e7eef6', background: disabled ? 'rgba(255,255,255,.08)' : 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '26px', py: '13px', borderRadius: '9px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, transition: 'transform .2s', '&:hover': disabled ? {} : { transform: 'translateY(-1px)' } }}>
                            <PlayArrow sx={{ fontSize: 17 }} /> {isBacktesting ? 'Running…' : 'Run backtest'}
                          </Box>
                        </Box>
                      </Tooltip>
                      )}
                    </Box>

                    {/* range caption + deployment state + panel toggles */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: '14px', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <Mono sx={{ fontSize: '11.5px', color: AT.muted }}>{btCaption}</Mono>
                        {selectedBotForBacktest && (() => {
                          const live = selectedBotForBacktest.status === 'active';
                          return (
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: AT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.08em', px: '9px', py: '4px', borderRadius: '6px', color: live ? '#7ee2b8' : '#d9a441', background: live ? 'rgba(126,226,184,.1)' : 'rgba(217,164,65,.1)', border: `1px solid ${live ? 'rgba(126,226,184,.32)' : 'rgba(217,164,65,.32)'}` }}>
                              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', background: live ? '#7ee2b8' : '#d9a441' }} />
                              {live ? 'DEPLOYED — RUNNING LIVE' : 'NOT DEPLOYED — PAUSED'}
                            </Box>
                          );
                        })()}
                        {btDraftDirty && (
                          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.08em', px: '9px', py: '4px', borderRadius: '6px', color: '#9cc3e4', background: 'rgba(77,143,199,.1)', border: '1px solid rgba(77,143,199,.32)' }}>
                            UNSAVED CHANGES
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '16px' }}>
                        {selectedBotForBacktest && (
                          <Box onClick={() => setBtEditOpen((o) => !o)} sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', fontWeight: 600, color: '#9cc3e4', cursor: 'pointer' }}>
                            <Settings sx={{ fontSize: 14 }} />{btEditOpen ? 'Hide reconfigure' : 'Show reconfigure'}
                          </Box>
                        )}
                        <Box onClick={() => setBtAdvOpen((o) => !o)} sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', fontWeight: 600, color: '#9cc3e4', cursor: 'pointer' }}>
                          <Settings sx={{ fontSize: 14 }} />{btAdvOpen ? 'Hide advanced' : 'Show advanced'}
                        </Box>
                      </Box>
                    </Box>

                    {/* Advanced params (commission / slippage / lookback).
                        Kept ABOVE the reconfigure panel: this is a compact
                        three-column row, reconfigure is a tall block, and with
                        reconfigure open by default the reverse order pushed
                        these controls so far down they read as unavailable. */}
                    <Collapse in={btAdvOpen}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1.4fr' }, gap: '16px', mt: '16px', pt: '16px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                        <Box>
                          <Box component="label" sx={labelSx}>COMMISSION (%)</Box>
                          <Box component="input" type="number" step="0.001" min="0" value={backtestParams.commission}
                            onChange={(e) => { setBtCostsTouchedFor(btCostsContextKey); setBacktestParams((p) => ({ ...p, commission: Number(e.target.value) || 0 })); }}
                            sx={inputSx} />
                        </Box>
                        <Box>
                          <Box component="label" sx={labelSx}>SLIPPAGE (%)</Box>
                          <Box component="input" type="number" step="0.001" min="0" value={backtestParams.slippage}
                            onChange={(e) => { setBtCostsTouchedFor(btCostsContextKey); setBacktestParams((p) => ({ ...p, slippage: Number(e.target.value) || 0 })); }}
                            sx={inputSx} />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px', fontFamily: AT_MONO, fontSize: '10px', letterSpacing: '.1em', color: AT.muted2 }}>
                            <span>LOOKBACK WINDOW</span><span style={{ color: '#cdd6e8' }}>{btDays} days</span>
                          </Box>
                          {/* 1825, matching the 5-year date-picker bounds — this slider and
                              the pickers drive the same backtestDateRange, and the pair
                              disagreed: pickers reached 5 years back while the slider
                              (missed in the first pass because its attrs are strings, not
                              JSX numbers) still stopped at 365 and CLAMPED the readout. */}
                          <Box component="input" type="range" min="7" max="1825"
                            value={Math.min(1825, Math.max(7, btDays))}
                            onChange={(e) => { const days = Number(e.target.value); const end = backtestDateRange[1]; setBtRange(''); setBacktestDateRange([Math.max(rangeBounds.min, end - days * MS_DAY), end]); }}
                            sx={{ width: '100%', accentColor: AT.accent, cursor: 'pointer', mt: '8px' }} />
                        </Box>
                      </Box>

                      {/* Risk exits. Until these existed the sell expression was
                          the only way out of a position, so one trade that never
                          met its exit condition could give back many wins. */}
                      <Box sx={{ mt: '18px', pt: '16px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                        <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, mb: '4px' }}>
                          RISK EXITS
                        </Mono>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: '16px' }}>
                          <Box>
                            <Box component="label" sx={labelSx}>STOP LOSS (%)</Box>
                            <Box component="input" type="number" step="0.1" min="0" placeholder="off"
                              value={backtestParams.stopLossPct}
                              onChange={(e) => setBacktestParams((p) => ({ ...p, stopLossPct: e.target.value }))}
                              sx={inputSx} />
                          </Box>
                          <Box>
                            <Box component="label" sx={labelSx}>TAKE PROFIT (%)</Box>
                            <Box component="input" type="number" step="0.1" min="0" placeholder="off"
                              value={backtestParams.takeProfitPct}
                              onChange={(e) => setBacktestParams((p) => ({ ...p, takeProfitPct: e.target.value }))}
                              sx={inputSx} />
                          </Box>
                          <Box>
                            <Box component="label" sx={labelSx}>TRAILING STOP (%)</Box>
                            <Box component="input" type="number" step="0.1" min="0" placeholder="off"
                              value={backtestParams.trailingStopPct}
                              onChange={(e) => setBacktestParams((p) => ({ ...p, trailingStopPct: e.target.value }))}
                              sx={inputSx} />
                          </Box>
                          <Box>
                            <Box component="label" sx={labelSx}>MAX BARS HELD</Box>
                            <Box component="input" type="number" step="1" min="0" placeholder="off"
                              value={backtestParams.maxBarsInTrade}
                              onChange={(e) => setBacktestParams((p) => ({ ...p, maxBarsInTrade: e.target.value }))}
                              sx={inputSx} />
                          </Box>
                        </Box>
                        {btDraftIsStock && (
                          <Box sx={{ mt: '16px' }}>
                            <Box component="label" sx={labelSx}>FLATTEN BEFORE CLOSE (MIN)</Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 3fr' }, gap: '12px', alignItems: 'center' }}>
                              <Box component="input" type="number" step="1" min="0" placeholder="off"
                                value={backtestParams.flattenBeforeCloseMin}
                                onChange={(e) => setBacktestParams((p) => ({ ...p, flattenBeforeCloseMin: e.target.value }))}
                                sx={inputSx} />
                              <Mono sx={{ fontSize: '11px', color: AT.muted }}>
                                Closes any open position this many minutes before the session ends,
                                using the real calendar — an early close counts down to 13:00, not 16:00.
                                A minute-scale signal expresses no view on overnight risk, so holding
                                through the close is exposure the strategy never asked for.
                              </Mono>
                            </Box>
                          </Box>
                        )}

                        {/* A bar can trade through both levels; we can't see the
                            intrabar path, so say which way the tie is broken. */}
                        {(Number(backtestParams.stopLossPct) > 0 || Number(backtestParams.trailingStopPct) > 0) &&
                         Number(backtestParams.takeProfitPct) > 0 && (
                          <Mono sx={{ fontSize: '11px', color: AT.muted, mt: '10px', display: 'block' }}>
                            If one bar reaches both the stop and the target, the run books the stop —
                            results stay conservative rather than assuming the favourable fill.
                          </Mono>
                        )}

                        {/* The live bot has no stop-loss support yet. Backtesting
                            with one and deploying without it would overstate the
                            deployed strategy's downside, so say so here rather
                            than letting the numbers imply protection. */}
                        {buildRiskConfig() && (
                          // The design system's caution amber, not a one-off orange —
                          // the same honesty colour the result card uses.
                          <Box sx={{ display: 'flex', gap: '8px', mt: '12px', p: '10px 12px', borderRadius: '10px',
                                     background: 'rgba(217,164,65,.06)', border: '1px solid rgba(217,164,65,.3)' }}>
                            <Mono sx={{ fontSize: '11px', color: '#d3b988', lineHeight: 1.5 }}>
                              Backtest only. Live bots don&apos;t place stop or target orders yet — a strategy
                              deployed from this run will exit on its sell logic alone, so expect deeper
                              drawdown live.
                            </Mono>
                          </Box>
                        )}
                      </Box>

                      {/* Where the cost defaults came from, and a way back to them. */}
                      {selectedBotForBacktest && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '12px', flexWrap: 'wrap' }}>
                          <Mono sx={{ fontSize: '11px', color: AT.muted }}>
                            {btCostsAreCustom
                              ? 'Custom costs — venue defaults overridden'
                              : `Defaults for ${backtestCostNote(selectedBotForBacktest)}`}
                          </Mono>
                          {btCostsAreCustom && (
                            <Box
                              onClick={() => {
                                const { commission, slippage } = defaultBacktestCosts(selectedBotForBacktest);
                                setBacktestParams((p) => ({ ...p, commission, slippage }));
                                setBtCostsTouchedFor(null);
                              }}
                              sx={{ fontSize: '11px', fontWeight: 600, color: '#9cc3e4', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                              Reset to venue defaults
                            </Box>
                          )}
                        </Box>
                      )}
                    </Collapse>
                    </>)}

                    {/* Reconfigure: timeframe + entry/exit logic only. Edits are a
                        local draft — "Run backtest" uses them, "Deploy" saves them. */}
                    {/* Outside the collapse below: that collapse is gated on a selected bot,
                        and Explore deliberately does not need one — leaving the switcher
                        inside it meant you could not reach Explore without first picking a
                        bot, which is the exact dependency being removed. */}
                    {/* Mode pills. Two words, not two sentences: the labels used to read
                        OPTIMISE THIS BOT / EXPLORE SYMBOLS, which is the hint doing the
                        label's job. The hint moved to the title, and the context line
                        beside them says which bot "this" refers to — the thing the long
                        labels were really trying to convey. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '16px', flexWrap: 'wrap' }}>
                    {[['optimize', 'OPTIMIZE', 'Improve the logic this bot already runs'],
                    ['explore', 'EXPLORE', 'Find a symbol worth trading, then start a bot on it']].map(([id, lbl, hint]) => (
                    <BtChip key={id}
                    title={hint}
                    active={btResearchTab === id}
                    onClick={() => {
                    if (id === btResearchTab) return;
                    setBtResearchTab(id);
                    // The tuner's results swap with the tab: Optimize and
                    // Explore tune different symbols, and carrying one's sweep
                    // under the other's heading is a wrong answer, not a
                    // convenience. No auto-plan here — see swapSweepContext.
                    swapSweepContext(sweepStashKey(btResearchTab, btSweepKind), sweepStashKey(id, btSweepKind));
                    }}>
                    {lbl}
                    </BtChip>
                    ))}
                    {selectedBotForBacktest && (
                      <Num size={11} color={BT.faint} sx={{ ml: '8px' }}>
                        {[selectedBotForBacktest.symbol,
                          btDraft?.timeframe,
                          selectedBotForBacktest.exchange,
                          selectedBotForBacktest.simulated ? 'paper' : 'live',
                        ].filter(Boolean).join(' · ')}
                        {/* Whether this bot is TRADING is the one fact a user
                            must never have to infer from button labels —
                            especially right after Save & backtest, where the
                            answer is "no, not until you deploy it". */}
                        <Box component="span" sx={{
                          ml: '8px', px: '7px', py: '2px', borderRadius: '5px',
                          fontSize: '9.5px', fontWeight: 700, letterSpacing: '.06em',
                          color: selectedBotForBacktest.status === 'active' ? '#0d1f12' : '#e7d5ad',
                          background: selectedBotForBacktest.status === 'active' ? AT.positive : 'rgba(217,164,65,.18)',
                          border: selectedBotForBacktest.status === 'active' ? 'none' : '1px solid rgba(217,164,65,.4)',
                        }}>
                          {selectedBotForBacktest.status === 'active' ? 'TRADING' : 'PAUSED'}
                        </Box>
                      </Num>
                    )}
                    </Box>

                    <Collapse in={btEditOpen && (!!selectedBotForBacktest || btResearchTab === 'explore')}>
                      {(btDraft || btResearchTab === 'explore') && (
                        <Box sx={{ mt: '16px', pt: '16px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                          {btResearchTab !== 'explore' && (<>
                          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '18px', flexWrap: 'wrap', mb: '16px' }}>
                            <Box sx={{ width: 170 }}>
                              <Box component="label" sx={labelSx}>TIMEFRAME</Box>
                              {/* btDraft is NULL in Explore with no bot selected — that is the
                                  point of Explore, and the guard above admits it deliberately.
                                  Reading .timeframe off it crashed the whole dashboard the
                                  moment you clicked Explore before picking a bot. Optional
                                  everywhere in this block, and setBtDraft seeds an object
                                  rather than spreading null. */}
                              <Box component="select"
                                value={btDraft?.timeframe || ''}
                                onChange={(e) => setBtDraft(d => ({ ...(d || {}), timeframe: e.target.value }))}
                                sx={{ ...inputSx, cursor: 'pointer', appearance: 'none' }}>
                                {!btTimeframeOptions.includes(btDraft?.timeframe) && btDraft?.timeframe && (
                                  <option value={btDraft?.timeframe}>{btDraft?.timeframe}</option>
                                )}
                                {btTimeframeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                              </Box>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 220 }}>
                              <Mono sx={{ fontSize: '11px', color: AT.muted2 }}>
                                {selectedBotForBacktest?.symbol} · {selectedBotForBacktest?.exchangeName || '—'}
                              </Mono>
                            </Box>
                            <Box sx={{ display: 'inline-flex', gap: '9px', flexWrap: 'wrap' }}>
                              <Box component="button"
                                disabled={!btDraftDirty || btDeploying}
                                onClick={() => setBtDraft({
                                  botId: selectedBotForBacktest.id,
                                  timeframe: String(selectedBotForBacktest.interval || '').toLowerCase(),
                                  // Revert on an expression bot must restore the
                                  // expression, not the builder's reading of it.
                                  ...draftLogicFields(
                                    selectedBotForBacktest.logic?.buy,
                                    selectedBotForBacktest.logic?.sell,
                                  ),
                                })}
                                sx={{ fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: '#9cc3e4', background: 'transparent', border: '1px solid rgba(77,143,199,.3)', px: '18px', py: '11px', borderRadius: '9px', cursor: (!btDraftDirty || btDeploying) ? 'default' : 'pointer', opacity: (!btDraftDirty || btDeploying) ? 0.45 : 1 }}>
                                Revert
                              </Box>
                              <Box component="button"
                                disabled={btDeploying}
                                onClick={deployDraftAsNewBot}
                                sx={{ fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: '#9cc3e4', background: 'transparent', border: '1px solid rgba(77,143,199,.3)', px: '18px', py: '11px', borderRadius: '9px', cursor: btDeploying ? 'default' : 'pointer', opacity: btDeploying ? 0.45 : 1, whiteSpace: 'nowrap' }}>
                                Deploy as new bot
                              </Box>
                              <Box component="button"
                                disabled={btDeploying || (!btDraftDirty && selectedBotForBacktest?.status === 'active')}
                                onClick={() => deployDraft(false)}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: AT_SANS, fontSize: '13.5px', fontWeight: 600, color: '#e7eef6', background: (btDeploying || (!btDraftDirty && selectedBotForBacktest?.status === 'active')) ? 'rgba(255,255,255,.08)' : 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '20px', py: '11px', borderRadius: '9px', cursor: btDeploying ? 'default' : 'pointer', opacity: btDeploying ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                                <PlayArrow sx={{ fontSize: 15 }} />
                                {/* One name through the whole flow (handoff §6): the dirty
                                    state changes what gets saved, not what the action is. */}
                                {btDeploying
                                  ? 'Deploying…'
                                  : selectedBotForBacktest?.status === 'active'
                                    ? (btDraftDirty ? 'Update live bot' : 'Already deployed')
                                    : 'Deploy as live bot'}
                              </Box>
                            </Box>
                          </Box>

                          {/* Deployment gate — shown only when a deploy was
                              blocked. Everything else here produces
                              information; this is the one thing that produces a
                              decision, which is what makes the rest binding. */}
                          {btGate && !btGate.passed && (
                            <Box sx={{ mb: '16px', p: '16px 18px', borderRadius: '10px', background: 'rgba(224,90,90,.08)', border: '1px solid rgba(224,90,90,.35)' }}>
                              <Mono sx={{ fontSize: '10px', letterSpacing: '.12em', color: AT.negative, display: 'block', mb: '4px' }}>
                                DEPLOY BLOCKED
                              </Mono>
                              <Typography sx={{ fontSize: '13px', color: '#e7d5ad', lineHeight: 1.55, mb: '12px' }}>
                                This strategy has not cleared the checks below. You can still deploy it, but say why —
                                the reason is stored on the bot.
                              </Typography>

                              {(btGate.checks || []).map((c) => (
                                <Box key={c.id} sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', py: '4px' }}>
                                  <Mono sx={{ fontSize: '10.5px', fontWeight: 700, color: c.passed ? AT.positive : AT.negative, width: 40, flex: 'none' }}>
                                    {c.passed ? 'PASS' : 'FAIL'}
                                  </Mono>
                                  <Mono sx={{ fontSize: '11.5px', color: c.passed ? AT.body : '#e7d5ad', minWidth: 210 }}>{c.label}</Mono>
                                  <Mono sx={{ fontSize: '10.5px', color: AT.muted }}>{c.detail}</Mono>
                                </Box>
                              ))}

                              {btGate.evaluatedRun && (
                                <Mono sx={{ fontSize: '10.5px', color: AT.muted2, mt: '9px', display: 'block' }}>
                                  {`evaluated against ${btGate.evaluatedRun.symbol} ${btGate.evaluatedRun.timeframe}`}
                                </Mono>
                              )}

                              <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap', mt: '13px', pt: '12px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
                                <Box component="input"
                                  value={btGateReason}
                                  onChange={(e) => setBtGateReason(e.target.value)}
                                  placeholder="Why deploy anyway? (recorded on the bot)"
                                  sx={{ ...inputSx, flex: 1, minWidth: 240, fontSize: '12.5px' }} />
                                <Box component="button"
                                  disabled={!btGateReason.trim() || btDeploying}
                                  onClick={() => deployDraft(true)}
                                  sx={{ fontFamily: AT_SANS, fontSize: '12.5px', fontWeight: 600,
                                        color: btGateReason.trim() ? '#e7eef6' : AT.muted,
                                        background: btGateReason.trim() ? 'rgba(224,90,90,.35)' : 'rgba(255,255,255,.06)',
                                        border: '1px solid rgba(224,90,90,.4)', px: '16px', py: '10px', borderRadius: '9px',
                                        cursor: btGateReason.trim() && !btDeploying ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                                  Deploy anyway
                                </Box>
                                <Box component="button" onClick={() => { setBtGate(null); setBtGateReason(''); }}
                                  sx={{ fontFamily: AT_SANS, fontSize: '12.5px', fontWeight: 600, color: '#9cc3e4', background: 'transparent',
                                        border: '1px solid rgba(77,143,199,.3)', px: '16px', py: '10px', borderRadius: '9px', cursor: 'pointer' }}>
                                  Cancel
                                </Box>
                              </Box>
                            </Box>
                          )}

                          </>)}

                          {/* Standalone exploration setup. Shown only when no bot
                              is selected — with one, Explore uses that bot's venue,
                              timeframe and strategy, so duplicating the controls
                              would just create two sources of truth. */}
                          {btExploreStandalone && (
                            <Box sx={{ mb: '16px', p: '14px 16px', borderRadius: '10px', background: 'rgba(77,143,199,.05)', border: '1px solid rgba(77,143,199,.22)' }}>
                              <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, display: 'block', mb: '4px' }}>
                                EXPLORATION SETUP
                              </Mono>

                              {/* ── What is loaded, and the way out ──
                                  Explore's state was spread down the page: the strategy sat above
                                  Step 2, the symbol only appeared in the tuner after a batch, and
                                  the risk exits were inside Step 2's panel. Nothing showed all
                                  three at once, and deploying was reachable only from a discovery
                                  row or the end of a sweep — so after tuning, the thing you had
                                  just tuned was not visible and could not be started.

                                  This is a read-out, not a second set of inputs: every value is
                                  rendered from the same state the controls below own, so it cannot
                                  drift from them. */}
                              <BtPanel sx={{ mb: '13px', p: '13px 15px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Micro size={9.5} sx={{ display: 'block', mb: '7px' }}>LOADED</Micro>

                                    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'baseline', mb: '7px' }}>
                                      <Num size={13} color={btWorkingSymbol ? BT.bright : BT.muted}>
                                        {btWorkingSymbol || 'no symbol yet'}
                                      </Num>
                                      <Num size={11} color={BT.faint}>
                                        {(btEffectiveTimeframe || '—').toUpperCase()}
                                      </Num>
                                      {btActiveBot?.exchangeName && (
                                        <Num size={11} color={BT.faint}>{btActiveBot.exchangeName}</Num>
                                      )}
                                      {btTuneSymbol && (
                                        <Num size={10} color={BT.amber}>tuned</Num>
                                      )}
                                    </Box>

                                    {btDraftLogic.buy || btDraftLogic.sell ? (
                                      <Box sx={{ display: 'grid', gap: '2px' }}>
                                        <Mono sx={{ fontSize: '11px', color: AT.bright, wordBreak: 'break-word' }}>
                                          <span style={{ color: AT.positive }}>entry</span>&nbsp; {btDraftLogic.buy || '(none)'}
                                        </Mono>
                                        <Mono sx={{ fontSize: '11px', color: AT.bright, wordBreak: 'break-word' }}>
                                          <span style={{ color: AT.negative }}>exit</span>&nbsp;&nbsp; {btDraftLogic.sell || '(none)'}
                                        </Mono>
                                      </Box>
                                    ) : (
                                      <Mono sx={{ fontSize: '11px', color: '#d9a441' }}>
                                        No strategy loaded — describe one below, or copy from a bot.
                                      </Mono>
                                    )}

                                    {/* Risk reads from backtestParams, the same object Step 2's
                                        panel and the sweep write to, so what is shown here is
                                        exactly what a run would use. */}
                                    <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', mt: '8px' }}>
                                      {(() => {
                                        const shown = [
                                          ['SL', backtestParams.stopLossPct, '%'],
                                          ['TP', backtestParams.takeProfitPct, '%'],
                                          ['TRAIL', backtestParams.trailingStopPct, '%'],
                                          ['MAX BARS', backtestParams.maxBarsInTrade, ''],
                                        ].filter(([, v]) => Number(v) > 0);
                                        if (!shown.length) {
                                          return <Num size={10.5} color={BT.muted}>no risk exits set</Num>;
                                        }
                                        return shown.map(([k, v, u]) => (
                                          <Num key={k} size={10.5} color={BT.soft}>
                                            <span style={{ color: BT.muted }}>{k}</span> {v}{u}
                                          </Num>
                                        ));
                                      })()}
                                      {/* The costs every run here is charged. Same source the
                                          batch, sweep and discovery hurdle read, so "what
                                          slippage does Explore use" is answered on screen
                                          rather than by asking. */}
                                      <Num size={10.5} color={BT.soft}>
                                        <span style={{ color: BT.muted }}>COSTS</span>{' '}
                                        {`${Number(backtestParams.commission) || 0}% + ${Number(backtestParams.slippage) || 0}% slip`}
                                        <span style={{ color: BT.muted }}>
                                          {btCostsAreCustom ? ' · yours' : ' · venue default'}
                                        </span>
                                      </Num>
                                    </Box>
                                  </Box>

                                  {/* Deploy is available whenever there is something to deploy,
                                      not only at the end of a flow. It reuses createBotForSymbol,
                                      so the strategy, timeframe and tuned risk all travel exactly
                                      as they do from a discovery row. */}
                                  <Box sx={{ flex: 'none' }}>
                                    <Box component="button" type="button"
                                      disabled={!btWorkingSymbol || !btDraftLogic.buy}
                                      onClick={() => createBotForSymbol(btWorkingSymbol)}
                                      title={
                                        !btDraftLogic.buy ? 'Load or write an entry condition first'
                                          : !btWorkingSymbol ? 'Add a symbol above, or run discovery and pick one'
                                            : `Start a bot on ${btWorkingSymbol} with this strategy and risk`
                                      }
                                      sx={{
                                        fontFamily: AT_SANS, fontSize: '12.5px', fontWeight: 600,
                                        color: (!btWorkingSymbol || !btDraftLogic.buy) ? BT.muted : '#e7eef6',
                                        background: (!btWorkingSymbol || !btDraftLogic.buy)
                                          ? 'rgba(255,255,255,.06)' : 'linear-gradient(180deg,#3d5c7b,#33506b)',
                                        border: 'none', px: '16px', py: '10px', borderRadius: '9px',
                                        cursor: (!btWorkingSymbol || !btDraftLogic.buy) ? 'default' : 'pointer',
                                        whiteSpace: 'nowrap',
                                      }}>
                                      Start a bot
                                    </Box>
                                  </Box>
                                </Box>
                              </BtPanel>

                              <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap', mb: '11px' }}>
                                <Box component="select" value={btExploreExchangeId}
                                  onChange={(e) => setBtExploreExchangeId(e.target.value)} sx={pickerSelectSx}>
                                  <option value="">exchange…</option>
                                  {userExchanges.map(x => (
                                    <option key={x.id} value={x.id}>{exchangeDisplayName(x) + alpacaModeSuffix(x)}</option>
                                  ))}
                                </Box>
                                <Box component="select" value={btExploreTimeframe}
                                  onChange={(e) => setBtExploreTimeframe(e.target.value)} sx={pickerSelectSx}>
                                  {/* The same list Reconfigure offers, from the same memo —
                                      tick included, and dropped automatically on an equity
                                      venue. Explore hard-coded its own shorter list, so tick
                                      was unreachable here even though the batch path carries
                                      isTick end to end. */}
                                  {btTimeframeOptions.map(tf => <option key={tf} value={tf}>{tf}</option>)}
                                </Box>
                                {/* Say WHY tick is missing rather than just omitting it.
                                    Silently dropping an option looks identical to the
                                    feature not existing, which is what sent this back
                                    as a bug report. */}
                                {!btTimeframeOptions.includes('tick') && (
                                  <Mono sx={{ alignSelf: 'center', fontSize: '10.5px', color: AT.muted2 }}>
                                    no tick on Alpaca — its free feed has no tick history
                                  </Mono>
                                )}
                                {/* A starting point rather than a dependency: it copies
                                    the logic in and the bot is not referenced again. The
                                    handoff labels this STARTING STRATEGY inside the AI
                                    panel; it lives here in the setup row instead so there
                                    is exactly one copy of the control, directly above the
                                    panel in the same reading order. */}
                                <Micro size={9.5} sx={{ alignSelf: 'center', display: 'inline-block' }}>STARTING STRATEGY</Micro>
                                <Box component="select" value=""
                                  onChange={(e) => {
                                    const b2 = bots.find(x => String(x.id) === e.target.value);
                                    if (!b2) return;
                                    const cb = b2.logic?.buy || b2.buy_logic || '';
                                    const cs = b2.logic?.sell || b2.sell_logic || '';
                                    setBtExploreBuy(cb);
                                    setBtExploreSell(cs);
                                    // Land in whichever editor can actually hold it.
                                    // Parsing arithmetic or a conditional into builder
                                    // rows silently rewrites the strategy.
                                    const safe = canRepresentInBuilder(cb) && canRepresentInBuilder(cs);
                                    setBtExploreMode(safe ? 'builder' : 'expression');
                                    setBtExploreBuyConds(safe ? (parseLogicToConditions(cb) || []) : []);
                                    setBtExploreSellConds(safe ? (parseLogicToConditions(cs) || []) : []);
                                    if (b2.exchangeId) setBtExploreExchangeId(String(b2.exchangeId));
                                    if (b2.interval) setBtExploreTimeframe(String(b2.interval).toLowerCase());
                                    showToast('success', `Copied ${b2.name}'s strategy as a starting point.`);
                                  }}
                                  sx={pickerSelectSx}>
                                  <option value="">copy from an existing bot…</option>
                                  {bots.filter(b2 => (b2.logic?.buy || b2.buy_logic)).map(b2 => (
                                    <option key={b2.id} value={b2.id}>{b2.name}</option>
                                  ))}
                                </Box>
                                <Box component="span" sx={{ fontSize: '12px', color: BT.muted, alignSelf: 'center' }}>
                                  or describe a new one below
                                </Box>
                              </Box>

                              <Box sx={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,.12)', borderRadius: '8px', overflow: 'hidden', mb: '11px' }}>
                                {['builder', 'expression'].map((mo) => (
                                  <Box key={mo} component="button"
                                    onClick={() => {
                                      if (mo === btExploreMode) return;
                                      // Builder -> expression carries the built strings
                                      // across. The reverse is refused when the text
                                      // cannot round-trip, same guard as the bot editor.
                                      if (mo === 'expression') {
                                        setBtExploreBuy(buildLogicFromConditions(btExploreBuyConds));
                                        setBtExploreSell(buildLogicFromConditions(btExploreSellConds));
                                        setBtExploreMode('expression');
                                        return;
                                      }
                                      if (!canRepresentInBuilder(btExploreBuy) || !canRepresentInBuilder(btExploreSell)) {
                                        showToast('info', 'This uses arithmetic or a conditional, which the builder cannot show. Staying in expression mode.');
                                        return;
                                      }
                                      setBtExploreBuyConds(parseLogicToConditions(btExploreBuy) || []);
                                      setBtExploreSellConds(parseLogicToConditions(btExploreSell) || []);
                                      setBtExploreMode('builder');
                                    }}
                                    sx={{ fontFamily: AT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.06em',
                                          textTransform: 'uppercase', px: '13px', py: '6px', border: 'none', cursor: 'pointer',
                                          color: btExploreMode === mo ? '#06121f' : AT.muted,
                                          background: btExploreMode === mo ? AT.accent : 'transparent' }}>
                                    {mo}
                                  </Box>
                                ))}
                              </Box>

                              {btExploreMode === 'builder' ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
                                  <ConditionsPanel
                                    type="buy"
                                    conditions={btExploreBuyConds}
                                    indicators={btIndicators}
                                    marketType={btDraftMarketType}
                                    baseInterval={btExploreTimeframe}
                                    isStock={btDraftIsStock}
                                    onChange={setBtExploreBuyConds}
                                  />
                                  <ConditionsPanel
                                    type="sell"
                                    conditions={btExploreSellConds}
                                    indicators={btIndicators}
                                    marketType={btDraftMarketType}
                                    baseInterval={btExploreTimeframe}
                                    isStock={btDraftIsStock}
                                    onChange={setBtExploreSellConds}
                                  />
                                </Box>
                              ) : (
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
                                {[['buy', 'ENTRY', AT.positive, btExploreBuy, setBtExploreBuy, 'close < SMA(20) * 0.98'],
                                  ['sell', 'EXIT', AT.negative, btExploreSell, setBtExploreSell, 'close > SMA(20) * 1.02']].map(
                                  ([k, label, col, val, setter, ph]) => {
                                    const err = validateLogicExpression(val);
                                    return (
                                      <Box key={k}>
                                        <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: col, mb: '6px', display: 'block' }}>{label}</Mono>
                                        <Box component="textarea" value={val} spellCheck={false} rows={3}
                                          onChange={(e) => setter(e.target.value)} placeholder={ph}
                                          sx={{ width: '100%', fontFamily: AT_MONO, fontSize: '12.5px', lineHeight: 1.6,
                                                color: AT.bright, background: AT.inner, borderRadius: '9px', px: '12px', py: '10px',
                                                outline: 'none', resize: 'vertical',
                                                border: `1px solid ${err ? 'rgba(224,90,90,.55)' : 'rgba(255,255,255,.1)'}` }} />
                                        {err && <Mono sx={{ fontSize: '10.5px', color: AT.negative, mt: '4px', display: 'block' }}>{err}</Mono>}
                                      </Box>
                                    );
                                  })}
                              </Box>
                              )}

                              {/* SHORT SIDE — stock venues only (Alpaca supports shorting;
                                  crypto spot cannot). Always expression strings, shown in
                                  both editor modes so they are never silently in play. */}
                              {btDraftIsStock && (
                                <Box sx={{ mt: '12px' }}>
                                  <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted, mb: '6px', display: 'block' }}>
                                    SHORT SIDE
                                  </Mono>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
                                    {[['shortOpen', 'OPEN SHORT', AT.negative, btExploreShortOpen, setBtExploreShortOpen, 'close > SMA(20) * 1.03'],
                                      ['shortClose', 'CLOSE SHORT', AT.positive, btExploreShortClose, setBtExploreShortClose, 'close < SMA(20)']].map(
                                      ([k, label, col, val, setter, ph]) => {
                                        const err = validateLogicExpression(val);
                                        return (
                                          <Box key={k}>
                                            <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: col, mb: '6px', display: 'block' }}>{label}</Mono>
                                            <Box component="textarea" value={val} spellCheck={false} rows={2}
                                              onChange={(e) => setter(e.target.value)} placeholder={ph}
                                              sx={{ width: '100%', fontFamily: AT_MONO, fontSize: '12.5px', lineHeight: 1.6,
                                                    color: AT.bright, background: AT.inner, borderRadius: '9px', px: '12px', py: '10px',
                                                    outline: 'none', resize: 'vertical',
                                                    border: `1px solid ${err ? 'rgba(224,90,90,.55)' : 'rgba(255,255,255,.1)'}` }} />
                                            {err && <Mono sx={{ fontSize: '10.5px', color: AT.negative, mt: '4px', display: 'block' }}>{err}</Mono>}
                                          </Box>
                                        );
                                      })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* AI ASSIST renders for BOTH explore shapes. With no bot the
                              output fills the explore editor; with a bot it writes that
                              bot's draft — the same split btDraftLogic reads, so the
                              assistant always edits the strategy that is on screen. */}
                          {btResearchTab === 'explore' && (
                          <>
                          {/* ── AI ASSIST · natural language → conditions ──
                              Between the starting-strategy row and the editor, in
                              reading order: pick a starting point, or describe one,
                              then refine what landed. Its output goes into the SAME
                              editor below — the assistant has no strategy of its own,
                              so everything downstream (signal check, breadth, tuning)
                              tests exactly what is on screen. */}
                          <Box sx={{
                            background: BT.card, border: `1px solid ${BT.hairline2}`,
                            borderRadius: BT.rInner, overflow: 'hidden', mb: '14px',
                          }}>
                            <Box
                              onClick={() => setBtNlOpen(o => !o)}
                              sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '12px 16px', cursor: 'pointer', background: BT.inner, borderBottom: btNlOpen ? `1px solid ${BT.hairline2}` : 'none' }}>
                              <Box component="span" sx={{
                                width: 8, height: 8, borderRadius: '50%', background: BT.accent,
                                animation: 'clp 2.4s ease-in-out infinite',
                                '@keyframes clp': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 1 } },
                                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                              }} />
                              <Box component="span" sx={{ fontSize: '13.5px', fontWeight: 700, letterSpacing: '.04em', color: BT.bright }}>AI ASSIST</Box>
                              <Box component="span" sx={{ fontSize: '12px', color: BT.dim }}>natural language → conditions</Box>
                              <Num size={11} color={BT.dim} sx={{ ml: 'auto' }}>
                                {(btEffectiveTimeframe || '1h').toUpperCase()} {btNlOpen ? '▴' : '▾'}
                              </Num>
                            </Box>
                            {btNlOpen && (
                              <Box sx={{ p: '16px' }}>
                                <Box sx={{ fontSize: '12.5px', color: BT.body, mb: '12px' }}>
                                  Describe a strategy in plain English. The assistant writes the entry and exit
                                  conditions into the editor below.
                                </Box>
                                <Micro size={10} sx={{ mb: '8px' }}>EXAMPLES</Micro>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mb: '14px' }}>
                                  {btNlExamples.map((ex) => (
                                    <Box key={ex} component="button" type="button"
                                      onClick={() => runNlAssist(ex)}
                                      disabled={btNlBusy}
                                      sx={{
                                        display: 'flex', gap: '10px', alignItems: 'baseline', textAlign: 'left',
                                        fontFamily: AT_SANS, fontSize: '13px', color: btNlBusy ? BT.muted : BT.link,
                                        background: 'transparent', border: 'none', p: '2px', cursor: btNlBusy ? 'default' : 'pointer',
                                        '&:hover': btNlBusy ? {} : { color: BT.soft },
                                        '&:focus-visible': { outline: `2px solid ${BT.accent}`, outlineOffset: '2px' },
                                      }}>
                                      <Box component="span" sx={{ color: BT.faint, fontFamily: BT.mono }}>&gt;</Box>
                                      {ex}
                                    </Box>
                                  ))}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <Box component="span" sx={{ fontFamily: BT.mono, color: BT.faint }}>&gt;</Box>
                                  <BtTextInput
                                    value={btNlPrompt}
                                    onChange={(e) => setBtNlPrompt(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') runNlAssist(); }}
                                    placeholder="Describe your strategy…"
                                    disabled={btNlBusy}
                                    aria-label="Describe your strategy"
                                    sx={{ flex: 1, fontFamily: AT_SANS, fontSize: '13.5px', borderRadius: '12px', padding: '12px 15px' }} />
                                  <Box component="button" type="button"
                                    onClick={() => runNlAssist()}
                                    disabled={btNlBusy || !btNlPrompt.trim()}
                                    aria-label="Generate conditions"
                                    sx={{
                                      width: 44, height: 44, borderRadius: '50%', flex: 'none',
                                      background: BT.inner, border: `1px solid ${btNlBusy || !btNlPrompt.trim() ? BT.inputBd : 'rgba(77,143,199,.5)'}`,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      color: btNlBusy || !btNlPrompt.trim() ? BT.muted : BT.link,
                                      cursor: btNlBusy || !btNlPrompt.trim() ? 'default' : 'pointer', fontSize: '15px',
                                      '&:focus-visible': { outline: `2px solid ${BT.accent}`, outlineOffset: '2px' },
                                    }}>
                                    {btNlBusy ? '…' : '➤'}
                                  </Box>
                                </Box>
                                {btNlNote && (
                                  <Box sx={{
                                    mt: '12px', p: '11px 14px', borderRadius: BT.rPanel, fontSize: '12.5px', lineHeight: 1.55,
                                    color: btNlNote.tone === 'error' ? '#efb7b0' : btNlNote.tone === 'ok' ? '#7fd4ae' : BT.body,
                                    background: btNlNote.tone === 'error' ? 'rgba(217,123,114,.07)' : btNlNote.tone === 'ok' ? 'rgba(70,184,138,.08)' : 'rgba(255,255,255,.03)',
                                    border: `1px solid ${btNlNote.tone === 'error' ? 'rgba(217,123,114,.32)' : btNlNote.tone === 'ok' ? 'rgba(70,184,138,.3)' : BT.hairline2}`,
                                  }}>
                                    {btNlNote.tone === 'ok' ? '✓ ' : ''}{btNlNote.text}
                                    {/* Warnings ride with the success note, not behind it —
                                        "loaded, but the volume condition may never fire" is
                                        one sentence, not a toast and a hidden detail. */}
                                    {(btNlNote.warnings || []).map((w, i) => (
                                      <Box key={i} sx={{ color: BT.amber, mt: '5px' }}>{w}</Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                          </>
                          )}

                        </Box>
                      )}
                    </Collapse>


                    {selectedBotForBacktest && !check.supported && <UnsupportedAlert check={check} />}
                  </>
                );
              })()}
            </Paper>


            {/* §7b — Live Backtest Stream (idle / running / complete in place) */}
            {btResearchTab !== 'explore' && (() => {
              const ls = liveBacktest?.status;
              const running = isBacktesting || ls === 'queued' || ls === 'running';
              const stopped = ls === 'canceled';
              const complete = !!backtestResults && !running;
              const btStatus = running ? 'running' : complete ? 'complete' : stopped ? 'stopped' : 'idle';
              const stMap = { idle: { label: 'IDLE', c: AT.muted }, running: { label: 'RUNNING', c: AT.accent }, complete: { label: 'COMPLETE', c: AT.positive }, stopped: { label: 'STOPPED', c: AT.negative } };
              const st = stMap[btStatus];
              const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
              return (
                <Paper ref={backtestPanelRef} elevation={0} sx={{ p: '24px', mt: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', bgcolor: AT.card }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2.25, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BarChart sx={{ color: '#7fb2dc', fontSize: 16 }} /></Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: AT.bright }}>Live Backtest Stream</Typography>
                    </Box>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: AT_MONO, fontSize: '11px', letterSpacing: '.06em', fontWeight: 600, color: st.c, background: `${st.c}1f`, border: `1px solid ${st.c}59`, px: '12px', py: '5px', borderRadius: '7px' }}>
                      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: st.c }} />{st.label}
                    </Box>
                  </Box>

                  {btStatus === 'idle' || btStatus === 'stopped' ? (
                    <Box sx={{ textAlign: 'center', color: AT.muted, fontSize: '13.5px', p: '30px 20px', border: '1px dashed rgba(255,255,255,.1)', borderRadius: '10px' }}>
                      Configure parameters above and run a backtest to stream live progress, equity and trades.
                    </Box>
                  ) : running ? (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px', fontSize: '12.5px', color: AT.body }}>
                        <span>Progress</span><Mono sx={{ color: '#cdd6e8' }}>{ls === 'queued' ? '—' : `${Math.round(backtestProgress)}%`}</Mono>
                      </Box>
                      <Box sx={{ height: 8, borderRadius: '5px', background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}>
                        <Box sx={{ width: ls === 'queued' ? '12%' : `${Math.round(backtestProgress)}%`, height: '100%', borderRadius: '5px', background: 'linear-gradient(90deg,#3f6fb0,#4d8fc7)', transition: 'width .3s ease' }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.75, mt: 1.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '13px', color: '#8593ad' }}>{ls === 'queued' ? 'Waiting for an available worker…' : 'Replaying strategy over historical bars…'}</Typography>
                        <Box component="button" onClick={() => setConfirmCancelOpen(true)} disabled={isCanceling}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: AT.negative, background: 'rgba(217,123,114,.08)', border: '1px solid rgba(217,123,114,.35)', px: '16px', py: '9px', borderRadius: '8px', cursor: isCanceling ? 'default' : 'pointer' }}>
                          <Cancel sx={{ fontSize: 14 }} /> {isCanceling ? 'Stopping…' : 'Stop'}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    (() => {
                      const m = backtestResults.metrics || {};
                      const ret = isNum(m.returnPct) ? Number(m.returnPct) : (parseFloat(String(m.totalReturn || '').replace('%', '')) || 0);
                      const benchPct = isNum(m.benchmark?.returnPct) ? Number(m.benchmark.returnPct) : 0;
                      const alpha = ret - benchPct;
                      const up = alpha >= 0;
                      const vC = up ? AT.positive : AT.negative;
                      const cap = Number(backtestParams.initialCapital) || 0;
                      const finalEq = isNum(m.finalEquity) ? Number(m.finalEquity) : cap * (1 + ret / 100);
                      const maxDD = isNum(m.maxDrawdown) ? Number(m.maxDrawdown) : (isNum(m.maxDrawdownPct) ? Number(m.maxDrawdownPct) : null);
                      const verdictLabel = up ? (Number(m.sharpeRatio) >= 1 ? 'STRONG' : 'MODERATE') : 'WEAK';
                      const recText = up ? 'Beat buy & hold over this period — a candidate to deploy.' : 'Trailed buy & hold over this period — refine before deploying.';
                      // 4-section metrics strip (backtest handoff §7b): TOTAL RETURN ·
                      // RISK (Sharpe tag + Max DD bar) · WIN RATE split · ROBUSTNESS.
                      const ddAbs = maxDD != null ? Math.abs(Number(maxDD)) : null;
                      const ddW = ddAbs != null ? `${Math.min(ddAbs / 25 * 100, 100).toFixed(0)}%` : '0%';
                      const sh = Number(m.sharpeRatio);
                      const sq = !Number.isFinite(sh) ? ['—', AT.muted, 'rgba(255,255,255,.05)', 'rgba(255,255,255,.12)']
                        : sh >= 1.5 ? ['STRONG', AT.positive, 'rgba(70,184,138,.12)', 'rgba(70,184,138,.3)']
                        : sh >= 1 ? ['GOOD', AT.positive, 'rgba(70,184,138,.12)', 'rgba(70,184,138,.3)']
                        : sh >= 0 ? ['WEAK', '#d9a441', 'rgba(217,164,65,.12)', 'rgba(217,164,65,.3)']
                        : ['POOR', AT.negative, 'rgba(217,123,114,.12)', 'rgba(217,123,114,.3)'];
                      const totalTr = isNum(m.totalTrades) ? Number(m.totalTrades) : (Array.isArray(backtestResults.trades) ? backtestResults.trades.length : 0);
                      const winRateN = isNum(m.winRate) ? Number(m.winRate) : 0;
                      const winsN = isNum(m.wins) ? Number(m.wins) : Math.round(totalTr * winRateN / 100);
                      const lossesN = isNum(m.losses) ? Number(m.losses) : Math.max(0, totalTr - winsN);
                      const eqVals = (Array.isArray(backtestResults.equityCurve) ? backtestResults.equityCurve : []).map((p) => Array.isArray(p) ? Number(p[1]) : Number(p?.v ?? p?.equity ?? p?.value)).filter(Number.isFinite);
                      const stratVals = eqVals.length >= 2 ? eqVals : [cap, finalEq];
                      const bStart = stratVals[0], bEnd = bStart * (1 + benchPct / 100);
                      const benchVals = stratVals.map((_, i) => bStart + (bEnd - bStart) * (i / (stratVals.length - 1 || 1)));
                      const lo = Math.min(...stratVals, ...benchVals), hi = Math.max(...stratVals, ...benchVals);
                      const sp = atPathScaled(stratVals, lo, hi, 720, 150);
                      const bp = atPathScaled(benchVals, lo, hi, 720, 150);
                      // Equity-curve axis labels — Y shows the value scale (so the $ loss/gain
                      // is readable off the plot), X shows the tested date range.
                      const eqAxis = (v) => {
                        if (!Number.isFinite(v)) return '—';
                        const span = Math.abs(hi - lo);
                        if (span < 1000) return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
                        if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
                        return `${(v / 1000).toFixed(1)}K`;
                      };
                      const eqYHi = eqAxis(hi), eqYMid = eqAxis((hi + lo) / 2), eqYLo = eqAxis(lo);
                      const _bt0 = backtestDateRange?.[0], _bt1 = backtestDateRange?.[1];
                      const eqDate = (ms) => (Number.isFinite(ms) ? new Date(ms).toLocaleDateString() : '—');
                      const eqXStart = eqDate(_bt0);
                      const eqXMid = (Number.isFinite(_bt0) && Number.isFinite(_bt1)) ? eqDate((_bt0 + _bt1) / 2) : '—';
                      const eqXEnd = eqDate(_bt1);
                      const allTrades = (Array.isArray(backtestResults.trades) ? backtestResults.trades : []).slice().reverse();
                      const TRADES_PER_PAGE = 10;
                      const tradesPageCount = Math.max(1, Math.ceil(allTrades.length / TRADES_PER_PAGE));
                      const tradesPage = Math.min(btTradesPage, tradesPageCount - 1);
                      const trades = allTrades.slice(tradesPage * TRADES_PER_PAGE, tradesPage * TRADES_PER_PAGE + TRADES_PER_PAGE);
                      // Why a run came back empty, straight from the engine. A
                      // strategy whose expression failed on every bar used to be
                      // indistinguishable here from one that never triggered:
                      // both rendered as a clean 0-trade report.
                      const btWarnings = Array.isArray(m.warnings) ? m.warnings : [];
                      return (
                        <Box>
                          {btWarnings.length > 0 && (
                            <Box sx={{ mb: 2.25, p: '16px 18px', background: 'rgba(217,164,65,.10)', border: '1px solid rgba(217,164,65,.35)', borderRadius: '12px' }}>
                              <Mono sx={{ fontSize: '10px', letterSpacing: '.12em', color: '#d9a441', mb: '9px', display: 'block' }}>
                                {totalTr === 0 ? 'WHY THIS RUN HAS NO TRADES' : 'THIS RUN REPORTED PROBLEMS'}
                              </Mono>
                              {btWarnings.map((w, wi) => (
                                <Typography key={wi} sx={{ fontSize: '13px', color: '#e7d5ad', lineHeight: 1.55, mt: wi ? '7px' : 0 }}>{w}</Typography>
                              ))}
                            </Box>
                          )}
                          {/* ── Result card: handoff §7 order, fixed ──
                              Trust banner → metrics band → equity curve → trades, then the
                              supplementary strip and diagnostics. The caveat is read BEFORE
                              the headline number, or it is read too late. */}
                          {(() => {
                            const fp = Number(m.validation?.foldsProfitable);
                            const ft = Number(m.validation?.foldsTotal);
                            if (!Number.isFinite(fp) || !Number.isFinite(ft) || ft < 2) return null;
                            const weak = fp * 2 <= ft;
                            return (
                              <TrustBanner
                                tone={weak ? 'amber' : 'green'}
                                pill={`${fp} OF ${ft} FOLDS`}
                                text={weak
                                  ? 'Profitable in a minority of the period. Validate on another window first.'
                                  : `Profitable across ${fp} of ${ft} equal time slices. Consistent over this period, though still measured on it.`}
                              />
                            );
                          })()}

                          {/* Metrics band — one bordered strip, exactly four divided
                              sections (handoff §7b). ROBUSTNESS is the only tinted cell:
                              it says whether to believe the other three. */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.1fr 1.1fr 1.2fr 1.1fr' }, background: '#0b0e15', border: '1px solid rgba(255,255,255,.07)', borderRadius: '12px', mb: 2, overflow: 'hidden' }}>
                            {/* TOTAL RETURN */}
                            <Box sx={{ p: '16px 18px', borderRight: { md: '1px solid rgba(255,255,255,.06)' }, borderBottom: { xs: '1px solid rgba(255,255,255,.06)', md: 'none' } }}>
                              <Micro size={9.5} sx={{ mb: '10px' }}>TOTAL RETURN</Micro>
                              <Num size={22} weight={600} color={ret >= 0 ? AT.positive : AT.negative} sx={{ display: 'block' }}>
                                {ret >= 0 ? '+' : ''}{ret.toFixed(2)}%
                              </Num>
                              <Num size={12} color={BT.body} sx={{ display: 'block', mt: '6px' }}>
                                {atShortUsd(finalEq)} from {atShortUsd(cap)}
                              </Num>
                            </Box>
                            {/* RISK */}
                            <Box sx={{ p: '16px 18px', borderRight: { md: '1px solid rgba(255,255,255,.06)' }, borderBottom: { xs: '1px solid rgba(255,255,255,.06)', md: 'none' } }}>
                              <Micro size={9.5} sx={{ mb: '12px' }}>RISK</Micro>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', mb: '10px' }}>
                                <Box component="span" sx={{ fontSize: '11.5px', color: AT.body }}>Sharpe</Box>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                                  <Mono sx={{ fontSize: '13px', fontWeight: 600, color: AT.bright }}>{fmtRatio(m.sharpeRatio)}</Mono>
                                  <Mono sx={{ fontSize: '8.5px', letterSpacing: '.06em', fontWeight: 700, color: sq[1], background: sq[2], border: `1px solid ${sq[3]}`, px: '7px', py: '2px', borderRadius: '5px' }}>{sq[0]}</Mono>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                                <Box component="span" sx={{ fontSize: '11.5px', color: AT.body, flex: 'none' }}>Max DD</Box>
                                <Box sx={{ flex: 1, height: 6, borderRadius: '3px', background: 'rgba(255,255,255,.05)', overflow: 'hidden' }}><Box sx={{ width: ddW, height: '100%', borderRadius: '3px', background: AT.negative }} /></Box>
                                <Mono sx={{ fontSize: '12px', fontWeight: 600, color: '#e0a59d', flex: 'none' }}>{ddAbs != null ? `−${ddAbs.toFixed(1)}%` : '—'}</Mono>
                              </Box>
                            </Box>
                            {/* WIN RATE */}
                            <Box sx={{ p: '16px 18px', borderRight: { md: '1px solid rgba(255,255,255,.06)' }, borderBottom: { xs: '1px solid rgba(255,255,255,.06)', md: 'none' } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', mb: '10px' }}>
                                <Micro size={9.5} sx={{ display: 'inline-block' }}>WIN RATE</Micro>
                                <Mono sx={{ fontSize: '10.5px', color: AT.muted }}>{`${totalTr} trades`}</Mono>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '9px', mb: '9px' }}>
                                <Mono sx={{ fontSize: '22px', fontWeight: 600, color: AT.bright }}>{isNum(m.winRate) ? `${winRateN.toFixed(0)}%` : '—'}</Mono>
                                <Mono sx={{ fontSize: '11px', color: AT.body }}>{`${winsN}W · ${lossesN}L`}</Mono>
                              </Box>
                              <Box sx={{ display: 'flex', height: 6, borderRadius: '3px', overflow: 'hidden', background: 'rgba(217,123,114,.45)' }}><Box sx={{ width: `${Math.min(winRateN, 100)}%`, height: '100%', background: AT.positive }} /></Box>
                            </Box>
                            {/* ROBUSTNESS — always tinted and edge-marked so it reads as a
                                different KIND of number; the value keeps honest coloring
                                (amber weak / green consistent / muted unknown). */}
                            {(() => {
                              const rfp = Number(m.validation?.foldsProfitable);
                              const rft = Number(m.validation?.foldsTotal);
                              const known = Number.isFinite(rfp) && Number.isFinite(rft) && rft >= 2;
                              const weak = known && rfp * 2 <= rft;
                              return (
                                <Box sx={{ p: '16px 18px', background: 'rgba(217,164,65,.05)', boxShadow: BT.cautionEdge }}>
                                  <Micro size={9.5} color={BT.amber} sx={{ mb: '10px' }}>ROBUSTNESS</Micro>
                                  <Num size={18} weight={600} color={!known ? BT.muted : weak ? BT.amber : BT.positive} sx={{ display: 'block' }}>
                                    {known ? `${rfp} of ${rft} folds` : '—'}
                                  </Num>
                                  <Box sx={{ fontFamily: AT_MONO, fontSize: '11.5px', color: BT.body, mt: '6px', lineHeight: 1.45 }}>
                                    {known
                                      ? (weak ? 'profitable in a minority of the period' : 'profitable across the period')
                                      : 'not enough equity history to split'}
                                  </Box>
                                  {known && (
                                    <SplitBar segments={[
                                      { value: rfp, color: BT.positive },
                                      { value: Math.max(0, rft - rfp), color: 'rgba(217,123,114,.45)' },
                                    ]} />
                                  )}
                                </Box>
                              );
                            })()}
                          </Box>

                          {/* equity curve */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25, flexWrap: 'wrap' }}>
                            <Mono sx={{ fontSize: '10.5px', letterSpacing: '.08em', color: BT.label }}>
                              {(() => {
                                const bars = Number(backtestResults?.candlesLoaded ?? m.candlesLoaded);
                                return Number.isFinite(bars) && bars > 0
                                  ? `EQUITY CURVE · ${bars.toLocaleString()} BARS`
                                  : `EQUITY CURVE${QUOTE ? ` · ${QUOTE}` : ''}`;
                              })()}
                            </Mono>
                            {/* When the bar budget trimmed the request (YTD on a 1m bot asks
                                for ~326k candles), the curve covers less calendar than the
                                pickers asked for — say which window actually ran, in amber,
                                where the reader is already looking at the curve. */}
                            {m.window?.clampedToMaxBars && (
                              <Mono sx={{ fontSize: '10.5px', color: '#d9a441' }}>
                                {`ran ${format(new Date(m.window.startTime), 'MMM d, yyyy')} → ${format(new Date(m.window.endTime), 'MMM d, yyyy')} — the requested range exceeds the ${Number(m.window.maxBars).toLocaleString()}-bar budget at this timeframe`}
                              </Mono>
                            )}
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#8593ad' }}><Box component="span" sx={{ width: 14, height: '2px', bgcolor: ret >= 0 ? AT.positive : AT.negative }} />Strategy</Box>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#8593ad' }}><Box component="span" sx={{ width: 14, borderTop: `2px dashed ${AT.muted}` }} />Buy &amp; hold</Box>
                            {/* The holdout deserves a legend entry of its own. Everything
                                right of the boundary is the part the strategy was never
                                fitted on, which is the only part of this curve that is
                                evidence rather than description. */}
                            {Number.isFinite(Number(m.validation?.holdoutPct)) && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#8593ad' }}>
                                <Box component="span" sx={{ width: 14, borderTop: `2px dashed ${BT.amber}` }} />
                                Holdout: never used for fitting
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: '10px', mb: 2.5 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 218, py: '2px', minWidth: 52, textAlign: 'right', fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: '10px', color: AT.muted2 }}>
                              <span>{eqYHi}</span><span>{eqYMid}</span><span>{eqYLo}</span>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* currentColor drives the line, area gradient and end dot at
                                  once — the prototype colors the whole curve by the run's
                                  sign, not by brand accent. */}
                              <Box sx={{ color: ret >= 0 ? AT.positive : AT.negative }}>
                                <svg viewBox="0 0 720 150" preserveAspectRatio="none" style={{ width: '100%', height: 218, display: 'block' }}>
                                  <defs><linearGradient id="bt-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="currentColor" stopOpacity="0.16" /><stop offset="1" stopColor="currentColor" stopOpacity="0" /></linearGradient></defs>
                                  <g stroke="rgba(255,255,255,.05)" strokeWidth="1"><line x1="0" y1="8" x2="720" y2="8" /><line x1="0" y1="75" x2="720" y2="75" /><line x1="0" y1="142" x2="720" y2="142" /></g>
                                  {/* Holdout region, drawn UNDER the curve so it tints rather
                                      than obscures. Positioned from the run's own holdoutPct
                                      instead of a hardcoded 70%, because buildValidation clamps
                                      it to 10–60 and a fixed line would quietly lie about where
                                      the split actually fell. */}
                                  {(() => {
                                    const hp = Number(m.validation?.holdoutPct);
                                    if (!Number.isFinite(hp) || hp <= 0 || hp >= 100) return null;
                                    const x = 720 * (1 - hp / 100);
                                    return (
                                      <g>
                                        <rect x={x} y="0" width={720 - x} height="150" fill="rgba(217,164,65,.04)" />
                                        <line x1={x} y1="0" x2={x} y2="150" stroke={BT.amber} strokeWidth="1.5" strokeDasharray="4 5" strokeOpacity="0.7" />
                                      </g>
                                    );
                                  })()}
                                  <path d={sp.area} fill="url(#bt-area)" />
                                  <path d={bp.line} fill="none" stroke={AT.muted} strokeWidth="1.5" strokeDasharray="5 5" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d={sp.line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <circle cx={sp.lastX} cy={sp.lastY} r="3.5" fill="currentColor" />
                                </svg>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '7px', fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: '10px', color: AT.muted2 }}>
                                <span>{eqXStart}</span><span>{eqXMid}</span><span>{eqXEnd}</span>
                              </Box>
                            </Box>
                          </Box>

                          <Mono sx={{ fontSize: '11px', letterSpacing: '.08em', color: AT.muted2, mb: '6px', display: 'block' }}>{`TRADES (${allTrades.length})`}</Mono>
                          <Box sx={{ overflowX: 'auto' }}>
                            <Box sx={{ minWidth: 520 }}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.7fr 1fr 0.9fr 0.8fr', gap: '12px', p: '8px 4px', borderBottom: '1px solid rgba(255,255,255,.07)', fontFamily: AT_MONO, fontSize: '10px', letterSpacing: '.1em', color: AT.muted }}>
                                <span>DATE</span><span>TIME</span><span>SIDE</span><span style={{ textAlign: 'right' }}>PRICE</span><span style={{ textAlign: 'right' }}>QTY</span><span style={{ textAlign: 'right' }}>P/L</span>
                              </Box>
                              {trades.length === 0 && <Typography sx={{ fontSize: '12.5px', color: AT.muted, py: 1.5 }}>No trades.</Typography>}
                              {trades.map((tr, i) => {
                                const side = String(tr.side || tr.type || '').toUpperCase();
                                const sc = side === 'BUY' ? AT.positive : side === 'SELL' ? AT.negative : AT.accent;
                                const pnl = Number(tr.pnl ?? tr.profit);
                                const ts = tr.time || tr.timestamp || tr.date;
                                const dt = ts ? new Date(ts) : null;
                                return (
                                  <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.7fr 1fr 0.9fr 0.8fr', gap: '12px', alignItems: 'center', p: '11px 4px', borderBottom: '1px solid rgba(255,255,255,.04)', fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: '12.5px' }}>
                                    <Box component="span" sx={{ color: AT.body }}>{dt ? dt.toLocaleDateString() : '—'}</Box>
                                    <Box component="span" sx={{ color: AT.muted }}>{dt ? dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</Box>
                                    <Box component="span"><Box component="span" sx={{ display: 'inline-flex', fontSize: '10.5px', fontWeight: 700, color: sc, background: `${sc}1f`, px: '9px', py: '2px', borderRadius: '5px' }}>{side || '—'}</Box></Box>
                                    <Box component="span" sx={{ color: '#c9d2e3', textAlign: 'right' }}>{isNum(tr.price) ? Number(tr.price).toLocaleString() : '—'}</Box>
                                    <Box component="span" sx={{ color: AT.body, textAlign: 'right' }}>{isNum(tr.qty ?? tr.amount) ? Number(tr.qty ?? tr.amount).toLocaleString(undefined, { maximumFractionDigits: 6 }) : '—'}</Box>
                                    <Box component="span" sx={{ color: Number.isFinite(pnl) ? (pnl >= 0 ? AT.positive : AT.negative) : AT.faint, fontWeight: 600, textAlign: 'right' }}>{Number.isFinite(pnl) ? `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ${QUOTE || ''}` : '—'}</Box>
                                    {/* Options trades: contract identity + pricing source on a
                                        second full-width line — REAL fills vs MODELED
                                        (Black-Scholes) is the number-one honesty marker. */}
                                    {tr.occSymbol && (
                                      <Box component="span" sx={{ gridColumn: '1 / -1', mt: '-6px', pb: '2px', fontSize: '10.5px', color: AT.muted, display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <span>{formatOccForDisplay(tr.occSymbol)}</span>
                                        {tr.pricingSource && (
                                          <Box component="span" sx={{ fontSize: '9px', fontWeight: 700, letterSpacing: '.06em', px: '5px', py: '1px', borderRadius: '4px',
                                            color: tr.pricingSource === 'real' ? AT.positive : '#d9a441',
                                            background: tr.pricingSource === 'real' ? 'rgba(70,184,138,.12)' : 'rgba(217,164,65,.12)',
                                            border: `1px solid ${tr.pricingSource === 'real' ? 'rgba(70,184,138,.3)' : 'rgba(217,164,65,.3)'}` }}>
                                            {tr.pricingSource === 'real' ? 'REAL' : 'MODELED'}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                          {/* Footer per the handoff: count · shown · view all. The pager is
                              a kept feature; "view all" opens the full report's trade list. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                            <Mono sx={{ fontSize: '11px', color: AT.faint }}>
                              {`${allTrades.length} trades · showing ${trades.length} · `}
                              <Box component="span"
                                onClick={() => { setActiveBacktestTab('trades'); setBacktestDialogOpen(true); }}
                                sx={{ color: '#9cc3e4', cursor: 'pointer', '&:hover': { color: '#cdd6e8' } }}>
                                view all
                              </Box>
                            </Mono>
                            {allTrades.length > TRADES_PER_PAGE && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Box component="button" disabled={tradesPage === 0} onClick={() => setBtTradesPage((p) => Math.max(0, p - 1))} sx={{ fontFamily: AT_SANS, fontSize: '12px', fontWeight: 600, color: tradesPage === 0 ? AT.muted : '#cdd6e8', background: 'transparent', border: '1px solid rgba(255,255,255,.14)', px: '12px', py: '6px', borderRadius: '7px', cursor: tradesPage === 0 ? 'default' : 'pointer', opacity: tradesPage === 0 ? 0.5 : 1 }}>Prev</Box>
                                <Mono sx={{ fontSize: '11.5px', color: AT.body, minWidth: 46, textAlign: 'center' }}>{tradesPage + 1} / {tradesPageCount}</Mono>
                                <Box component="button" disabled={tradesPage >= tradesPageCount - 1} onClick={() => setBtTradesPage((p) => Math.min(tradesPageCount - 1, p + 1))} sx={{ fontFamily: AT_SANS, fontSize: '12px', fontWeight: 600, color: tradesPage >= tradesPageCount - 1 ? AT.muted : '#cdd6e8', background: 'transparent', border: '1px solid rgba(255,255,255,.14)', px: '12px', py: '6px', borderRadius: '7px', cursor: tradesPage >= tradesPageCount - 1 ? 'default' : 'pointer', opacity: tradesPage >= tradesPageCount - 1 ? 0.5 : 1 }}>Next</Box>
                              </Box>
                            )}
                          </Box>

                          {/* ── Supplementary, after the specced sequence ──
                              The verdict strip carries what the removed hero card owned:
                              the α/Sharpe judgement and the ONLY deploy + full-report
                              affordances visible with the config section closed. The
                              setBtEditOpen(true) is load-bearing — deployDraft reports a
                              blocked deploy through a panel that lives inside that
                              Collapse, so the button must open it or a rejection shows
                              nothing. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mt: 2.25, p: '14px 16px', background: BT.inner, border: `1px solid ${BT.hairline2}`, borderRadius: '10px', flexWrap: 'wrap' }}>
                            <Box component="span" sx={{ display: 'inline-flex', fontFamily: AT_MONO, fontSize: '10px', fontWeight: 700, letterSpacing: '.08em', color: '#06121f', background: vC, px: '10px', py: '4px', borderRadius: '6px' }}>{verdictLabel}</Box>
                            <Box sx={{ flex: 1, minWidth: 220 }}>
                              <Typography sx={{ fontSize: '12.5px', color: '#8593ad', lineHeight: 1.5 }}>
                                {recText} {ret >= 0 ? '+' : ''}{ret.toFixed(2)}% vs {benchPct >= 0 ? '+' : ''}{benchPct.toFixed(2)}% buy &amp; hold · {alpha >= 0 ? '+' : ''}{alpha.toFixed(2)}% alpha at {fmtRatio(m.sharpeRatio)} Sharpe.
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                              <Box component="button"
                                disabled={btDeploying}
                                onClick={() => {
                                  if (selectedBotForBacktest?.status === 'active' && !btDraftDirty) {
                                    showToast('info', `"${selectedBotForBacktest.name}" is already deployed and running live.`);
                                    return;
                                  }
                                  setBtEditOpen(true);
                                  deployDraft(false);
                                }}
                                sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: '#e7eef6', background: btDeploying ? 'rgba(255,255,255,.08)' : 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '18px', py: '10px', borderRadius: '9px', cursor: btDeploying ? 'default' : 'pointer', whiteSpace: 'nowrap', transition: 'transform .2s', '&:hover': btDeploying ? {} : { transform: 'translateY(-1px)' } }}>
                                {btDeploying
                                  ? 'Deploying…'
                                  : selectedBotForBacktest?.status === 'active'
                                    ? (btDraftDirty ? 'Update live bot' : 'Already deployed')
                                    : 'Deploy as live bot'}
                              </Box>
                              {selectedBotForBacktest && btDraft && (
                                <Box component="button" onClick={deployDraftAsNewBot} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontFamily: AT_SANS, fontSize: '12.5px', fontWeight: 600, color: '#9cc3e4', background: 'transparent', border: '1px solid rgba(77,143,199,.3)', px: '16px', py: '9px', borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                  Deploy as new bot
                                </Box>
                              )}
                              <Box component="button" onClick={() => { setActiveBacktestTab('summary'); setBacktestDialogOpen(true); }} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontFamily: AT_SANS, fontSize: '12.5px', fontWeight: 600, color: '#9cc3e4', background: 'transparent', border: '1px solid rgba(77,143,199,.3)', px: '16px', py: '9px', borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                View full report
                              </Box>
                            </Box>
                          </Box>

                          {/* ── Robustness ──
                              Return alone cannot say whether a result is an edge
                              or a month of luck. These four are the checks that
                              can: does it hold up on data it was not tuned on,
                              is it separable from random entries, does it survive
                              worse fills, and was it measured with a fill model
                              that could actually be traded. */}
                          {(() => {
                            const v = m.validation;
                            const sig = m.significance;
                            const cs = m.costStress;
                            if (!v && !sig && !cs && !m.fillMode) return null;

                            const num = (x, d = 2) => (Number.isFinite(Number(x)) ? Number(x).toFixed(d) : '—');
                            const pct = (x, d = 2) => (Number.isFinite(Number(x)) ? `${Number(x) >= 0 ? '+' : ''}${Number(x).toFixed(d)}%` : '—');
                            const tone = (x) => (Number.isFinite(Number(x)) ? (Number(x) >= 0 ? AT.positive : AT.negative) : AT.muted);

                            // Chips only claim something when the evidence exists
                            // to claim it — an absent check reads "not measured",
                            // never as a pass.
                            const chips = [];
                            if (v?.train && v?.holdout) {
                              const t = Number(v.train.returnPct), h = Number(v.holdout.returnPct);
                              const ok = t > 0 && h > 0;
                              chips.push({
                                label: ok ? 'HOLDS UP OUT OF SAMPLE' : (t > 0 && h <= 0 ? 'OVERFIT — DIES OUT OF SAMPLE' : 'UNPROFITABLE IN SAMPLE'),
                                good: ok,
                              });
                            }
                            if (sig?.randomEntry && Number.isFinite(sig.randomEntry.strategyPercentile)) {
                              const p = Number(sig.randomEntry.strategyPercentile);
                              chips.push({ label: p >= 90 ? `BEATS RANDOM (${p.toFixed(0)}th pct)` : `NO BETTER THAN RANDOM (${p.toFixed(0)}th pct)`, good: p >= 90 });
                            }
                            if (typeof cs?.survives2x === 'boolean') {
                              chips.push({ label: cs.survives2x ? 'SURVIVES 2× SLIPPAGE' : 'FAILS AT 2× SLIPPAGE', good: cs.survives2x });
                            }
                            if (sig && sig.sufficient === false) {
                              chips.push({ label: `ONLY ${sig.tradeCount} TRADES — NOT MEASURABLE`, good: false });
                            }
                            if (m.fillMode === 'same_close') {
                              chips.push({ label: 'LOOKAHEAD FILLS — INFLATED', good: false });
                            }

                            const segRow = (seg, label, sub) => (
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                                <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, width: 108, flex: 'none' }}>{label}</Mono>
                                {seg ? (
                                  <>
                                    <Mono sx={{ fontSize: '15px', fontWeight: 600, color: tone(seg.returnPct), width: 78, textAlign: 'right' }}>{pct(seg.returnPct)}</Mono>
                                    <Mono sx={{ fontSize: '11.5px', color: '#8593ad' }}>
                                      {`${seg.totalTrades ?? 0} trades · win ${num(seg.winRate, 0)}% · PF ${seg.profitFactor == null ? '∞' : num(seg.profitFactor)}`}
                                    </Mono>
                                  </>
                                ) : <Mono sx={{ fontSize: '11.5px', color: AT.muted }}>no trades in this span</Mono>}
                                {sub && <Mono sx={{ fontSize: '10.5px', color: AT.muted2 }}>{sub}</Mono>}
                              </Box>
                            );

                            return (
                              <Box sx={{ mb: 2.5, p: '18px 20px', background: '#0b0e15', border: '1px solid rgba(255,255,255,.07)', borderRadius: '12px' }}>
                                <Mono sx={{ fontSize: '10px', letterSpacing: '.12em', color: AT.muted2, mb: '13px', display: 'block' }}>ROBUSTNESS</Mono>

                                {chips.length > 0 && (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: '16px' }}>
                                    {chips.map((c, ci) => (
                                      <Box key={ci} component="span" sx={{
                                        fontFamily: AT_MONO, fontSize: '10px', fontWeight: 700, letterSpacing: '.06em',
                                        color: c.good ? AT.positive : AT.negative,
                                        background: c.good ? 'rgba(70,190,130,.10)' : 'rgba(224,90,90,.10)',
                                        border: `1px solid ${c.good ? 'rgba(70,190,130,.32)' : 'rgba(224,90,90,.32)'}`,
                                        px: '9px', py: '4px', borderRadius: '5px',
                                      }}>{c.label}</Box>
                                    ))}
                                  </Box>
                                )}

                                {v?.train && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px', mb: '14px' }}>
                                    {segRow(v.train, `TRAIN ${100 - v.holdoutPct}%`, 'the span you tuned against')}
                                    {segRow(v.holdout, `HOLDOUT ${v.holdoutPct}%`, 'never shown to the AI advisor')}
                                  </Box>
                                )}

                                {Array.isArray(v?.folds) && v.folds.length > 1 && (
                                  <Box sx={{ mb: '14px' }}>
                                    <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, mb: '7px', display: 'block' }}>
                                      {`STABILITY — ${v.foldsProfitable}/${v.foldsTotal} EQUAL TIME FOLDS PROFITABLE · sd ${num(v.foldReturnStd)}`}
                                    </Mono>
                                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'flex-end', height: 42 }}>
                                      {(() => {
                                        const mx = Math.max(...v.folds.map(f => Math.abs(Number(f.returnPct) || 0)), 0.01);
                                        return v.folds.map((f, fi) => {
                                          const r = Number(f.returnPct) || 0;
                                          return (
                                            <Box key={fi} title={`fold ${fi + 1}: ${pct(r)} · ${f.totalTrades ?? 0} trades`}
                                              sx={{ flex: 1, height: `${Math.max(3, (Math.abs(r) / mx) * 100)}%`, minHeight: 3,
                                                    background: r >= 0 ? AT.positive : AT.negative, opacity: .78, borderRadius: '2px' }} />
                                          );
                                        });
                                      })()}
                                    </Box>
                                  </Box>
                                )}

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pt: '12px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                                  {sig?.sufficient && (
                                    <Mono sx={{ fontSize: '11.5px', color: '#8593ad' }}>
                                      {`Mean trade ${num(sig.meanTradePct, 3)}% · t=${num(sig.tStat)} · 90% CI [${num(sig.ci90?.[0], 3)}, ${num(sig.ci90?.[1], 3)}]`}
                                      {sig.randomEntry ? ` · random entries averaged ${num(sig.randomEntry.meanTradePct, 3)}%` : ''}
                                    </Mono>
                                  )}
                                  {cs?.levels?.length > 0 && (
                                    <Mono sx={{ fontSize: '11.5px', color: '#8593ad' }}>
                                      {`Slippage ${num(cs.baseSlippagePct, 3)}% → `}
                                      {cs.levels.map((l, li) => (
                                        <Box key={li} component="span" sx={{ color: tone(l.returnPct), mr: '10px' }}>{`${l.multiple}× ${pct(l.returnPct)}`}</Box>
                                      ))}
                                    </Mono>
                                  )}
                                  {m.fillMode && (
                                    <Mono sx={{ fontSize: '11.5px', color: m.fillMode === 'same_close' ? AT.negative : AT.muted2 }}>
                                      {m.fillMode === 'next_open'
                                        ? 'Fills: next bar open — signals are traded at the first price available after the bar that produced them.'
                                        : 'Fills: signal-bar close — this run could see the price it traded at. Re-run it for a tradeable number.'}
                                    </Mono>
                                  )}
                                </Box>
                              </Box>
                            );
                          })()}
                        </Box>
                      );
                    })()
                  )}
                </Paper>


              );
            })()}
            {/* §7a2 — Research, in the handoff order: the RESULT precedes the
                research that acts on it. Step 1 find a strategy (advisor), the
                loaded-strategy block and editor, and Step 2 tune the numbers.
                In Explore this also hosts signal check, symbol discovery and
                the tuner. Not gated on btEditOpen: these are page sections,
                not part of the reconfigure drawer. */}
            {(btDraft || btResearchTab === 'explore') && (
              <Paper elevation={0} sx={{ p: '24px', mt: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', bgcolor: AT.card }}>
                <Box>
                          {btResearchTab === 'optimize' && (<>
                          {/* AI advisor — reasons over this bot's backtest
                              results (saved history server-side, plus the run
                              currently on screen) and proposes a refinement. */}
                          <Box sx={{ mb: '16px', p: '14px 16px', borderRadius: '10px', background: 'rgba(77,143,199,.05)', border: '1px solid rgba(77,143,199,.22)' }}>
                            <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, display: 'block', mb: '9px' }}>
                              STEP 1 · FIND A STRATEGY — ADVISOR &amp; SEARCH
                            </Mono>
                            <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                              <Box component="input"
                                value={btAiPrompt}
                                onChange={(e) => setBtAiPrompt(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !btAiLoading) askBacktestAdvisor(); }}
                                placeholder={backtestResults?.metrics
                                  ? 'e.g. "drawdown is too deep — tighten the exit"'
                                  : 'e.g. "make entries stricter" (run a backtest for richer advice)'}
                                sx={{ ...inputSx, flex: 1, minWidth: 220, fontSize: '13.5px' }} />
                              <Box component="button"
                                disabled={btAiLoading || !btAiPrompt.trim()}
                                onClick={askBacktestAdvisor}
                                sx={{ fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: '#e7eef6', background: (btAiLoading || !btAiPrompt.trim()) ? 'rgba(255,255,255,.08)' : 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '18px', py: '11px', borderRadius: '9px', cursor: (btAiLoading || !btAiPrompt.trim()) ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                                {btAiLoading ? 'Thinking…' : 'Ask'}
                              </Box>
                              {/* The measured path. "Ask" returns an opinion;
                                  this returns a ranked leaderboard of variants
                                  that were actually backtested. */}
                              <Box component="button"
                                disabled={btSearchBusy || !selectedBotForBacktest || !btDraft}
                                onClick={runStrategySearch}
                                title="Generate several variants, backtest each, and rank them on data they were not tuned against"
                                sx={{ fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: btSearchBusy ? AT.muted : '#9cc3e4', background: 'transparent', border: `1px solid ${btSearchBusy ? 'rgba(255,255,255,.12)' : 'rgba(77,143,199,.35)'}`, px: '16px', py: '11px', borderRadius: '9px', cursor: btSearchBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                                {btSearchBusy ? 'Searching…' : 'Search & rank'}
                              </Box>
                              {/* Refine edits the strategy on screen; explore
                                  asks for different ideas outright. A local
                                  search around a dead idea returns dead ideas,
                                  so the mode matters once a signal check says
                                  the entry has no edge left to tune. */}
                              <Box sx={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,.12)', borderRadius: '9px', overflow: 'hidden', alignSelf: 'stretch' }}>
                                {['refine', 'explore'].map((m) => (
                                  <Box key={m} component="button"
                                    onClick={() => setBtSearchMode(m)}
                                    disabled={btSearchBusy}
                                    title={m === 'refine'
                                      ? 'Edit the strategy on screen — change the entry, the exit, the risk exits, or tune its parameters (4 candidates)'
                                      : 'Cover new ground — untried indicator families, coarse and fine parameter sweeps around the best result so far, and regime-conditional rules (8 candidates)'}
                                    sx={{
                                      fontFamily: AT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.06em',
                                      textTransform: 'uppercase', px: '12px', border: 'none', cursor: btSearchBusy ? 'default' : 'pointer',
                                      color: btSearchMode === m ? '#06121f' : AT.muted,
                                      background: btSearchMode === m ? AT.accent : 'transparent',
                                    }}>
                                    {m}
                                  </Box>
                                ))}
                              </Box>
                            </Box>

                            {btSearch && (
                              <Box sx={{ mt: '14px', p: '14px 16px', background: 'rgba(255,255,255,.02)', border: '1px solid rgba(255,255,255,.07)', borderRadius: '10px' }}>
                                <Mono sx={{ fontSize: '10px', letterSpacing: '.12em', color: AT.muted2, mb: '10px', display: 'block' }}>
                                  {btSearch.status === 'completed'
                                    ? `RANKED BY HOLDOUT RETURN · ${btSearch.measured ?? (btSearch.results || []).length} MEASURED`
                                    : btSearch.status === 'failed'
                                      ? 'SEARCH FAILED'
                                      : btSearch.phase === 'backtesting'
                                        ? `BACKTESTING ${btSearch.candidateCount || ''} CANDIDATES…`
                                        : 'GENERATING CANDIDATES…'}
                                </Mono>

                                {btSearch.status === 'failed' && (
                                  <Typography sx={{ fontSize: '12.5px', color: AT.negative }}>{btSearch.error || 'Unknown error'}</Typography>
                                )}

                                {btSearch.status === 'running' && (
                                  <Typography sx={{ fontSize: '12.5px', color: '#8593ad' }}>
                                    Each candidate is a full backtest on your current window and costs. This usually takes a few minutes.
                                  </Typography>
                                )}

                                {Array.isArray(btSearch.results) && btSearch.results.map((c) => {
                                  const s = c.summary;
                                  const pos = (x) => (Number.isFinite(Number(x)) ? Number(x) >= 0 : null);
                                  const fmtP = (x) => (Number.isFinite(Number(x)) ? `${Number(x) >= 0 ? '+' : ''}${Number(x).toFixed(2)}%` : '—');
                                  return (
                                    <Box key={c.jobId || c.rank} sx={{ py: '11px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', mb: '6px' }}>
                                        <Mono sx={{ fontSize: '12px', fontWeight: 700, color: c.rank === 1 ? AT.accent : AT.muted2 }}>{`#${c.rank}`}</Mono>
                                        {c.failed ? (
                                          <Mono sx={{ fontSize: '12px', color: AT.negative }}>did not complete</Mono>
                                        ) : (
                                          <>
                                            <Mono sx={{ fontSize: '15px', fontWeight: 600, color: pos(s?.holdoutReturnPct) === null ? AT.muted : (pos(s?.holdoutReturnPct) ? AT.positive : AT.negative) }}>
                                              {fmtP(s?.holdoutReturnPct)}
                                            </Mono>
                                            <Mono sx={{ fontSize: '10.5px', color: AT.muted2 }}>
                                              {c.rankBasis === 'holdout' ? `holdout · ${s?.holdoutTrades ?? 0} trades` : c.rankBasis}
                                            </Mono>
                                            <Mono sx={{ fontSize: '11px', color: '#8593ad' }}>
                                              {`train ${fmtP(s?.trainReturnPct)} · ${s?.totalTrades ?? 0} trades total`}
                                              {s?.survives2x === false ? ' · fails 2× slippage' : ''}
                                              {Number.isFinite(s?.randomEntryPercentile) ? ` · ${Number(s.randomEntryPercentile).toFixed(0)}th pct vs random` : ''}
                                            </Mono>
                                            <Box component="button" onClick={() => applySearchCandidate(c)}
                                              sx={{ ml: 'auto', fontFamily: AT_SANS, fontSize: '11.5px', fontWeight: 600, color: '#9cc3e4', background: 'transparent', border: '1px solid rgba(77,143,199,.3)', px: '11px', py: '5px', borderRadius: '7px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                              Load
                                            </Box>
                                          </>
                                        )}
                                      </Box>
                                      {/* Edge vs the cost of harvesting it. A
                                          negative return from 400 trades and one
                                          from 12 are different diagnoses: the
                                          first is turnover, the second is the
                                          rule. Only this separates them. */}
                                      {!c.failed && s?.verdict && s.verdict !== 'unknown' && (
                                        <Mono sx={{
                                          fontSize: '10.5px', mb: '5px', display: 'block',
                                          color: s.verdict === 'clears costs' ? AT.positive
                                            : s.verdict === 'marginal' ? '#d9a441' : AT.negative,
                                        }}>
                                          {`${s.verdict.toUpperCase()} · ${Number(s.grossPerTradePct).toFixed(4)}% gross per trade`}
                                          {Number.isFinite(btSearch?.roundTripCostPct) ? ` vs ${Number(btSearch.roundTripCostPct).toFixed(3)}% cost` : ''}
                                          {Number.isFinite(s.costDragPct) ? ` · ${s.trades} trades cost ${Number(s.costDragPct).toFixed(1)}% of capital` : ''}
                                        </Mono>
                                      )}
                                      {c.buyLogic && <Box sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.positive, wordBreak: 'break-word' }}>{`BUY  ${c.buyLogic}`}</Box>}
                                      {c.sellLogic && <Box sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.negative, wordBreak: 'break-word' }}>{`SELL ${c.sellLogic}`}</Box>}
                                      {c.rationale && <Typography sx={{ fontSize: '12px', color: '#8593ad', mt: '5px', lineHeight: 1.5 }}>{c.rationale}</Typography>}
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}

                            {btAiResult && (
                              <Box sx={{ mt: '12px' }}>
                                {(btAiResult.explanation || btAiResult.reply) && (
                                  <Typography sx={{ fontSize: '13px', color: '#cdd6e8', lineHeight: 1.55, mb: '10px' }}>
                                    {btAiResult.reply || btAiResult.explanation}
                                  </Typography>
                                )}
                                {(btAiResult.buyLogic || btAiResult.sellLogic || btAiResult.risk) && (
                                  <>
                                    <Box sx={{ display: 'grid', gap: '6px', mb: '10px' }}>
                                      {btAiResult.buyLogic && (
                                        <Box sx={{ fontFamily: AT_MONO, fontSize: '11.5px', color: AT.positive, wordBreak: 'break-word' }}>
                                          BUY  {btAiResult.buyLogic}
                                        </Box>
                                      )}
                                      {btAiResult.sellLogic && (
                                        <Box sx={{ fontFamily: AT_MONO, fontSize: '11.5px', color: AT.negative, wordBreak: 'break-word' }}>
                                          SELL {btAiResult.sellLogic}
                                        </Box>
                                      )}
                                    </Box>
                                    {/* Proposed risk exits — shown before applying so the
                                        numbers are reviewed, not just accepted. */}
                                    {btAiResult.risk && (
                                      <Box sx={{ mb: '10px' }}>
                                        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                          {[
                                            ['Stop', btAiResult.risk.stopLossPct, '%'],
                                            ['Target', btAiResult.risk.takeProfitPct, '%'],
                                            ['Trail', btAiResult.risk.trailingStopPct, '%'],
                                            ['Max bars', btAiResult.risk.maxBarsInTrade, ''],
                                            ['Flatten', btAiResult.risk.flattenBeforeCloseMin, 'm pre-close'],
                                          ].filter(([, v]) => Number(v) > 0).map(([k, v, u]) => (
                                            <Box key={k} sx={{ fontFamily: AT_MONO, fontSize: '11px', color: '#9cc3e4', background: 'rgba(77,143,199,.12)', border: '1px solid rgba(77,143,199,.25)', px: '9px', py: '4px', borderRadius: '6px' }}>
                                              {k} {v}{u}
                                            </Box>
                                          ))}
                                        </Box>
                                        {btAiResult.risk.rationale && (
                                          <Typography sx={{ fontSize: '11.5px', color: AT.muted, mt: '7px', lineHeight: 1.5 }}>
                                            {btAiResult.risk.rationale}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                    <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                                      <Box component="button" onClick={applyAdvisorSuggestion}
                                        sx={{ fontFamily: AT_SANS, fontSize: '12.5px', fontWeight: 600, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '16px', py: '9px', borderRadius: '8px', cursor: 'pointer' }}>
                                        Apply to draft
                                      </Box>
                                      <Box component="button" onClick={() => setBtAiResult(null)}
                                        sx={{ fontFamily: AT_SANS, fontSize: '12.5px', fontWeight: 600, color: '#9cc3e4', background: 'transparent', border: '1px solid rgba(77,143,199,.3)', px: '16px', py: '9px', borderRadius: '8px', cursor: 'pointer' }}>
                                        Dismiss
                                      </Box>
                                    </Box>
                                  </>
                                )}
                                {Array.isArray(btAiResult.warnings) && btAiResult.warnings.length > 0 && (
                                  <Typography sx={{ fontSize: '11.5px', color: '#d9a441', mt: '9px' }}>
                                    {btAiResult.warnings.join(' · ')}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>

                          </>)}
                          {/* ── The strategy every check below runs on ──
                              The research tools used to take their input from
                              different places -- signal check from its own text
                              box, breadth from the draft -- so two panels could
                              be answering questions about two different
                              strategies with nothing on screen saying so. They
                              all read the loaded draft now, and this states
                              which strategy that is. */}
                          <Box sx={{ mb: '16px', p: '12px 16px', borderRadius: '10px',
                                     background: btDraftLogic.buy || btDraftLogic.sell ? 'rgba(70,184,138,.06)' : 'rgba(217,164,65,.06)',
                                     border: `1px solid ${btDraftLogic.buy || btDraftLogic.sell ? 'rgba(70,184,138,.25)' : 'rgba(217,164,65,.3)'}` }}>
                            <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, display: 'block', mb: '6px' }}>
                              LOADED STRATEGY — EVERY CHECK BELOW USES THIS
                            </Mono>
                            {btDraftLogic.buy || btDraftLogic.sell ? (
                              <Box sx={{ display: 'grid', gap: '3px' }}>
                                <Mono sx={{ fontSize: '11.5px', color: AT.bright }}>
                                  <span style={{ color: AT.positive }}>entry</span>&nbsp; {btDraftLogic.buy || '(none)'}
                                </Mono>
                                <Mono sx={{ fontSize: '11.5px', color: AT.bright }}>
                                  <span style={{ color: AT.negative }}>exit</span>&nbsp;&nbsp; {btDraftLogic.sell || '(none)'}
                                </Mono>
                                {/* Four-sided bots: the short legs ARE simulated (they ride
                                    along verbatim from the bot) — showing only entry/exit
                                    here made the run look long-only. */}
                                {(btDraftLogic.shortOpen || btDraftLogic.shortClose) && (
                                  <>
                                    <Mono sx={{ fontSize: '11.5px', color: AT.bright }}>
                                      <span style={{ color: '#d9a441' }}>short</span>&nbsp; {btDraftLogic.shortOpen || '(none)'}
                                    </Mono>
                                    <Mono sx={{ fontSize: '11.5px', color: AT.bright }}>
                                      <span style={{ color: AT.accent }}>cover</span>&nbsp; {btDraftLogic.shortClose || '(none)'}
                                    </Mono>
                                  </>
                                )}
                              </Box>
                            ) : (
                              <Mono sx={{ fontSize: '11.5px', color: '#d9a441' }}>
                                Nothing loaded. Run Search &amp; rank above and load a candidate, or edit the
                                strategy in Reconfigure bot — then the checks below have something to test.
                              </Mono>
                            )}
                          </Box>


                          {btResearchTab === 'explore' && (<>
                          {/* Breadth — the same strategy across many symbols.
                              The headline is the SHARE that worked, never the
                              average: an average is carried by one outlier,
                              which is the failure mode this exists to expose. */}
                          <Box sx={{ mb: '16px', p: '14px 16px', borderRadius: '10px', background: 'rgba(255,255,255,.02)', border: '1px solid rgba(255,255,255,.09)' }}>
                            <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, display: 'block', mb: '4px' }}>
                              STEP 2 · SYMBOL DISCOVERY
                            </Mono>
                            {/* One reading, not a toggle. The breadth/discovery chips split
                                the same result into two views, and the answer to "does it
                                generalise" is visible in the discovery view anyway — the
                                share-that-worked line renders either way. Two buttons whose
                                difference is sort order were two buttons too many. */}

                            {/* Lookback for THIS check (handoff explore §2). Breadth used
                                to silently reuse Optimize's date range, so changing the
                                backtest window quietly changed what breadth measured. */}
                            {/* On tick the slider does not apply — the run uses the
                                engine's 12h tick window instead. Leaving a live
                                "365 days" control above a 12h run is the kind of
                                lie that makes a result impossible to interpret. */}
                            <Box sx={{ maxWidth: 380, mb: '13px', opacity: btDraftIsTick ? 0.45 : 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '9px' }}>
                                <Micro size={9.5} sx={{ display: 'inline-block' }}>LOOKBACK WINDOW</Micro>
                                <Num size={10.5} color={btExploreEffectiveDays.clamped ? BT.amber : BT.soft}>
                                  {btDraftIsTick
                                    ? 'last 12 hours'
                                    : btExploreEffectiveDays.clamped
                                      ? `${btExploreEffectiveDays.days} of ${btExLookbackDays} days`
                                      : `${btExLookbackDays} days`}
                                </Num>
                              </Box>
                              {/* 5 years. The bar budget governs what actually runs — the
                                  effective-days readout above the thumb already shows the
                                  clamp per timeframe (1m tops out ~34 days at 50k bars,
                                  1h covers the full 5y). */}
                              <Box component="input" type="range" min={7} max={1825}
                                value={btExLookbackDays}
                                disabled={btDraftIsTick}
                                onChange={(e) => setBtExLookbackDays(Number(e.target.value))}
                                aria-label="Lookback window in days"
                                sx={{ width: '100%', accentColor: BT.accent, cursor: btDraftIsTick ? 'default' : 'pointer' }} />
                              {!btDraftIsTick && btExploreEffectiveDays.clamped && (
                                <Mono sx={{ fontSize: '10.5px', color: AT.muted2, mt: '6px', display: 'block', lineHeight: 1.5 }}>
                                  {`${btEffectiveTimeframe} bars cap the window at ~${btExploreEffectiveDays.days} days `}
                                  (50,000 bars per symbol) — a longer window at this timeframe is more data than a
                                  batch can hold. Use a higher timeframe to look further back.
                                </Mono>
                              )}
                              {btDraftIsTick && (
                                <Mono sx={{ fontSize: '10.5px', color: AT.muted2, mt: '6px', display: 'block', lineHeight: 1.5 }}>
                                  Tick runs use the last 12 hours — the loader walks tick history in 12h
                                  chunks, so a multi-day window is hundreds of sequential fetches per symbol.
                                  Keep the symbol list short here.
                                </Mono>
                              )}
                            </Box>

                            {/* Risk exits, stated where they act (handoff explore §2).
                                Same backtestParams fields as the config drawer — one
                                source of truth, so the values here and there cannot
                                disagree about what every symbol was tested with. */}
                            <BtPanel sx={{ mb: '13px' }}>
                              <Micro size={9.5} sx={{ mb: '11px' }}>RISK EXITS</Micro>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: '12px' }}>
                                {[
                                  ['stopLossPct', 'STOP LOSS %'],
                                  ['takeProfitPct', 'TAKE PROFIT %'],
                                  ['trailingStopPct', 'TRAILING STOP %'],
                                  ['maxBarsInTrade', 'MAX BARS HELD'],
                                ].map(([key, lbl]) => (
                                  <Box key={key}>
                                    <Box component="label" htmlFor={`ex-risk-${key}`} sx={{
                                      display: 'block', fontFamily: BT.mono, fontSize: '9px', letterSpacing: '.08em',
                                      color: BT.muted, mb: '6px', whiteSpace: 'nowrap',
                                    }}>{lbl}</Box>
                                    <BtTextInput id={`ex-risk-${key}`} type="number"
                                      value={backtestParams[key] ?? ''}
                                      onChange={(e) => setBacktestParams((p) => ({ ...p, [key]: e.target.value }))}
                                      placeholder="—"
                                      sx={{ width: '100%', background: '#070a0f', borderRadius: '7px', padding: '8px 10px', fontSize: '12.5px' }} />
                                  </Box>
                                ))}
                              </Box>
                            </BtPanel>

                            {renderSymbolPicker(setBtBatchSymbols)}
                            <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                              <Box component="input"
                                value={btBatchSymbols}
                                onChange={(e) => setBtBatchSymbols(e.target.value)}
                                placeholder="symbols, comma separated"
                                sx={{ ...inputSx, flex: 1, minWidth: 240, fontFamily: AT_MONO, fontSize: '12.5px' }} />
                              <Box component="button"
                                disabled={btBatchBusy || !btActiveBot || !btDraftLogic.buy}
                                onClick={runSymbolBatch}
                                sx={{ fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: '#e7eef6', background: btBatchBusy ? 'rgba(255,255,255,.08)' : 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '18px', py: '11px', borderRadius: '9px', cursor: btBatchBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                                {btBatchBusy ? 'Testing…' : 'Test breadth'}
                              </Box>
                            </Box>

                            {btBatch && (
                              <Box sx={{ mt: '13px', pt: '12px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                                {btBatch.status === 'running' && (
                                  <Mono sx={{ fontSize: '11.5px', color: '#8593ad' }}>
                                    {(() => {
                                      // Re-renders on every poll (~4s), so the clock ticks
                                      // without its own timer.
                                      const done = Number(btBatch.completedSymbols) || 0;
                                      const total = btBatch.symbolCount || '';
                                      const secs = btBatchStartedAt ? Math.floor((Date.now() - btBatchStartedAt) / 1000) : null;
                                      const clock = secs === null ? '' : ` · ${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
                                      return `Backtesting ${total} symbols — ${done > 0 ? `${done} of ${total} done` : 'none finished yet'}${clock}`;
                                    })()}
                                    {/* The way out. A slow batch held the user hostage for up
                                        to 10 minutes per symbol; canceling signals every
                                        per-symbol job and keeps whatever already finished. */}
                                    <Box component="button" type="button"
                                      onClick={async () => {
                                        try {
                                          await backtestService.cancelBatch(btBatch.id);
                                          if (btBatchPollRef.current) { clearInterval(btBatchPollRef.current); btBatchPollRef.current = null; }
                                          setBtBatchBusy(false);
                                          setBtBatch(b => ({ ...(b || {}), status: 'canceled' }));
                                          showToast('info', 'Batch stopped. Symbols that already finished are kept.');
                                        } catch (err) {
                                          showToast('error', err?.response?.data?.error || 'Could not stop the batch.');
                                        }
                                      }}
                                      sx={{ ml: '12px', fontFamily: AT_SANS, fontSize: '11px', fontWeight: 600, color: '#e0a5a0',
                                            background: 'transparent', border: '1px solid rgba(224,90,90,.35)',
                                            px: '10px', py: '3px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                      Stop
                                    </Box>
                                  </Mono>
                                )}
                                {btBatch.status === 'canceled' && (
                                  <Mono sx={{ fontSize: '12px', color: AT.muted, display: 'block' }}>
                                    Stopped. {Number(btBatch.completedSymbols) > 0 ? `${btBatch.completedSymbols} symbol(s) finished before the stop and are kept below when results exist.` : 'No symbols had finished.'}
                                  </Mono>
                                )}
                                {/* Silence is a symptom. A healthy batch settles its first
                                    symbol within a couple of minutes; three with nothing
                                    means the worker probably isn't consuming, and saying so
                                    beats letting the user watch a clock to the 10-minute
                                    timeout that will say it anyway. */}
                                {btBatch.status === 'running' && btBatchStartedAt
                                  && Date.now() - btBatchStartedAt > 180_000
                                  && !(Number(btBatch.completedSymbols) > 0) && (
                                  <Mono sx={{ fontSize: '11px', color: '#d9a441', mt: '6px', display: 'block', lineHeight: 1.5 }}>
                                    Nothing has finished after 3 minutes — the backtest worker may be down or
                                    overloaded. Each symbol times out at 10 minutes with a diagnosis.
                                  </Mono>
                                )}
                                {btBatch.status === 'failed' && (
                                  <Typography sx={{ fontSize: '12.5px', color: AT.negative }}>{btBatch.error || 'Batch failed'}</Typography>
                                )}
                                {btBatch.status === 'completed' && btBatch.summary && (() => {
                                  const s = btBatch.summary;

                                  // When nothing measurable came back the server
                                  // sends only {symbolsRequested, symbolsCompleted:
                                  // 0, symbolsFailed, verdict} — no median, no mean,
                                  // no bySymbol. The summary is still truthy, so the
                                  // block below rendered and crashed the whole screen
                                  // on undefined.toFixed(). Report the failure, which
                                  // is the actionable half anyway: WHICH symbols had
                                  // no data.
                                  if (!Number.isFinite(s.medianReturnPct)) {
                                    const failed = Array.isArray(s.failedSymbols) ? s.failedSymbols : [];
                                    return (
                                      <>
                                        <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '10px', fontWeight: 700, letterSpacing: '.06em', color: '#d9a441', background: 'rgba(217,164,65,.1)', border: '1px solid rgba(217,164,65,.35)', px: '9px', py: '4px', borderRadius: '5px' }}>
                                          {String(s.verdict || 'no measurable runs').toUpperCase()}
                                        </Box>
                                        <Mono sx={{ fontSize: '11.5px', color: AT.muted, mt: '9px', display: 'block', lineHeight: 1.6 }}>
                                          {`${s.symbolsFailed ?? 0} of ${s.symbolsRequested ?? 0} symbols produced no result, so there is nothing to compare.`}
                                          {' Usually the symbols have no data for this window, or they are not tradeable on this bot’s exchange.'}
                                        </Mono>
                                        {failed.length > 0 && (
                                          <Mono sx={{ fontSize: '10.5px', color: AT.muted2, mt: '7px', display: 'block' }}>
                                            {`no data: ${failed.slice(0, 20).join(', ')}${failed.length > 20 ? ` +${failed.length - 20} more` : ''}`}
                                          </Mono>
                                        )}
                                        {Array.isArray(s.skippedSymbols) && s.skippedSymbols.length > 0 && (
                                          <Mono sx={{ fontSize: '10.5px', color: AT.muted2, mt: '7px', display: 'block' }}>
                                            {`skipped (not enough history): ${s.skippedSymbols.slice(0, 20).join(', ')}${s.skippedSymbols.length > 20 ? ` +${s.skippedSymbols.length - 20} more` : ''}`}
                                          </Mono>
                                        )}
                                      </>
                                    );
                                  }

                                  const good = s.verdict === 'broad';
                                  const warn = s.verdict === 'broad but concentrated in one symbol' || s.verdict === 'mixed';
                                  const col = good ? AT.positive : warn ? '#d9a441' : AT.negative;
                                  return (
                                    <>
                                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', mb: '10px' }}>
                                        <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '10px', fontWeight: 700, letterSpacing: '.06em', color: col, background: `${col}1a`, border: `1px solid ${col}55`, px: '9px', py: '4px', borderRadius: '5px' }}>
                                          {String(s.verdict || '').toUpperCase()}
                                        </Box>
                                        <Mono sx={{ fontSize: '11.5px', color: '#8593ad' }}>
                                          {`${s.symbolsProfitable}/${s.symbolsCompleted} profitable · median ${fmtPctSigned(s.medianReturnPct)} · mean ${fmtPctSigned(s.meanReturnPct)}`}
                                          {/* Beating buy-and-hold is a different bar from being
                                              profitable: +5% in a +20% market is the market's
                                              result, not the strategy's. Guarded on != null so
                                              records saved before the field existed render as
                                              before. */}
                                          {s.beatBenchmarkCount != null ? ` · ${s.beatBenchmarkCount}/${s.benchmarkedCount} beat buy-and-hold` : ''}
                                          {s.symbolsFailed ? ` · ${s.symbolsFailed} failed` : ''}
                                          {s.symbolsSkipped ? ` · ${s.symbolsSkipped} skipped (no history)` : ''}
                                        </Mono>
                                      </Box>
                                      <Mono sx={{ fontSize: '10.5px', color: AT.muted2, mb: '8px', display: 'block' }}>
                                        {`scored on ${s.basis}`}
                                        {Number.isFinite(s.topSymbolSharePct) ? ` · best symbol is ${s.topSymbolSharePct.toFixed(0)}% of the total` : ''}
                                        {` · ${s.survives2xCount} survive 2× slippage · ${s.beatRandomCount} beat random entries`}
                                      </Mono>
                                      {(btBatch.feeAuto?.zeroFeeSymbols || []).length > 0 && (
                                        <Mono sx={{ fontSize: '10.5px', color: AT.muted2, mb: '8px', display: 'block' }}>
                                          {`${btBatch.feeAuto.zeroFeeSymbols.length} zero-fee pair(s) charged 0% commission automatically: ${btBatch.feeAuto.zeroFeeSymbols.slice(0, 8).join(', ')}${btBatch.feeAuto.zeroFeeSymbols.length > 8 ? '…' : ''}`}
                                        </Mono>
                                      )}
                                      {[...(s.bySymbol || [])]
                                        // Best first, always — "which symbols work" is the
                                        // question this panel answers now that the breadth
                                        // reading has folded into it.
                                        .sort((a, b) => (b.returnPct ?? -1e9) - (a.returnPct ?? -1e9))
                                        .map((r) => (
                                        <Box key={r.symbol} sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', py: '3px' }}>
                                          <Mono sx={{ fontSize: '11.5px', color: AT.body, width: 96, flex: 'none' }}>{r.symbol}</Mono>
                                          <Mono sx={{ fontSize: '12.5px', fontWeight: 600, color: r.returnPct >= 0 ? AT.positive : AT.negative, width: 74, textAlign: 'right' }}>
                                            {fmtPctSigned(r.returnPct)}
                                          </Mono>
                                          <Mono sx={{ fontSize: '10.5px', color: AT.muted2 }}>
                                            {`${r.totalTrades} trades · ${r.basis}${r.survives2x === false ? ' · fails 2×' : ''}`}
                                          </Mono>
                                          {/* Both horizons and the benchmark. On holdout basis
                                              the big number is the holdout, so this line carries
                                              the full-run figure plus the HOLDOUT's benchmark and
                                              alpha (matching the number above it); on in-sample
                                              basis the big number IS the full run, so only the
                                              benchmark pair is added — never the same figure
                                              twice. Nulls (older records, no benchmark) simply
                                              omit their segment. */}
                                          {(() => {
                                            const onHoldout = r.basis === 'holdout';
                                            const bench = onHoldout ? r.holdoutBenchmarkPct : r.benchmarkPct;
                                            const alpha = onHoldout ? r.holdoutAlphaPct : r.alphaPct;
                                            const parts = [];
                                            if (onHoldout && Number.isFinite(r.overallReturnPct)) parts.push(`full ${fmtPctSigned(r.overallReturnPct)}`);
                                            if (Number.isFinite(bench)) parts.push(`hold ${fmtPctSigned(bench)}`);
                                            if (!parts.length && !Number.isFinite(alpha)) return null;
                                            return (
                                              <Mono sx={{ fontSize: '10.5px', color: AT.muted2 }}>
                                                {parts.join(' · ')}
                                                {Number.isFinite(alpha) && (
                                                  <>
                                                    {parts.length ? ' · ' : ''}
                                                    <Box component="span" sx={{ color: alpha >= 0 ? AT.positive : AT.negative }}>
                                                      {`α ${fmtPctSigned(alpha)}`}
                                                    </Box>
                                                  </>
                                                )}
                                              </Mono>
                                            );
                                          })()}
                                          {/* Tune and Explore are offered for EVERY completed row,
                                              not just the profitable ones: a marginal symbol is
                                              exactly the case where tuning might change the answer,
                                              and refusing to load it forces a re-run to find out.
                                              Deploying stays gated on a positive return. */}
                                          {Number.isFinite(r.returnPct) && (
                                            <Box sx={{ ml: 'auto', display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
                                              <Box component="button" onClick={() => tuneDiscoveredSymbol(r.symbol)}
                                                title="Load this symbol into the tuning step below"
                                                sx={{ fontFamily: AT_SANS, fontSize: '11px', fontWeight: 600, color: '#9cc3e4',
                                                      background: 'transparent', border: '1px solid rgba(77,143,199,.3)',
                                                      px: '10px', py: '4px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                Tune this
                                              </Box>
                                              {r.returnPct > 0 && (
                                                <Box component="button" onClick={() => createBotForSymbol(r.symbol)}
                                                  sx={{ fontFamily: AT_SANS, fontSize: '11px', fontWeight: 600, color: '#9cc3e4',
                                                        background: 'transparent', border: '1px solid rgba(77,143,199,.3)',
                                                        px: '10px', py: '4px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                  Start a bot here
                                                </Box>
                                              )}
                                            </Box>
                                          )}
                                        </Box>
                                      ))}
                                      <Mono sx={{ fontSize: '10.5px', color: AT.muted, mt: '9px', display: 'block', lineHeight: 1.6 }}>
                                        Picking the best of {s.symbolsCompleted} symbols is itself a choice made from many
                                        tries — the winner is partly luck. Prefer one that also survives 2× costs and beats
                                        random entries, and re-test it on its own before committing capital.
                                      </Mono>
                                    </>
                                  );
                                })()}
                              </Box>
                            )}
                          </Box>

                          </>)}
                          {/* The sweep panel serves BOTH modes. Optimize tunes the bot you
                              came in with; Explore tunes the strategy you just found, on the
                              symbol breadth picked — inline, so you never leave Explore and
                              lose it. Same pipeline either way: one plan call, one job, one
                              honesty card. A second tuner would be a second thing to keep
                              honest. */}
                          {(btResearchTab === 'optimize' || (btResearchTab === 'explore' && btBatch && ['completed', 'partial'].includes(btBatch.status))) && (<>
                          {btResearchTab !== 'explore' && (<>
                          {/* Strategy editor BEFORE the tuner, so Tune for the
                              symbol is the flow's final step, as the handoff ends
                              Explore on it. Edit, then tune what you edited. */}
                          {/* Builder vs raw expression. The engine already
                              supports arithmetic on operands and conditionals -
                              mathjs evaluates them and normalizeLogicExpression
                              leaves them intact. Only this UI could not express
                              them, so this is an escape hatch, not a new engine
                              capability. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', mb: '12px' }}>
                            <Box sx={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,.12)', borderRadius: '9px', overflow: 'hidden' }}>
                              {['builder', 'expression'].map((mo) => (
                                <Box key={mo} component="button"
                                  onClick={() => setBtDraft(d => {
                                    if (!d || d.mode === mo) return d;
                                    // Builder -> expression carries the built string across so
                                    // nothing is lost. The reverse is refused when the raw text
                                    // cannot round-trip, which is the point of the guard.
                                    if (mo === 'expression') {
                                      return { ...d, mode: 'expression', forcedExpression: false,
                                        rawBuy: buildLogicFromConditions(d.buyConditions),
                                        rawSell: buildLogicFromConditions(d.sellConditions),
                                        rawShortOpen: buildLogicFromConditions(d.shortOpenConditions || []),
                                        rawShortClose: buildLogicFromConditions(d.shortCloseConditions || []) };
                                    }
                                    if (!canRepresentInBuilder(d.rawBuy) || !canRepresentInBuilder(d.rawSell)
                                        || !canRepresentInBuilder(d.rawShortOpen || '') || !canRepresentInBuilder(d.rawShortClose || '')) {
                                      showToast('info', 'This expression uses arithmetic or a conditional, which the builder cannot show. Staying in expression mode.');
                                      return d;
                                    }
                                    return { ...d, mode: 'builder', forcedExpression: false,
                                      buyConditions: parseLogicToConditions(d.rawBuy) || [],
                                      sellConditions: parseLogicToConditions(d.rawSell) || [],
                                      shortOpenConditions: parseLogicToConditions(d.rawShortOpen || '') || [],
                                      shortCloseConditions: parseLogicToConditions(d.rawShortClose || '') || [] };
                                  })}
                                  sx={{ fontFamily: AT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.06em',
                                        textTransform: 'uppercase', px: '13px', py: '6px', border: 'none', cursor: 'pointer',
                                        color: btDraft?.mode === mo ? '#06121f' : AT.muted,
                                        background: btDraft?.mode === mo ? AT.accent : 'transparent' }}>
                                  {mo}
                                </Box>
                              ))}
                            </Box>
                            {btDraft?.forcedExpression && (
                              <Mono sx={{ fontSize: '10.5px', color: '#d9a441' }}>
                                This strategy uses arithmetic or a conditional - the builder cannot show it without changing it.
                              </Mono>
                            )}
                          </Box>

                          {btDraft?.mode === 'expression' ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px' }}>
                              {[['rawBuy', 'ENTRY', AT.positive], ['rawSell', 'EXIT', AT.negative]].map(([key, label, col]) => {
                                const err = validateExpression(btDraft[key]);
                                return (
                                  <Box key={key}>
                                    <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: col, mb: '7px', display: 'block' }}>{label}</Mono>
                                    <Box component="textarea"
                                      value={btDraft?.[key] || ''}
                                      onChange={(e) => { const v = e.target.value; setBtDraft(d => ({ ...(d || {}), [key]: v })); }}
                                      spellCheck={false}
                                      rows={3}
                                      placeholder={key === 'rawBuy' ? 'close < SMA(20) * 0.98' : 'close > SMA(20) * 1.02'}
                                      sx={{ width: '100%', fontFamily: AT_MONO, fontSize: '12.5px', lineHeight: 1.6,
                                            color: AT.bright, background: AT.inner, borderRadius: '9px', px: '13px', py: '11px',
                                            outline: 'none', resize: 'vertical',
                                            border: `1px solid ${err ? 'rgba(224,90,90,.55)' : 'rgba(255,255,255,.1)'}`,
                                            '&:focus': { borderColor: err ? 'rgba(224,90,90,.7)' : 'rgba(77,143,199,.5)' } }} />
                                    {err && <Mono sx={{ fontSize: '10.5px', color: AT.negative, mt: '5px', display: 'block' }}>{err}</Mono>}
                                  </Box>
                                );
                              })}
                              <Mono sx={{ gridColumn: '1 / -1', fontSize: '10.5px', color: AT.muted, lineHeight: 1.6 }}>
                                {'Arithmetic: SMA(20) * 0.98, close - ATR(14) * 2  |  Conditional: RSI(14) < 30 ? A : B  |  '}
                                {'Joins: AND OR NOT  |  Functions: min max abs'}
                                {btDraftIsStock ? '  |  Session: Hour, MinutesSinceOpen, MinutesToClose' : '  |  Time: Hour'}
                              </Mono>
                              {/* Short legs — stock bots only. Editing them here refines
                                  all four sides of a long+short strategy, matching the
                                  LOADED STRATEGY summary. */}
                              {btDraftIsStock && (btDraftLogic.shortOpen || btDraftLogic.shortClose || btDraft?.rawShortOpen != null) && (
                                <>
                                  {[['rawShortOpen', 'OPEN SHORT', '#d9a441'], ['rawShortClose', 'CLOSE SHORT', AT.accent]].map(([key, label, col]) => {
                                    const err = validateExpression(btDraft?.[key] || '');
                                    return (
                                      <Box key={key}>
                                        <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: col, mb: '7px', display: 'block' }}>{label}</Mono>
                                        <Box component="textarea"
                                          value={btDraft?.[key] || ''}
                                          onChange={(e) => { const v = e.target.value; setBtDraft(d => ({ ...(d || {}), [key]: v })); }}
                                          spellCheck={false} rows={3}
                                          placeholder={key === 'rawShortOpen' ? 'close > SMA(20) * 1.03' : 'close < SMA(20)'}
                                          sx={{ width: '100%', fontFamily: AT_MONO, fontSize: '12.5px', lineHeight: 1.6,
                                                color: AT.bright, background: AT.inner, borderRadius: '9px', px: '13px', py: '11px',
                                                outline: 'none', resize: 'vertical',
                                                border: `1px solid ${err ? 'rgba(224,90,90,.55)' : 'rgba(255,255,255,.1)'}`,
                                                '&:focus': { borderColor: err ? 'rgba(224,90,90,.7)' : 'rgba(77,143,199,.5)' } }} />
                                        {err && <Mono sx={{ fontSize: '10.5px', color: AT.negative, mt: '5px', display: 'block' }}>{err}</Mono>}
                                      </Box>
                                    );
                                  })}
                                </>
                              )}
                            </Box>
                          ) : (
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px' }}>
                            {/* With no bot selected btDraft is null, so `btDraft?.mode` is
                                undefined and this builder branch is the one that renders —
                                the else of a ternary is the easy place to forget a null.
                                Empty arrays, not a crash: the builder is usable before a
                                bot exists, which is what Explore is for. */}
                            <ConditionsPanel
                              type="buy"
                              conditions={btDraft?.buyConditions || []}
                              indicators={btIndicators}
                              marketType={btDraftMarketType}
                              baseInterval={btDraft?.timeframe}
                              isStock={btDraftIsStock}
                              onChange={(next) => setBtDraft(d => ({ ...(d || {}), buyConditions: next }))}
                            />
                            <ConditionsPanel
                              type="sell"
                              conditions={btDraft?.sellConditions || []}
                              indicators={btIndicators}
                              marketType={btDraftMarketType}
                              baseInterval={btDraft?.timeframe}
                              isStock={btDraftIsStock}
                              onChange={(next) => setBtDraft(d => ({ ...(d || {}), sellConditions: next }))}
                            />
                            {/* Short legs — stock bots only. Full visual builder, so a
                                long+short bot refines all four sides here instead of
                                showing two while LOADED STRATEGY shows four. */}
                            {btDraftIsStock && (btDraftLogic.shortOpen || btDraftLogic.shortClose || (btDraft?.shortOpenConditions?.length ?? 0) > 0 || (btDraft?.shortCloseConditions?.length ?? 0) > 0) && (
                              <>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                  <Mono sx={{ fontSize: '10px', letterSpacing: '.1em', color: AT.muted, mt: '4px', display: 'block' }}>SHORT SIDE</Mono>
                                </Box>
                                <ConditionsPanel
                                  type="short_open"
                                  conditions={btDraft?.shortOpenConditions || []}
                                  indicators={btIndicators}
                                  marketType={btDraftMarketType}
                                  baseInterval={btDraft?.timeframe}
                                  isStock={btDraftIsStock}
                                  onChange={(next) => setBtDraft(d => ({ ...(d || {}), shortOpenConditions: next }))}
                                />
                                <ConditionsPanel
                                  type="short_close"
                                  conditions={btDraft?.shortCloseConditions || []}
                                  indicators={btIndicators}
                                  marketType={btDraftMarketType}
                                  baseInterval={btDraft?.timeframe}
                                  isStock={btDraftIsStock}
                                  onChange={(next) => setBtDraft(d => ({ ...(d || {}), shortCloseConditions: next }))}
                                />
                              </>
                            )}
                          </Box>
                          )}

                          </>)}

                          {/* The step heading lives on the sweep card itself ("Step 3 ·
                              Tune for the symbol"); this row is just the symbol chip that
                              the handoff puts beside it. */}
                          {btResearchTab === 'explore' && btBatch && ['completed', 'partial'].includes(btBatch.status) && (
                            <Box ref={btTunePanelRef} sx={{ mb: '10px', scrollMarginTop: '16px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <Micro size={9.5} sx={{ display: 'inline-block' }}>SYMBOL</Micro>
                                <Box component="select"
                                  value={btTuneSymbol}
                                  onChange={(e) => { setBtTuneSymbol(e.target.value); setBtSweepPlan(null); setBtSweep(null); invalidateSweepStash('explore'); }}
                                  sx={{
                                    fontFamily: AT_MONO, fontSize: '12.5px', color: BT.bright,
                                    background: BT.inner, border: `1px solid ${BT.inputBd}`,
                                    borderRadius: BT.rChip, padding: '9px 13px', outline: 'none', cursor: 'pointer',
                                  }}>
                                  <option value="">Pick a symbol…</option>
                                  {([...(btBatch.summary?.bySymbol || [])]
                                    .sort((a, b) => (b.returnPct ?? -Infinity) - (a.returnPct ?? -Infinity))
                                    .map((r) => (
                                      <option key={r.symbol} value={r.symbol}>
                                        {`${r.symbol}${Number.isFinite(r.returnPct) ? ` · ${r.returnPct >= 0 ? '+' : ''}${Number(r.returnPct).toFixed(2)}%` : ''}`}
                                      </option>
                                    )))}
                                </Box>
                              </Box>
                            </Box>
                          )}

                          {/* ── Parameter sweep ──
                              Search finds the FORM of a strategy; this tunes its
                              NUMBERS. Those numbers were picked once and, across
                              36 measured candidates on one symbol, never varied.

                              The headline is deliberately the SHAPE of each
                              response, not a winning row: given enough
                              combinations something always looks excellent by
                              luck, and a plateau is the only version of that
                              which tends to survive contact with new data. */}
                          <Box sx={{
                            mb: '16px', p: '18px 20px', borderRadius: BT.rCard,
                            background: BT.card, border: `1px solid ${BT.hairline}`,
                            boxShadow: '0 1px 2px rgba(0,0,0,.22)',
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', mb: '8px' }}>
                              {/* Handoff §12 header: icon tile + sentence-case title. */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                                <Box sx={{
                                  width: 30, height: 30, borderRadius: '8px', flex: 'none',
                                  background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7fb2dc',
                                }}>
                                  <Box component="svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                                    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                                    <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                                    <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
                                  </Box>
                                </Box>
                                <Box component="span" sx={{ fontSize: '15px', fontWeight: 700, color: BT.bright }}>
                                  {btResearchTab === 'explore' ? 'Step 3 · Tune for the symbol' : 'Step 2 · Tune the numbers'}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {[['risk', 'RISK EXITS'], ['indicator', 'INDICATORS'], ['discover', 'DISCOVER']].map(([k, lbl]) => (
                                  <BtChip key={k} active={btSweepKind === k}
                                    onClick={() => switchSweepKind(k)}>
                                    {lbl}
                                  </BtChip>
                                ))}
                              </Box>
                            </Box>

                            {!btSweepPlan && (
                              <BtGhostBtn onClick={() => loadSweepPlan(btSweepKind)}>
                                {btSweepKind === 'discover' ? 'Build the candidate set' : 'Find tunable parameters'}
                              </BtGhostBtn>
                            )}

                            {btSweepPlan && (
                              <>
                                {/* Discovery has no axes by design — the server builds the
                                    candidates — so the axis-less state is normal here, not the
                                    "nothing tunable" failure it means for the other two kinds. */}
                                {btSweepKind === 'discover' && (
                                  <BtPanel sx={{ mb: '12px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                      <Micro size={9.5} sx={{ display: 'inline-block' }}>CANDIDATES TO SCREEN</Micro>
                                      <BtTextInput type="number" min={20} max={btSweepPlan.cap || 400} step={20}
                                        value={btSweepMaxCandidates}
                                        onChange={(e) => setBtSweepMaxCandidates(Number(e.target.value) || 0)}
                                        sx={{ width: '96px', fontSize: '12.5px', padding: '8px 11px' }} />
                                      <Num size={11} color={BT.muted}>
                                        {`${btSweepPlan.generated ?? '—'} generated from the catalogue`}
                                        {btSweepPlan.truncated ? ` · ${btSweepPlan.truncated} beyond the cap will not be screened` : ''}
                                      </Num>
                                    </Box>
                                  </BtPanel>
                                )}

                                {btSweepKind !== 'discover' && !(btSweepPlan.axes || []).length && (
                                  <Notice tone="amber" title="Nothing tunable found in this strategy">
                                    {btSweepKind === 'risk'
                                      ? 'Add a stop-loss or take-profit exit to your logic to sweep risk parameters.'
                                      : 'Indicator sweeps need a call with a number in it, like RSI(14). Thresholds are only swept for indicators with a bounded range.'}
                                  </Notice>
                                )}
                                {/* Two presentations of the same axes. Explore renders the
                                    handoff's HYPERPARAMETERS grid: label over a comma-list
                                    input, no checkboxes — a field with two or more values is
                                    swept, a single value rides along fixed, which is exactly
                                    sweepAxesPayload's filter. Optimize keeps checkbox rows,
                                    where unchecked axes dim rather than disappear: the grid
                                    you did not run is part of reading the one you did. */}
                                {btResearchTab === 'explore' && btSweepKind !== 'discover' && (btSweepPlan.axes || []).length > 0 ? (
                                  <BtPanel sx={{ mb: '4px' }}>
                                    <Micro size={9.5} sx={{ mb: '11px' }}>HYPERPARAMETERS TO TUNE</Micro>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: '12px' }}>
                                      {(btSweepPlan.axes || []).map((a) => (
                                        <Box key={a.key} sx={{ minWidth: 0 }}>
                                          <Box component="label" htmlFor={`tune-${a.key}`} sx={{
                                            display: 'block', fontFamily: BT.mono, fontSize: '9px', letterSpacing: '.06em',
                                            color: BT.muted, textTransform: 'uppercase', whiteSpace: 'nowrap',
                                            overflow: 'hidden', textOverflow: 'ellipsis', mb: '6px',
                                          }}>{a.label}</Box>
                                          <BtTextInput
                                            id={`tune-${a.key}`}
                                            value={btSweepVals[a.key] ?? ''}
                                            onChange={(e) => setBtSweepVals((p) => ({ ...p, [a.key]: e.target.value }))}
                                            aria-label={`${a.label} values to sweep`}
                                            sx={{ width: '100%', background: '#070a0f', borderRadius: '7px', padding: '8px 10px', fontSize: '12.5px' }} />
                                        </Box>
                                      ))}
                                    </Box>
                                  </BtPanel>
                                ) : (btSweepPlan.axes || []).map((a) => (
                                  <Box key={a.key} sx={{
                                    display: 'flex', alignItems: 'center', gap: '10px', py: '6px', flexWrap: 'wrap',
                                    opacity: btSweepSel[a.key] ? 1 : 0.55, transition: 'opacity .15s',
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                  }}>
                                    <Box component="input" type="checkbox"
                                      id={`sweep-axis-${a.key}`}
                                      checked={!!btSweepSel[a.key]}
                                      onChange={(e) => setBtSweepSel((p) => ({ ...p, [a.key]: e.target.checked }))}
                                      sx={{ accentColor: BT.accent, cursor: 'pointer' }} />
                                    <Box component="label" htmlFor={`sweep-axis-${a.key}`}
                                      sx={{ fontFamily: BT.mono, fontSize: '12px', color: BT.body, minWidth: '190px', cursor: 'pointer' }}>
                                      {a.label}
                                    </Box>
                                    <BtTextInput
                                      value={btSweepVals[a.key] ?? ''}
                                      onChange={(e) => setBtSweepVals((p) => ({ ...p, [a.key]: e.target.value }))}
                                      disabled={!btSweepSel[a.key]}
                                      aria-label={`${a.label} values to sweep`}
                                      sx={{ flex: 1, minWidth: '180px', fontSize: '12.5px', padding: '8px 11px' }} />
                                  </Box>
                                ))}

                                {(btSweepKind === 'discover' || (btSweepPlan.axes || []).length > 0) && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mt: '14px', flexWrap: 'wrap' }}>
                                    {/* Over the cap no longer disables: the server samples the
                                        grid and reports total vs tested. Refusing here would
                                        forbid exactly the runs the sampler exists for. */}
                                    <BtPrimaryBtn
                                      disabled={btSweepBusy}
                                      onClick={runSweep}>
                                      {btSweepKind === 'discover'
                                        ? (btSweepBusy ? 'Running…' : 'Run discovery')
                                        : btResearchTab === 'explore'
                                          ? (btSweepBusy ? 'Tuning…' : 'Run tuning')
                                          : (btSweepBusy ? 'Sweeping…' : 'Run sweep')}
                                    </BtPrimaryBtn>
                                    {/* Says what will actually run. Over the cap the server
                                        samples, so the honest phrasing is "N of M", not a red
                                        number implying the run is forbidden. */}
                                    <Num size={11.5} color={BT.muted}>
                                      {(() => {
                                        // Discover's count already sits beside its own input two
                                        // lines up; repeating it here was pure noise.
                                        if (btSweepKind === 'discover') return null;
                                        const cap = btSweepPlan.cap || 200;
                                        const over = btSweepCombos > cap;
                                        // Counts and the sampling disclosure are information;
                                        // the how-to clauses that used to trail them were
                                        // description, and went in the de-cluttering pass.
                                        return over
                                          ? `${btSweepCombos} combinations · ${cap} will be sampled, evenly across every value`
                                          : `${btSweepCombos} combination${btSweepCombos === 1 ? '' : 's'}`;
                                      })()}
                                    </Num>
                                  </Box>
                                )}
                              </>
                            )}

                            {/* Never a bare phase string. A user who cannot see movement
                                assumes the job has hung, and the phase alone gives no sense
                                of how long "screening" lasts. Stepper + bar + ETA. */}
                            {btSweep && btSweep.status === 'running' && (
                              <ProgressStepper
                                phase={btSweep.phase}
                                progress={btSweep.progress}
                                // A risk grid has no screen/backtest split — one 'sweeping'
                                // phase covers the whole run. The record's own kind decides,
                                // not btSweepKind: the user may be looking at another tab
                                // while this one runs.
                                steps={btSweep.kind === 'risk' ? RISK_PHASES : undefined}
                                pct={(() => {
                                  const done = Number(btSweep.completed), total = Number(btSweep.total);
                                  if (Number.isFinite(done) && Number.isFinite(total) && total > 0) {
                                    // Indicator/discover: screening is the first 70% of the
                                    // job, matching the runner's onProgress split. Risk: the
                                    // grid IS the whole job, so the bar runs to 100 — the 70
                                    // cap left every risk sweep looking stuck near done.
                                    return btSweep.kind === 'risk'
                                      ? Math.min(100, (done / total) * 100)
                                      : Math.min(70, (done / total) * 70);
                                  }
                                  return undefined;
                                })()}
                                etaSeconds={(() => {
                                  const done = Number(btSweep.completed), total = Number(btSweep.total);
                                  if (!btSweepStartedAt || !Number.isFinite(done) || !Number.isFinite(total) || done <= 0 || total <= 0) return undefined;
                                  const elapsed = (Date.now() - btSweepStartedAt) / 1000;
                                  const perItem = elapsed / done;
                                  return Math.max(1, perItem * (total - done));
                                })()}
                              />
                            )}
                            {/* Slow and dead look identical from a polled record — the poll
                                succeeds either way. Movement is the discriminator: three
                                minutes with neither phase nor count changing means the
                                worker is gone or wedged, and the Stop button is the way out
                                (the runner checkpoints, so whatever completed is kept). */}
                            {btSweep && btSweep.status === 'running' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', mt: '8px' }}>
                                {btSweep.jobId && (
                                  <Box component="button" type="button"
                                    onClick={async () => {
                                      try {
                                        await backtestService.cancel(btSweep.jobId);
                                        showToast('info', 'Stop requested — the sweep checkpoints, so finished combinations are kept.');
                                      } catch (err) {
                                        showToast('error', err?.response?.data?.error || 'Could not stop the sweep.');
                                      }
                                    }}
                                    sx={{ fontFamily: AT_SANS, fontSize: '11px', fontWeight: 600, color: '#e0a5a0',
                                          background: 'transparent', border: '1px solid rgba(224,90,90,.35)',
                                          px: '10px', py: '3px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    Stop sweep
                                  </Box>
                                )}
                                {btSweepProgressRef.current.at > 0
                                  && Date.now() - btSweepProgressRef.current.at > 180_000 && (
                                  <Mono sx={{ fontSize: '11px', color: '#d9a441', lineHeight: 1.5 }}>
                                    No progress for {Math.floor((Date.now() - btSweepProgressRef.current.at) / 60000)} minutes —
                                    the worker may have died. Stop the sweep and re-run, or check the backtest worker.
                                  </Mono>
                                )}
                              </Box>
                            )}

                            {/* A failed sweep says why, and offers the way back. Before this
                                the record simply stopped updating and the panel sat on its
                                last phase forever. */}
                            {btSweep && btSweep.status === 'failed' && (
                              <Notice tone="red" title="Sweep failed"
                                sx={{ mt: '14px' }}
                                action={<BtGhostBtn onClick={() => runSweep()}>Retry</BtGhostBtn>}>
                                {btSweep.error || 'The sweep stopped before producing a result.'}
                              </Notice>
                            )}

                            {btSweep && ['completed', 'partial'].includes(btSweep.status) && (
                              <Box sx={{ mt: '13px', pt: '11px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                                {btSweep.status === 'partial' && (
                                  <Mono sx={{ fontSize: '11px', color: AT.amber, mb: '9px', display: 'block' }}>
                                    {`STOPPED EARLY — ${btSweep.completed} of ${btSweep.total} measured. Ranked on what finished.`}
                                  </Mono>
                                )}

                                {/* ── Results order is FIXED: honesty, then surfaces, then
                                    leaderboard. Read the other way round, a user takes the
                                    winner and never reaches the caveat explaining it is
                                    noise. The handoff pins this order for that reason. ── */}

                                {btSweep.summary && (
                                  <HonestyCard
                                    pill={(() => {
                                      const c = btSweep.summary.trainHoldoutRankCorr;
                                      // Discovery's FDR is top-level; risk/indicator grids carry
                                      // theirs on the summary. Either failing to clear the bar is
                                      // the same headline.
                                      const fdr = btSweep.fdr || btSweep.summary.fdr;
                                      if (fdr && !fdr.survivors) return 'NOTHING SURVIVED';
                                      if (Number.isFinite(c) && c < 0.1) return 'DID NOT TRANSFER';
                                      return null;
                                    })()}
                                    stats={btSweep.summary.trials > 0
                                      // "0 tried · rank corr n/a · selection spread n/a" told a
                                      // zero-survivor discovery nothing — hide the line entirely
                                      // when there were no backtested rows to summarise.
                                      ? `${btSweep.summary.trials} tried · ${btSweep.summary.unrankedCount} unmeasurable · `
                                        + `rank corr ${btSweep.summary.trainHoldoutRankCorr == null ? 'n/a' : btSweep.summary.trainHoldoutRankCorr.toFixed(2)} · `
                                        + `selection spread ${btSweep.summary.selectionSpread == null ? 'n/a' : btSweep.summary.selectionSpread.toFixed(2)}`
                                      : null}
                                    notes={btSweep.summary.notes || []}
                                  >
                                    {/* The PLAIN answer, first — deploy, keep, or change tack.
                                        Server-computed from the same figures the notes cite, so
                                        this sentence and the statistics cannot disagree. The
                                        notes below become the "how we know". */}
                                    {btSweep.summary.plain && (() => {
                                      const p = btSweep.summary.plain;
                                      const col = p.tone === 'good' ? BT.positive : p.tone === 'caution' ? BT.amber : BT.negative;
                                      return (
                                        <Box sx={{ mb: '14px', pb: '13px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                                          <Box sx={{ fontSize: '15.5px', fontWeight: 700, color: col, lineHeight: 1.45, mb: '6px' }}>
                                            {p.headline}
                                          </Box>
                                          <Box sx={{ fontSize: '12.5px', color: BT.soft, lineHeight: 1.55 }}>
                                            {p.action}
                                          </Box>
                                        </Box>
                                      );
                                    })()}
                                    {/* Discovery's headline is not the winner, it is how much of
                                        the shortlist to distrust. "K survived" means nothing
                                        without the q that produced it and the count expected to
                                        be noise. A zero-survivor run is a real answer and is
                                        stated as one, not left as an empty table. */}
                                    {btSweep.fdr && (
                                      <Box sx={{ mb: '12px' }}>
                                        {!btSweep.fdr.survivors ? (
                                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
                                            {/* 44px, per the handoff — the zero IS the result. */}
                                            <Num size={44} color={BT.amber} weight={600}>0</Num>
                                            <Box component="span" sx={{ fontSize: '14.5px', color: BT.soft, flex: 1, minWidth: '240px', lineHeight: 1.5 }}>
                                              {`of ${btSweep.fdr.m} candidates survived false-discovery control at q=${btSweep.fdr.q}. `}
                                              <Box component="span" sx={{ fontWeight: 700, color: BT.bright }}>
                                                Nothing here is distinguishable from chance.
                                              </Box>
                                            </Box>
                                          </Box>
                                        ) : (
                                          <Box component="span" sx={{ fontSize: '13px', color: BT.soft, lineHeight: 1.5 }}>
                                            {`${btSweep.fdr.survivors} of ${btSweep.fdr.m} candidates survived false-discovery control at q=${btSweep.fdr.q}. About ${btSweep.fdr.expectedFalse} of them are expected to be noise.`}
                                          </Box>
                                        )}
                                        {/* The funnel, so the numbers above are auditable. The
                                            cost-hurdle stage sits between FDR and backtesting:
                                            a candidate can be statistically real and still not
                                            pay for its own round trip. The BH stage is the one
                                            that decided the outcome, so it alone reads amber. */}
                                        <Box sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap', mt: '14px' }}>
                                          {[
                                            [`${btSweep.total ?? '—'} screened`, 'grey'],
                                            [`${btSweep.fdr.m} measurable`, 'grey'],
                                            [`${btSweep.fdr.survivors} survived BH at q=${btSweep.fdr.q}`, 'amber'],
                                            ...(Number.isFinite(Number(btSweep.roundTripCostPct)) && Array.isArray(btSweep.screened)
                                              ? [[`${btSweep.screened.filter(r => !r.failed && !r.rejected).length} cleared 1.5× round-trip cost`, 'grey']] : []),
                                            [`${(btSweep.ranked || []).length} backtested`, 'grey'],
                                          ].map(([s, tone], i) => (
                                            <VerdictPill key={i} tone={tone} size={11} sx={{ padding: '5px 11px', fontWeight: 400, letterSpacing: 0 }}>{s}</VerdictPill>
                                          ))}
                                        </Box>
                                        {/* A zero-survivor run ends with somewhere to go, not a
                                            dead end. Neither action re-runs anything by itself —
                                            spending another screen is the user's click, not a
                                            side effect. */}
                                        {!btSweep.fdr.survivors && btSweep.kind === 'discover' && (
                                          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '13px' }}>
                                            <BtGhostBtn onClick={() => {
                                              setBtSweep(null);
                                              showToast('info', 'Pick a different period above, then run discovery again.');
                                            }} sx={{ fontSize: '12px', padding: '8px 14px' }}>
                                              Try a different window
                                            </BtGhostBtn>
                                            <BtGhostBtn onClick={() => {
                                              const cap = btSweepPlan?.cap || 400;
                                              setBtSweepMaxCandidates(cap);
                                              setBtSweep(null);
                                              showToast('info', `Candidate set raised to ${cap}. Run discovery again — note the correction gets stricter, not looser.`);
                                            }} sx={{ fontSize: '12px', padding: '8px 14px' }}>
                                              Widen the candidate set
                                            </BtGhostBtn>
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                    {/* Discovery's survived-but-unharvestable state. 31 entries
                                        beating chance while none clears costs left an EMPTY
                                        leaderboard — the honest verdict with nothing visible
                                        under it, which read as "no output". The survivors are
                                        already in the payload; show them, with the arithmetic
                                        that disqualified them. Deliberately no Load button: a
                                        screen-level edge that cannot pay its own costs must not
                                        get a one-click path toward deployment — the list exists
                                        to explain, and to inform trying a cheaper venue. */}
                                    {btSweep.kind === 'discover'
                                      && (btSweep.fdr?.survivors || 0) > 0
                                      && (btSweep.ranked || []).length === 0
                                      && Array.isArray(btSweep.screened) && (() => {
                                      const survivors = btSweep.screened
                                        .filter(r => r.survivesFdr && Number.isFinite(r.edgePct))
                                        .sort((a, b) => b.edgePct - a.edgePct);
                                      if (!survivors.length) return null;
                                      const hurdle = Number(btSweep.costHurdlePct);
                                      return (
                                        <Box sx={{ mb: '13px', pb: '12px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                                          <Micro size={9.5} sx={{ display: 'block', mb: '7px' }}>
                                            SURVIVED THE LUCK FILTER · BELOW THE COST HURDLE
                                          </Micro>
                                          <Mono sx={{ fontSize: '11px', color: BT.body, mb: '8px', display: 'block', lineHeight: 1.5 }}>
                                            {Number.isFinite(hurdle)
                                              ? `Each needs a forward edge of at least ${hurdle.toFixed(3)}% to be worth a backtest at this venue's costs; the best here is ${survivors[0].edgePct.toFixed(3)}%.`
                                              : 'None reached the cost hurdle for this venue, so none was backtested.'}
                                          </Mono>
                                          {survivors.slice(0, 10).map((r) => (
                                            <Box key={r.id} sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', py: '2px' }}>
                                              <Mono sx={{ fontSize: '11px', color: BT.soft, minWidth: 170 }}>{r.label}</Mono>
                                              <Mono sx={{ fontSize: '11px', color: BT.muted }}>
                                                {`edge ${r.edgePct.toFixed(3)}% · ${r.signals ?? 0} signals`}
                                              </Mono>
                                            </Box>
                                          ))}
                                          {survivors.length > 10 && (
                                            <Mono sx={{ fontSize: '10.5px', color: BT.muted, mt: '4px', display: 'block' }}>
                                              {`+${survivors.length - 10} more, all below the hurdle`}
                                            </Mono>
                                          )}
                                        </Box>
                                      );
                                    })()}
                                    {/* Labels the notes below as evidence for the verdict above,
                                        so the dense statistics read as backing, not as the
                                        message itself. */}
                                    {btSweep.summary.plain && (btSweep.summary.notes || []).length > 0 && (
                                      <Micro size={9.5} sx={{ display: 'block', mb: '8px', mt: '2px' }}>HOW WE KNOW</Micro>
                                    )}
                                  </HonestyCard>
                                )}

                                {/* The surface. A plateau reads as a run of similar
                                    colour, a spike as one bright cell among dull
                                    ones — which is the judgement being asked for. */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '14px', mt: '14px' }}>
                                {(btSweep.surface?.axes || []).map((ax) => {
                                  const v = surfaceTone(ax.verdict);
                                  return (
                                    <BtPanel key={ax.key} sx={{ borderRadius: BT.rInner, p: '16px' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mb: '12px', flexWrap: 'wrap' }}>
                                        <Num size={12} color={BT.soft}>{ax.label}</Num>
                                        {/* The verdict is the most valuable thing on this
                                            panel and used to render as small grey text. */}
                                        <VerdictPill tone={v.tone}>{v.label}</VerdictPill>
                                      </Box>
                                      <SurfaceCells
                                        points={ax.points}
                                        bestValue={ax.best?.value}
                                        unit={ax.unit}
                                      />
                                      <Box sx={{ fontSize: '12px', color: BT.body, lineHeight: 1.5 }}>
                                        {ax.verdictText}
                                      </Box>
                                      {ax.recommended != null && (
                                        <Num size={10.5} color={BT.muted} sx={{ display: 'block', mt: '6px' }}>
                                          {`recommend ${ax.recommended}${ax.unit || ''} · ${ax.recommendedWhy}`}
                                        </Num>
                                      )}
                                    </BtPanel>
                                  );
                                })}
                                </Box>

                                {/* Leaderboard, last. Every row carries an explicit VERDICT,
                                    because a ranked list with no judgement invites the reader
                                    to treat row 1 as the answer. */}
                                {/* No header over nothing: an empty table under column labels
                                    reads as broken, and the zero-survivor / below-costs states
                                    have their own explanations above. */}
                                {(btSweep.ranked || []).length > 0 && (
                                <Box sx={{ mt: '14px' }}>
                                  <Box sx={{
                                    display: 'grid', gridTemplateColumns: LB_COLUMNS,
                                    gap: '12px', p: '0 4px 10px', borderBottom: `1px solid ${BT.hairline2}`,
                                  }}>
                                    <Micro size={10}>#</Micro>
                                    <Micro size={10}>VARIANT</Micro>
                                    <LbHideSmall><Micro size={10} sx={{ textAlign: 'right' }}>HOLDOUT</Micro></LbHideSmall>
                                    <LbHideSmall><Micro size={10} sx={{ textAlign: 'right' }}>TRADES</Micro></LbHideSmall>
                                    <LbHideSmall><Micro size={10}>VERDICT</Micro></LbHideSmall>
                                    <Micro size={10} />
                                  </Box>
                                </Box>
                                )}
                                {(btSweep.ranked || []).slice(0, 12).map((r) => {
                                  const v = rowVerdict(r, btSweep.summary);
                                  const loadable = !r.isBase && (r.risk || r.buyLogic);
                                  return (
                                    <Box key={r.id || r.rank} sx={{
                                      display: 'grid', gridTemplateColumns: LB_COLUMNS,
                                      gap: '12px', alignItems: 'center', p: '11px 4px',
                                      borderBottom: '1px solid rgba(255,255,255,.04)',
                                    }}>
                                      <Num size={11} color={BT.faint}>{r.rank}</Num>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Num size={12.5} color={r.isBase ? BT.bright : BT.body} sx={{ wordBreak: 'break-word' }}>
                                          {r.label}{r.isBase ? '  (current)' : ''}
                                        </Num>
                                        {/* Narrow screens: the figures and the verdict fold in
                                            under the name rather than scrolling off to the right.
                                            A horizontally scrolled verdict is a hidden verdict,
                                            which on this page is the same as deleting it. */}
                                        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: '10px', flexWrap: 'wrap', mt: '6px' }}>
                                          <Num size={12} weight={600}
                                            color={(r.summary?.holdoutReturnPct ?? 0) >= 0 ? BT.positive : BT.negative}>
                                            {r.summary?.holdoutReturnPct == null ? '—' : `${r.summary.holdoutReturnPct.toFixed(2)}%`}
                                          </Num>
                                          <Num size={11} color={BT.dim}>{`${r.summary?.holdoutTrades ?? 0} trd`}</Num>
                                          <VerdictPill tone={v.tone}>{v.label}</VerdictPill>
                                        </Box>
                                        {/* A discovered candidate's exit is usually a MIRROR of its
                                            entry, chosen so candidates stay comparable — not a
                                            recommendation. It belongs on the row, not only in the
                                            notes, because the row is what gets loaded. */}
                                        {r.exitSource && r.exitSource !== 'your sell logic' && (
                                          <Num size={10} color={BT.amber} sx={{ display: 'block', mt: '3px' }}>
                                            exit: {r.exitSource}
                                          </Num>
                                        )}
                                      </Box>
                                      <LbHideSmall>
                                        <Num size={12.5} weight={600} sx={{ textAlign: 'right', display: 'block' }}
                                          color={(r.summary?.holdoutReturnPct ?? 0) >= 0 ? BT.positive : BT.negative}>
                                          {r.summary?.holdoutReturnPct == null ? '—' : `${r.summary.holdoutReturnPct.toFixed(2)}%`}
                                        </Num>
                                      </LbHideSmall>
                                      <LbHideSmall>
                                        <Num size={11.5} color={BT.dim} sx={{ textAlign: 'right', display: 'block' }}>
                                          {r.summary?.holdoutTrades ?? 0}
                                        </Num>
                                      </LbHideSmall>
                                      <LbHideSmall><VerdictPill tone={v.tone}>{v.label}</VerdictPill></LbHideSmall>
                                      <Box sx={{ textAlign: 'right' }}>
                                        {/* Load, not deploy — and for a row the honesty check
                                            flagged, not even that without asking. This click is
                                            one step from real money. */}
                                        {loadable && (
                                          <Box component="button" type="button"
                                            onClick={() => (v.noise ? setBtPendingLoad(r) : applyLeaderboardRow(r))}
                                            sx={{
                                              fontFamily: AT_SANS, fontSize: '11.5px', fontWeight: 600,
                                              color: v.noise ? BT.muted : BT.link, background: 'transparent',
                                              border: `1px solid ${v.noise ? 'rgba(255,255,255,.14)' : 'rgba(77,143,199,.28)'}`,
                                              padding: '6px 14px', borderRadius: '7px', cursor: 'pointer',
                                              '&:focus-visible': { outline: `2px solid ${BT.accent}`, outlineOffset: '2px' },
                                            }}>
                                            {v.noise ? 'Load…' : 'Load'}
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}

                                {btPendingLoad && (
                                  <ConfirmBar
                                    text="The honesty check called this result noise. Load into a bot draft anyway?"
                                    onCancel={() => setBtPendingLoad(null)}
                                    onConfirm={() => applyLeaderboardRow(btPendingLoad, { flaggedNoise: true })}
                                  />
                                )}
                                {!btPendingLoad && btLoadedMsg && (
                                  <SuccessNote>{btLoadedMsg}</SuccessNote>
                                )}

                                {/* ── TUNED card (explore, handoff §11) ──
                                    Rendered LAST and only when the winner survived the
                                    honesty gates — a noise-flagged winner gets no green
                                    card, because the HonestyCard above already said why.
                                    The order honesty → surfaces → leaderboard → TUNED is
                                    fixed: the celebration never precedes the caveats. */}
                                {btResearchTab === 'explore' && (() => {
                                  const top = (btSweep.ranked || []).find(r => !r.isBase && r.summary);
                                  if (!top) return null;
                                  const v = rowVerdict(top, btSweep.summary);
                                  if (v.noise) return null;
                                  const h = top.summary?.holdoutReturnPct;
                                  if (!Number.isFinite(h) || h <= 0) return null;
                                  const ax = (btSweepPlan?.axes || []).find(a => a.key === top.axisKey);
                                  const diff = top.axisKey != null && top.axisValue != null
                                    ? `${ax?.label || top.axisKey} ${ax?.baseValue != null ? `${ax.baseValue}→` : ''}${top.axisValue} · other fields unchanged`
                                    : 'parameters as shown on the winning row · other fields unchanged';
                                  return (
                                    <Box sx={{ mt: '12px', background: BT.inner, border: '1px solid rgba(70,184,138,.28)', borderRadius: BT.rPanel, p: '16px' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', mb: '10px' }}>
                                        <VerdictPill tone="solidGreen" size={10} sx={{ padding: '4px 10px' }}>TUNED</VerdictPill>
                                        <Num size={12.5} color={BT.body}>
                                          best on holdout: <Box component="span" sx={{ color: BT.positive, fontWeight: 600 }}>{`${h >= 0 ? '+' : ''}${h.toFixed(1)}%`}</Box>
                                          {` · ${top.summary?.holdoutTrades ?? top.summary?.totalTrades ?? 0} trades`}
                                          {top.summary?.survives2x === true ? ' · survives 2× costs' : ''}
                                        </Num>
                                      </Box>
                                      <Num size={12} color={BT.soft} sx={{ display: 'block', mb: '14px' }}>{diff}</Num>
                                      <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        <Box component="button" type="button"
                                          onClick={() => {
                                            applyLeaderboardRow(top);
                                            setBtLoadedMsg('Loaded as the Step 1 strategy.');
                                          }}
                                          sx={{
                                            fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: BT.link,
                                            background: 'rgba(77,143,199,.08)', border: '1px solid rgba(77,143,199,.4)',
                                            padding: '10px 20px', borderRadius: '9px', cursor: 'pointer',
                                            '&:focus-visible': { outline: `2px solid ${BT.accent}`, outlineOffset: '2px' },
                                          }}>
                                          Load strategy to explore more
                                        </Box>
                                        <BtPrimaryBtn onClick={() => createBotForSymbol(btTuneSymbol || btSweep.symbol)}>
                                          Start a bot
                                        </BtPrimaryBtn>
                                      </Box>
                                    </Box>
                                  );
                                })()}
                              </Box>
                            )}
                          </Box>

                          </>)}
                </Box>
              </Paper>
            )}


            {/* §7c — Recent Backtests */}
            {btResearchTab !== 'explore' && (
            <Paper elevation={0} sx={{ p: '24px', mt: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', bgcolor: AT.card }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.25 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><History sx={{ color: '#7fb2dc', fontSize: 16 }} /></Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: AT.bright }}>Recent Backtests</Typography>
                {(() => {
                  const total = recentBacktests.length;
                  const pages = Math.max(1, Math.ceil(total / BT_HISTORY_PAGE_SIZE));
                  const page = Math.min(btHistoryPage, pages - 1);
                  const from = total ? page * BT_HISTORY_PAGE_SIZE + 1 : 0;
                  const to = Math.min(total, (page + 1) * BT_HISTORY_PAGE_SIZE);
                  const btn = (label, onClick, disabled) => (
                    <Box component="button" onClick={onClick} disabled={disabled}
                      sx={{ fontFamily: AT_MONO, fontSize: '11px', color: disabled ? AT.muted2 : '#9cc3e4',
                            background: 'transparent', border: '1px solid rgba(77,143,199,.28)', borderRadius: '6px',
                            px: '8px', py: '3px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .45 : 1 }}>
                      {label}
                    </Box>
                  );
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: 'auto' }}>
                      <Mono sx={{ fontSize: '12px', color: AT.muted }}>
                        {total ? `${from}–${to} of ${total}` : 'none'}
                      </Mono>
                      {pages > 1 && (
                        <>
                          {btn('‹ prev', () => setBtHistoryPage(p => Math.max(0, p - 1)), page <= 0)}
                          {btn('next ›', () => setBtHistoryPage(p => Math.min(pages - 1, p + 1)), page >= pages - 1)}
                        </>
                      )}
                    </Box>
                  );
                })()}
              </Box>
              {recentBacktests.length === 0 ? (
                <Typography sx={{ fontSize: '13.5px', color: AT.muted, py: 1.5 }}>No saved backtests yet.</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 760 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 1fr 0.9fr 0.9fr 0.9fr 0.9fr 40px', gap: '12px', p: '0 4px 11px', borderBottom: '1px solid rgba(255,255,255,.07)', fontFamily: AT_MONO, fontSize: '10.5px', letterSpacing: '.08em', color: AT.muted }}>
                      <span>BOT</span><span>PERIOD</span><span style={{ textAlign: 'right' }}>RETURN</span><span style={{ textAlign: 'right' }}>SHARPE</span><span style={{ textAlign: 'right' }}>WIN</span><span style={{ textAlign: 'right' }}>MAX DD</span><span style={{ textAlign: 'right' }}>DATE</span><span />
                    </Box>
                    {recentBacktests
                      .slice(btHistoryPage * BT_HISTORY_PAGE_SIZE, (btHistoryPage + 1) * BT_HISTORY_PAGE_SIZE)
                      .map((bt) => {
                      const m = bt.metrics || {};
                      const botName = bt.bot_name || botsById[bt.bot_id] || (bt.bot_id != null ? `Bot #${bt.bot_id}` : 'Unknown Bot');
                      const ret = isNum(m.returnPct) ? Number(m.returnPct) : parseFloat(String(m.totalReturn || '').replace('%', ''));
                      const retStr = Number.isFinite(ret) ? `${ret >= 0 ? '+' : ''}${ret.toFixed(2)}%` : (m.totalReturn ?? '—');
                      const retC = Number.isFinite(ret) ? (ret >= 0 ? AT.positive : AT.negative) : AT.body;
                      const dd = isNum(m.maxDrawdown) ? `${Number(m.maxDrawdown).toFixed(1)}%` : (isNum(m.maxDrawdownPct) ? `${Number(m.maxDrawdownPct).toFixed(1)}%` : '—');
                      const period = [bt.symbol, bt.timeframe].filter(Boolean).join(' · ') || '—';
                      // Per-row equity sparkline synthesized from the return (list rows carry
                      // summary metrics, not the full equity series).
                      const spark = (() => {
                        const n = 16, g = Math.max(-0.95, (Number.isFinite(ret) ? ret : 0) / 100);
                        const vals = [];
                        for (let i = 0; i < n; i++) { const t = i / (n - 1); vals.push(100 * (1 + g * t) + 1.4 * Math.sin(3.7 + i * 1.7)); }
                        vals[0] = 100; vals[n - 1] = 100 * (1 + g);
                        return atPathScaled(vals, Math.min(...vals), Math.max(...vals), 720, 150).line;
                      })();
                      const sparkColor = Number.isFinite(ret) && ret < 0 ? AT.negative : AT.accent;
                      return (
                        <Box key={bt.id} onClick={() => openSavedBacktest(bt)} sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 1fr 0.9fr 0.9fr 0.9fr 0.9fr 40px', gap: '12px', alignItems: 'center', p: '13px 4px', borderBottom: '1px solid rgba(255,255,255,.04)', cursor: 'pointer', transition: 'background .15s', '&:hover': { background: 'rgba(255,255,255,.03)' } }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
                            <Box component="span" sx={{ fontSize: '13.5px', fontWeight: 600, color: AT.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{botName}</Box>
                            <Box component="svg" viewBox="0 0 720 150" preserveAspectRatio="none" sx={{ width: 84, height: 22, display: 'block', color: sparkColor }}><path d={spark} fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" /></Box>
                          </Box>
                          <Mono sx={{ fontSize: '12.5px', color: AT.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{period}</Mono>
                          <Mono sx={{ fontSize: '13px', fontWeight: 600, color: retC, textAlign: 'right' }}>{retStr}</Mono>
                          <Mono sx={{ fontSize: '13px', color: '#c9d2e3', textAlign: 'right' }}>{m.sharpeRatio != null ? fmtRatio(m.sharpeRatio) : '—'}</Mono>
                          <Mono sx={{ fontSize: '13px', color: '#c9d2e3', textAlign: 'right' }}>{isNum(m.winRate) ? `${Number(m.winRate).toFixed(0)}%` : '—'}</Mono>
                          <Mono sx={{ fontSize: '13px', color: '#c9d2e3', textAlign: 'right' }}>{dd}</Mono>
                          <Mono sx={{ fontSize: '12.5px', color: '#8593ad', textAlign: 'right' }}>{new Date(bt.created_at || bt.createdAt).toLocaleDateString()}</Mono>
                          {/* Delete straight from the list — deliberately NOT dependent
                              on the row opening, so a report that fails to load can
                              still be cleared out. */}
                          <Tooltip title="Delete this backtest" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteBacktest(bt); }}
                              sx={{ color: AT.muted, '&:hover': { color: AT.negative, background: 'rgba(255,255,255,.06)' } }}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>
            )}

            {bots.length === 0 && (
              <Paper elevation={0} sx={{
                p: 4,
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 2
              }}>
                <Typography variant="h6" gutterBottom>No trading bots available</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Create a trading bot first to run backtests
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setActiveTab('create')}
                  sx={{
                    bgcolor: 'grey.800',
                    '&:hover': { bgcolor: 'grey.700' }
                  }}
                >
                  Create New Bot
                </Button>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === 'advisor' && (
          <AITradingAdvisor
            bots={bots}
            botHistories={liveBotHistories}
            futuresInfo={futuresInfo}
          />
        )}


        <Dialog
          open={confirmDeleteOpen}
          onClose={() => !deleting && setConfirmDeleteOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmber color="warning" />
            Confirm delete
          </DialogTitle>

          <DialogContent dividers>
            <Typography gutterBottom>
              Delete this saved backtest permanently?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {viewingSavedBacktestId}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteSavedBacktest}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(confirmDeleteBotId)}
          onClose={() => !isDeletingBot && setConfirmDeleteBotId(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              Please confirm to delete the bot.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteBotId(null)} disabled={isDeletingBot}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={async () => {
                if (!confirmDeleteBotId) return;
                try {
                  setIsDeletingBot(true);
                  await tradingBotService.delete(confirmDeleteBotId);
                  setBots(prev => prev.filter(b => b.id !== confirmDeleteBotId));
                  setConfirmDeleteBotId(null);
                } catch (err) {
                  console.error('Failed to delete bot:', err);
                } finally {
                  setIsDeletingBot(false);
                }
              }}
              disabled={isDeletingBot}
            >
              {isDeletingBot ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quick Bot: which venue? Only appears with more than one exchange —
            the choice decides whether the starter bot trades a crypto pair or a
            US equity, so it can't be guessed from list order. */}
        <Dialog
          open={quickBotPickerOpen}
          onClose={() => setQuickBotPickerOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Which exchange should this bot use?</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mb: 2 }}>
              Quick Bot sets up an RSI Reversal strategy with safe defaults. The venue
              decides what it trades.
            </Typography>
            <ExchangeSelect
              exchanges={userExchanges}
              value={quickBotExchange}
              onChange={(v) => setQuickBotExchange(v)}
              label="Exchange"
            />
            {quickBotExchange && (() => {
              const cls = getAssetClassFromExchange(quickBotExchange);
              const isStock = cls === 'stock_equity';
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <AssetClassChip assetClass={cls} />
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    Starts on <strong>{isStock ? 'AAPL' : 'BTC/USDT'}</strong>, funded in{' '}
                    <strong>{defaultFundingAssetForClass(isStock ? 'stock_equity' : 'crypto')}</strong>.
                  </Typography>
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQuickBotPickerOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!quickBotExchange}
              onClick={() => { setQuickBotPickerOpen(false); startQuickBot(quickBotExchange); }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete a saved backtest straight from the history list. */}
        <Dialog
          open={Boolean(confirmDeleteBacktest)}
          onClose={() => !deleting && setConfirmDeleteBacktest(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete saved backtest?</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              Delete the saved result for{' '}
              <strong>
                {confirmDeleteBacktest?.bot_name
                  || botsById[confirmDeleteBacktest?.bot_id]
                  || (confirmDeleteBacktest?.bot_id != null ? `Bot #${confirmDeleteBacktest.bot_id}` : 'this bot')}
              </strong>
              {confirmDeleteBacktest?.symbol ? ` (${confirmDeleteBacktest.symbol})` : ''}?
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              This removes the stored report only. The bot and its live trading are unaffected.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteBacktest(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={() => handleDeleteSavedBacktest(confirmDeleteBacktest?.id)}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Resume refused: this bot's logic has never been backtested. Not a
            confirmation dialog — there is no "go anyway" button, because the
            whole point is that untested logic does not reach live capital. The
            way out is to test it, so the dialog does that in one click. */}
        <Dialog open={Boolean(btDeployBlocked)} onClose={() => setBtDeployBlocked(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>Backtest required before going live</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '13.5px', mb: 1.5 }}>
              <strong>{btDeployBlocked?.bot?.name || 'This bot'}</strong> has no saved backtest for the
              logic it is about to trade, so there is no evidence it works.
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: AT.muted, mb: 1.5 }}>
              {(btDeployBlocked?.gate?.checks || []).find(c => c.id === 'tested')?.detail
                || 'No saved run matches this logic.'}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: AT.muted2 }}>
              Webhook bots and scanner profiles are exempt — their signals come from outside the engine.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBtDeployBlocked(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                const b = btDeployBlocked?.bot;
                setBtDeployBlocked(null);
                if (b) { setSelectedBotForBacktest(b); setActiveTab('backtest'); setBtEditOpen(true); }
              }}
            >
              Backtest it now
            </Button>
          </DialogActions>
        </Dialog>

        {/* Redeploying onto a LIVE bot: the strategy swap takes effect on the
            next evaluation, and the bot may already be holding a position
            opened under the old rules. Confirm before touching it. */}
        <Dialog
          open={Boolean(btDeployConfirm)}
          onClose={() => !btDeploying && setBtDeployConfirm(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Update a running bot?</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              <strong>{btDeployConfirm?.name}</strong> is deployed and trading live.
              Saving replaces its entry/exit logic and timeframe immediately — the
              change applies from its next evaluation.
            </Typography>
            <Typography sx={{ mt: 1.5, fontSize: '13.5px', color: 'text.secondary' }}>
              Any position it currently holds was opened under the old strategy and
              will stay open. The new exit logic decides when it closes.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBtDeployConfirm(null)} disabled={btDeploying}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => deployDraft(true)} disabled={btDeploying}>
              {btDeploying ? 'Updating…' : 'Update live bot'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Soft-warning dialog: server returned 409 because the user already
            has an active bot on this exchange + symbol. Clicking "Create
            Anyway" resubmits with confirm_duplicate=true to bypass the check. */}
        <Dialog
          open={!!duplicateBotConflict}
          onClose={() => setDuplicateBotConflict(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmber color="warning" />
            Duplicate Bot Warning
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You already have a bot on{' '}
              <strong>{duplicateBotConflict?.conflict?.symbol}</strong> on this exchange
              {duplicateBotConflict?.conflict?.bot_name && (
                <> — <em>"{duplicateBotConflict.conflict.bot_name}"</em></>
              )}
              {' '}(bot #{duplicateBotConflict?.conflict?.bot_id}).
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Two bots on the same symbol will compete for fills and independently
              size positions. If their entry/exit logic differs, they can fight
              each other — one buying while the other sells.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Create another bot on this symbol anyway?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDuplicateBotConflict(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<AddCircleOutline />}
              onClick={async () => {
                const conflict = duplicateBotConflict;
                if (!conflict) return;
                setDuplicateBotConflict(null);
                const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
                await submitBotPayloadDirect(conflict.pendingPayload, conflict.stopped, selectedExchange);
              }}
            >
              Create Anyway
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmCancelOpen}
          onClose={() => !isCanceling && setConfirmCancelOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmber color="warning" />
            Cancel backtest?
          </DialogTitle>

          <DialogContent dividers>
            <Typography gutterBottom>
              This will signal the running backtest to stop. Progress so far may not be saved.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Job: {liveBacktest?.jobId}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setConfirmCancelOpen(false)} disabled={isCanceling}>
              Keep Running
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              onClick={cancelBacktest}
              disabled={isCanceling}
            >
              {isCanceling ? 'Stopping…' : 'Stop Backtest'}
            </Button>
          </DialogActions>
        </Dialog>


        {/* Backtest Results Dialog */}
        <Dialog
          open={backtestDialogOpen}
          onClose={closeBacktestDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { background: '#0c0f16', backgroundImage: 'none', border: '1px solid rgba(255,255,255,.09)', borderRadius: '16px', color: AT.text, maxWidth: 920 } }}
        >
          {/* header — backtest results modal (handoff) */}
          <Box sx={{ p: '22px 26px 0', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7fb2dc', flex: 'none' }}><BarChart sx={{ fontSize: 16 }} /></Box>
              <Typography sx={{ fontSize: '17px', fontWeight: 700, color: AT.bright }}>Backtest Results: {selectedBotForBacktest?.name || '—'}</Typography>
            </Box>
            <Mono sx={{ fontSize: '11.5px', letterSpacing: '.08em', textTransform: 'uppercase', color: AT.muted2, mt: '9px', ml: '41px', display: 'block' }}>
              {[selectedBotForBacktest?.symbol, selectedBotForBacktest?.interval].filter(Boolean).join(' · ') || `${format(backtestDateRange[0], 'MMM d, yyyy')} – ${format(backtestDateRange[1], 'MMM d, yyyy')}`}
            </Mono>
            <Box sx={{ display: 'flex', gap: '4px', mt: '18px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
              {[['summary', 'SUMMARY'], ['equity', 'EQUITY CURVE'], ['trades', 'TRADES']].map(([val, label]) => {
                const on = activeBacktestTab === val;
                return (
                  <Box key={val} onClick={() => setActiveBacktestTab(val)} sx={{ p: '11px 16px', cursor: 'pointer', fontFamily: AT_MONO, fontSize: '12px', letterSpacing: '.05em', fontWeight: 600, color: on ? '#9cc3e4' : AT.body, borderBottom: on ? '2px solid #4d8fc7' : '2px solid transparent', mb: '-1px', transition: 'color .15s' }}>{label}</Box>
                );
              })}
            </Box>
          </Box>
          <DialogContent sx={{ p: '22px 26px' }}>

            {activeBacktestTab === 'summary' && backtestResults && (() => {
              const m = backtestResults?.metrics || {};
              const bench = m.benchmark || {};
              const selected = selectedBotForBacktest || {};
              const savedCfg = safeParseMaybe(backtestResults?.backtest_config) || safeParseMaybe(backtestResults?.config) || {};
              const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
              const retN = isNum(m.returnPct) ? Number(m.returnPct) : (parseFloat(String(m.totalReturn || '').replace('%', '')) || 0);
              const benchN = isNum(bench.returnPct) ? Number(bench.returnPct) : 0;
              const alphaN = retN - benchN;
              const won = alphaN >= 0;
              const money = (v) => (Number.isFinite(v) ? `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${QUOTE || ''}` : '—');
              const pctv = (v) => (Number.isFinite(v) ? `${Number(v).toFixed(2)}%` : '—');
              const fmtDate = (ms) => (Number.isFinite(ms) ? new Date(ms).toLocaleString() : '—');
              const pnlStr = isNum(m.pnl) ? `${Number(m.pnl) >= 0 ? '+' : '-'}${Math.abs(Number(m.pnl)).toFixed(2)} ${QUOTE || ''}` : '—';
              const metrics = [
                { k: 'Total Trades', v: isNum(m.totalTrades) ? String(Number(m.totalTrades)) : '0', c: AT.bright },
                { k: 'Profit / Loss', v: pnlStr, c: isNum(m.pnl) ? (Number(m.pnl) >= 0 ? AT.positive : AT.negative) : AT.body },
                { k: 'Sharpe Ratio', v: fmtRatio(m.sharpeRatio), c: AT.bright },
                { k: 'Total Return', v: isNum(m.returnPct) ? fmtPct(m.returnPct) : (m.totalReturn ?? '—'), c: retN >= 0 ? AT.positive : AT.negative },
                { k: 'Benchmark Return', v: isNum(bench.returnPct) ? fmtPct(bench.returnPct) : '—', c: AT.body },
                { k: 'Win Rate', v: isNum(m.winRate) ? fmtPct(m.winRate) : '—', c: AT.bright },
              ];
              const cfgRows = [
                { k: 'Symbol', v: savedCfg.symbol ?? selected.symbol ?? '—' },
                { k: 'Timeframe', v: String(savedCfg.timeframe ?? selected.interval ?? '—').toUpperCase() },
                { k: 'Start', v: fmtDate(savedCfg.start_time_ms ?? backtestDateRange?.[0]) },
                { k: 'End', v: fmtDate(savedCfg.end_time_ms ?? backtestDateRange?.[1]) },
                { k: 'Initial Capital', v: money(Number(savedCfg.initialCapital ?? backtestParams.initialCapital) || 0) },
                { k: 'Commission', v: pctv(Number(savedCfg.commission ?? backtestParams.commission)) },
                { k: 'Slippage', v: pctv(Number(savedCfg.slippage ?? backtestParams.slippage)) },
              ];
              const strat = savedCfg.strategy ?? { buy: selected.logic?.buy, sell: selected.logic?.sell };
              const listSx = { border: '1px solid rgba(255,255,255,.07)', borderRadius: '10px', overflow: 'hidden', background: '#0b0e15' };
              const rowSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '14px 16px', borderBottom: '1px solid rgba(255,255,255,.05)' };
              return (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '18px', p: '14px 16px', background: won ? 'rgba(70,184,138,.10)' : 'rgba(217,123,114,.10)', border: '1px solid rgba(255,255,255,.07)', borderRadius: '10px', flexWrap: 'wrap' }}>
                    <Mono sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '.08em', color: '#06121f', background: won ? AT.positive : AT.negative, px: '11px', py: '5px', borderRadius: '6px' }}>{won ? 'OUTPERFORMED' : 'UNDERPERFORMED'}</Mono>
                    <Typography sx={{ fontSize: '13.5px', color: '#cdd6e8' }}>{`vs ${benchN >= 0 ? '+' : ''}${benchN.toFixed(2)}% buy & hold · ${alphaN >= 0 ? '+' : ''}${alphaN.toFixed(2)}% alpha`}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: AT.bright, mb: '12px' }}>Performance Metrics</Typography>
                  <Box sx={{ ...listSx, mb: '26px' }}>
                    {metrics.map((r) => (
                      <Box key={r.k} sx={rowSx}>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#cdd6e8' }}>{r.k}</Typography>
                        <Mono sx={{ fontSize: '13.5px', fontWeight: 600, color: r.c }}>{r.v}</Mono>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: AT.bright, mb: '12px' }}>Backtest &amp; Strategy Config</Typography>
                  <Box sx={listSx}>
                    {cfgRows.map((r) => (
                      <Box key={r.k} sx={rowSx}>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#cdd6e8' }}>{r.k}</Typography>
                        <Mono sx={{ fontSize: '13.5px', color: '#e8edf7' }}>{r.v}</Mono>
                      </Box>
                    ))}
                    <Box sx={{ p: '14px 16px', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
                      <Mono sx={{ fontSize: '9.5px', letterSpacing: '.06em', color: AT.positive, mb: '6px', display: 'block' }}>BUY LOGIC</Mono>
                      <Box sx={{ fontFamily: AT_MONO, fontSize: '12px', color: '#c9d2e3', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{strat?.buy || '—'}</Box>
                    </Box>
                    <Box sx={{ p: '14px 16px' }}>
                      <Mono sx={{ fontSize: '9.5px', letterSpacing: '.06em', color: AT.negative, mb: '6px', display: 'block' }}>SELL LOGIC</Mono>
                      <Box sx={{ fontFamily: AT_MONO, fontSize: '12px', color: '#c9d2e3', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{strat?.sell || '—'}</Box>
                    </Box>
                  </Box>
                </Box>
              );
            })()}

            {activeBacktestTab === 'equity' && backtestResults && (() => {
              const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
              return (
                <Box>
                  <Mono sx={{ fontSize: '11px', letterSpacing: '.08em', color: AT.muted2, mb: '12px', display: 'block' }}>EQUITY CURVE</Mono>
                  <Box sx={{ height: 340, background: '#0b0e15', border: '1px solid rgba(255,255,255,.07)', borderRadius: '10px', p: '12px' }}>
                    {Array.isArray(backtestResults.equityCurve) && backtestResults.equityCurve.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={toChartData(backtestResults.equityCurve)} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                          <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(t) => new Date(t).toLocaleDateString()} minTickGap={60} tick={{ fill: '#6f7c96', fontSize: 11 }} stroke="rgba(255,255,255,.1)" />
                          <YAxis dataKey="equity" tickFormatter={(v) => `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${QUOTE || ''}`} width={80} tick={{ fill: '#6f7c96', fontSize: 11 }} stroke="rgba(255,255,255,.1)" />
                          <RechartsTooltip contentStyle={{ background: '#0c0f16', border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, color: '#e8edf7' }} labelFormatter={(t) => new Date(t).toLocaleString()} formatter={(value) => [`${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${QUOTE || ''}`, 'Equity']} />
                          <Line type="monotone" dataKey="equity" dot={false} strokeWidth={2} stroke={AT.accent} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: AT.muted }}>No equity data.</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })()}

            {activeBacktestTab === 'trades' && backtestResults && (() => {
              const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
              const rows = (Array.isArray(backtestResults.trades) ? backtestResults.trades : []).slice().reverse();
              return (
                <Box>
                  <Mono sx={{ fontSize: '11px', letterSpacing: '.08em', color: AT.muted2, mb: '10px', display: 'block' }}>{`TRADE HISTORY (${rows.length})`}</Mono>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 520 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 1fr 1fr 1fr', gap: '12px', p: '0 4px 11px', borderBottom: '1px solid rgba(255,255,255,.07)', fontFamily: AT_MONO, fontSize: '10.5px', letterSpacing: '.08em', color: AT.muted }}>
                        <span>TIME</span><span>SIDE</span><span style={{ textAlign: 'right' }}>PRICE</span><span style={{ textAlign: 'right' }}>QTY</span><span style={{ textAlign: 'right' }}>P&amp;L</span>
                      </Box>
                      {rows.length === 0 && <Typography sx={{ fontSize: '13px', color: AT.muted, py: 2 }}>No trades.</Typography>}
                      {rows.map((tr, i) => {
                        const side = String(tr.side || tr.type || '').toUpperCase();
                        const sc = side === 'BUY' ? AT.positive : side === 'SELL' ? AT.negative : AT.accent;
                        const pnl = Number(tr.pnl ?? tr.profit);
                        return (
                          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 1fr 1fr 1fr', gap: '12px', alignItems: 'center', p: '12px 4px', borderBottom: '1px solid rgba(255,255,255,.04)', fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: '12.5px' }}>
                            <Box component="span" sx={{ color: AT.body }}>{tr.time || tr.timestamp || tr.date ? new Date(tr.time || tr.timestamp || tr.date).toLocaleString() : '—'}</Box>
                            <Box component="span"><Box component="span" sx={{ display: 'inline-flex', fontSize: '11px', fontWeight: 700, color: sc, background: `${sc}1f`, px: '10px', py: '3px', borderRadius: '6px' }}>{side || '—'}</Box></Box>
                            <Box component="span" sx={{ color: '#c9d2e3', textAlign: 'right' }}>{isNum(tr.price) ? Number(tr.price).toLocaleString() : '—'}</Box>
                            <Box component="span" sx={{ color: AT.body, textAlign: 'right' }}>{isNum(tr.qty ?? tr.amount) ? Number(tr.qty ?? tr.amount).toLocaleString(undefined, { maximumFractionDigits: 6 }) : '—'}</Box>
                            <Box component="span" sx={{ color: Number.isFinite(pnl) ? (pnl >= 0 ? AT.positive : AT.negative) : AT.body, fontWeight: 600, textAlign: 'right' }}>{Number.isFinite(pnl) ? `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ${QUOTE || ''}` : '—'}</Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              );
            })()}

          </DialogContent>
          {/* footer — backtest results modal (handoff) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', p: '16px 26px', borderTop: '1px solid rgba(255,255,255,.08)', flexShrink: 0, flexWrap: 'wrap' }}>
            <Box component="button" onClick={closeBacktestDialog} sx={{ fontFamily: AT_SANS, fontSize: '13.5px', fontWeight: 600, color: '#cdd6e8', background: 'transparent', border: '1px solid rgba(255,255,255,.14)', borderRadius: '9px', px: '20px', py: '11px', cursor: 'pointer', transition: 'border-color .2s', '&:hover': { borderColor: 'rgba(255,255,255,.3)' } }}>Close</Box>
            {viewingSavedBacktestId && (
              <Box component="button" onClick={() => setConfirmDeleteOpen(true)} sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: AT_SANS, fontSize: '13.5px', fontWeight: 600, color: AT.negative, background: 'rgba(217,123,114,.08)', border: '1px solid rgba(217,123,114,.4)', borderRadius: '9px', px: '18px', py: '11px', cursor: 'pointer', transition: 'background .2s', '&:hover': { background: 'rgba(217,123,114,.16)' } }}><Delete sx={{ fontSize: 15 }} /> Delete</Box>
            )}
            {!isSavedView && (
              <Box component="button" onClick={handleSaveBacktest} disabled={!backtestResults || !selectedBotForBacktest} sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: AT_SANS, fontSize: '13.5px', fontWeight: 600, color: '#9cc3e4', background: 'rgba(77,143,199,.08)', border: '1px solid rgba(77,143,199,.4)', borderRadius: '9px', px: '18px', py: '11px', cursor: (!backtestResults || !selectedBotForBacktest) ? 'default' : 'pointer', opacity: (!backtestResults || !selectedBotForBacktest) ? 0.5 : 1, transition: 'background .2s', '&:hover': { background: 'rgba(77,143,199,.16)' } }}><CheckCircle sx={{ fontSize: 15 }} /> Save backtest</Box>
            )}
            {/* Was toggleBotStatus() — a TOGGLE, so on an already-running bot the
                "Activate" button paused it. Now it only ever activates, and says
                so when the bot is already live. */}
            <Box component="button"
              onClick={() => {
                setBacktestDialogOpen(false);
                const bot = selectedBotForBacktest;
                if (!bot?.id) return;
                if (bot.status === 'active' && !btDraftDirty) {
                  showToast('info', `"${bot.name}" is already deployed and running live.`);
                  return;
                }
                setBtEditOpen(true);
                deployDraft(false);
              }}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: AT_SANS, fontSize: '13.5px', fontWeight: 600, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', borderRadius: '9px', px: '20px', py: '11px', cursor: 'pointer', transition: 'transform .2s', '&:hover': { transform: 'translateY(-1px)' } }}>
              <PlayArrow sx={{ fontSize: 15 }} />
              {selectedBotForBacktest?.status === 'active'
                ? (btDraftDirty ? 'Update live bot' : 'Already deployed')
                : 'Deploy this strategy'}
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Box>
    </ThemeProvider>
  );
}